import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../hooks/useAuth';
import { useQueryClient, useInfiniteQuery, useQuery } from '@tanstack/react-query';
import { useSortableData } from '../hooks/useSortableData'; // ✅ ADD THIS LINE
import api from '../services/api';
// ✅ XLSX is now lazy loaded - removes ~500KB from initial bundle
import '../index.css';
import { VoiceTaskManager } from '../components/voiceTaskManager';
import GTMHub from './GTMHub';
import UseCaseManager from './UseCaseManager';
import CompetitiveIntel from './CompetitiveIntel';
import Competitions from './Competitions';
import ROICalculator from './ROICalculator';
import AIAssistant from './AIAssistant';
import NetworkGraphNew from './NetworkGraphNew';
import WorldRelationshipView from './WorldRelationshipView';
import DiscoveryQA from './DiscoveryQA';
import contexqLogo from '../assets/contexq-logo.jpg';


// Sortable Table Header Component - IMPROVED VISIBILITY
function SortableTableHeader({ label, sortKey, currentSort, onSort }) {
    const isSorted = currentSort?.key === sortKey;
    const direction = currentSort?.direction;
    
    return (
        <th 
            onClick={() => onSort(sortKey)}
            style={{ 
                cursor: 'pointer', 
                userSelect: 'none',
                position: 'relative',
                paddingRight: '32px',
                whiteSpace: 'nowrap'
            }}
            onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = '#f3f4f6';
            }}
            onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = '';
            }}
        >
            <div style={{ 
                display: 'flex', 
                alignItems: 'center', 
                gap: '8px',
                justifyContent: 'space-between'
            }}>
                <span>{label}</span>
                <span style={{ 
                    fontSize: '14px',
                    fontWeight: 'bold',
                    color: isSorted ? '#667eea' : '#9ca3af',
                    transition: 'all 0.2s',
                    minWidth: '12px',
                    textAlign: 'center'
                }}>
                    {isSorted ? (direction === 'ascending' ? '↑' : '↓') : '↕'}
                </span>
            </div>
        </th>
    );
}

// ✅ LAZY LOAD XLSX - Only loads when user clicks export/import
let xlsxModule = null;
const getXLSX = async () => {
    if (!xlsxModule) {
        xlsxModule = await import('xlsx');
    }
    return xlsxModule;
};

// Utility function for Excel Export (lazy loaded)
const exportToExcel = async (data, filename) => {
    const XLSX = await getXLSX();
    const ws = XLSX.utils.json_to_sheet(data);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Sheet1");
    XLSX.writeFile(wb, `${filename}_${new Date().toISOString().split('T')[0]}.xlsx`);
};

// Utility function for Excel Import (lazy loaded)
const importFromExcel = async (file, callback) => {
    const XLSX = await getXLSX();
    const reader = new FileReader();
    reader.onload = (e) => {
        const data = new Uint8Array(e.target.result);
        const workbook = XLSX.read(data, { type: 'array' });
        const firstSheet = workbook.Sheets[workbook.SheetNames[0]];
        const jsonData = XLSX.utils.sheet_to_json(firstSheet);
        callback(jsonData);
    };
    reader.readAsArrayBuffer(file);
};

// Loading state wrapper component
function LoadingOverlay({ show, message = 'Saving...' }) {
    if (!show) return null;
    
    return (
        <div style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0, 0, 0, 0.3)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 9999
        }}>
            <div style={{
                background: 'white',
                padding: '24px 48px',
                borderRadius: '12px',
                boxShadow: '0 8px 32px rgba(0, 0, 0, 0.2)',
                display: 'flex',
                alignItems: 'center',
                gap: '16px'
            }}>
                <div className="loading-spinner"></div>
                <span style={{ fontSize: '16px', fontWeight: '500' }}>{message}</span>
            </div>
        </div>
    );
}

// Toast notification component
function Toast({ message, type = 'success', onClose }) {
    useEffect(() => {
        const timer = setTimeout(onClose, 3000);
        return () => clearTimeout(timer);
    }, [onClose]);

    return (
        <div className={`toast ${type}`}>
            <span>{type === 'success' ? '✓' : '✗'}</span>
            <span>{message}</span>
        </div>
    );
}

// Optimistic update helper
const optimisticUpdate = (currentData, newItem, operation = 'add') => {
    switch(operation) {
        case 'add':
            return [...currentData, { ...newItem, Id: Date.now() }]; // Temporary ID
        case 'update':
            return currentData.map(item => item.Id === newItem.Id ? { ...item, ...newItem } : item);
        case 'delete':
            return currentData.filter(item => item.Id !== newItem);
        default:
            return currentData;
    }
};

// Chunked import helper - ADD THIS HERE
async function importInChunks(data, apiMethod, chunkSize = 10) {
    const chunks = [];
    for (let i = 0; i < data.length; i += chunkSize) {
        chunks.push(data.slice(i, i + chunkSize));
    }
    
    let processed = 0;
    for (const chunk of chunks) {
        await Promise.all(chunk.map(item => apiMethod(item)));
        processed += chunk.length;
        console.log(`Imported ${processed}/${data.length} items`);
    }
}

// Parse JSON field helper
const parseJsonField = (field) => {
    if (!field) return [];
    try {
        return typeof field === 'string' ? JSON.parse(field) : field;
    } catch {
        return [];
    }
};

// Main CRM Application Component
// Main CRM Application Component - OPTIMIZED
function CRMApp() {
    const { account, accounts, loading: authLoading, login, logout } = useAuth();
    const queryClient = useQueryClient();
    const [activeTab, setActiveTab] = useState(() => {
        try {
            const fromPath = (window.location.pathname || '/').replace(/^\//, '');
            if (fromPath) return fromPath;
            return localStorage.getItem('contexq-active-tab') || 'dashboard';
        } catch {
            return 'dashboard';
        }
    });

    // Keep URL path + localStorage in sync with active tab
    useEffect(() => {
        try {
            const currentPath = (window.location.pathname || '/').replace(/^\//, '');
            if (currentPath !== activeTab) {
                window.history.pushState(null, '', `/${activeTab}`);
            }
            localStorage.setItem('contexq-active-tab', activeTab);
        } catch {}
    }, [activeTab]);

    // Respond to browser back/forward → switch tab when URL changes
    useEffect(() => {
        const onPopState = () => {
            const fromPath = (window.location.pathname || '/').replace(/^\//, '');
            if (fromPath && fromPath !== activeTab) setActiveTab(fromPath);
        };
        window.addEventListener('popstate', onPopState);
        return () => window.removeEventListener('popstate', onPopState);
    }, [activeTab]);
    const [showSalesIntel, setShowSalesIntel] = useState(false);
    const [authState, setAuthState] = useState({ checking: true, authorized: false, isAdmin: false, allowedTabs: [], error: null });

    const userEmail = accounts?.[0]?.username || '';

    useEffect(() => {
        if (!userEmail) return;
        // Local dev bypass: skip /api/auth-check entirely on localhost so the
        // developer doesn't get stuck on Access Denied when running npm start.
        const isLocalDev = typeof window !== 'undefined' && window.location.hostname === 'localhost';
        if (isLocalDev) {
            setAuthState({
                checking: false,
                authorized: true,
                isAdmin: true,
                allowedTabs: ['dashboard','deals','activities','investors','partnerships','internal','externalContacts','network','ai','competitors','usecases','gtm','roi','discovery'],
                error: null,
            });
            return;
        }

        // Cold-start tolerance: a 500/network blip on the first call is NOT a denial.
        // Only a clean HTTP 200 from /api/auth-check is a real authorization decision.
        // Retry up to 4 times with backoff so a sleeping Function App / SQL pool
        // doesn't render Access Denied to legitimate users.
        let cancelled = false;
        const sleep = (ms) => new Promise((res) => setTimeout(res, ms));

        const checkAccess = async () => {
            const API_BASE = process.env.REACT_APP_API_URL || '/api';
            const url = `${API_BASE}/auth-check?email=${encodeURIComponent(userEmail)}`;
            const maxAttempts = 4;

            for (let attempt = 1; attempt <= maxAttempts; attempt++) {
                try {
                    const r = await fetch(url, { credentials: 'same-origin' });
                    if (r.ok) {
                        const data = await r.json();
                        if (cancelled) return;
                        setAuthState({
                            checking: false,
                            authorized: !!data.authorized,
                            isAdmin: !!data.isAdmin,
                            allowedTabs: data.allowedTabs || [],
                            error: null,
                        });
                        return;
                    }
                    console.warn(`auth-check attempt ${attempt}: HTTP ${r.status}`);
                } catch (e) {
                    console.warn(`auth-check attempt ${attempt} threw:`, e?.message || e);
                }
                if (attempt < maxAttempts) await sleep(attempt * 800); // 0.8s, 1.6s, 2.4s
            }

            // All retries exhausted -> transient backend error, NOT a denial.
            if (!cancelled) {
                setAuthState({ checking: false, authorized: false, isAdmin: false, allowedTabs: [], error: 'transient' });
            }
        };

        setAuthState((s) => ({ ...s, checking: true, error: null }));
        checkAccess();
        return () => { cancelled = true; };
    }, [userEmail]);
    const [data, setData] = useState({
        deals: [],
        activities: [],
        investors: [],
        partnerships: [],
        internal: [],
        externalContacts: []
    });
    const [searchTerm, setSearchTerm] = useState('');
    const [loading, setLoading] = useState(true);
    const [loadingMessage, setLoadingMessage] = useState('Connecting to server...');
    const [error, setError] = useState(null);
    const [globalLoading, setGlobalLoading] = useState(false);
    const [toast, setToast] = useState(null);

    useEffect(() => {
        if (userEmail) {
            loadAllData();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [userEmail]);

    const showToast = (message, type = 'success') => {
        setToast({ message, type });
    };

    const loadAllData = async () => {
        try {
            setLoading(true);
            setError(null);
            setLoadingMessage('Connecting to server...');

            // ✅ Show progress to user during cold start
            const loadingTimer = setTimeout(() => {
                setLoadingMessage('Waking up the server (first load may take a moment)...');
            }, 3000);

            const loadingTimer2 = setTimeout(() => {
                setLoadingMessage('Almost there, fetching your data...');
            }, 8000);

            // ✅ ONE API CALL - MUCH FASTER!
            const bulkData = await api.getAllData(true);

            // Clear timers once data is received
            clearTimeout(loadingTimer);
            clearTimeout(loadingTimer2);

            console.log('✅ Bulk data received:', bulkData);

            setData({
                deals: Array.isArray(bulkData?.deals) ? bulkData.deals : [],
                activities: Array.isArray(bulkData?.activities) ? bulkData.activities : [],
                investors: Array.isArray(bulkData?.investors) ? bulkData.investors : [],
                partnerships: Array.isArray(bulkData?.partnerships) ? bulkData.partnerships : [],
                internal: Array.isArray(bulkData?.internal) ? bulkData.internal : [],
                externalContacts: Array.isArray(bulkData?.externalContacts) ? bulkData.externalContacts : []
            });

            // Prefetch REMOVED — was firing 3 background calls (competitions,
            // gtm-accounts, use-cases) on every login. Killed since each tab now
            // handles its own load-on-open + TanStack cache. First click on
            // those tabs pays ~300 ms one-time; every login saves 3 roundtrips.

            console.log('✅ Data loaded successfully:', {
                deals: bulkData?.deals?.length || 0,
                activities: bulkData?.activities?.length || 0,
                investors: bulkData?.investors?.length || 0,
                partnerships: bulkData?.partnerships?.length || 0,
                internal: bulkData?.internal?.length || 0,
                externalContacts: bulkData?.externalContacts?.length || 0,
                timestamp: bulkData?.timestamp
            });

        } catch (error) {
            console.error('❌ Error loading data:', error);
            setError('Failed to load data. Please click Retry.');
            // ✅ SET EMPTY ARRAYS ON ERROR
            setData({
                deals: [],
                activities: [],
                investors: [],
                partnerships: [],
                internal: [],
                externalContacts: []
            });
        } finally {
            setLoading(false);
        }
    };

    // Optimistic local removal helpers — used by Delete handlers in views to
    // make rows vanish immediately. If the API later fails, the caller can
    // invoke loadAllData() to restore from server.
    const removeActivitiesLocal = (ids) => {
        const idSet = new Set(ids);
        setData(prev => ({ ...prev, activities: prev.activities.filter(a => !idSet.has(a.Id)) }));
    };
    const addActivitiesLocal = (items) => {
        if (!Array.isArray(items) || items.length === 0) return;
        setData(prev => ({ ...prev, activities: [...items, ...prev.activities] }));
    };

    // Generic local-state helpers for all entity types
    const upsertLocal = (key, item) => {
        if (!item || !item.Id) return;
        setData(prev => {
            const list = prev[key] || [];
            const exists = list.some(x => x.Id === item.Id);
            const next = exists ? list.map(x => x.Id === item.Id ? item : x) : [item, ...list];
            return { ...prev, [key]: next };
        });
    };
    const addDealsLocal           = (item) => upsertLocal('deals', item);
    const addInvestorsLocal       = (item) => upsertLocal('investors', item);
    const addPartnershipsLocal    = (item) => upsertLocal('partnerships', item);
    const addExternalContactsLocal= (item) => upsertLocal('externalContacts', item);
    const addInternalLocal        = (item) => upsertLocal('internal', item);
    const upsertActivityLocal     = (item) => upsertLocal('activities', item);
    const removeDealsLocal = (ids) => {
        const idSet = new Set(ids);
        setData(prev => ({ ...prev, deals: prev.deals.filter(d => !idSet.has(d.Id)) }));
    };
    const removeInvestorsLocal = (ids) => {
        const idSet = new Set(ids);
        setData(prev => ({ ...prev, investors: prev.investors.filter(i => !idSet.has(i.Id)) }));
    };
    const removePartnershipsLocal = (ids) => {
        const idSet = new Set(ids);
        setData(prev => ({ ...prev, partnerships: prev.partnerships.filter(p => !idSet.has(p.Id)) }));
    };
    const removeExternalContactsLocal = (ids) => {
        const idSet = new Set(ids);
        setData(prev => ({ ...prev, externalContacts: prev.externalContacts.filter(c => !idSet.has(c.Id)) }));
    };
    const removeInternalLocal = (ids) => {
        const idSet = new Set(ids);
        setData(prev => ({ ...prev, internal: prev.internal.filter(p => !idSet.has(p.Id)) }));
    };

    // Optimized data change handlers
    const handleDataChange = async (category, operation, item) => {
        // Optimistic UI update
        setData(prevData => ({
            ...prevData,
            [category]: optimisticUpdate(prevData[category], item, operation)
        }));

        // Show success immediately
        const messages = {
            add: 'Added successfully',
            update: 'Updated successfully',
            delete: 'Deleted successfully'
        };
        showToast(messages[operation] || 'Success');

        // Refresh data in background
        try {
            // Only refresh the specific category for faster updates
            const refreshMap = {
                deals: api.getDeals,
                activities: api.getActivities,
                investors: api.getInvestors,
                partnerships: api.getPartnerships,
                
                internal: api.getInternal,
                externalContacts: api.getExternalContacts
            };

            if (refreshMap[category]) {
                const updatedData = await refreshMap[category]();
                setData(prevData => ({
                    ...prevData,
                    [category]: updatedData || []
                }));
            }
        } catch (error) {
            console.error('Error refreshing data:', error);
            showToast('Data refreshed with errors', 'error');
        }
    };

    const handleLogout = () => logout();

    if (authLoading) {
        return (
            <div style={{
                minHeight: '100vh',
                width: '100%',
                background: 'linear-gradient(135deg, #0c2340 0%, #1e3a8a 50%, #2563eb 100%)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: '#fff',
                fontFamily: "-apple-system,BlinkMacSystemFont,'Segoe UI',Roboto,Helvetica,Arial,sans-serif",
                fontSize: 14
            }}>Checking sign-in status…</div>
        );
    }

    if (!account) {
        return (
            <div style={{
                minHeight: '100vh',
                width: '100%',
                background: 'linear-gradient(135deg, #0c2340 0%, #1e3a8a 50%, #2563eb 100%)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                padding: '24px',
                fontFamily: "-apple-system,BlinkMacSystemFont,'Segoe UI',Roboto,Helvetica,Arial,sans-serif"
            }}>
                <div style={{
                    width: '100%',
                    maxWidth: 440,
                    background: '#ffffff',
                    borderRadius: 16,
                    padding: '44px 40px 36px 40px',
                    boxShadow: '0 20px 50px rgba(12,35,64,0.25), 0 4px 12px rgba(12,35,64,0.08)',
                    textAlign: 'center'
                }}>
                    <img src={contexqLogo} alt="ContexQ" style={{ width: 150, height: 'auto', display: 'block', margin: '0 auto 28px auto' }} />
                    <h1 style={{
                        margin: '0 0 8px 0',
                        fontSize: 24,
                        fontWeight: 700,
                        color: '#0c2340',
                        letterSpacing: '-0.4px'
                    }}>Enterprise CRM</h1>
                    <p style={{
                        margin: '0 0 28px 0',
                        color: '#64748b',
                        fontSize: 14,
                        lineHeight: 1.5
                    }}>Sign in with your ContexQ account to continue.</p>
                    <button onClick={() => login('aad')} style={{
                        width: '100%',
                        display: 'inline-flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: 12,
                        background: '#0c2340',
                        backgroundImage: 'linear-gradient(135deg,#0c2340 0%,#1e40af 60%,#2563eb 100%)',
                        color: '#ffffff',
                        border: 'none',
                        borderRadius: 10,
                        padding: '13px 20px',
                        fontSize: 15,
                        fontWeight: 600,
                        cursor: 'pointer',
                        boxShadow: '0 4px 12px rgba(12,35,64,0.25)',
                        transition: 'transform 0.1s ease, box-shadow 0.1s ease'
                    }}
                    onMouseDown={e => { e.currentTarget.style.transform = 'translateY(1px)'; }}
                    onMouseUp={e => { e.currentTarget.style.transform = 'translateY(0)'; }}
                    onMouseLeave={e => { e.currentTarget.style.transform = 'translateY(0)'; }}
                    >
                        <svg width="18" height="18" viewBox="0 0 21 21" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
                            <rect x="1"  y="1"  width="9" height="9" fill="#f25022"/>
                            <rect x="11" y="1"  width="9" height="9" fill="#7fba00"/>
                            <rect x="1"  y="11" width="9" height="9" fill="#00a4ef"/>
                            <rect x="11" y="11" width="9" height="9" fill="#ffb900"/>
                        </svg>
                        Sign in with Microsoft
                    </button>
                    <div style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 12,
                        margin: '18px 0',
                        color: '#94a3b8',
                        fontSize: 11,
                        letterSpacing: 0.6,
                        textTransform: 'uppercase'
                    }}>
                        <div style={{ flex: 1, height: 1, background: '#e5e7eb' }} />
                        or
                        <div style={{ flex: 1, height: 1, background: '#e5e7eb' }} />
                    </div>
                    <button onClick={() => login('google')} style={{
                        width: '100%',
                        display: 'inline-flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: 12,
                        background: '#ffffff',
                        color: '#1f2937',
                        border: '1px solid #d1d5db',
                        borderRadius: 10,
                        padding: '13px 20px',
                        fontSize: 15,
                        fontWeight: 600,
                        cursor: 'pointer',
                        boxShadow: '0 1px 3px rgba(0,0,0,0.05)',
                        transition: 'transform 0.1s ease, box-shadow 0.1s ease, background 0.15s ease'
                    }}
                    onMouseDown={e => { e.currentTarget.style.transform = 'translateY(1px)'; }}
                    onMouseUp={e => { e.currentTarget.style.transform = 'translateY(0)'; }}
                    onMouseLeave={e => { e.currentTarget.style.transform = 'translateY(0)'; e.currentTarget.style.background = '#ffffff'; }}
                    onMouseEnter={e => { e.currentTarget.style.background = '#f9fafb'; }}
                    >
                        <svg width="18" height="18" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
                            <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                            <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                            <path fill="#FBBC05" d="M5.84 14.1c-.22-.66-.35-1.36-.35-2.1s.13-1.44.35-2.1V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l3.66-2.83z"/>
                            <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                        </svg>
                        Sign in with Google
                    </button>
                    <div style={{
                        marginTop: 28,
                        paddingTop: 20,
                        borderTop: '1px solid #f1f5f9',
                        fontSize: 11,
                        color: '#94a3b8',
                        letterSpacing: 0.3
                    }}>
                        Authorized personnel only · ContexQ Pte Ltd
                    </div>
                </div>
            </div>
        );
    }

    if (loading) {
        return (
            <div style={{
                minHeight: '100vh',
                width: '100%',
                background: 'linear-gradient(135deg, #0c2340 0%, #1e3a8a 50%, #2563eb 100%)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                padding: '24px',
                fontFamily: "-apple-system,BlinkMacSystemFont,'Segoe UI',Roboto,Helvetica,Arial,sans-serif"
            }}>
                <style>{`@keyframes contexq-spin{to{transform:rotate(360deg)}}`}</style>
                <div style={{
                    width: '100%',
                    maxWidth: 440,
                    background: '#ffffff',
                    borderRadius: 16,
                    padding: '44px 40px 40px 40px',
                    boxShadow: '0 20px 50px rgba(12,35,64,0.25), 0 4px 12px rgba(12,35,64,0.08)',
                    textAlign: 'center'
                }}>
                    <img src={contexqLogo} alt="ContexQ" style={{ width: 150, height: 'auto', display: 'block', margin: '0 auto 28px auto' }} />
                    <h1 style={{
                        margin: '0 0 8px 0',
                        fontSize: 24,
                        fontWeight: 700,
                        color: '#0c2340',
                        letterSpacing: '-0.4px'
                    }}>Enterprise CRM</h1>
                    <p style={{
                        margin: '0 0 28px 0',
                        color: '#64748b',
                        fontSize: 14,
                        lineHeight: 1.5
                    }}>{loadingMessage || 'Loading…'}</p>
                    <div style={{
                        width: 36,
                        height: 36,
                        margin: '0 auto',
                        border: '3px solid #e5e7eb',
                        borderTopColor: '#2563eb',
                        borderRadius: '50%',
                        animation: 'contexq-spin 0.8s linear infinite'
                    }} />
                </div>
            </div>
        );
    }

    // Don't block the entire app on error — show error banner but still render nav + new tabs

    const ALL_TABS = [
        { id: 'dashboard', label: 'Dashboard' },
        { id: 'deals', label: 'Deals' },
        { id: 'activities', label: 'Activities & Tasks' },
        { id: 'investors', label: 'Investors' },
        { id: 'opportunities', label: 'Opportunities' },
        { id: 'partnerships', label: 'Partnerships' },
        { id: 'internal', label: 'Internal' },
        { id: 'externalContacts', label: 'External Contacts' },
        { id: 'network', label: 'Network Graph' },
        { id: 'ai', label: 'SalesActivity' },
        { id: 'competitors', label: 'Competitor' },
        { id: 'usecases', label: 'Use-cases' },
        { id: 'gtm', label: 'GTMStrategy' },
        { id: 'roi', label: 'ROI Calculator' },
        { id: 'discovery', label: 'Discovery Q&A' },
    ];

    // Filter tabs based on user's allowed list. Admins see everything + the Admin tab.
    const tabs = (authState.isAdmin
        ? [...ALL_TABS, { id: 'admin', label: '⚙ Admin' }]
        : ALL_TABS.filter(t => authState.allowedTabs.includes(t.id)));

    const salesIntelTabs = [];

   
    const renderContent = () => {


        // ✅ SAFE DATA ACCESS WITH FALLBACKS - CRITICAL!
        const safeData = {
            deals: data?.deals || [],
            activities: data?.activities || [],
            investors: data?.investors || [],
            partnerships: data?.partnerships || [],
            internal: data?.internal || [],
            externalContacts: data?.externalContacts || []
        };


        // 🔍 DEBUG: Check safeData structure
        console.log('🔍 renderContent safeData:', {
            hasActivities: !!safeData.activities,
            activitiesLength: safeData.activities?.length,
            activitiesSample: safeData.activities?.[0]
        });


        // Common props to pass to all views
        const commonProps = {
            searchTerm,
            onDataChange: loadAllData,
            showToast,
            setGlobalLoading
        };

        switch(activeTab) {
            case 'dashboard':
                return <Dashboard 
                    data={safeData} 
                    onNavigate={setActiveTab} 
                    onDataChange={loadAllData} 
                />;
            
            case 'deals':
                return <DealsView
                    data={safeData.deals}
                    onLocalRemove={removeDealsLocal}
                    onLocalUpsert={addDealsLocal}
                    {...commonProps}
                />;

            case 'activities':
                return <ActivitiesTab
                    activitiesProps={{
                        data: safeData.activities,
                        internal: safeData.internal,
                        deals: safeData.deals,
                        investors: safeData.investors,
                        onLocalRemove: removeActivitiesLocal,
                        onLocalAdd: addActivitiesLocal,
                        onLocalUpsert: upsertActivityLocal,
                        ...commonProps,
                    }}
                    showToast={showToast}
                    internalTeam={safeData.internal}
                />;
            
            case 'investors':
                return <InvestorsView
                    data={safeData.investors}
                    onLocalRemove={removeInvestorsLocal}
                    onLocalUpsert={addInvestorsLocal}
                    {...commonProps}
                />;

            case 'opportunities':
                return <OpportunitiesTab
                    data={safeData.investors}
                    onLocalRemove={removeInvestorsLocal}
                    onLocalUpsert={addInvestorsLocal}
                    {...commonProps}
                />;

            case 'partnerships':
                return <PartnershipsView
                    data={safeData.partnerships}
                    onLocalRemove={removePartnershipsLocal}
                    onLocalUpsert={addPartnershipsLocal}
                    {...commonProps}
                />;

            case 'internal':
                return <InternalView
                    data={safeData.internal}
                    deals={safeData.deals}
                    onLocalRemove={removeInternalLocal}
                    onLocalUpsert={addInternalLocal}
                    {...commonProps}
                />;
            
            case 'externalContacts':
                return <ExternalContactsView
                    data={safeData.externalContacts}
                    internal={safeData.internal}
                    onLocalRemove={removeExternalContactsLocal}
                    onLocalUpsert={addExternalContactsLocal}
                    {...commonProps}
                />;
            
            case 'gtm':
                return <GTMHub />;

            case 'usecases':
                return <UseCaseManager />;

            case 'competitors':
                return <Competitions />;

            case 'roi':
                return <ROICalculator />;

            case 'network':
                return <NetworkGraphNew />;

            case 'worldmap':
                return <WorldRelationshipView />;

            case 'ai':
                return <AIAssistant data={safeData} />;

            case 'discovery':
                return <DiscoveryQA />;

            case 'admin':
                return <AdminUsersPanel callerEmail={userEmail} />;

            default:
                return <Dashboard 
                    data={safeData} 
                    onNavigate={setActiveTab} 
                    onDataChange={loadAllData} 
                />;
        }
    };


    // ===== Access gate =====
    if (authState.checking) {
        return (
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100vh', color: '#6b7280', fontSize: 14 }}>
                Checking access…
            </div>
        );
    }
    // Transient backend failure (cold start / network blip) - NOT a real denial.
    // Show a "waking up" screen with a Retry button instead of the lock icon, so
    // legitimate users don't get told they're denied when the DB is just resuming.
    if (authState.error === 'transient') {
        return (
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100vh', background: '#f8fafc' }}>
                <div style={{ background: '#fff', borderRadius: 16, padding: 40, maxWidth: 440, boxShadow: '0 10px 30px rgba(0,0,0,0.08)', textAlign: 'center' }}>
                    <div style={{ fontSize: 48, marginBottom: 12 }}>⚠️</div>
                    <h2 style={{ margin: 0, color: '#111827' }}>Couldn't verify access</h2>
                    <p style={{ color: '#6b7280', marginTop: 12, lineHeight: 1.6 }}>
                        This is usually temporary while the service wakes up. Please try again in a few seconds.
                    </p>
                    <p style={{ color: '#9ca3af', fontSize: 12, marginTop: 16 }}>Signed in as <strong>{userEmail}</strong></p>
                    <button onClick={() => window.location.reload()} style={{ marginTop: 16, padding: '8px 16px', borderRadius: 8, border: '1px solid #2563eb', background: '#2563eb', color: '#fff', cursor: 'pointer', fontSize: 13, fontWeight: 600 }}>Retry</button>
                    <button onClick={handleLogout} style={{ marginTop: 16, marginLeft: 8, padding: '8px 16px', borderRadius: 8, border: '1px solid #e5e7eb', background: '#fff', cursor: 'pointer', fontSize: 13 }}>Sign out</button>
                </div>
            </div>
        );
    }
    if (!authState.authorized) {
        return (
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100vh', background: '#f8fafc' }}>
                <div style={{ background: '#fff', borderRadius: 16, padding: 40, maxWidth: 440, boxShadow: '0 10px 30px rgba(0,0,0,0.08)', textAlign: 'center' }}>
                    <div style={{ fontSize: 48, marginBottom: 12 }}>🔒</div>
                    <h2 style={{ margin: 0, color: '#111827' }}>Access Denied</h2>
                    <p style={{ color: '#6b7280', marginTop: 12, lineHeight: 1.6 }}>
                        You don't have permission to access this CRM. Please contact the admin to request access.
                    </p>
                    <p style={{ color: '#9ca3af', fontSize: 12, marginTop: 16 }}>Signed in as <strong>{userEmail}</strong></p>
                    <button onClick={handleLogout} style={{ marginTop: 16, padding: '8px 16px', borderRadius: 8, border: '1px solid #e5e7eb', background: '#fff', cursor: 'pointer', fontSize: 13 }}>Sign out</button>
                </div>
            </div>
        );
    }

    return (
        <div className="crm-container" style={{ display: 'flex', minHeight: '100vh', background: '#f8fafc' }}>
            {/* SIDEBAR */}
            <aside style={{ width: 220, background: '#1e293b', color: '#e2e8f0', display: 'flex', flexDirection: 'column', position: 'sticky', top: 0, height: '100vh', flexShrink: 0 }}>
                <div style={{ padding: '20px 18px', borderBottom: '1px solid rgba(255,255,255,0.08)', display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                    <img src={contexqLogo} alt="ContexQ" style={{ width: 140, height: 'auto', objectFit: 'contain', mixBlendMode: 'screen' }} />
                    <div style={{ fontSize: 11, color: '#94a3b8', marginTop: 8, letterSpacing: 1, textTransform: 'uppercase', fontWeight: 600 }}>Enterprise CRM</div>
                </div>
                <nav style={{ flex: 1, overflowY: 'auto', padding: '12px 8px' }}>
                    {tabs.map(tab => (
                        <button
                            key={tab.id}
                            onClick={() => { setActiveTab(tab.id); setSearchTerm(''); setShowSalesIntel(false); }}
                            style={{
                                display: 'block', width: '100%', textAlign: 'left',
                                padding: '10px 14px', marginBottom: 2,
                                background: activeTab === tab.id ? '#3b82f6' : 'transparent',
                                color: activeTab === tab.id ? '#fff' : '#cbd5e1',
                                border: 'none', borderRadius: 8,
                                fontSize: 13, fontWeight: activeTab === tab.id ? 600 : 500,
                                cursor: 'pointer', fontFamily: 'inherit', transition: 'background 0.15s',
                            }}
                            onMouseEnter={e => { if (activeTab !== tab.id) e.currentTarget.style.background = 'rgba(255,255,255,0.06)'; }}
                            onMouseLeave={e => { if (activeTab !== tab.id) e.currentTarget.style.background = 'transparent'; }}
                        >
                            {tab.label}
                        </button>
                    ))}
                </nav>
                <div style={{ padding: 14, borderTop: '1px solid rgba(255,255,255,0.08)' }}>
                    <div style={{ fontSize: 12, color: '#cbd5e1', marginBottom: 4 }}>{account?.name || userEmail}</div>
                    <div style={{ fontSize: 10, color: '#64748b', marginBottom: 8, wordBreak: 'break-all' }}>{userEmail}</div>
                    <button onClick={handleLogout} style={{ width: '100%', background: 'rgba(220,38,38,0.15)', color: '#fca5a5', border: '1px solid rgba(220,38,38,0.3)', padding: '6px 12px', borderRadius: 6, fontSize: 12, fontWeight: 600, cursor: 'pointer' }}>
                        Sign Out
                    </button>
                </div>
            </aside>

            {/* MAIN CONTENT */}
            <main style={{ flex: 1, minWidth: 0, padding: '20px 24px' }}>
                {error && (
                    <div style={{ background: '#fef2f2', border: '1px solid #fca5a5', borderRadius: 8, padding: '8px 16px', margin: '0 0 12px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                        <span style={{ color: '#dc2626', fontSize: 13 }}>{error} (CRM data unavailable — GTM, Use Cases, Competitors, ROI Calculator still work)</span>
                        <button onClick={loadAllData} style={{ background: '#dc2626', color: '#fff', border: 'none', borderRadius: 6, padding: '4px 12px', fontSize: 12, fontWeight: 600, cursor: 'pointer' }}>Retry</button>
                    </div>
                )}

                <div className="content-card">
                {activeTab !== 'network' && activeTab !== 'worldmap' && activeTab !== 'ai' && activeTab !== 'dashboard' && activeTab !== 'gtm' && activeTab !== 'usecases' && activeTab !== 'competitors' && activeTab !== 'roi' && activeTab !== 'admin' && activeTab !== 'discovery' && (
                    <input
                        type="text"
                        className="search-bar"
                        placeholder={`Search ${tabs.find(t => t.id === activeTab)?.label}...`}
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                    />
                )}
                {renderContent()}
                </div>
            </main>

            <LoadingOverlay show={globalLoading} />
            {toast && <Toast message={toast.message} type={toast.type} onClose={() => setToast(null)} />}
        </div>
    );
}



// Dashboard Component
// Dashboard Component - 
function Dashboard({ data, onNavigate, onDataChange }) {
    // ✅ SAFE DATA ACCESS
    const deals = Array.isArray(data?.deals) ? data.deals : [];
    const activities = Array.isArray(data?.activities) ? data.activities : [];
    const investors = Array.isArray(data?.investors) ? data.investors : [];
    const partnerships = Array.isArray(data?.partnerships) ? data.partnerships : [];
    const internal = Array.isArray(data?.internal) ? data.internal : [];
    const externalContacts = Array.isArray(data?.externalContacts) ? data.externalContacts : [];

    const totalDealsValue = deals.reduce((sum, d) => sum + Number(d.Value || 0), 0);
    const totalFunding = investors.reduce((sum, i) => sum + Number(i.Amount || 0), 0);
    
    // ✅ USE ACTIVITIES 
    const activeTasks = activities.filter(a => 
        a.Status !== 'Completed' && a.Status !== 'Cancelled'
    ).length;
    
    const activePartnerships = partnerships.filter(p => p.Status === 'Active').length;
    
    const avgDealValue = deals.length > 0 ? totalDealsValue / deals.length : 0;
    const highPriorityTasks = activities.filter(a => 
        a.Priority === 'High' && a.Status !== 'Completed'
    ).length;

    return (
        <div>
            <h2 style={{ marginBottom: '24px', fontSize: '28px', fontWeight: '700' }}>
                Dashboard Overview
            </h2>
            
            <div className="stats-grid" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))' }}>
                <div className="stat-card">
                    <h3>Total Deals</h3>
                    <p style={{ fontSize: '36px' }}>{deals.length}</p>
                    <small style={{ opacity: 0.9 }}>
                        ${totalDealsValue.toLocaleString()} total value
                    </small>
                </div>
                
                <div className="stat-card">
                    <h3>Active Partnerships</h3>
                    <p style={{ fontSize: '36px' }}>{activePartnerships}</p>
                    <small style={{ opacity: 0.9 }}>
                        {partnerships.length} total partnerships
                    </small>
                </div>
                
                <div className="stat-card">
                    <h3>Total Funding</h3>
                    <p style={{ fontSize: '36px' }}>${totalFunding.toLocaleString()}</p>
                    <small style={{ opacity: 0.9 }}>
                        {investors.length} funding sources
                    </small>
                </div>
                
                <div className="stat-card">
                    <h3>Active Tasks</h3>
                    <p style={{ fontSize: '36px' }}>{activeTasks}</p>
                    <small style={{ opacity: 0.9 }}>
                        {highPriorityTasks} high priority • {activities.length} total
                    </small>
                </div>
                
                <div className="stat-card">
                    <h3>Team Size</h3>
                    <p style={{ fontSize: '36px' }}>{internal.length}</p>
                    <small style={{ opacity: 0.9 }}>
                        {externalContacts.length} external contacts
                    </small>
                </div>
                
                <div className="stat-card">
                    <h3>Avg Deal Value</h3>
                    <p style={{ fontSize: '36px' }}>${Math.round(avgDealValue).toLocaleString()}</p>
                    <small style={{ opacity: 0.9 }}>
                        Per deal average
                    </small>
                </div>
            </div>

            <div style={{ marginTop: '40px', display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '24px' }}>
                {/* Recent Activities */}
                <div style={{ background: '#f9fafb', padding: '24px', borderRadius: '12px' }}>
                    <h3 style={{ marginBottom: '16px', fontSize: '18px', fontWeight: '600' }}>
                        Recent Activities
                    </h3>
                    {activities.length > 0 ? (
                        activities
                            .sort((a, b) => new Date(b.Date) - new Date(a.Date))
                            .slice(0, 5)
                            .map((activity, idx) => (
                                <div 
                                    key={idx} 
                                    style={{ 
                                        marginBottom: '12px', 
                                        paddingBottom: '12px', 
                                        borderBottom: idx < 4 ? '1px solid #e5e7eb' : 'none' 
                                    }}
                                >
                                    <strong style={{ fontSize: '14px' }}>{activity.Title || activity.Task}</strong>
                                    <p style={{ fontSize: '12px', color: '#6b7280', marginTop: '4px' }}>
                                        {activity.Type || activity.Category} • {new Date(activity.Date).toLocaleDateString()}
                                        {activity.Priority && (
                                            <span style={{ 
                                                marginLeft: '8px',
                                                padding: '2px 8px',
                                                borderRadius: '4px',
                                                background: activity.Priority === 'High' ? '#fee2e2' : '#fef3c7',
                                                color: activity.Priority === 'High' ? '#991b1b' : '#92400e',
                                                fontSize: '11px',
                                                fontWeight: '600'
                                            }}>
                                                {activity.Priority}
                                            </span>
                                        )}
                                    </p>
                                </div>
                            ))
                    ) : (
                        <p style={{ color: '#9ca3af', fontSize: '14px' }}>No activities yet</p>
                    )}
                </div>

                {/* Top Deals */}
                <div style={{ background: '#f9fafb', padding: '24px', borderRadius: '12px' }}>
                    <h3 style={{ marginBottom: '16px', fontSize: '18px', fontWeight: '600' }}>
                        Top Deals by Value
                    </h3>
                    {deals.length > 0 ? (
                        deals
                            .sort((a, b) => Number(b.Value) - Number(a.Value))
                            .slice(0, 5)
                            .map((deal, idx) => (
                                <div 
                                    key={idx} 
                                    style={{ 
                                        marginBottom: '12px', 
                                        paddingBottom: '12px', 
                                        borderBottom: idx < 4 ? '1px solid #e5e7eb' : 'none' 
                                    }}
                                >
                                    <strong style={{ fontSize: '14px' }}>{deal.Title}</strong>
                                    <p style={{ fontSize: '12px', color: '#6b7280', marginTop: '4px' }}>
                                        {deal.Company} • ${Number(deal.Value).toLocaleString()}
                                        <span style={{ 
                                            marginLeft: '8px',
                                            padding: '2px 8px',
                                            borderRadius: '4px',
                                            background: '#dbeafe',
                                            color: '#1e40af',
                                            fontSize: '11px',
                                            fontWeight: '600'
                                        }}>
                                            {deal.Probability}%
                                        </span>
                                    </p>
                                </div>
                            ))
                    ) : (
                        <p style={{ color: '#9ca3af', fontSize: '14px' }}>No deals yet</p>
                    )}
                </div>
            </div>

            {/* Quick Actions */}
            <div style={{ marginTop: '32px' }}>
                <h3 style={{ marginBottom: '16px', fontSize: '18px', fontWeight: '600' }}>
                    Quick Actions
                </h3>
                <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
                    <button 
                        className="btn btn-primary"
                        onClick={() => onNavigate && onNavigate('deals')}
                        style={{ padding: '12px 24px' }}
                    >
                        + New Deal
                    </button>
                    <button 
                        className="btn btn-primary"
                        onClick={() => onNavigate && onNavigate('activities')}
                        style={{ padding: '12px 24px' }}
                    >
                        + New Activity
                    </button>
                    <button 
                        className="btn btn-primary"
                        onClick={() => onNavigate && onNavigate('partnerships')}
                        style={{ padding: '12px 24px' }}
                    >
                        + New Partnership
                    </button>
                    <button 
                        className="btn btn-secondary"
                        onClick={() => onDataChange && onDataChange()}
                        style={{ padding: '12px 24px' }}
                    >
                        ↻ Refresh Data
                    </button>
                </div>
            </div>
        </div>
    );
}


// Deals View Component
// Deals View Component - UPDATED WITHOUT CONTACT COLUMN + EXPORT/IMPORT
function DealsView({ searchTerm, showToast, setGlobalLoading }) {
    // ─────────────────────────────────────────────────────────────────────────
    // Server-side pagination via /api/deals?limit=&offset=&...
    // Deals are NOT shipped in bulkData anymore. useQuery keyed on filter params.
    // ─────────────────────────────────────────────────────────────────────────
    const queryClient = useQueryClient();
    const API_BASE = process.env.REACT_APP_API_URL || '/api';

    const [showModal, setShowModal] = useState(false);
    const [editingDeal, setEditingDeal] = useState(null);
    const [saving, setSaving] = useState(false);
    const [allContacts, setAllContacts] = useState([]);
    const [internalTeam, setInternalTeam] = useState([]);
    const [selectedIds, setSelectedIds] = useState(new Set());
    const [deleteMode, setDeleteMode] = useState(false);
    const fileInputRef = useRef(null);

    // Pagination + filter state
    const [page, setPage] = useState(0);
    const [pageSize, setPageSize] = useState(50);
    const [stageFilter, setStageFilter] = useState('all');
    const [sortField, setSortField] = useState('createdAt');
    const [sortDir, setSortDir] = useState('desc');

    // Debounce search from parent
    const [debouncedSearch, setDebouncedSearch] = useState(searchTerm || '');
    useEffect(() => {
        const t = setTimeout(() => setDebouncedSearch(searchTerm || ''), 300);
        return () => clearTimeout(t);
    }, [searchTerm]);

    // Reset to page 0 when filters change
    useEffect(() => { setPage(0); }, [debouncedSearch, stageFilter, pageSize, sortField, sortDir]);

    const params = React.useMemo(() => ({
        limit: pageSize,
        offset: page * pageSize,
        search: debouncedSearch || undefined,
        stage: stageFilter !== 'all' ? stageFilter : undefined,
        sort: sortField,
        dir: sortDir,
    }), [pageSize, page, debouncedSearch, stageFilter, sortField, sortDir]);

    const queryKey = ['deals', params];

    const { data: pageData, isLoading, isFetching } = useQuery({
        queryKey,
        queryFn: async () => {
            const qs = new URLSearchParams();
            Object.entries(params).forEach(([k, v]) => {
                if (v !== undefined && v !== null && v !== '') qs.set(k, String(v));
            });
            const r = await fetch(`${API_BASE}/deals?${qs.toString()}`);
            if (!r.ok) throw new Error(`deals ${r.status}`);
            return r.json();
        },
        keepPreviousData: true,
        refetchOnWindowFocus: false,
    });

    const sortedDeals = pageData?.rows || [];
    const totalRows = pageData?.total ?? 0;
    const totalPages = Math.max(1, Math.ceil(totalRows / pageSize));
    const serverCounts = pageData?.counts || null;

    // Sort-header adapter for SortableTableHeader
    const sortConfig = React.useMemo(() => ({
        key: { createdAt: 'CreatedAt', closeDate: 'CloseDate', value: 'Value', probability: 'Probability', company: 'Company', title: 'Title' }[sortField] || 'CreatedAt',
        direction: sortDir === 'desc' ? 'descending' : 'ascending',
    }), [sortField, sortDir]);
    const requestSort = React.useCallback((columnLabel) => {
        const rev = { CreatedAt: 'createdAt', CloseDate: 'closeDate', Value: 'value', Probability: 'probability', Company: 'company', Title: 'title' };
        const nextField = rev[columnLabel] || sortField;
        if (nextField === sortField) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
        else { setSortField(nextField); setSortDir('asc'); }
    }, [sortField]);

    // Cache mutations
    const cacheRemoveIds = React.useCallback((ids) => {
        const set = new Set(ids);
        queryClient.setQueriesData({ queryKey: ['deals'] }, (old) => {
            if (!old?.rows) return old;
            return { ...old, rows: old.rows.filter(r => !set.has(r.Id)) };
        });
    }, [queryClient]);
    const cacheUpsertRow = React.useCallback((row) => {
        if (!row?.Id) return;
        queryClient.setQueriesData({ queryKey: ['deals'] }, (old) => {
            if (!old?.rows) return old;
            let replaced = false;
            const rows = old.rows.map(r => { if (r.Id === row.Id) { replaced = true; return row; } return r; });
            if (!replaced) rows.unshift(row);
            return { ...old, rows };
        });
    }, [queryClient]);
    const invalidateAll = React.useCallback(() => {
        queryClient.invalidateQueries({ queryKey: ['deals'] });
    }, [queryClient]);

    const toggleSelect = (id) => {
        setSelectedIds(prev => { const n = new Set(prev); if (n.has(id)) n.delete(id); else n.add(id); return n; });
    };
    const selectAllVisible = (ids) => {
        setSelectedIds(prev => {
            const allSel = ids.every(id => prev.has(id));
            const n = new Set(prev);
            if (allSel) ids.forEach(id => n.delete(id)); else ids.forEach(id => n.add(id));
            return n;
        });
    };
    const clearSelection = () => setSelectedIds(new Set());
    const bulkDelete = async () => {
        const ids = Array.from(selectedIds);
        if (ids.length === 0) return;
        if (!window.confirm(`Delete ${ids.length} selected deal${ids.length === 1 ? '' : 's'}?`)) return;
        cacheRemoveIds(ids);
        clearSelection();
        setDeleteMode(false);
        showToast(`${ids.length} deal${ids.length === 1 ? '' : 's'} deleted!`);
        try {
            for (const id of ids) await api.deleteDeal(id);
        } catch (error) {
            showToast('Delete failed: ' + error.message + ' — restoring.', 'error');
            invalidateAll();
        }
    };

    useEffect(() => {
        loadContacts();
        loadInternalTeam();
    }, []);

    const loadContacts = async () => {
        try {
            const res = await api.getExternalContacts();
            // Endpoint now returns {rows, total, hasMore, ...} for pagination.
            // Legacy: could also be a bare array. Handle both.
            const contacts = Array.isArray(res) ? res : (Array.isArray(res?.rows) ? res.rows : []);
            console.log('All contacts loaded:', contacts.length);
            setAllContacts(contacts);
        } catch (error) {
            console.error('Failed to load contacts:', error);
            setAllContacts([]);
            showToast?.('Failed to load contacts', 'error');
        }
    };

    const loadInternalTeam = async () => {
        try {
            const team = await api.getInternal();
            console.log('Internal team loaded:', team?.length);
            setInternalTeam(team || []);
        } catch (error) {
            console.error('Failed to load internal team:', error);
            setInternalTeam([]);
            showToast?.('Failed to load internal team', 'error');
        }
    };

    // Filter contacts by type
    const externalContacts = allContacts.filter(c => c.IsInternal === false || c.IsInternal === 0 || !c.IsInternal);
    const internalContacts = allContacts.filter(c => c.IsInternal === true || c.IsInternal === 1);

    // NOTE: filtering + sorting is now server-side. `sortedDeals`, `sortConfig`,
    // and `requestSort` are defined above from the useQuery response.

    const parseJsonField = (field) => {
        if (!field) return [];
        try {
            return typeof field === 'string' ? JSON.parse(field) : field;
        } catch {
            return [];
        }
    };

    // Export to CSV — loop server pages to get every matching row.
    const handleExport = async () => {
        try {
            setGlobalLoading(true);
            const all = [];
            const batchSize = 200;
            for (let off = 0; off < totalRows; off += batchSize) {
                const qs = new URLSearchParams();
                Object.entries({ ...params, limit: batchSize, offset: off }).forEach(([k, v]) => {
                    if (v !== undefined && v !== null && v !== '') qs.set(k, String(v));
                });
                const r = await fetch(`${API_BASE}/deals?${qs.toString()}`);
                if (!r.ok) throw new Error(`export ${r.status}`);
                const body = await r.json();
                all.push(...(body.rows || []));
            }
            const exportData = all.map(deal => {
                const customerContactsList = parseJsonField(deal.CustomerContacts);
                const relationshipOwners = parseJsonField(deal.ContexQContacts);
                return {
                    'Deal Title': deal.Title,
                    'Company': deal.Company,
                    'Value': deal.Value,
                    'Stage': deal.Stage,
                    'Probability': deal.Probability,
                    'Close Date': deal.CloseDate ? new Date(deal.CloseDate).toLocaleDateString() : '',
                    'Customer Contacts': customerContactsList.join(', '),
                    'Relationship Owner': relationshipOwners.join(', '),
                    'Lead Generator': deal.LeadGenerator || '',
                    'Comments': deal.Comments || ''
                };
            });

            const headers = Object.keys(exportData[0] || {});
            const csv = [
                headers.join(','),
                ...exportData.map(row => headers.map(h => `"${row[h] || ''}"`).join(','))
            ].join('\n');
            const blob = new Blob([csv], { type: 'text/csv' });
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = `Deals_Export_${new Date().toISOString().split('T')[0]}.csv`;
            a.click();
            window.URL.revokeObjectURL(url);
            showToast(`Exported ${exportData.length} deal${exportData.length === 1 ? '' : 's'}!`);
        } catch (e) {
            showToast('Export failed: ' + e.message, 'error');
        } finally {
            setGlobalLoading(false);
        }
    };

    // Import from Excel
    const handleImport = (e) => {
        const file = e.target.files[0];
        if (!file) return;

        const reader = new FileReader();
        reader.onload = async (event) => {
            try {
                setGlobalLoading(true);
                const text = event.target.result;
                const lines = text.split('\n');
                const headers = lines[0].split(',').map(h => h.trim().replace(/"/g, ''));
                
                const deals = [];
                for (let i = 1; i < lines.length; i++) {
                    if (!lines[i].trim()) continue;
                    
                    const values = lines[i].split(',').map(v => v.trim().replace(/"/g, ''));
                    const deal = {};
                    headers.forEach((header, index) => {
                        deal[header] = values[index];
                    });
                    
                    if (deal['Deal Title'] && deal['Company']) {
                        // Parse common date formats → YYYY-MM-DD (what SQL expects)
                        const parseDate = (s) => {
                            if (!s) return new Date().toISOString().split('T')[0];
                            const trimmed = String(s).trim();
                            // Already ISO?
                            if (/^\d{4}-\d{2}-\d{2}/.test(trimmed)) return trimmed.slice(0, 10);
                            // DD/MM/YYYY or D/M/YYYY (locale format)
                            const m = trimmed.match(/^(\d{1,2})[\/\-](\d{1,2})[\/\-](\d{4})$/);
                            if (m) {
                                const [, d, mo, y] = m;
                                return `${y}-${mo.padStart(2, '0')}-${d.padStart(2, '0')}`;
                            }
                            // Fallback: let Date parse, fall back to today if invalid
                            const dt = new Date(trimmed);
                            if (!isNaN(dt)) return dt.toISOString().split('T')[0];
                            return new Date().toISOString().split('T')[0];
                        };
                        deals.push({
                            title: deal['Deal Title'],
                            company: deal['Company'],
                            value: parseFloat(deal['Value']) || 0,
                            stage: deal['Stage'] || 'New',
                            probability: parseInt(deal['Probability']) || 50,
                            closeDate: parseDate(deal['Close Date']),
                            contact: 'Imported',
                            customerContacts: deal['Customer Contacts'] ? deal['Customer Contacts'].split(',').map(s => s.trim()) : [],
                            contexqContacts: deal['Relationship Owner'] ? deal['Relationship Owner'].split(',').map(s => s.trim()) : [],
                            comments: deal['Comments'] || '',
                            leadGenerator: deal['Lead Generator'] || ''
                        });
                    }
                }
                
                // Import deals
                for (const deal of deals) {
                    await api.createDeal(deal);
                }
                
                showToast(`Successfully imported ${deals.length} deals!`);
                invalidateAll();
            } catch (error) {
                showToast('Import failed: ' + error.message, 'error');
            } finally {
                setGlobalLoading(false);
            }
        };

        reader.readAsText(file);
        e.target.value = '';
    };

    // Stat totals come from the server counts block (accurate across all
    // matching rows, not just the current page). Avg probability isn't in the
    // counts block yet — we show a per-page-average as a placeholder.
    const pipelineValue = serverCounts?.pipelineValue ?? 0;
    const wonValue = serverCounts?.wonValue ?? 0;
    const avgProbability = sortedDeals.length > 0
        ? Math.round(sortedDeals.reduce((sum, d) => sum + Number(d.Probability || 0), 0) / sortedDeals.length)
        : 0;

    return (
        <div>
            {/* Stats cards */}
            <div className="stats-grid">
                <div className="stat-card">
                    <h3>Total Deals</h3>
                    <p>{totalRows}</p>
                </div>
                <div className="stat-card">
                    <h3>Pipeline Value</h3>
                    <p>${pipelineValue.toLocaleString()}</p>
                </div>
                <div className="stat-card">
                    <h3>Won Value</h3>
                    <p>${wonValue.toLocaleString()}</p>
                </div>
                <div className="stat-card">
                    <h3>Avg Probability</h3>
                    <p>{avgProbability}%</p>
                </div>
            </div>

            {/* Filter bar */}
            <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'center', marginBottom: 12, fontSize: 13 }}>
                <select value={stageFilter} onChange={e => setStageFilter(e.target.value)} style={{ padding: '7px 10px', border: '1px solid #cbd5e1', borderRadius: 6, background: '#fff' }}>
                    <option value="all">All stages</option>
                    <option value="New">New</option>
                    <option value="Qualified">Qualified</option>
                    <option value="Proposal">Proposal</option>
                    <option value="Negotiation">Negotiation</option>
                    <option value="Closed Won">Closed Won</option>
                    <option value="Closed Lost">Closed Lost</option>
                </select>
                <select value={pageSize} onChange={e => setPageSize(Number(e.target.value))} style={{ padding: '7px 10px', border: '1px solid #cbd5e1', borderRadius: 6, background: '#fff' }}>
                    <option value={25}>25 per page</option>
                    <option value={50}>50 per page</option>
                    <option value={100}>100 per page</option>
                </select>
                {stageFilter !== 'all' && (
                    <button onClick={() => setStageFilter('all')}
                            style={{ background: 'transparent', border: 'none', color: '#2563eb', cursor: 'pointer', fontWeight: 500 }}>
                        Clear filter
                    </button>
                )}
                <div style={{ flex: 1 }} />
                {isFetching && !isLoading && <span style={{ color: '#64748b', fontSize: 12 }}>Loading…</span>}
                <span style={{ color: '#64748b', fontSize: 12 }}>
                    Showing {sortedDeals.length ? page * pageSize + 1 : 0}–{page * pageSize + sortedDeals.length} of {totalRows}
                </span>
            </div>

            {/* Action buttons */}
            <div style={{ display: 'flex', gap: '12px', marginBottom: '20px', flexWrap: 'wrap' }}>
                <button 
                    className="add-btn" 
                    onClick={() => { setEditingDeal(null); setShowModal(true); }}
                >
                    + Add New Deal
                </button>
                <button 
                    className="add-btn" 
                    onClick={handleExport}
                    style={{ background: '#1e293b' }}
                >
                    📤 Export to Excel
                </button>
                <button 
                    className="add-btn" 
                    onClick={() => fileInputRef.current?.click()}
                    style={{ background: '#1e293b' }}
                >
                    📥 Import from Excel
                </button>
                <input
                    ref={fileInputRef}
                    type="file"
                    accept=".csv,.xlsx,.xls"
                    style={{ display: 'none' }}
                    onChange={handleImport}
                />
                {deleteMode ? (
                    <button className="add-btn" onClick={() => { setDeleteMode(false); clearSelection(); }} style={{ background: '#64748b' }}>✕ Cancel</button>
                ) : (
                    <button className="add-btn" onClick={() => setDeleteMode(true)} style={{ background: '#dc2626', display: 'inline-flex', alignItems: 'center', gap: 6 }} title="Select rows to delete">
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="3 6 5 6 21 6"></polyline><path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6"></path><path d="M10 11v6M14 11v6"></path><path d="M9 6V4a2 2 0 0 1 2-2h2a2 2 0 0 1 2 2v2"></path></svg>
                        Delete
                    </button>
                )}
            </div>

            {/* Deals table - WITHOUT CONTACT COLUMN */}
            <div className="table-container">
                <table>
                    <thead>
                        <tr>
                            {deleteMode && (
                                <th style={{ width: 32 }}>
                                    <input
                                        type="checkbox"
                                        checked={sortedDeals.length > 0 && sortedDeals.every(d => selectedIds.has(d.Id))}
                                        onChange={() => selectAllVisible(sortedDeals.map(d => d.Id))}
                                        style={{ width: 16, height: 16, cursor: 'pointer', accentColor: '#1e293b' }}
                                        title="Select all"
                                    />
                                </th>
                            )}
                            <SortableTableHeader label="Deal Title" sortKey="Title" currentSort={sortConfig} onSort={requestSort} />
                            <SortableTableHeader label="Company" sortKey="Company" currentSort={sortConfig} onSort={requestSort} />
                            <SortableTableHeader label="Value" sortKey="Value" currentSort={sortConfig} onSort={requestSort} />
                            <SortableTableHeader label="Stage" sortKey="Stage" currentSort={sortConfig} onSort={requestSort} />
                            <SortableTableHeader label="Probability" sortKey="Probability" currentSort={sortConfig} onSort={requestSort} />
                            <SortableTableHeader label="Close Date" sortKey="CloseDate" currentSort={sortConfig} onSort={requestSort} />
                            <SortableTableHeader label="Customer Contacts" sortKey="CustomerContacts" currentSort={sortConfig} onSort={requestSort} />
                            <SortableTableHeader label="Relationship Owner" sortKey="ContexQContacts" currentSort={sortConfig} onSort={requestSort} />
                            <SortableTableHeader label="Lead Generator" sortKey="LeadGenerator" currentSort={sortConfig} onSort={requestSort} />
                            <SortableTableHeader label="Comments" sortKey="Comments" currentSort={sortConfig} onSort={requestSort} />

                            <th>Actions</th>
                        </tr>
                    </thead>
                    <tbody>
                        {sortedDeals.map(deal => {
                            const customerContactsList = parseJsonField(deal.CustomerContacts);
                            const relationshipOwners = parseJsonField(deal.ContexQContacts);
                            
                            return (
                                <tr key={deal.Id} style={{ background: selectedIds.has(deal.Id) ? '#eff6ff' : undefined }}>
                                    {deleteMode && (
                                        <td>
                                            <input
                                                type="checkbox"
                                                checked={selectedIds.has(deal.Id)}
                                                onChange={() => toggleSelect(deal.Id)}
                                                style={{ width: 16, height: 16, cursor: 'pointer', accentColor: '#1e293b' }}
                                            />
                                        </td>
                                    )}
                                    <td><strong>{deal.Title}</strong></td>
                                    <td>{deal.Company}</td>
                                    <td>${Number(deal.Value).toLocaleString()}</td>
                                    <td>
                                        <span className={`status-badge status-${deal.Stage.toLowerCase().replace(' ', '-')}`}>
                                            {deal.Stage}
                                        </span>
                                    </td>
                                    <td>{deal.Probability}%</td>
                                    <td>{new Date(deal.CloseDate).toLocaleDateString()}</td>
                                    <td>
                                        {customerContactsList.length > 0 ? (
                                            <div className="tag-group">
                                                {customerContactsList.map((contact, idx) => (
                                                    <span key={idx} className="badge badge-customer">
                                                        {contact}
                                                    </span>
                                                ))}
                                            </div>
                                        ) : <span style={{ color: '#9ca3af' }}>N/A</span>}
                                    </td>
                                    <td>
                                        {relationshipOwners.length > 0 ? (
                                            <div className="tag-group">
                                                {relationshipOwners.map((owner, idx) => (
                                                    <span key={idx} className="badge badge-partner">
                                                        {owner}
                                                    </span>
                                                ))}
                                            </div>
                                        ) : <span style={{ color: '#9ca3af' }}>N/A</span>}
                                    </td>


                                    {/* Lead Generator column */}
                                    <td>
                                        {deal.LeadGenerator ? (
                                            <span style={{ fontSize: '13px', color: '#374151' }}>{deal.LeadGenerator}</span>
                                        ) : (
                                            <span style={{ color: '#9ca3af', fontSize: '13px' }}>—</span>
                                        )}
                                    </td>

                                    {/* Comments column */}
                                    <td style={{ minWidth: '260px' }}>
                                        {deal.Comments ? (
                                            <span
                                                title={deal.Comments}
                                                style={{
                                                    fontSize: '13px',
                                                    color: '#374151',
                                                    display: 'block',
                                                    whiteSpace: 'normal',
                                                    lineHeight: 1.4
                                                }}
                                            >
                                                {deal.Comments}
                                            </span>
                                        ) : (
                                            <span style={{ color: '#9ca3af', fontSize: '13px' }}>No comments</span>
                                        )}
                                    </td>


                                    <td style={{ whiteSpace: 'nowrap', minWidth: 70 }}>
                                        <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                                            <button
                                                title="Edit"
                                                onClick={() => { setEditingDeal(deal); setShowModal(true); }}
                                                style={{ background: 'none', border: 'none', color: '#2563eb', cursor: 'pointer', padding: '4px', display: 'inline-flex', alignItems: 'center' }}
                                            >
                                                <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/></svg>
                                            </button>
                                            <button
                                                title="Delete"
                                                onClick={async () => {
                                                    if (!window.confirm('Delete this deal?')) return;
                                                    cacheRemoveIds([deal.Id]);
                                                    showToast('Deal deleted successfully!');
                                                    try {
                                                        await api.deleteDeal(deal.Id);
                                                    } catch (error) {
                                                        showToast('Failed to delete: ' + error.message + ' — restoring.', 'error');
                                                        invalidateAll();
                                                    }
                                                }}
                                                style={{ background: 'none', border: 'none', color: '#dc2626', cursor: 'pointer', padding: '4px', display: 'inline-flex', alignItems: 'center' }}
                                            >
                                                <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="3 6 5 6 21 6"/><path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6"/><path d="M10 11v6M14 11v6"/><path d="M9 6V4a2 2 0 0 1 2-2h2a2 2 0 0 1 2 2v2"/></svg>
                                            </button>
                                        </div>
                                    </td>
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
                {isLoading && (
                    <div style={{ padding: 40, textAlign: 'center', color: '#64748b', fontSize: 14 }}>Loading deals…</div>
                )}
                {!isLoading && sortedDeals.length === 0 && (
                    <div style={{ padding: 40, textAlign: 'center', color: '#9ca3af', fontSize: 14 }}>No deals match your filters.</div>
                )}
            </div>

            {/* Pagination footer */}
            {totalPages > 1 && (
                <div style={{ display: 'flex', gap: 6, alignItems: 'center', justifyContent: 'center', marginTop: 16, flexWrap: 'wrap', fontSize: 13 }}>
                    <button onClick={() => setPage(0)} disabled={page === 0}
                            style={{ padding: '6px 10px', border: '1px solid #cbd5e1', borderRadius: 6, background: '#fff', cursor: page === 0 ? 'default' : 'pointer', opacity: page === 0 ? 0.4 : 1 }}>« First</button>
                    <button onClick={() => setPage(p => Math.max(0, p - 1))} disabled={page === 0}
                            style={{ padding: '6px 10px', border: '1px solid #cbd5e1', borderRadius: 6, background: '#fff', cursor: page === 0 ? 'default' : 'pointer', opacity: page === 0 ? 0.4 : 1 }}>‹ Prev</button>
                    {(() => {
                        const shown = new Set([0, totalPages - 1]);
                        for (let i = Math.max(0, page - 3); i <= Math.min(totalPages - 1, page + 3); i++) shown.add(i);
                        const arr = Array.from(shown).sort((a, b) => a - b);
                        const out = [];
                        let prev = -1;
                        for (const i of arr) {
                            if (i > prev + 1) out.push(<span key={`gap-${i}`} style={{ color: '#94a3b8', padding: '0 4px' }}>…</span>);
                            out.push(
                                <button key={i} onClick={() => setPage(i)}
                                        style={{ padding: '6px 10px', border: '1px solid ' + (i === page ? '#2563eb' : '#cbd5e1'), borderRadius: 6, background: i === page ? '#2563eb' : '#fff', color: i === page ? '#fff' : '#0c2340', fontWeight: i === page ? 700 : 500, cursor: 'pointer', minWidth: 34 }}>
                                    {i + 1}
                                </button>
                            );
                            prev = i;
                        }
                        return out;
                    })()}
                    <button onClick={() => setPage(p => Math.min(totalPages - 1, p + 1))} disabled={page >= totalPages - 1}
                            style={{ padding: '6px 10px', border: '1px solid #cbd5e1', borderRadius: 6, background: '#fff', cursor: page >= totalPages - 1 ? 'default' : 'pointer', opacity: page >= totalPages - 1 ? 0.4 : 1 }}>Next ›</button>
                    <button onClick={() => setPage(totalPages - 1)} disabled={page >= totalPages - 1}
                            style={{ padding: '6px 10px', border: '1px solid #cbd5e1', borderRadius: 6, background: '#fff', cursor: page >= totalPages - 1 ? 'default' : 'pointer', opacity: page >= totalPages - 1 ? 0.4 : 1 }}>Last »</button>
                </div>
            )}

            {selectedIds.size > 0 && (
                <div style={{ position: 'fixed', bottom: 20, left: '50%', transform: 'translateX(-50%)', background: '#1e293b', color: '#ffffff', padding: '12px 20px', borderRadius: 10, boxShadow: '0 10px 30px rgba(0,0,0,0.25)', display: 'flex', alignItems: 'center', gap: 16, zIndex: 1000 }}>
                    <span style={{ fontSize: 14, fontWeight: 600 }}>{selectedIds.size} selected</span>
                    <button onClick={bulkDelete} style={{ background: '#dc2626', color: '#ffffff', border: 'none', padding: '8px 16px', borderRadius: 6, fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>Delete</button>
                    <button onClick={() => { clearSelection(); setDeleteMode(false); }} style={{ background: 'transparent', color: '#cbd5e1', border: 'none', cursor: 'pointer', fontSize: 13 }}>Cancel</button>
                </div>
            )}

            {/* Modal */}
            {showModal && (
                <DealModal
                    deal={editingDeal}
                    allDeals={sortedDeals}
                    externalContacts={externalContacts}
                    internalContacts={internalContacts}
                    internalTeam={internalTeam}
                    onClose={() => setShowModal(false)}
                    onSave={async (dealData) => {
                        try {
                            setSaving(true);
                            let saved;
                            if (editingDeal) {
                                saved = await api.updateDeal(editingDeal.Id, dealData);
                                if (saved && saved.Id) cacheUpsertRow(saved);
                                showToast('Deal updated successfully!');
                            } else {
                                try {
                                    saved = await api.createDeal(dealData);
                                    if (saved && saved.Id) cacheUpsertRow(saved);
                                    showToast('Deal created successfully!');
                                } catch (err) {
                                    if (err.status === 409 && err.body?.matchType) {
                                        const { matchType, existingDeal, incomingStage, incomingProbability } = err.body;
                                        if (matchType === 'exact_duplicate') {
                                            alert('This deal already exists in the system. Nothing changed.');
                                            setSaving(false);
                                            return;
                                        }
                                        if (matchType === 'probability_only') {
                                            alert(`This deal already exists with probability ${existingDeal.Probability}%.\n\nYou can edit the existing deal directly to update probability to ${incomingProbability}%.`);
                                            setSaving(false);
                                            return;
                                        }
                                        const verb = matchType === 'stage_and_probability'
                                            ? `update stage to "${incomingStage}" and probability to ${incomingProbability}%`
                                            : `update stage from "${existingDeal.Stage}" to "${incomingStage}"`;
                                        if (window.confirm(`This deal already exists.\n\n${verb.charAt(0).toUpperCase() + verb.slice(1)}?`)) {
                                            await api.updateDeal(existingDeal.Id, { ...dealData });
                                            showToast(`Deal updated to stage "${incomingStage}"`);
                                            setShowModal(false);
                                            invalidateAll();
                                            return;
                                        } else {
                                            setSaving(false);
                                            return;
                                        }
                                    }
                                    throw err;
                                }
                            }
                            setShowModal(false);
                            loadContacts();
                            loadInternalTeam();
                        } catch (error) {
                            invalidateAll();
                            showToast('Failed to save: ' + error.message, 'error');
                        } finally {
                            setSaving(false);
                        }
                    }}
                    saving={saving}
                />
            )}
        </div>
    );
}

// Deals Model Component
function DealModal({ deal, allDeals, externalContacts, internalContacts, internalTeam, onClose, onSave, saving }) {
    // ✅ SAFE DEFAULTS
    const safeExternalContacts = Array.isArray(externalContacts) ? externalContacts : [];
    const safeInternalContacts = Array.isArray(internalContacts) ? internalContacts : [];
    const safeInternalTeam = Array.isArray(internalTeam) ? internalTeam : [];
    const safeAllDeals = Array.isArray(allDeals) ? allDeals : [];
    
    // Combine internal sources for relationship owners
    const allInternalPeople = [
        ...safeInternalContacts.map(c => ({ name: c.Name, email: c.Email, source: 'ExternalContacts' })),
        ...safeInternalTeam.map(t => ({ name: t.Name, email: t.Email, source: 'internal' }))
    ];
    
    // Remove duplicates by name
    const uniqueInternalPeople = Array.from(
        new Map(allInternalPeople.map(p => [p.name, p])).values()
    );

    console.log('DealModal Debug:', {
        externalContacts: safeExternalContacts.length,
        internalContacts: safeInternalContacts.length,
        internalTeam: safeInternalTeam.length,
        uniqueInternal: uniqueInternalPeople.length
    });

    const parseJsonField = (field) => {
        if (!field) return [];
        try {
            return typeof field === 'string' ? JSON.parse(field) : field;
        } catch {
            return [];
        }
    };

    const [formData, setFormData] = useState(deal ? {
        title: deal.Title,
        company: deal.Company,
        value: deal.Value,
        stage: deal.Stage,
        probability: deal.Probability,
        closeDate: new Date(deal.CloseDate).toISOString().split('T')[0],
        contact: deal.Contact,
        comments: deal.Comments || '',
        leadGenerator: deal.LeadGenerator || ''
    } : {
        title: '',
        company: '',
        value: '',
        stage: 'New',
        probability: '',
        closeDate: '',
        contact: '',
        comments: '',
        leadGenerator: ''
    });

    // Company list = External Contacts companies + companies already used on deals
    const existingCompanyNames = [...new Set([
        ...safeExternalContacts.map(c => c.Company).filter(Boolean),
        ...safeAllDeals.map(d => d.Company).filter(Boolean)
    ])];
    const dealCompanyIsCustom = deal && deal.Company && !existingCompanyNames.includes(deal.Company);

    const [companyInputType, setCompanyInputType] = useState(dealCompanyIsCustom ? 'custom' : 'select');
    const [customCompany, setCustomCompany] = useState(dealCompanyIsCustom ? deal.Company : '');

    // Customer Contacts (External only)
    const [selectedCustomerContacts, setSelectedCustomerContacts] = useState(
        deal ? parseJsonField(deal.CustomerContacts) : []
    );
    const [customerContactInputType, setCustomerContactInputType] = useState('select');
    const [customCustomerContact, setCustomCustomerContact] = useState('');

    // Relationship Owner (Internal only)
    const [selectedRelationshipOwners, setSelectedRelationshipOwners] = useState(
        deal ? parseJsonField(deal.ContexQContacts) : []
    );
    const [relationshipOwnerInputType, setRelationshipOwnerInputType] = useState('select');
    const [customRelationshipOwner, setCustomRelationshipOwner] = useState('');

    // ✅ SAFE MAPPING - Line 696 fix
    const companies = [...existingCompanyNames].sort();
    const customerContactNames = safeExternalContacts.map(c => ({ 
        name: c.Name, 
        company: c.Company,
        email: c.Email 
    }));
    const internalNames = uniqueInternalPeople.map(p => p.name);

    const handleAddCustomerContact = (contactName) => {
        if (contactName && !selectedCustomerContacts.includes(contactName)) {
            setSelectedCustomerContacts([...selectedCustomerContacts, contactName]);
        }
    };

    const handleRemoveCustomerContact = (contactName) => {
        setSelectedCustomerContacts(selectedCustomerContacts.filter(c => c !== contactName));
    };

    const handleAddCustomCustomerContact = () => {
        if (customCustomerContact.trim()) {
            handleAddCustomerContact(customCustomerContact.trim());
            setCustomCustomerContact('');
        }
    };

    const handleAddRelationshipOwner = (ownerName) => {
        if (ownerName && !selectedRelationshipOwners.includes(ownerName)) {
            setSelectedRelationshipOwners([...selectedRelationshipOwners, ownerName]);
        }
    };

    const handleRemoveRelationshipOwner = (ownerName) => {
        setSelectedRelationshipOwners(selectedRelationshipOwners.filter(o => o !== ownerName));
    };

    const handleAddCustomRelationshipOwner = () => {
        if (customRelationshipOwner.trim()) {
            handleAddRelationshipOwner(customRelationshipOwner.trim());
            setCustomRelationshipOwner('');
        }
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        
        const finalCompany = companyInputType === 'custom' ? customCompany : formData.company;

        onSave({
            title: formData.title,
            company: finalCompany,
            value: parseFloat(formData.value),
            stage: formData.stage,
            probability: parseInt(formData.probability),
            closeDate: formData.closeDate,
            contact: 'Deal Owner', //✅ Use placeholder since backend still requires it
            customerContacts: selectedCustomerContacts,
            contexqContacts: selectedRelationshipOwners,
            comments: formData.comments,
            leadGenerator: formData.leadGenerator

        });
    };

    return (
        <div className="modal-overlay" onClick={onClose}>
            <div className="modal modal-large" onClick={(e) => e.stopPropagation()} style={{ maxHeight: '90vh', overflowY: 'auto' }}>
                <h2>{deal ? 'Edit Deal' : 'Add New Deal'}</h2>
                <form onSubmit={handleSubmit}>
                    {/* Company field */}
                    <div className="form-group">
                        <label>Company *</label>
                        <div style={{ display: 'flex', gap: '8px', marginBottom: '8px' }}>
                            <button 
                                type="button" 
                                className={`btn ${companyInputType === 'select' ? 'btn-primary' : 'btn-secondary'}`}
                                onClick={() => setCompanyInputType('select')}
                            >
                                Select Existing ({companies.length})
                            </button>
                            <button 
                                type="button" 
                                className={`btn ${companyInputType === 'custom' ? 'btn-primary' : 'btn-secondary'}`}
                                onClick={() => setCompanyInputType('custom')}
                            >
                                Enter New
                            </button>
                        </div>
                        
                        {companyInputType === 'select' ? (
                            <select 
                                required 
                                value={formData.company} 
                                onChange={(e) => setFormData({...formData, company: e.target.value})}
                            >
                                <option value="">Select Company</option>
                                {companies.map((company, idx) => (
                                    <option key={idx} value={company}>{company}</option>
                                ))}
                            </select>
                        ) : (
                            <input 
                                type="text" 
                                required 
                                placeholder="Enter new company name"
                                value={customCompany} 
                                onChange={(e) => setCustomCompany(e.target.value)} 
                            />
                        )}
                    </div>

                    {/* Deal Title */}
                    <div className="form-group">
                        <label>Deal Title *</label>
                        <input type="text" required value={formData.title} onChange={(e) => setFormData({...formData, title: e.target.value})} />
                    </div>

                    {/* Customer Contacts - EXTERNAL ONLY */}
                    <div className="form-group">
                        <label>Customer Contact(s) - External People ({customerContactNames.length} available)</label>
                        
                        {selectedCustomerContacts.length > 0 && (
                            <div style={{ 
                                marginBottom: '12px', 
                                padding: '12px', 
                                background: '#f0f9ff', 
                                borderRadius: '8px',
                                display: 'flex',
                                flexWrap: 'wrap',
                                gap: '8px'
                            }}>
                                {selectedCustomerContacts.map(contact => (
                                    <span 
                                        key={contact}
                                        style={{
                                            background: '#10b981',
                                            color: 'white',
                                            padding: '4px 12px',
                                            borderRadius: '12px',
                                            fontSize: '14px',
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: '8px'
                                        }}
                                    >
                                        {contact}
                                        <button
                                            type="button"
                                            onClick={() => handleRemoveCustomerContact(contact)}
                                            style={{
                                                background: 'transparent',
                                                border: 'none',
                                                color: 'white',
                                                cursor: 'pointer',
                                                fontSize: '16px',
                                                padding: '0'
                                            }}
                                        >
                                            ×
                                        </button>
                                    </span>
                                ))}
                            </div>
                        )}
                        
                        <div style={{ display: 'flex', gap: '8px', marginBottom: '8px' }}>
                            <button 
                                type="button" 
                                className={`btn ${customerContactInputType === 'select' ? 'btn-primary' : 'btn-secondary'}`}
                                onClick={() => setCustomerContactInputType('select')}
                            >
                                Select Existing
                            </button>
                            <button 
                                type="button" 
                                className={`btn ${customerContactInputType === 'custom' ? 'btn-primary' : 'btn-secondary'}`}
                                onClick={() => setCustomerContactInputType('custom')}
                            >
                                Enter New
                            </button>
                        </div>
                        
                        {customerContactInputType === 'select' ? (
                            <select 
                                onChange={(e) => {
                                    if (e.target.value) {
                                        handleAddCustomerContact(e.target.value);
                                        e.target.value = '';
                                    }
                                }}
                            >
                                <option value="">Select Customer Contact to Add</option>
                                {customerContactNames.map((contact, idx) => (
                                    <option key={idx} value={contact.name}>
                                        {contact.name} ({contact.company})
                                    </option>
                                ))}
                            </select>
                        ) : (
                            <div style={{ display: 'flex', gap: '8px' }}>
                                <input 
                                    type="text" 
                                    placeholder="Enter new customer contact name"
                                    value={customCustomerContact} 
                                    onChange={(e) => setCustomCustomerContact(e.target.value)}
                                    style={{ flex: 1 }}
                                />
                                <button 
                                    type="button" 
                                    className="btn btn-primary"
                                    onClick={handleAddCustomCustomerContact}
                                >
                                    Add
                                </button>
                            </div>
                        )}
                    </div>

                    {/* Relationship Owner - INTERNAL ONLY */}
                    <div className="form-group">
                        <label>Relationship Owner (ContexQ Team) - ({internalNames.length} available)</label>
                        
                        {selectedRelationshipOwners.length > 0 && (
                            <div style={{ 
                                marginBottom: '12px', 
                                padding: '12px', 
                                background: '#f0f9ff', 
                                borderRadius: '8px',
                                display: 'flex',
                                flexWrap: 'wrap',
                                gap: '8px'
                            }}>
                                {selectedRelationshipOwners.map(owner => (
                                    <span 
                                        key={owner}
                                        style={{
                                            background: '#667eea',
                                            color: 'white',
                                            padding: '4px 12px',
                                            borderRadius: '12px',
                                            fontSize: '14px',
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: '8px'
                                        }}
                                    >
                                        {owner}
                                        <button
                                            type="button"
                                            onClick={() => handleRemoveRelationshipOwner(owner)}
                                            style={{
                                                background: 'transparent',
                                                border: 'none',
                                                color: 'white',
                                                cursor: 'pointer',
                                                fontSize: '16px',
                                                padding: '0'
                                            }}
                                        >
                                            ×
                                        </button>
                                    </span>
                                ))}
                            </div>
                        )}
                        
                        <div style={{ display: 'flex', gap: '8px', marginBottom: '8px' }}>
                            <button 
                                type="button" 
                                className={`btn ${relationshipOwnerInputType === 'select' ? 'btn-primary' : 'btn-secondary'}`}
                                onClick={() => setRelationshipOwnerInputType('select')}
                            >
                                Select from Team
                            </button>
                            <button 
                                type="button" 
                                className={`btn ${relationshipOwnerInputType === 'custom' ? 'btn-primary' : 'btn-secondary'}`}
                                onClick={() => setRelationshipOwnerInputType('custom')}
                            >
                                Enter New
                            </button>
                        </div>
                        
                        {relationshipOwnerInputType === 'select' ? (
                            <select 
                                onChange={(e) => {
                                    if (e.target.value) {
                                        handleAddRelationshipOwner(e.target.value);
                                        e.target.value = '';
                                    }
                                }}
                            >
                                <option value="">Select Team Member to Add</option>
                                {internalNames.map((name, idx) => (
                                    <option key={idx} value={name}>{name}</option>
                                ))}
                            </select>
                        ) : (
                            <div style={{ display: 'flex', gap: '8px' }}>
                                <input 
                                    type="text" 
                                    placeholder="Enter new relationship owner name"
                                    value={customRelationshipOwner} 
                                    onChange={(e) => setCustomRelationshipOwner(e.target.value)}
                                    style={{ flex: 1 }}
                                />
                                <button 
                                    type="button" 
                                    className="btn btn-primary"
                                    onClick={handleAddCustomRelationshipOwner}
                                >
                                    Add
                                </button>
                            </div>
                        )}
                    </div>

                    {/* Value */}
                    <div className="form-group">
                        <label>Value ($) *</label>
                        <input type="number" required value={formData.value} onChange={(e) => setFormData({...formData, value: e.target.value})} />
                    </div>

                    {/* Stage */}
                    <div className="form-group">
                        <label>Stage *</label>
                        <select value={formData.stage} onChange={(e) => setFormData({...formData, stage: e.target.value})}>
                            <option>New</option>
                            <option>Qualified</option>
                            <option>Proposal</option>
                            <option>Negotiation</option>
                            <option>Closed Won</option>
                            <option>Closed Lost</option>
                        </select>
                    </div>

                    {/* Probability */}
                    <div className="form-group">
                        <label>Probability (%) *</label>
                        <input type="number" min="0" max="100" required value={formData.probability} onChange={(e) => setFormData({...formData, probability: e.target.value})} />
                    </div>

                    {/* Close Date */}
                    <div className="form-group">
                        <label>Expected Close Date *</label>
                        <input type="date" required value={formData.closeDate} onChange={(e) => setFormData({...formData, closeDate: e.target.value})} />
                    </div>


                    {/* Lead Generator */}
                    <div className="form-group">
                        <label>Lead Generator</label>
                        <input
                            type="text"
                            list="leadGeneratorOptions"
                            value={formData.leadGenerator}
                            onChange={(e) => setFormData({...formData, leadGenerator: e.target.value})}
                            placeholder="Start typing — picks from Internal team"
                        />
                        <datalist id="leadGeneratorOptions">
                            {(internalTeam || []).map(person => (
                                <option key={person.Id} value={person.Name} />
                            ))}
                        </datalist>
                    </div>

                    {/* Comments */}
                    <div className="form-group">
                        <label>Comments</label>
                        <textarea
                            value={formData.comments}
                            onChange={(e) => setFormData({...formData, comments: e.target.value})}
                            placeholder="Add any additional notes or comments about this deal..."
                            rows="4"
                            style={{
                                width: '100%',
                                padding: '10px 12px',
                                border: '2px solid #e5e7eb',
                                borderRadius: '8px',
                                fontSize: '14px',
                                fontFamily: 'Inter, sans-serif',
                                resize: 'vertical'
                            }}
                        />
                    </div>


                    {/* Actions */}
                    <div className="modal-actions">
                        <button type="submit" className="btn btn-primary" disabled={saving}>
                            {saving ? 'Saving...' : 'Save'}
                        </button>
                        <button type="button" className="btn btn-secondary" onClick={onClose} disabled={saving}>
                            Cancel
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
}



// Activities View Component - FIXED: Safe props + Force refresh
function ActivitiesView({ internal, deals, investors, searchTerm, onDataChange, showToast, setGlobalLoading }) {
    // ─────────────────────────────────────────────────────────────────────────
    // Server-side pagination + filtering via /api/activities?limit=&offset=&...
    // Activities are NO LONGER shipped in bulkData — this component owns its
    // own fetch lifecycle via TanStack Query useInfiniteQuery.
    //
    // Every filter/sort/sub-tab change updates the `params` object → becomes
    // part of the query key → auto-refetches. The search box is debounced
    // 300ms so typing doesn't fire one request per keystroke.
    // ─────────────────────────────────────────────────────────────────────────
    const queryClient = useQueryClient();
    const API_BASE = process.env.REACT_APP_API_URL || '/api';

    const safeInternal = Array.isArray(internal) ? internal : [];
    const safeDeals = Array.isArray(deals) ? deals : [];
    const safeInvestors = Array.isArray(investors) ? investors : [];

    const [showModal, setShowModal] = useState(false);
    const [editingActivity, setEditingActivity] = useState(null);
    const [viewingActivity, setViewingActivity] = useState(null);
    const [saving, setSaving] = useState(false);

    const [selectedCategory, setSelectedCategory] = useState('All');
    const [selectedOwner, setSelectedOwner] = useState('All');
    const [selectedDeal, setSelectedDeal] = useState('All');
    const [selectedInvestor, setSelectedInvestor] = useState('All');
    const [groupBy, setGroupBy] = useState('type'); // 'type' or 'status'

    const [sortBy, setSortBy] = useState('date');
    const [sortDirection, setSortDirection] = useState('desc');

    const [categories, setCategories] = useState(['Follow-up', 'Activity']);
    const STATUSES = ['Not Started', 'In Progress', 'Completed'];
    const [activeTab, setActiveTab] = useState('all'); // all | overdue | today | week | later | completed
    const [selectedIds, setSelectedIds] = useState(new Set());
    const [deleteMode, setDeleteMode] = useState(false);
    const fileInputRef = useRef(null);
    const sentinelRef = useRef(null); // triggers infinite-scroll load-more when visible

    // Debounce the search term so typing doesn't fire a request per keystroke.
    const [debouncedSearch, setDebouncedSearch] = useState(searchTerm || '');
    useEffect(() => {
        const t = setTimeout(() => setDebouncedSearch(searchTerm || ''), 300);
        return () => clearTimeout(t);
    }, [searchTerm]);

    // The current filter/sort snapshot — every field here is part of the
    // TanStack Query cache key. Change any of these -> automatic refetch.
    const params = React.useMemo(() => {
        const p = {
            limit: 50,
            search: debouncedSearch || undefined,
            sort: sortBy,
            dir: sortDirection,
        };
        // Sub-tab is a dueWindow filter on the server
        if (activeTab !== 'all') p.dueWindow = activeTab;
        // Category filter (only relevant when NOT grouped by status)
        if (groupBy !== 'status' && selectedCategory !== 'All') p.type = selectedCategory;
        if (groupBy === 'status' && selectedCategory !== 'All') p.status = selectedCategory;
        // Owner
        if (selectedOwner === 'Unassigned') p.owner = '__unassigned__';
        else if (selectedOwner !== 'All') p.owner = selectedOwner;
        // Related-to (Deal/Investor)
        if (selectedDeal !== 'All') p.relatedTo = selectedDeal;
        else if (selectedInvestor !== 'All') p.relatedTo = selectedInvestor;
        return p;
    }, [debouncedSearch, sortBy, sortDirection, activeTab, groupBy, selectedCategory, selectedOwner, selectedDeal, selectedInvestor]);

    const queryKey = ['activities', params];

    // The core infinite-scroll fetch. Each page = 50 rows. First page also
    // carries a `counts` block used for the sub-tab badges.
    const {
        data: pagesData,
        isLoading,
        isFetching,
        isFetchingNextPage,
        fetchNextPage,
        hasNextPage,
        refetch,
    } = useInfiniteQuery({
        queryKey,
        queryFn: async ({ pageParam = 0 }) => {
            const qs = new URLSearchParams();
            Object.entries(params).forEach(([k, v]) => {
                if (v !== undefined && v !== null && v !== '') qs.set(k, String(v));
            });
            qs.set('offset', String(pageParam));
            const r = await fetch(`${API_BASE}/activities?${qs.toString()}`);
            if (!r.ok) throw new Error(`activities ${r.status}`);
            return r.json();
        },
        getNextPageParam: (last) => last.hasMore ? last.offset + last.rows.length : undefined,
        // Keep sub-tab counts + rows visible while the next filter query runs
        keepPreviousData: true,
        refetchOnWindowFocus: false,
    });

    // Flat rows across all loaded pages, with the same shape the render code expects
    // (Category field mirrored from Type, DaysSinceAssigned derived like before).
    const sortedActivities = React.useMemo(() => {
        const flat = (pagesData?.pages || []).flatMap(p => p.rows || []);
        return flat.map(a => {
            const assignedDate = a.AssignedDate || a.Date || a.DueDate;
            const daysSinceAssigned = assignedDate
                ? Math.floor((new Date() - new Date(assignedDate)) / (1000 * 60 * 60 * 24))
                : 0;
            return { ...a, Category: a.Category || a.Type, DaysSinceAssigned: daysSinceAssigned };
        });
    }, [pagesData]);

    // Server-side counts drive the sub-tab badges + type-filter counts.
    // If we haven't loaded yet, fall back to zeros so the UI renders shells.
    const serverCounts = pagesData?.pages?.[0]?.counts || null;
    const totalMatchingRows = pagesData?.pages?.[0]?.total ?? 0;

    // Owner dropdown options — must come from Internal team since we no longer
    // have the full activities list to scrape distinct owners from.
    const allOwners = React.useMemo(() => {
        return Array.from(new Set(safeInternal.map(p => p.Name).filter(Boolean))).sort();
    }, [safeInternal]);

    // Unassigned badge count — comes from the server counts, but only if the
    // OWNER filter is currently 'All'. Otherwise showing this count would be
    // misleading. When the filter isn't 'All' we hide the badge.
    const unassignedCount = null; // Deferred: not surfaced in initial rewrite; can be added by asking server for a dedicated count.

    // ─────────────────────────────────────────────────────────────────────────
    // Optimistic cache mutations — replace the old onLocalRemove/Add/Upsert.
    // These update the paginated cache in place so the UI is instant, then
    // the network call reconciles.
    // ─────────────────────────────────────────────────────────────────────────
    const cacheRemoveIds = React.useCallback((ids) => {
        const set = new Set(ids);
        queryClient.setQueriesData({ queryKey: ['activities'] }, (old) => {
            if (!old?.pages) return old;
            return {
                ...old,
                pages: old.pages.map(page => ({
                    ...page,
                    rows: page.rows.filter(r => !set.has(r.Id)),
                    total: page.total != null ? Math.max(0, page.total - (page.rows.filter(r => set.has(r.Id)).length)) : page.total,
                })),
            };
        });
    }, [queryClient]);

    const cacheAddRows = React.useCallback((rows) => {
        if (!rows || rows.length === 0) return;
        const withCat = rows.map(r => ({ ...r, Category: r.Category || r.Type }));
        queryClient.setQueriesData({ queryKey: ['activities'] }, (old) => {
            if (!old?.pages || old.pages.length === 0) return old;
            const first = { ...old.pages[0], rows: [...withCat, ...old.pages[0].rows], total: (old.pages[0].total || 0) + withCat.length };
            return { ...old, pages: [first, ...old.pages.slice(1)] };
        });
    }, [queryClient]);

    const cacheUpsertRow = React.useCallback((row) => {
        if (!row?.Id) return;
        const withCat = { ...row, Category: row.Category || row.Type };
        queryClient.setQueriesData({ queryKey: ['activities'] }, (old) => {
            if (!old?.pages) return old;
            let replaced = false;
            const pages = old.pages.map(page => ({
                ...page,
                rows: page.rows.map(r => {
                    if (r.Id === withCat.Id) { replaced = true; return withCat; }
                    return r;
                }),
            }));
            // If the row wasn't in any loaded page, prepend to the first page (fresh insert)
            if (!replaced && pages[0]) {
                pages[0] = { ...pages[0], rows: [withCat, ...pages[0].rows], total: (pages[0].total || 0) + 1 };
            }
            return { ...old, pages };
        });
    }, [queryClient]);

    // Invalidate everything → forces a fresh fetch. Used after major changes
    // (import, refresh button) where fine-grained cache surgery isn't worth it.
    const invalidateAll = React.useCallback(() => {
        queryClient.invalidateQueries({ queryKey: ['activities'] });
    }, [queryClient]);

    // Infinite-scroll trigger: when the sentinel div at the bottom of the list
    // scrolls into view, ask for the next page. TanStack Query dedupes so
    // repeated triggers while a fetch is in flight are harmless.
    useEffect(() => {
        const el = sentinelRef.current;
        if (!el) return;
        const io = new IntersectionObserver((entries) => {
            entries.forEach(entry => {
                if (entry.isIntersecting && hasNextPage && !isFetchingNextPage) {
                    fetchNextPage();
                }
            });
        }, { rootMargin: '400px 0px' }); // fire a bit before it's fully visible
        io.observe(el);
        return () => io.disconnect();
    }, [hasNextPage, isFetchingNextPage, fetchNextPage]);

    const toggleSelect = (id) => {
        setSelectedIds(prev => {
            const next = new Set(prev);
            if (next.has(id)) next.delete(id); else next.add(id);
            return next;
        });
    };
    const selectAllVisible = (ids) => {
        setSelectedIds(prev => {
            const allSelected = ids.every(id => prev.has(id));
            const next = new Set(prev);
            if (allSelected) ids.forEach(id => next.delete(id));
            else ids.forEach(id => next.add(id));
            return next;
        });
    };
    const clearSelection = () => setSelectedIds(new Set());
    const bulkDelete = async () => {
        const ids = Array.from(selectedIds);
        if (ids.length === 0) return;
        if (!window.confirm(`Delete ${ids.length} selected activit${ids.length === 1 ? 'y' : 'ies'}?`)) return;
        // OPTIMISTIC: remove from cached pages instantly
        cacheRemoveIds(ids);
        clearSelection();
        setDeleteMode(false);
        showToast(`${ids.length} activit${ids.length === 1 ? 'y' : 'ies'} deleted!`);
        try {
            for (const id of ids) {
                await api.deleteActivity(id);
            }
        } catch (error) {
            // Server rejected → pull fresh truth so removed rows come back
            showToast('Delete failed: ' + error.message + ' — restoring.', 'error');
            invalidateAll();
        }
    };

    // Client-side filtering + sorting + grouping is now done by the server.
    // `sortedActivities` above is the flat list of rows the server returned
    // (already filtered + sorted). `groupedActivities` used to bucket by
    // Type/Status locally — we now render a flat scrollable list per sub-tab
    // instead, so the group split isn't needed. Any residual references to
    // `filteredActivities` / `groupedActivities` were removed with the render
    // pass; if any linger, the linter/build will flag them.

    const handleExport = async () => {
        const exportData = sortedActivities.map(activity => ({
            'Title/Task': activity.Title || activity.Task,
            'Category': activity.Category || activity.Type || '',
            'Task Owner(s)': activity.TaskOwner || '',
            'Days Since Assigned': activity.DaysSinceAssigned,
            'Date': activity.Date ? new Date(activity.Date).toLocaleDateString() : '',
            'Due Date': activity.DueDate ? new Date(activity.DueDate).toLocaleDateString() : '',
            'Status': activity.Status,
            'Related To': activity.RelatedTo || '',
            'Priority': activity.Priority || 'N/A',
            'Notes': activity.Notes || ''
        }));
        await exportToExcel(exportData, 'Activities_Tasks');
    };

    const handleImport = (e) => {
        const file = e.target.files[0];
        if (!file) return;

        // Parse any date format Excel might give us (number, ISO, DD/MM/YYYY, MM/DD/YYYY, Date object)
        const parseDate = (s) => {
            if (s === null || s === undefined || s === '') return null;
            if (s instanceof Date) return s.toISOString().split('T')[0];
            // Excel serial number (days since 1900)
            if (typeof s === 'number') {
                const d = new Date(Date.UTC(1899, 11, 30) + s * 86400000);
                return isNaN(d) ? null : d.toISOString().split('T')[0];
            }
            const trimmed = String(s).trim();
            if (/^\d{4}-\d{2}-\d{2}/.test(trimmed)) return trimmed.slice(0, 10);
            // DD/MM/YYYY or D/M/YYYY (locale format)
            const m = trimmed.match(/^(\d{1,2})[\/\-](\d{1,2})[\/\-](\d{4})$/);
            if (m) {
                const [, d, mo, y] = m;
                return `${y}-${mo.padStart(2, '0')}-${d.padStart(2, '0')}`;
            }
            const dt = new Date(trimmed);
            return isNaN(dt) ? null : dt.toISOString().split('T')[0];
        };

        importFromExcel(file, async (jsonData) => {
            try {
                setGlobalLoading(true);
                // Dedup within THIS import batch only. With server-side pagination
                // we no longer have the full activities list in memory to check
                // against, so cross-import duplicate detection now falls to a UNIQUE
                // constraint / conflict handling on the server side (future work).
                const seenInBatch = new Set();

                let imported = 0;
                let skipped = 0;
                const created = [];
                for (const row of jsonData) {
                    const activityData = {
                        type: row.Category || row.category || row.Type || row.type || 'Follow-up',
                        subtype: row['Sub-task type'] || row.Subtype || row.subtype || null,
                        title: row['Title/Task'] || row.Title || row.title || row.Task || row.task,
                        taskOwner: row['Task Owner(s)'] || row.TaskOwner || row.taskOwner || '',
                        date: parseDate(row.Date || row.date) || new Date().toISOString().split('T')[0],
                        dueDate: parseDate(row['Due Date'] || row.DueDate || row.dueDate),
                        status: row.Status || row.status || 'Pending',
                        relatedTo: row['Related To'] || row.RelatedTo || row.relatedTo || '',
                        priority: row.Priority || row.priority || 'Medium',
                        notes: row.Notes || row.notes || ''
                    };
                    if (!activityData.title) continue;
                    const key = `${activityData.title.trim().toLowerCase()}|${activityData.date}`;
                    if (seenInBatch.has(key)) {
                        skipped++;
                        continue;
                    }
                    const newRow = await api.createActivity(activityData);
                    if (newRow && newRow.Id) created.push(newRow);
                    seenInBatch.add(key);
                    imported++;
                }
                // Refresh the whole cache — much simpler + safer than surgical
                // inserts after a bulk import.
                invalidateAll();
                if (imported > 0 && skipped > 0) showToast(`Imported ${imported}, skipped ${skipped} duplicate${skipped === 1 ? '' : 's'} in batch`);
                else if (imported > 0) showToast(`Imported ${imported} activit${imported === 1 ? 'y' : 'ies'}!`);
                else if (skipped > 0) showToast(`No new rows — ${skipped} duplicates in file`, 'error');
                else showToast('No rows found in file', 'error');
            } catch (error) {
                showToast('Import failed: ' + error.message, 'error');
            } finally {
                setGlobalLoading(false);
            }
        });
        e.target.value = '';
    };

    const handleVoiceTaskCreate = async (taskData) => {
        try {
            setGlobalLoading(true);
            
            const taskOwnerString = Array.isArray(taskData.taskOwner) 
                ? taskData.taskOwner.join(', ')
                : taskData.taskOwner;

            const activityData = {
                type: taskData.type,
                title: taskData.title,
                taskOwner: taskOwnerString,
                date: taskData.date,
                status: taskData.status,
                relatedTo: taskData.relatedTo,
                priority: taskData.priority,
                notes: taskData.notes || ''
            };

            const created = await api.createActivity(activityData);
            if (created && created.Id) cacheAddRows([created]);
            showToast('Voice task created successfully! 🎤');
        } catch (error) {
            showToast('Failed to create task: ' + error.message, 'error');
            throw error;
        } finally {
            setGlobalLoading(false);
        }
    };

    const handleEmailClick = (activity, recipient = null) => {
        const owners = activity.TaskOwner ? activity.TaskOwner.split(', ') : [];
        const targetOwners = recipient ? [recipient] : owners;
        
        const emailAddresses = targetOwners
            .map(ownerName => {
                const person = safeInternal.find(p => p.Name === ownerName);
                return person ? person.Email : null;
            })
            .filter(email => email)
            .join(';');
        
        if (!emailAddresses) {
            alert('No email addresses found for the selected task owner(s).');
            return;
        }
        
        const title = activity.Title || activity.Task;
        const relatedTo = activity.RelatedTo || 'the opportunity';
        const notes = activity.Notes || '';
        const dueDate = new Date(activity.Date || activity.DueDate).toLocaleDateString();
        const priority = activity.Priority || 'Medium';
        
        const subject = `Task Reminder: ${title}`;
        const body = `Hello,

This is a reminder about the following task:

━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
Task: ${title}
Related To: ${relatedTo}
Priority: ${priority}
Due Date: ${dueDate}
Days Since Assigned: ${activity.DaysSinceAssigned}
Status: ${activity.Status}
━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━

${notes ? `Additional Notes:\n${notes}\n\n` : ''}Please update the status when completed or if you need any assistance.

Best regards,
ContexQ CRM Team`;

        const mailtoLink = `mailto:${emailAddresses}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
        window.location.href = mailtoLink;
    };

    // ===== Sub-tab counts from the server's `counts` block =====
    // The server returns bucket totals in one query on the first page of the
    // active filter set. This means badges show real totals across the WHOLE
    // filtered set, not just what's currently loaded on screen.
    const BUCKETS = [
        { key: 'overdue', title: 'Overdue', sub: 'Action required' },
        { key: 'today', title: 'Today', sub: new Date().toLocaleDateString(undefined, { weekday: 'short', day: 'numeric', month: 'short' }) },
        { key: 'week', title: 'This week', sub: 'Next 7 days' },
        { key: 'later', title: 'Later', sub: 'Beyond this week' },
        { key: 'completed', title: 'Recently completed', sub: 'Done' }
    ];
    const c = serverCounts || {};
    const tabDefs = [
        { key: 'all', label: 'All', count: c.all ?? totalMatchingRows },
        { key: 'overdue', label: 'Overdue', count: c.overdue ?? 0, danger: true },
        { key: 'today', label: 'Today', count: c.today ?? 0 },
        { key: 'week', label: 'This week', count: c.week ?? 0 },
        { key: 'later', label: 'Later', count: c.later ?? 0 },
        { key: 'completed', label: 'Completed', count: c.completed ?? 0 }
    ];

    // Type-segmented counts come from the server's counts block too.
    const typeCounts = {
        All: c.all ?? totalMatchingRows,
        Activity: c.typeActivity ?? 0,
        'Follow-up': c.typeFollowup ?? 0,
    };

    // ===== Helpers =====
    const priorityColorOf = (p) => ({ critical: '#dc2626', high: '#dc2626', medium: '#d97706', low: '#94a3b8' })[(p || 'medium').toLowerCase()] || '#94a3b8';
    const statusStyleOf = (s) => ({
        'not started': { bg: '#f1f5f9', color: '#475569' },
        'pending':     { bg: '#fef3c7', color: '#92400e' },
        'in progress': { bg: '#dbeafe', color: '#1e40af' },
        'scheduled':   { bg: '#dbeafe', color: '#1e40af' },
        'completed':   { bg: '#d1fae5', color: '#065f46' },
        'cancelled':   { bg: '#fee2e2', color: '#991b1b' }
    }[(s || 'not started').toLowerCase()] || { bg: '#f1f5f9', color: '#475569' });
    const datePillOf = (a) => {
        const d = a.DueDate || a.Date;
        if (!d) return { text: '—', color: '#64748b', weight: 400 };
        const due = new Date(d); due.setHours(0, 0, 0, 0);
        const t0 = new Date(); t0.setHours(0, 0, 0, 0);
        const diff = Math.round((due - t0) / 86400000);
        if ((a.Status || '').toLowerCase() === 'completed')
            return { text: due.toLocaleDateString(undefined, { day: 'numeric', month: 'short' }), color: '#64748b', weight: 400 };
        if (diff < 0) return { text: `${Math.abs(diff)}d overdue`, color: '#dc2626', weight: 600 };
        if (diff === 0) return { text: 'Today', color: '#d97706', weight: 600 };
        if (diff <= 7) return { text: due.toLocaleDateString(undefined, { weekday: 'short', day: 'numeric', month: 'short' }), color: '#2563eb', weight: 500 };
        return { text: due.toLocaleDateString(undefined, { day: 'numeric', month: 'short' }), color: '#3f4146', weight: 400 };
    };
    const iconFor = (subtype) => {
        const s = (subtype || '').toLowerCase();
        const ICONS = {
            meeting: { bg: '#E4EEF7', color: '#2E6FB0', path: <><rect x="2" y="4" width="12" height="9" rx="1" /><path d="M5 2v3M11 2v3M2 7h12" /></> },
            call:    { bg: '#ECE8F4', color: '#6B5B95', path: <path d="M3 3l2-1 2 3-1 1c.5 1.5 2 3 3.5 3.5l1-1 3 2-1 2c-2 1-7-1-8.5-2.5S2 5 3 3z" /> },
            email:   { bg: '#E4EEF7', color: '#2E6FB0', path: <><rect x="2" y="3" width="12" height="10" rx="1" /><path d="M2 4l6 5 6-5" /></> },
            prep:    { bg: '#FEF6E0', color: '#B7791F', path: <><path d="M3 2h7l3 3v9H3z" /><path d="M10 2v3h3M5 8h6M5 11h4" /></> },
            deliverable: { bg: '#FEF6E0', color: '#B7791F', path: <><path d="M2 5l6-3 6 3v6l-6 3-6-3z" /><path d="M2 5l6 3 6-3M8 8v6" /></> },
            review:  { bg: '#E4F2E9', color: '#2F855A', path: <><circle cx="6" cy="8" r="2" /><circle cx="11" cy="8" r="2" /><path d="M4 6V4h8v2" /></> },
            research:{ bg: '#E4F2E9', color: '#2F855A', path: <><circle cx="7" cy="7" r="4" /><path d="M10 10l4 4" /></> },
            note:    { bg: '#f1f5f9', color: '#64748b', path: <><rect x="3" y="3" width="10" height="10" rx="1" /><path d="M6 7h4M6 10h3" /></> },
            task:    { bg: '#f1f5f9', color: '#64748b', path: <><rect x="3" y="3" width="10" height="10" rx="1" /><path d="M6 8l1.5 1.5L11 6" /></> }
        };
        return ICONS[s] || { bg: '#f1f5f9', color: '#64748b', path: <><rect x="3" y="3" width="10" height="10" rx="1" /><path d="M6 7h4M6 10h3" /></> };
    };
    const ownersOf = (activity) => {
        if (!activity.TaskOwner) return [];
        if (typeof activity.TaskOwner === 'string') {
            try {
                const parsed = JSON.parse(activity.TaskOwner);
                return Array.isArray(parsed) ? parsed : activity.TaskOwner.split(', ');
            } catch {
                return activity.TaskOwner.split(', ');
            }
        }
        return Array.isArray(activity.TaskOwner) ? activity.TaskOwner : [];
    };

    const dueThisWeek = (c.today ?? 0) + (c.week ?? 0);

    return (
        <div style={{ maxWidth: 1080, margin: '0 auto' }}>
            {/* Header */}
            <div style={{ display: 'flex', alignItems: 'flex-start', gap: '16px', marginBottom: '20px', flexWrap: 'wrap' }}>
                <div>
                    <h1 style={{ fontSize: 24, fontWeight: 600, letterSpacing: '-0.015em', color: '#15171a', margin: 0 }}>Activities &amp; Tasks</h1>
                    <div style={{ fontSize: 13, color: '#71757c', marginTop: 4 }}>{totalMatchingRows} matching · {dueThisWeek} due this week</div>
                </div>
                <div style={{ flex: 1 }} />
                <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                    <button className="add-btn" onClick={() => setShowModal(true)}>+ Add Activity/Task</button>
                    <VoiceTaskManager onTaskCreate={handleVoiceTaskCreate} internalPeople={safeInternal} />
                    <button className="add-btn" onClick={handleExport}>Export</button>
                    <button className="add-btn" onClick={() => fileInputRef.current.click()}>Import</button>
                    <button className="add-btn" onClick={async () => {
                        setGlobalLoading(true);
                        invalidateAll();
                        // Wait one frame so refetch kicks off before we hide the loader
                        await new Promise(res => setTimeout(res, 0));
                        setGlobalLoading(false);
                        showToast('Data refreshed!');
                    }}>↻ Refresh</button>
                    {deleteMode ? (
                        <button
                            className="add-btn"
                            onClick={() => { setDeleteMode(false); clearSelection(); }}
                            style={{ background: '#64748b' }}
                        >✕ Cancel</button>
                    ) : (
                        <button
                            className="add-btn"
                            onClick={() => setDeleteMode(true)}
                            style={{ background: '#dc2626', display: 'inline-flex', alignItems: 'center', gap: 6 }}
                            title="Select rows to delete"
                        >
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="3 6 5 6 21 6"></polyline><path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6"></path><path d="M10 11v6M14 11v6"></path><path d="M9 6V4a2 2 0 0 1 2-2h2a2 2 0 0 1 2 2v2"></path></svg>
                            Delete
                        </button>
                    )}
                    <input ref={fileInputRef} type="file" accept=".xlsx,.xls" style={{ display: 'none' }} onChange={handleImport} />
                </div>
            </div>

            {/* Tab strip */}
            <div style={{ borderBottom: '1px solid #e5e7eb', marginBottom: '16px', display: 'flex', gap: '2px', flexWrap: 'wrap' }}>
                {tabDefs.map(t => {
                    const active = activeTab === t.key;
                    return (
                        <button
                            key={t.key}
                            onClick={() => setActiveTab(t.key)}
                            style={{
                                display: 'inline-flex', alignItems: 'center', gap: 8, padding: '10px 14px',
                                fontSize: 13.5, fontWeight: 500,
                                color: active ? (t.danger ? '#dc2626' : '#15171a') : '#71757c',
                                background: 'transparent', border: 'none', cursor: 'pointer',
                                borderBottom: '2px solid ' + (active ? (t.danger ? '#dc2626' : '#15171a') : 'transparent'),
                                marginBottom: '-1px'
                            }}
                        >
                            {t.label}
                            <span style={{
                                fontSize: 11.5, padding: '1px 7px', borderRadius: 10, minWidth: 22, textAlign: 'center',
                                background: active ? (t.danger ? '#dc2626' : '#15171a') : '#f1f5f9',
                                color: active ? '#ffffff' : '#71757c'
                            }}>{t.count}</span>
                        </button>
                    );
                })}
            </div>

            {/* Filter line */}
            <div style={{ display: 'flex', alignItems: 'center', gap: '14px', marginBottom: '20px', flexWrap: 'wrap' }}>
                {/* Type segmented control */}
                <div style={{ display: 'inline-flex', background: '#ffffff', border: '1px solid #e5e7eb', borderRadius: 7, padding: 3, gap: 2 }}>
                    {[{ k: 'All', l: 'All' }, { k: 'Activity', l: 'Tasks' }, { k: 'Follow-up', l: 'Follow-ups' }].map(seg => {
                        const active = selectedCategory === seg.k;
                        return (
                            <button
                                key={seg.k}
                                onClick={() => setSelectedCategory(seg.k)}
                                style={{
                                    border: 'none', padding: '6px 14px', fontSize: 13, fontWeight: 500, cursor: 'pointer', borderRadius: 5,
                                    background: active ? '#1e293b' : 'transparent', color: active ? '#ffffff' : '#71757c',
                                    display: 'inline-flex', alignItems: 'center', gap: 7
                                }}
                            >
                                {seg.l}
                                <span style={{ fontSize: 11.5, padding: '1px 6px', borderRadius: 10, minWidth: 22, textAlign: 'center', background: active ? 'rgba(255,255,255,0.2)' : 'rgba(0,0,0,0.06)' }}>{typeCounts[seg.k]}</span>
                            </button>
                        );
                    })}
                </div>

                {/* Entity filters */}
                <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                    <label style={{ fontSize: '13px', fontWeight: 600, color: '#4b5563' }}>Filter by</label>
                    <select
                        value={selectedDeal}
                        onChange={(e) => { setSelectedDeal(e.target.value); setSelectedInvestor('All'); }}
                        style={{ padding: '7px 10px', border: '1px solid #e5e7eb', borderRadius: '6px', fontSize: '13px', background: '#ffffff' }}
                    >
                        <option value="All">Deal: All</option>
                        {[...new Set(safeDeals.map(d => d.Title).filter(Boolean))].sort().map(title => (
                            <option key={title} value={title}>{title}</option>
                        ))}
                    </select>
                    <select
                        value={selectedInvestor}
                        onChange={(e) => { setSelectedInvestor(e.target.value); setSelectedDeal('All'); }}
                        style={{ padding: '7px 10px', border: '1px solid #e5e7eb', borderRadius: '6px', fontSize: '13px', background: '#ffffff' }}
                    >
                        <option value="All">Investor: All</option>
                        {[...new Set(safeInvestors.map(i => i.Name).filter(Boolean))].sort().map(name => (
                            <option key={name} value={name}>{name}</option>
                        ))}
                    </select>
                    <select
                        value={selectedOwner}
                        onChange={(e) => setSelectedOwner(e.target.value)}
                        style={{ padding: '7px 10px', border: '1px solid #e5e7eb', borderRadius: '6px', fontSize: '13px', background: '#ffffff' }}
                    >
                        <option value="All">Owner: All</option>
                        <option value="Unassigned">Unassigned</option>
                        {allOwners.map(name => (
                            <option key={name} value={name}>{name}</option>
                        ))}
                    </select>
                    {(selectedDeal !== 'All' || selectedInvestor !== 'All' || selectedOwner !== 'All') && (
                        <button
                            onClick={() => { setSelectedDeal('All'); setSelectedInvestor('All'); setSelectedOwner('All'); }}
                            style={{ background: 'none', border: 'none', color: '#2563eb', cursor: 'pointer', fontSize: '13px', fontWeight: 500 }}
                        >Clear</button>
                    )}
                </div>

                <div style={{ flex: 1 }} />

                {/* Priority legend */}
                <div style={{ display: 'inline-flex', alignItems: 'center', gap: 12, fontSize: 12, color: '#71757c' }}>
                    Priority:
                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5 }}><span style={{ width: 7, height: 7, borderRadius: '50%', background: '#dc2626' }} />High</span>
                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5 }}><span style={{ width: 7, height: 7, borderRadius: '50%', background: '#d97706' }} />Medium</span>
                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5 }}><span style={{ width: 7, height: 7, borderRadius: '50%', background: '#94a3b8' }} />Low</span>
                </div>
            </div>

            {/* Task list — flat scroll (server returns rows for the active sub-tab) */}
            <div style={{ paddingBottom: '60px' }}>
                {sortedActivities.length > 0 && (() => {
                    const items = sortedActivities;
                    const activeBucket = BUCKETS.find(b => b.key === activeTab);
                    const itemIds = items.map(a => a.Id);
                    const allBucketSelected = itemIds.length > 0 && itemIds.every(id => selectedIds.has(id));
                    return (
                        <div style={{ marginBottom: '32px' }}>
                            {activeBucket && (
                                <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '10px' }}>
                                    {deleteMode && (
                                        <input
                                            type="checkbox"
                                            checked={allBucketSelected}
                                            onChange={() => selectAllVisible(itemIds)}
                                            style={{ width: 16, height: 16, cursor: 'pointer', accentColor: '#1e293b' }}
                                            title="Select all currently loaded"
                                        />
                                    )}
                                    <span style={{ fontSize: 16, fontWeight: 600, color: '#15171a', letterSpacing: '-0.01em' }}>{activeBucket.title}</span>
                                    <span style={{ fontSize: 13, color: '#71757c' }}>{activeBucket.sub}</span>
                                    <span style={{ fontSize: 12, color: '#9b9ea5', marginLeft: 'auto' }}>{totalMatchingRows} {totalMatchingRows === 1 ? 'task' : 'tasks'}</span>
                                </div>
                            )}
                            <div style={{ background: '#ffffff', border: '1px solid #e5e7eb', borderRadius: 10, overflow: 'hidden' }}>
                                {items.map((activity, idx) => {
                                    const owners = ownersOf(activity);
                                    const ownerText = owners.length > 0 ? owners.join(', ') : 'Unassigned';
                                    const isUnassigned = owners.length === 0;
                                    const icon = iconFor(activity.Subtype);
                                    const dp = datePillOf(activity);
                                    const st = statusStyleOf(activity.Status);
                                    const isDone = (activity.Status || '').toLowerCase() === 'completed';
                                    const category = activity.Category || activity.Type;
                                    const isSelected = selectedIds.has(activity.Id);
                                    return (
                                        <div
                                            key={activity.Id}
                                            onClick={() => setViewingActivity(activity)}
                                            style={{
                                                display: 'flex', alignItems: 'center', gap: '14px',
                                                padding: '14px 18px', cursor: 'pointer',
                                                borderTop: idx === 0 ? 'none' : '1px solid #e5e7eb',
                                                background: isSelected ? '#eff6ff' : 'transparent'
                                            }}
                                        >
                                            {deleteMode && (
                                                <input
                                                    type="checkbox"
                                                    checked={isSelected}
                                                    onClick={(e) => e.stopPropagation()}
                                                    onChange={() => toggleSelect(activity.Id)}
                                                    style={{ width: 16, height: 16, cursor: 'pointer', accentColor: '#1e293b', flexShrink: 0 }}
                                                />
                                            )}
                                            <span style={{ width: 8, height: 8, borderRadius: '50%', background: priorityColorOf(activity.Priority), flexShrink: 0 }} title={activity.Priority || 'Medium'} />
                                            <span style={{ width: 28, height: 28, borderRadius: 6, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, background: icon.bg, color: icon.color, opacity: isDone ? 0.55 : 1 }}>
                                                <svg width="14" height="14" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.6">{icon.path}</svg>
                                            </span>
                                            <div style={{ flex: 1, minWidth: 0 }}>
                                                <div style={{ fontSize: 14.5, fontWeight: 500, color: isDone ? '#71757c' : '#15171a', lineHeight: 1.35, marginBottom: 3, letterSpacing: '-0.005em', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', textDecoration: isDone ? 'line-through' : 'none' }}>
                                                    {activity.Title || activity.Task}
                                                </div>
                                                <div style={{ fontSize: 12.5, color: '#71757c', lineHeight: 1.3, display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
                                                    <span style={{ padding: '2px 8px', fontSize: 11, fontWeight: 600, borderRadius: 4, background: category === 'Follow-up' ? '#eff6ff' : '#f1f5f9', color: category === 'Follow-up' ? '#2563eb' : '#3f4146' }}>
                                                        {activity.Subtype || category}
                                                    </span>
                                                    <span style={{ color: '#d8d8d4' }}>·</span>
                                                    <span style={{ fontWeight: 500, color: '#3f4146' }}>{activity.RelatedTo || 'No project'}</span>
                                                    <span style={{ color: '#d8d8d4' }}>·</span>
                                                    <span style={{ color: isUnassigned ? '#dc2626' : '#71757c' }}>{ownerText}</span>
                                                </div>
                                            </div>
                                            <span style={{ fontSize: 12.5, color: dp.color, fontWeight: dp.weight, whiteSpace: 'nowrap', flexShrink: 0 }}>{dp.text}</span>
                                            <span style={{ fontSize: 11.5, fontWeight: 500, padding: '4px 10px', borderRadius: 12, whiteSpace: 'nowrap', flexShrink: 0, minWidth: 88, textAlign: 'center', background: st.bg, color: st.color }}>
                                                {activity.Status || 'Not Started'}
                                            </span>
                                            <span style={{ display: 'flex', gap: 4, flexShrink: 0 }}>
                                                <button
                                                    title="Edit"
                                                    onClick={(e) => { e.stopPropagation(); setEditingActivity(activity); setShowModal(true); }}
                                                    style={{ background: 'none', border: 'none', color: '#2563eb', cursor: 'pointer', padding: '4px', display: 'inline-flex', alignItems: 'center' }}
                                                >
                                                    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/></svg>
                                                </button>
                                                <button
                                                    title="Delete"
                                                    onClick={async (e) => {
                                                        e.stopPropagation();
                                                        if (!window.confirm(`Delete "${activity.Title || activity.Task}"?`)) return;
                                                        cacheRemoveIds([activity.Id]);
                                                        showToast('Activity deleted successfully!');
                                                        try {
                                                            await api.deleteActivity(activity.Id);
                                                        } catch (error) {
                                                            showToast('Failed to delete: ' + error.message + ' — restoring.', 'error');
                                                            invalidateAll();
                                                        }
                                                    }}
                                                    style={{ background: 'none', border: 'none', color: '#dc2626', cursor: 'pointer', padding: '4px', display: 'inline-flex', alignItems: 'center' }}
                                                >
                                                    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="3 6 5 6 21 6"/><path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6"/><path d="M10 11v6M14 11v6"/><path d="M9 6V4a2 2 0 0 1 2-2h2a2 2 0 0 1 2 2v2"/></svg>
                                                </button>
                                            </span>
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                    );
                })()}

                {/* Loading / sentinel row for infinite scroll */}
                <div ref={sentinelRef} style={{ height: 1 }} aria-hidden="true" />
                {isFetchingNextPage && (
                    <div style={{ textAlign: 'center', padding: '20px 0', color: '#64748b', fontSize: 13 }}>
                        Loading more…
                    </div>
                )}
                {!isLoading && sortedActivities.length === 0 && (
                    <div style={{ textAlign: 'center', padding: '48px 20px', color: '#94a3b8', fontSize: 14 }}>
                        No activities match your filters.
                    </div>
                )}
                {isLoading && (
                    <div style={{ textAlign: 'center', padding: '48px 0', color: '#64748b', fontSize: 14 }}>
                        Loading activities…
                    </div>
                )}
                {!hasNextPage && sortedActivities.length > 0 && (
                    <div style={{ textAlign: 'center', padding: '20px 0', color: '#cbd5e1', fontSize: 12 }}>
                        — end of list —
                    </div>
                )}
            </div>

            {showModal && (
                <ActivityModal
                    activity={editingActivity}
                    categories={categories}
                    internal={safeInternal}   // {/* ✅ SAFE PROPS */}
                    deals={safeDeals}          //{/* ✅ SAFE PROPS */}
                    investors={safeInvestors} // {/* ✅ SAFE PROPS */}
                    onClose={() => { setShowModal(false); setEditingActivity(null); }}
                    onSave={async (activityData) => {
                        try {
                            setSaving(true);
                            let saved;
                            if (editingActivity) {
                                saved = await api.updateActivity(editingActivity.Id, activityData);
                            } else {
                                // Client-side dupe check across all rows is no longer possible
                                // (we don't have the full list in memory). We now rely on the
                                // user's judgement + a server-side unique constraint (future work).
                                saved = await api.createActivity(activityData);
                            }
                            if (saved && saved.Id) cacheUpsertRow(saved);
                            setShowModal(false);
                            setEditingActivity(null);
                            showToast('Activity saved successfully!');
                        } catch (error) {
                            invalidateAll();
                            showToast('Failed to save: ' + error.message, 'error');
                        } finally {
                            setSaving(false);
                            setGlobalLoading(false);
                        }
                    }}
                    saving={saving}
                    onAddCategory={(newCategory) => {
                        if (!categories.includes(newCategory)) {
                            setCategories([...categories, newCategory]);
                        }
                    }}
                />
            )}

            {viewingActivity && (
                <ActivityDetailModal
                    activity={viewingActivity}
                    onClose={() => setViewingActivity(null)}
                    onEdit={() => { setEditingActivity(viewingActivity); setViewingActivity(null); setShowModal(true); }}
                />
            )}

            {selectedIds.size > 0 && (
                <div style={{
                    position: 'fixed', bottom: 20, left: '50%', transform: 'translateX(-50%)',
                    background: '#1e293b', color: '#ffffff', padding: '12px 20px', borderRadius: 10,
                    boxShadow: '0 10px 30px rgba(0,0,0,0.25)', display: 'flex', alignItems: 'center', gap: 16,
                    zIndex: 1000
                }}>
                    <span style={{ fontSize: 14, fontWeight: 600 }}>{selectedIds.size} selected</span>
                    <button
                        onClick={bulkDelete}
                        style={{ background: '#dc2626', color: '#ffffff', border: 'none', padding: '8px 16px', borderRadius: 6, fontSize: 13, fontWeight: 600, cursor: 'pointer' }}
                    >Delete</button>
                    <button
                        onClick={clearSelection}
                        style={{ background: 'transparent', color: '#cbd5e1', border: 'none', cursor: 'pointer', fontSize: 13 }}
                    >Cancel</button>
                </div>
            )}
        </div>
    );
}

// Activity Detail Modal — read-only view of one activity
function ActivityDetailModal({ activity, onClose, onEdit }) {
    let owners = [];
    if (activity.TaskOwner) {
        if (typeof activity.TaskOwner === 'string') {
            try {
                const parsed = JSON.parse(activity.TaskOwner);
                owners = Array.isArray(parsed) ? parsed : activity.TaskOwner.split(', ');
            } catch {
                owners = activity.TaskOwner.split(', ');
            }
        } else if (Array.isArray(activity.TaskOwner)) {
            owners = activity.TaskOwner;
        }
    }
    const fmt = (d) => d ? new Date(d).toLocaleDateString() : '—';
    const Row = ({ label, value }) => (
        <div style={{ display: 'flex', gap: '12px', padding: '10px 0', borderBottom: '1px solid #f1f5f9' }}>
            <div style={{ width: '140px', flexShrink: 0, fontSize: '12px', fontWeight: 600, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.04em' }}>{label}</div>
            <div style={{ flex: 1, fontSize: '14px', color: '#0f172a', whiteSpace: 'pre-wrap' }}>{value || '—'}</div>
        </div>
    );

    return (
        <div className="modal-overlay" onClick={onClose}>
            <div className="modal" onClick={(e) => e.stopPropagation()} style={{ maxHeight: '90vh', overflowY: 'auto' }}>
                <h2 style={{ marginBottom: '4px' }}>{activity.Title || activity.Task}</h2>
                <div style={{ display: 'flex', gap: '8px', marginBottom: '16px', flexWrap: 'wrap' }}>
                    {activity.Subtype && (
                        <span style={{ background: '#e2e8f0', color: '#475569', padding: '2px 8px', borderRadius: '4px', fontSize: '11px', fontWeight: 600, textTransform: 'uppercase' }}>{activity.Subtype}</span>
                    )}
                    <span style={{ background: '#dbeafe', color: '#1e40af', padding: '2px 8px', borderRadius: '4px', fontSize: '11px', fontWeight: 600 }}>{activity.Category || activity.Type}</span>
                </div>

                <Row label="Status" value={activity.Status} />
                <Row label="Priority" value={activity.Priority} />
                <Row label="Date Added" value={fmt(activity.Date)} />
                <Row label="Due Date" value={fmt(activity.DueDate)} />
                <Row label="Owner(s)" value={owners.length > 0 ? owners.join(', ') : 'Unassigned'} />
                <Row label="Related To" value={activity.RelatedTo} />
                <Row label="Comments" value={activity.Notes} />

                <div className="modal-actions" style={{ marginTop: '20px' }}>
                    <button type="button" className="btn btn-primary" onClick={onEdit}>Edit</button>
                    <button type="button" className="btn btn-secondary" onClick={onClose}>Close</button>
                </div>
            </div>
        </div>
    );
}

// Activity Modal Component - ENHANCED WITH MULTI-SELECT TASK OWNERS
function ActivityModal({ activity, categories, internal, deals, investors, onClose, onSave, saving, onAddCategory }) {
    const [formData, setFormData] = useState(activity ? {
        title: activity.Title || activity.Task,
        category: activity.Category || activity.Type || 'Follow-up',
        subtype: activity.Subtype || '',
        taskOwner: activity.TaskOwner || '',
        date: new Date(activity.Date || activity.DueDate).toISOString().split('T')[0],
        dueDate: activity.DueDate ? new Date(activity.DueDate).toISOString().split('T')[0] : '',
        status: activity.Status,
        relatedTo: activity.RelatedTo || '',
        relatedToType: activity.RelatedTo ? 'custom' : 'none',
        notes: activity.Notes || '',
        priority: activity.Priority || 'Medium'
    } : {
        title: '',
        category: 'Follow-up',
        subtype: '',
        taskOwner: '',
        date: new Date().toISOString().split('T')[0],
        dueDate: '',
        status: 'Pending',
        relatedTo: '',
        relatedToType: 'none',
        notes: '',
        priority: 'Medium'
    });

    const [selectedOwners, setSelectedOwners] = useState(
        activity && activity.TaskOwner ? activity.TaskOwner.split(', ') : []
    );
    const [newCategory, setNewCategory] = useState('');
    const [showNewCategory, setShowNewCategory] = useState(false);
    const [newSubtype, setNewSubtype] = useState('');
    const [showNewSubtype, setShowNewSubtype] = useState(false);
    const [customSubtypes, setCustomSubtypes] = useState([]);

    const subtypeOptions = formData.category === 'Activity'
        ? ['Task', 'Research', 'Prep', 'Deliverable', ...customSubtypes]
        : ['Meeting', 'Call', 'Email', 'Note', ...customSubtypes];

    const handleAddOwner = (ownerName) => {
        if (ownerName && !selectedOwners.includes(ownerName)) {
            setSelectedOwners([...selectedOwners, ownerName]);
        }
    };

    const handleRemoveOwner = (ownerName) => {
        setSelectedOwners(selectedOwners.filter(o => o !== ownerName));
    };

    const handleRelatedToTypeChange = (type) => {
        setFormData({...formData, relatedToType: type, relatedTo: ''});
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        
        const finalOwners = selectedOwners.join(', ');
        
        onSave({
            type: formData.category,
            subtype: formData.subtype || null,
            title: formData.title,
            taskOwner: finalOwners,
            date: formData.date,
            dueDate: formData.dueDate || null,
            status: formData.status,
            relatedTo: formData.relatedTo,
            priority: formData.priority,
            notes: formData.notes
        });
    };

    return (
        <div className="modal-overlay" onClick={onClose}>
            <div className="modal" onClick={(e) => e.stopPropagation()} style={{ maxHeight: '90vh', overflowY: 'auto' }}>
                <h2>{activity ? 'Edit Activity/Task' : 'Add New Activity/Task'}</h2>
                <form onSubmit={handleSubmit}>
                    <div className="form-group">
                        <label>Title/Task *</label>
                        <input type="text" required value={formData.title} onChange={(e) => setFormData({...formData, title: e.target.value})} />
                    </div>
                    
                    <div className="form-group">
                        <label>Category *</label>
                        <select value={formData.category} onChange={(e) => {
                            if (e.target.value === '__new__') {
                                setShowNewCategory(true);
                            } else {
                                setFormData({...formData, category: e.target.value, subtype: ''});
                            }
                        }}>
                            {categories.map(cat => (<option key={cat} value={cat}>{cat}</option>))}
                            <option value="__new__">+ Add New Category</option>
                        </select>
                        {showNewCategory && (
                            <div style={{ display: 'flex', gap: '8px', marginTop: '8px' }}>
                                <input type="text" placeholder="New category name" value={newCategory} onChange={(e) => setNewCategory(e.target.value)} style={{ flex: 1 }} />
                                <button type="button" className="btn btn-primary" onClick={() => {
                                    if (newCategory.trim()) {
                                        onAddCategory(newCategory.trim());
                                        setFormData({...formData, category: newCategory.trim()});
                                        setNewCategory('');
                                        setShowNewCategory(false);
                                    }
                                }}>Add</button>
                                <button type="button" className="btn btn-secondary" onClick={() => setShowNewCategory(false)}>Cancel</button>
                            </div>
                        )}
                    </div>

                    <div className="form-group">
                        <label>Sub-task type</label>
                        <select value={formData.subtype} onChange={(e) => {
                            if (e.target.value === '__new__') {
                                setShowNewSubtype(true);
                            } else {
                                setFormData({...formData, subtype: e.target.value});
                            }
                        }}>
                            <option value="">— None —</option>
                            {subtypeOptions.map(s => (<option key={s} value={s}>{s}</option>))}
                            <option value="__new__">+ Add New Sub-task</option>
                        </select>
                        {showNewSubtype && (
                            <div style={{ display: 'flex', gap: '8px', marginTop: '8px' }}>
                                <input type="text" placeholder="New sub-task name" value={newSubtype} onChange={(e) => setNewSubtype(e.target.value)} style={{ flex: 1 }} />
                                <button type="button" className="btn btn-primary" onClick={() => {
                                    if (newSubtype.trim()) {
                                        setCustomSubtypes([...customSubtypes, newSubtype.trim()]);
                                        setFormData({...formData, subtype: newSubtype.trim()});
                                        setNewSubtype('');
                                        setShowNewSubtype(false);
                                    }
                                }}>Add</button>
                                <button type="button" className="btn btn-secondary" onClick={() => setShowNewSubtype(false)}>Cancel</button>
                            </div>
                        )}
                    </div>

                    <div className="form-group">
                        <label>Task Owner(s) *</label>
                        
                        {/* Selected Owners Display */}
                        {selectedOwners.length > 0 && (
                            <div style={{ 
                                marginBottom: '12px', 
                                padding: '12px', 
                                background: '#f0f9ff', 
                                borderRadius: '8px',
                                display: 'flex',
                                flexWrap: 'wrap',
                                gap: '8px'
                            }}>
                                {selectedOwners.map(owner => {
                                    const person = internal.find(p => p.Name === owner);
                                    return (
                                        <span 
                                            key={owner}
                                            style={{
                                                background: '#667eea',
                                                color: 'white',
                                                padding: '4px 12px',
                                                borderRadius: '12px',
                                                fontSize: '14px',
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: '8px'
                                            }}
                                        >
                                            {owner} {person && person.Email && `(${person.Email})`}
                                            <button
                                                type="button"
                                                onClick={() => handleRemoveOwner(owner)}
                                                style={{
                                                    background: 'transparent',
                                                    border: 'none',
                                                    color: 'white',
                                                    cursor: 'pointer',
                                                    fontSize: '16px',
                                                    padding: '0'
                                                }}
                                            >
                                                ×
                                            </button>
                                        </span>
                                    );
                                })}
                            </div>
                        )}
                        
                        <div style={{ display: 'flex', gap: '8px' }}>
                            <select 
                                style={{ flex: 1 }}
                                onChange={(e) => {
                                    if (e.target.value) {
                                        handleAddOwner(e.target.value);
                                        e.target.value = '';
                                    }
                                }}
                            >
                                <option value="">Select Owner to Add</option>
                                {internal && internal.map(person => (
                                    <option key={person.Id} value={person.Name}>
                                        {person.Name} {person.Email && `(${person.Email})`}
                                    </option>
                                ))}
                            </select>
                        </div>
                        
                        {selectedOwners.length === 0 && (
                            <small style={{ color: '#ef4444', fontSize: '12px', marginTop: '4px', display: 'block' }}>
                                Please add at least one task owner
                            </small>
                        )}
                    </div>

                    <div className="form-group">
                        <label>Date *</label>
                        <input type="date" required value={formData.date} onChange={(e) => setFormData({...formData, date: e.target.value})} />
                    </div>

                    <div className="form-group">
                        <label>Due Date</label>
                        <input type="date" value={formData.dueDate} onChange={(e) => setFormData({...formData, dueDate: e.target.value})} />
                    </div>

                    <div className="form-group">
                        <label>Status *</label>
                        <select value={formData.status} onChange={(e) => setFormData({...formData, status: e.target.value})}>
                            <option>Pending</option>
                            <option>In Progress</option>
                            <option>Completed</option>
                            <option>Cancelled</option>
                        </select>
                    </div>

                    <div className="form-group">
                        <label>Priority *</label>
                        <select value={formData.priority} onChange={(e) => setFormData({...formData, priority: e.target.value})}>
                            <option>Low</option>
                            <option>Medium</option>
                            <option>High</option>
                        </select>
                    </div>

                    <div className="form-group">
                        <label>Related To</label>
                        <div style={{ display: 'flex', gap: '8px', marginBottom: '8px' }}>
                            <button type="button" className={`btn ${formData.relatedToType === 'deal' ? 'btn-primary' : 'btn-secondary'}`} onClick={() => handleRelatedToTypeChange('deal')}>Deal</button>
                            <button type="button" className={`btn ${formData.relatedToType === 'investor' ? 'btn-primary' : 'btn-secondary'}`} onClick={() => handleRelatedToTypeChange('investor')}>Investor/Funding</button>
                            <button type="button" className={`btn ${formData.relatedToType === 'custom' ? 'btn-primary' : 'btn-secondary'}`} onClick={() => handleRelatedToTypeChange('custom')}>Other</button>
                        </div>
                        
                        {formData.relatedToType === 'deal' && (
                            <select value={formData.relatedTo} onChange={(e) => setFormData({...formData, relatedTo: e.target.value})}>
                                <option value="">Select Deal</option>
                                {deals && deals.map(deal => (
                                    <option key={deal.Id} value={deal.Title}>{deal.Title} - {deal.Company}</option>
                                ))}
                            </select>
                        )}
                        
                        {formData.relatedToType === 'investor' && (
                            <select value={formData.relatedTo} onChange={(e) => setFormData({...formData, relatedTo: e.target.value})}>
                                <option value="">Select Investor/Funding</option>
                                {investors && investors.map(inv => (
                                    <option key={inv.Id} value={inv.Name}>{inv.Name} ({inv.Type})</option>
                                ))}
                            </select>
                        )}
                        
                        {formData.relatedToType === 'custom' && (
                            <input type="text" value={formData.relatedTo} onChange={(e) => setFormData({...formData, relatedTo: e.target.value})} placeholder="Enter custom text" />
                        )}
                    </div>

                    <div className="form-group">
                        <label>Notes</label>
                        <textarea value={formData.notes} onChange={(e) => setFormData({...formData, notes: e.target.value})} placeholder="Add notes..." rows="4" />
                    </div>

                    <div className="modal-actions">
                        <button type="submit" className="btn btn-primary" disabled={saving || selectedOwners.length === 0}>
                            {saving ? 'Saving...' : 'Save'}
                        </button>
                        <button type="button" className="btn btn-secondary" onClick={onClose}>Cancel</button>
                    </div>
                </form>
            </div>
        </div>
    );
}

// Partnerships View Component
// Partnerships View Component - WITH SORTING AND RELATIONSHIP OWNER
function PartnershipsView({ data, searchTerm, onDataChange, onLocalRemove, onLocalUpsert, showToast, setGlobalLoading }) {
    const [showModal, setShowModal] = useState(false);
    const [editingPartnership, setEditingPartnership] = useState(null);
    const [saving, setSaving] = useState(false);
    const [internalTeam, setInternalTeam] = useState([]);
    const [selectedIds, setSelectedIds] = useState(new Set());
    const [deleteMode, setDeleteMode] = useState(false);
    const fileInputRef = useRef(null);

    const toggleSelect = (id) => setSelectedIds(prev => { const n = new Set(prev); if (n.has(id)) n.delete(id); else n.add(id); return n; });
    const selectAllVisible = (ids) => setSelectedIds(prev => { const allSel = ids.every(id => prev.has(id)); const n = new Set(prev); if (allSel) ids.forEach(id => n.delete(id)); else ids.forEach(id => n.add(id)); return n; });
    const clearSelection = () => setSelectedIds(new Set());
    const bulkDelete = async () => {
        const ids = Array.from(selectedIds);
        if (ids.length === 0) return;
        if (!window.confirm(`Delete ${ids.length} selected partnership${ids.length === 1 ? '' : 's'}?`)) return;
        if (onLocalRemove) onLocalRemove(ids);
        clearSelection();
        setDeleteMode(false);
        showToast(`${ids.length} partnership${ids.length === 1 ? '' : 's'} deleted!`);
        try {
            for (const id of ids) await api.deletePartnership(id);
        } catch (error) {
            showToast('Delete failed: ' + error.message + ' — restoring.', 'error');
            await api.getAllData(true);
            await onDataChange();
        }
    };

    useEffect(() => {
        loadInternalTeam();
    }, []);

    const loadInternalTeam = async () => {
        try {
            const team = await api.getInternal();
            setInternalTeam(team || []);
        } catch (error) {
            console.error('Failed to load internal team:', error);
        }
    };

    const filteredPartnerships = data.filter(partnership =>
        partnership.Partner?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        partnership.PartnerType?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        partnership.Region?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        partnership.Status?.toLowerCase().includes(searchTerm.toLowerCase())
    );

    // ✅ SORTING
    const { items: sortedPartnerships, requestSort, sortConfig } = useSortableData(filteredPartnerships);

    const parseJsonField = (field) => {
        if (!field) return [];
        try {
            return typeof field === 'string' ? JSON.parse(field) : field;
        } catch {
            return field.split(',').map(s => s.trim());
        }
    };

    const handleExport = () => {
        const exportData = filteredPartnerships.map(p => {
            const relationshipOwners = parseJsonField(p.RelationshipOwner || '');
            return {
                'Region': p.Region || '',
                'Type': p.PartnerType || '',
                'Overall Status': p.Status || '',
                'Company Name': p.Partner || '',
                'Person Name': p.Contact || '',
                'Person Designation': p.PersonDesignation || '',
                'Relationship Owner': relationshipOwners.join(', '),
                'Latest Status': p.LatestStatus || '',
                'Opportunity Tagging': p.OpportunityTagging || '',
                'Comments': p.Comments || ''
            };
        });
        
        const headers = Object.keys(exportData[0] || {});
        const csv = [
            headers.join(','),
            ...exportData.map(row => headers.map(h => `"${row[h] || ''}"`).join(','))
        ].join('\n');
        
        const blob = new Blob([csv], { type: 'text/csv' });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `Partnerships_Export_${new Date().toISOString().split('T')[0]}.csv`;
        a.click();
        window.URL.revokeObjectURL(url);
        
        showToast('Partnerships exported successfully!');
    };

    const handleImport = (e) => {
        const file = e.target.files[0];
        if (!file) return;

        const parseDate = (s) => {
            if (s === null || s === undefined || s === '') return null;
            if (s instanceof Date) return s.toISOString().split('T')[0];
            if (typeof s === 'number') {
                const d = new Date(Date.UTC(1899, 11, 30) + s * 86400000);
                return isNaN(d) ? null : d.toISOString().split('T')[0];
            }
            const t = String(s).trim();
            if (/^\d{4}-\d{2}-\d{2}/.test(t)) return t.slice(0, 10);
            const m = t.match(/^(\d{1,2})[\/\-](\d{1,2})[\/\-](\d{4})$/);
            if (m) { const [, d, mo, y] = m; return `${y}-${mo.padStart(2,'0')}-${d.padStart(2,'0')}`; }
            const dt = new Date(t);
            return isNaN(dt) ? null : dt.toISOString().split('T')[0];
        };

        const reader = new FileReader();
        reader.onload = async (event) => {
            try {
                setGlobalLoading(true);
                const text = event.target.result;
                const lines = text.split('\n');
                const headers = lines[0].split(',').map(h => h.trim().replace(/"/g, ''));

                // Existing partnerships keyed by Partner name (case-insensitive) for dedupe
                const existingKeys = new Set((Array.isArray(data) ? data : []).map(p => (p.Partner || '').trim().toLowerCase()));

                const partnerships = [];
                for (let i = 1; i < lines.length; i++) {
                    if (!lines[i].trim()) continue;

                    const values = lines[i].split(',').map(v => v.trim().replace(/"/g, ''));
                    const row = {};
                    headers.forEach((header, index) => { row[header] = values[index]; });

                    const companyName = row['Company Name'] || row.Partner || row.Company;
                    if (companyName) {
                        partnerships.push({
                            partner: companyName,
                            partnerType: row.Type || row.PartnerType || 'ISV',
                            status: row['Overall Status'] || row.Status || 'Active',
                            startDate: parseDate(row['Start Date'] || row.StartDate) || null,
                            value: row.Value ? parseFloat(row.Value) : null,
                            contact: row['Person Name'] || row.Contact || '',
                            benefits: row.Benefits || '',
                            region: row.Region || '',
                            seniority: row.Seniority || '',
                            targetCustomer: row['Target Customer'] || row.TargetCustomer || '',
                            relationshipOwner: row['Relationship Owner'] || row.RelationshipOwner || '',
                            personDesignation: row['Person Designation'] || row.PersonDesignation || row.Designation || '',
                            latestStatus: row['Latest Status'] || row.LatestStatus || '',
                            opportunityTagging: row['Opportunity Tagging'] || row.OpportunityTagging || '',
                            comments: row.Comments || ''
                        });
                    }
                }

                let imported = 0;
                let skipped = 0;
                for (const partnership of partnerships) {
                    const key = (partnership.partner || '').trim().toLowerCase();
                    if (existingKeys.has(key)) { skipped++; continue; }
                    await api.createPartnership(partnership);
                    existingKeys.add(key);
                    imported++;
                }

                if (imported > 0 && skipped > 0) showToast(`Imported ${imported}, skipped ${skipped} duplicate${skipped === 1 ? '' : 's'}`);
                else if (imported > 0) showToast(`Imported ${imported} partnership${imported === 1 ? '' : 's'}!`);
                else if (skipped > 0) showToast(`No new rows — ${skipped} already exist`, 'error');
                else showToast('No rows found in file', 'error');
                await api.getAllData(true);
                await onDataChange();
            } catch (error) {
                showToast('Import failed: ' + error.message, 'error');
            } finally {
                setGlobalLoading(false);
            }
        };
        
        reader.readAsText(file);
        e.target.value = '';
    };

    const totalValue = filteredPartnerships.reduce((sum, p) => sum + Number(p.Value || 0), 0);
    const activeCount = filteredPartnerships.filter(p => p.Status === 'Active').length;

    return (
        <div>
            <div className="stats-grid">
                <div className="stat-card">
                    <h3>Total Partnerships</h3>
                    <p>{filteredPartnerships.length}</p>
                </div>
                <div className="stat-card">
                    <h3>Active Partnerships</h3>
                    <p>{activeCount}</p>
                </div>
                <div className="stat-card">
                    <h3>Total Value</h3>
                    <p>${totalValue.toLocaleString()}</p>
                </div>
            </div>

            <div style={{ display: 'flex', gap: '12px', marginBottom: '20px', flexWrap: 'wrap' }}>
                <button className="add-btn" onClick={() => { setEditingPartnership(null); setShowModal(true); }}>
                    + Add Partnership
                </button>
                <button 
                    className="add-btn" 
                    onClick={handleExport}
                    style={{ background: '#1e293b' }}
                >
                    📤 Export to Excel
                </button>
                <button 
                    className="add-btn" 
                    onClick={() => fileInputRef.current?.click()}
                    style={{ background: '#1e293b' }}
                >
                    📥 Import from Excel
                </button>
                <input
                    ref={fileInputRef}
                    type="file"
                    accept=".csv,.xlsx,.xls"
                    style={{ display: 'none' }}
                    onChange={handleImport}
                />
                {deleteMode ? (
                    <button className="add-btn" onClick={() => { setDeleteMode(false); clearSelection(); }} style={{ background: '#64748b' }}>✕ Cancel</button>
                ) : (
                    <button className="add-btn" onClick={() => setDeleteMode(true)} style={{ background: '#dc2626', display: 'inline-flex', alignItems: 'center', gap: 6 }} title="Select rows to delete">
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="3 6 5 6 21 6"/><path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6"/><path d="M10 11v6M14 11v6"/><path d="M9 6V4a2 2 0 0 1 2-2h2a2 2 0 0 1 2 2v2"/></svg>
                        Delete
                    </button>
                )}
            </div>

            <div className="table-container">
                <table>
                    <thead>
                        <tr>
                            {deleteMode && <th style={{ width: 32 }}><input type="checkbox" checked={sortedPartnerships.length > 0 && sortedPartnerships.every(p => selectedIds.has(p.Id))} onChange={() => selectAllVisible(sortedPartnerships.map(p => p.Id))} style={{ width: 16, height: 16, cursor: 'pointer', accentColor: '#1e293b' }} /></th>}
                            <SortableTableHeader label="Region" sortKey="Region" currentSort={sortConfig} onSort={requestSort} />
                            <SortableTableHeader label="Type" sortKey="PartnerType" currentSort={sortConfig} onSort={requestSort} />
                            <SortableTableHeader label="Overall Status" sortKey="Status" currentSort={sortConfig} onSort={requestSort} />
                            <SortableTableHeader label="Company Name" sortKey="Partner" currentSort={sortConfig} onSort={requestSort} />
                            <SortableTableHeader label="Person Name" sortKey="Contact" currentSort={sortConfig} onSort={requestSort} />
                            <SortableTableHeader label="Person Designation" sortKey="PersonDesignation" currentSort={sortConfig} onSort={requestSort} />
                            <SortableTableHeader label="Relationship Owner" sortKey="RelationshipOwner" currentSort={sortConfig} onSort={requestSort} />
                            <SortableTableHeader label="Latest Status" sortKey="LatestStatus" currentSort={sortConfig} onSort={requestSort} />
                            <SortableTableHeader label="Opportunity Tagging" sortKey="OpportunityTagging" currentSort={sortConfig} onSort={requestSort} />
                            <SortableTableHeader label="Comments" sortKey="Comments" currentSort={sortConfig} onSort={requestSort} />
                            <th>Actions</th>
                        </tr>
                    </thead>
                    <tbody>
                        {sortedPartnerships.map(p => {
                            const relationshipOwners = parseJsonField(p.RelationshipOwner || '');
                            const dash = <span style={{ color: '#9ca3af' }}>—</span>;

                            return (
                                <tr key={p.Id} style={{ background: selectedIds.has(p.Id) ? '#eff6ff' : undefined }}>
                                    {deleteMode && <td><input type="checkbox" checked={selectedIds.has(p.Id)} onChange={() => toggleSelect(p.Id)} style={{ width: 16, height: 16, cursor: 'pointer', accentColor: '#1e293b' }} /></td>}
                                    <td>{p.Region || dash}</td>
                                    <td><span className="status-badge status-proposal">{p.PartnerType}</span></td>
                                    <td><span className={`status-badge status-${p.Status?.toLowerCase()}`}>{p.Status || '—'}</span></td>
                                    <td><strong>{p.Partner}</strong></td>
                                    <td>{p.Contact || dash}</td>
                                    <td>{p.PersonDesignation || dash}</td>
                                    <td>
                                        {relationshipOwners.length > 0 ? (
                                            <div className="tag-group">
                                                {relationshipOwners.map((owner, idx) => (
                                                    <span key={idx} className="badge badge-partner">{owner}</span>
                                                ))}
                                            </div>
                                        ) : dash}
                                    </td>
                                    <td style={{ maxWidth: 260, whiteSpace: 'normal', lineHeight: 1.35 }}>{p.LatestStatus || dash}</td>
                                    <td>{p.OpportunityTagging || dash}</td>
                                    <td style={{ maxWidth: 300, whiteSpace: 'normal', lineHeight: 1.35 }}>{p.Comments || dash}</td>
                                    <td style={{ whiteSpace: 'nowrap', minWidth: 70 }}>
                                        <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                                            <button title="Edit" onClick={() => { setEditingPartnership(p); setShowModal(true); }} style={{ background: 'none', border: 'none', color: '#2563eb', cursor: 'pointer', padding: '4px', display: 'inline-flex', alignItems: 'center' }}>
                                                <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/></svg>
                                            </button>
                                            <button title="Delete" onClick={async () => {
                                                if (!window.confirm('Delete this partnership?')) return;
                                                if (onLocalRemove) onLocalRemove([p.Id]);
                                                showToast('Partnership deleted!');
                                                try { await api.deletePartnership(p.Id); }
                                                catch (error) { showToast('Failed to delete: ' + error.message + ' — restoring.', 'error'); await api.getAllData(true); await onDataChange(); }
                                            }} style={{ background: 'none', border: 'none', color: '#dc2626', cursor: 'pointer', padding: '4px', display: 'inline-flex', alignItems: 'center' }}>
                                                <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="3 6 5 6 21 6"/><path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6"/><path d="M10 11v6M14 11v6"/><path d="M9 6V4a2 2 0 0 1 2-2h2a2 2 0 0 1 2 2v2"/></svg>
                                            </button>
                                        </div>
                                    </td>
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
            </div>

            {selectedIds.size > 0 && (
                <div style={{ position: 'fixed', bottom: 20, left: '50%', transform: 'translateX(-50%)', background: '#1e293b', color: '#ffffff', padding: '12px 20px', borderRadius: 10, boxShadow: '0 10px 30px rgba(0,0,0,0.25)', display: 'flex', alignItems: 'center', gap: 16, zIndex: 1000 }}>
                    <span style={{ fontSize: 14, fontWeight: 600 }}>{selectedIds.size} selected</span>
                    <button onClick={bulkDelete} style={{ background: '#dc2626', color: '#ffffff', border: 'none', padding: '8px 16px', borderRadius: 6, fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>Delete</button>
                    <button onClick={() => { clearSelection(); setDeleteMode(false); }} style={{ background: 'transparent', color: '#cbd5e1', border: 'none', cursor: 'pointer', fontSize: 13 }}>Cancel</button>
                </div>
            )}

            {showModal && (
                <PartnershipModal
                    partnership={editingPartnership}
                    internalTeam={internalTeam}
                    onClose={() => setShowModal(false)}
                    onSave={async (formData) => {
                        try {
                            setSaving(true);
                            let saved;
                            if (editingPartnership) {
                                saved = await api.updatePartnership(editingPartnership.Id, formData);
                                showToast('Partnership updated successfully!');
                            } else {
                                // Duplicate check: same Partner name
                                const newKey = (formData.partner || '').trim().toLowerCase();
                                const exists = (data || []).some(p => (p.Partner || '').trim().toLowerCase() === newKey);
                                if (exists) {
                                    showToast('A partnership with this partner name already exists. Please edit the existing one.', 'error');
                                    setSaving(false);
                                    return;
                                }
                                saved = await api.createPartnership(formData);
                                showToast('Partnership created successfully!');
                            }
                            if (onLocalUpsert && saved && saved.Id) onLocalUpsert(saved);
                            setShowModal(false);
                        } catch (error) {
                            await api.getAllData(true);
                            await onDataChange();
                            showToast('Failed to save: ' + error.message, 'error');
                        } finally {
                            setSaving(false);
                        }
                    }}
                    saving={saving}
                />
            )}
        </div>
    );
}

// Partnership Modal — new column set requested by Ajay:
// Region · Type · Overall Status · Company Name · Person Name · Person Designation ·
// Relationship Owner · Latest Status · Opportunity Tagging · Comments
// Legacy fields (Seniority, TargetCustomer, StartDate, Value, Benefits) still preserved
// in the payload as null so no data is lost on save.
function PartnershipModal({ partnership, internalTeam, onClose, onSave, saving }) {
    const parseJsonField = (field) => {
        if (!field) return [];
        try {
            return typeof field === 'string' ? JSON.parse(field) : field;
        } catch {
            return field.split(',').map(s => s.trim()).filter(Boolean);
        }
    };

    const [formData, setFormData] = useState(partnership ? {
        region: partnership.Region || '',
        partnerType: partnership.PartnerType || 'ISV',
        status: partnership.Status || 'Active',
        partner: partnership.Partner || '',
        contact: partnership.Contact || '',
        personDesignation: partnership.PersonDesignation || '',
        latestStatus: partnership.LatestStatus || '',
        opportunityTagging: partnership.OpportunityTagging || '',
        comments: partnership.Comments || ''
    } : {
        region: '',
        partnerType: 'ISV',
        status: 'Active',
        partner: '',
        contact: '',
        personDesignation: '',
        latestStatus: '',
        opportunityTagging: '',
        comments: ''
    });

    const [selectedRelationshipOwners, setSelectedRelationshipOwners] = useState(
        partnership ? parseJsonField(partnership.RelationshipOwner || '') : []
    );
    const [customRelationshipOwner, setCustomRelationshipOwner] = useState('');

    const handleAddRelationshipOwner = (ownerName) => {
        const name = (ownerName || '').trim();
        if (name && !selectedRelationshipOwners.includes(name)) {
            setSelectedRelationshipOwners([...selectedRelationshipOwners, name]);
        }
    };
    const handleRemoveRelationshipOwner = (ownerName) => {
        setSelectedRelationshipOwners(selectedRelationshipOwners.filter(o => o !== ownerName));
    };

    const handleSubmit = (e) => {
        e.preventDefault();

        const dataToSend = {
            // New / core fields
            partner: formData.partner,
            partnerType: formData.partnerType,
            status: formData.status || null,
            region: formData.region || null,
            contact: formData.contact || null,
            personDesignation: formData.personDesignation || null,
            latestStatus: formData.latestStatus || null,
            opportunityTagging: formData.opportunityTagging || null,
            comments: formData.comments || null,
            relationshipOwner: JSON.stringify(selectedRelationshipOwners),
            // Legacy — preserve whatever was there so nothing gets wiped
            seniority: partnership?.Seniority || null,
            targetCustomer: partnership?.TargetCustomer || null,
            startDate: partnership?.StartDate ? new Date(partnership.StartDate).toISOString().split('T')[0] : null,
            value: partnership?.Value != null ? parseFloat(partnership.Value) : null,
            benefits: partnership?.Benefits || null,
        };

        console.log('Submitting partnership data:', dataToSend);
        onSave(dataToSend);
    };

    return (
        <div className="modal-overlay" onClick={onClose}>
            <div className="modal modal-large" onClick={(e) => e.stopPropagation()} style={{ maxHeight: '90vh', overflowY: 'auto' }}>
                <h2>{partnership ? 'Edit Partnership' : 'Add Partnership'}</h2>
                <form onSubmit={handleSubmit}>
                    <div className="form-group">
                        <label>Region</label>
                        <select
                            value={formData.region}
                            onChange={(e) => setFormData({...formData, region: e.target.value})}
                        >
                            <option value="">— Select —</option>
                            <option>Europe</option>
                            <option>North America</option>
                            <option>Asia Pacific</option>
                            <option>Latin America</option>
                            <option>Middle East &amp; Africa</option>
                            <option>India</option>
                            <option>Global</option>
                        </select>
                    </div>

                    <div className="form-group">
                        <label>Type *</label>
                        <select
                            required
                            value={formData.partnerType}
                            onChange={(e) => setFormData({...formData, partnerType: e.target.value})}
                        >
                            <option>ISV</option>
                            <option>SI</option>
                            <option>Reseller</option>
                            <option>Technology</option>
                            <option>Strategic</option>
                            <option>Channel</option>
                            <option>Referral</option>
                        </select>
                    </div>

                    <div className="form-group">
                        <label>Overall Status</label>
                        <select
                            value={formData.status}
                            onChange={(e) => setFormData({...formData, status: e.target.value})}
                        >
                            <option>Active</option>
                            <option>Inactive</option>
                            <option>Pending</option>
                            <option>Negotiation</option>
                            <option>Paused</option>
                        </select>
                    </div>

                    <div className="form-group">
                        <label>Company Name *</label>
                        <input
                            type="text"
                            required
                            value={formData.partner}
                            onChange={(e) => setFormData({...formData, partner: e.target.value})}
                            placeholder="Partner company"
                        />
                    </div>

                    <div className="form-group">
                        <label>Person Name</label>
                        <input
                            type="text"
                            value={formData.contact}
                            onChange={(e) => setFormData({...formData, contact: e.target.value})}
                            placeholder="Their primary contact person"
                        />
                    </div>

                    <div className="form-group">
                        <label>Person Designation</label>
                        <input
                            type="text"
                            value={formData.personDesignation}
                            onChange={(e) => setFormData({...formData, personDesignation: e.target.value})}
                            placeholder="e.g. VP of Partnerships, CTO"
                        />
                    </div>

                    {/* Relationship Owner - Multi-Select */}
                    <div className="form-group">
                        <label>Relationship Owner (our team)</label>

                        {selectedRelationshipOwners.length > 0 && (
                            <div style={{
                                marginBottom: '12px',
                                padding: '12px',
                                background: '#f0f9ff',
                                borderRadius: '8px',
                                display: 'flex',
                                flexWrap: 'wrap',
                                gap: '8px'
                            }}>
                                {selectedRelationshipOwners.map(owner => (
                                    <span
                                        key={owner}
                                        style={{
                                            background: '#667eea',
                                            color: 'white',
                                            padding: '4px 12px',
                                            borderRadius: '12px',
                                            fontSize: '14px',
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: '8px'
                                        }}
                                    >
                                        {owner}
                                        <button
                                            type="button"
                                            onClick={() => handleRemoveRelationshipOwner(owner)}
                                            style={{
                                                background: 'transparent',
                                                border: 'none',
                                                color: 'white',
                                                cursor: 'pointer',
                                                fontSize: '16px',
                                                padding: '0'
                                            }}
                                        >
                                            ×
                                        </button>
                                    </span>
                                ))}
                            </div>
                        )}

                        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                            <select
                                style={{ flex: '1 1 220px' }}
                                onChange={(e) => {
                                    if (e.target.value) {
                                        handleAddRelationshipOwner(e.target.value);
                                        e.target.value = '';
                                    }
                                }}
                            >
                                <option value="">Add from internal team…</option>
                                {(internalTeam || []).map(person => (
                                    <option key={person.Id} value={person.Name}>
                                        {person.Name}
                                    </option>
                                ))}
                            </select>
                            <input
                                type="text"
                                placeholder="Or type a custom name"
                                value={customRelationshipOwner}
                                onChange={(e) => setCustomRelationshipOwner(e.target.value)}
                                onKeyDown={(e) => {
                                    if (e.key === 'Enter') {
                                        e.preventDefault();
                                        handleAddRelationshipOwner(customRelationshipOwner);
                                        setCustomRelationshipOwner('');
                                    }
                                }}
                                style={{ flex: '1 1 200px' }}
                            />
                            <button
                                type="button"
                                className="btn btn-secondary"
                                onClick={() => {
                                    handleAddRelationshipOwner(customRelationshipOwner);
                                    setCustomRelationshipOwner('');
                                }}
                            >Add</button>
                        </div>
                    </div>

                    <div className="form-group">
                        <label>Latest Status</label>
                        <textarea
                            value={formData.latestStatus}
                            onChange={(e) => setFormData({...formData, latestStatus: e.target.value})}
                            placeholder="What's the last thing that happened here? e.g. Waiting on legal review after Sep 12 call"
                            rows="3"
                        />
                    </div>

                    <div className="form-group">
                        <label>Opportunity Tagging</label>
                        <input
                            type="text"
                            value={formData.opportunityTagging}
                            onChange={(e) => setFormData({...formData, opportunityTagging: e.target.value})}
                            placeholder="e.g. GTM deal #123, Q4 pipeline, Nexus-linked"
                        />
                    </div>

                    <div className="form-group">
                        <label>Comments</label>
                        <textarea
                            value={formData.comments}
                            onChange={(e) => setFormData({...formData, comments: e.target.value})}
                            placeholder="Anything else worth remembering about this partnership…"
                            rows="4"
                        />
                    </div>

                    <div className="modal-actions">
                        <button type="submit" className="btn btn-primary" disabled={saving}>
                            {saving ? 'Saving...' : 'Save'}
                        </button>
                        <button type="button" className="btn btn-secondary" onClick={onClose} disabled={saving}>
                            Cancel
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
}

// Investors View Component - WITH COMPETITION

// ─────────────────────────────────────────────────────────────────────────────
// InvestorsView v2 — Fundraising pipeline UI (matches the design mockup).
// Displays VCs / Angels / Accelerators / PE / Impact funds with:
//   - Left rail: segments + faceted filter chips
//   - Main:   sortable table with Fit pill, HQ, stages/verticals/geo tags, AUM
//   - Row click opens a placeholder detail view (Commit 2 will fill it in)
// Competitions/Awards & Grants live in a separate "Funding Programs" tab
// (planned) — they're filtered OUT of this view via Type != 'Competition'.
// ─────────────────────────────────────────────────────────────────────────────
const FIT_COLOR   = { fit: '#12996B', partial: '#B7791A', skip: '#CE4238' };
const FIT_LABEL   = { fit: 'Fit',     partial: 'Partial', skip: 'Skip' };
const FIT_BG      = { fit: '#E7F5EF', partial: '#FBF2E1', skip: '#FBECEA' };
const FIT_SUB     = { fit: 'Worth approaching', partial: 'Qualify first', skip: 'No action now' };
const FIT_RANK    = { fit: 0, partial: 1, skip: 2, '': 3 };

const STAGE_OPTIONS    = ['Pre-seed', 'Seed', 'Series A', 'Later'];
const VERTICAL_OPTIONS = ['Fintech', 'RegTech', 'Deep-tech', 'Enterprise AI', 'ESG'];
const GEO_OPTIONS      = ['SEA', 'India', 'MENA', 'Europe', 'US'];
const TYPE_OPTIONS     = ['VC', 'Angel', 'Accelerator', 'PE/Buyout', 'Impact'];
const STATUS_OPTIONS   = ['Actively seeking', 'Selective'];

function toArr(v) {
    if (Array.isArray(v)) return v;
    if (typeof v === 'string' && v.trim()) {
        try { const p = JSON.parse(v); return Array.isArray(p) ? p : []; } catch { return []; }
    }
    return [];
}

function InvestorsView({ data, searchTerm, onDataChange, onLocalRemove, onLocalUpsert, showToast, setGlobalLoading }) {
    const [showModal, setShowModal] = useState(false);
    const [editingInvestor, setEditingInvestor] = useState(null);
    const [saving, setSaving] = useState(false);
    const [selectedIds, setSelectedIds] = useState(new Set());
    const [deleteMode, setDeleteMode] = useState(false);
    const [viewingId, setViewingId] = useState(null); // Commit 2 fills in the detail view
    const fileInputRef = useRef(null);

    // Pipeline UI state — segment + faceted filter chips + sort
    const [segment, setSegment] = useState('all'); // all | target | mena | warm
    const [facets, setFacets] = useState({
        stage: new Set(), vertical: new Set(), geo: new Set(),
        type: new Set(), status: new Set(),
    });
    const [sortBy, setSortBy] = useState('fit');
    const toggleFacet = (group, value) => {
        setFacets(prev => {
            const next = { ...prev, [group]: new Set(prev[group]) };
            if (next[group].has(value)) next[group].delete(value);
            else next[group].add(value);
            return next;
        });
    };
    const resetAll = () => {
        setFacets({ stage: new Set(), vertical: new Set(), geo: new Set(), type: new Set(), status: new Set() });
        setSegment('all');
    };

    const toggleSelect = (id) => setSelectedIds(prev => { const n = new Set(prev); if (n.has(id)) n.delete(id); else n.add(id); return n; });
    const selectAllVisible = (ids) => setSelectedIds(prev => { const allSel = ids.every(id => prev.has(id)); const n = new Set(prev); if (allSel) ids.forEach(id => n.delete(id)); else ids.forEach(id => n.add(id)); return n; });
    const clearSelection = () => setSelectedIds(new Set());
    const bulkDelete = async () => {
        const ids = Array.from(selectedIds);
        if (ids.length === 0) return;
        if (!window.confirm(`Delete ${ids.length} selected entr${ids.length === 1 ? 'y' : 'ies'}?`)) return;
        if (onLocalRemove) onLocalRemove(ids);
        clearSelection();
        setDeleteMode(false);
        showToast(`${ids.length} entr${ids.length === 1 ? 'y' : 'ies'} deleted!`);
        try {
            for (const id of ids) await api.deleteInvestor(id);
        } catch (error) {
            showToast('Delete failed: ' + error.message + ' — restoring.', 'error');
            await api.getAllData(true);
            await onDataChange();
        }
    };

    // ── Filter pipeline ─────────────────────────────────────────────
    // 1. Drop Competition-type rows (those live in the future Funding Programs tab)
    // 2. Search
    // 3. Segment (target thesis / MENA / warm intro / all)
    // 4. Facet chips (stage / vertical / geo / type / status)
    // 5. Sort
    const q = (searchTerm || '').toLowerCase();
    const passesSearch = (i) => !q || (
        (i.Name || '').toLowerCase().includes(q) ||
        (i.Family || '').toLowerCase().includes(q) ||
        (i.HQ || '').toLowerCase().includes(q) ||
        toArr(i.Verticals).join(' ').toLowerCase().includes(q)
    );
    const passesSegment = (i, seg) => {
        const s = seg || segment;
        if (s === 'all') return true;
        const stages = toArr(i.Stages), verts = toArr(i.Verticals), geo = toArr(i.Geo);
        if (s === 'target')
            return i.Fit !== 'skip'
                && verts.some(v => ['Fintech', 'RegTech'].includes(v))
                && geo.some(g => ['SEA', 'India', 'MENA'].includes(g))
                && stages.some(x => ['Pre-seed', 'Seed', 'Series A'].includes(x));
        if (s === 'mena') return geo.includes('MENA');
        if (s === 'warm') return (i.RelIntro || '').toLowerCase().startsWith('warm');
        return true;
    };
    const passesFacets = (i) => {
        const stages = toArr(i.Stages), verts = toArr(i.Verticals), geo = toArr(i.Geo);
        for (const s of facets.stage)    if (!stages.includes(s)) return false;
        for (const v of facets.vertical) if (!verts.includes(v)) return false;
        for (const g of facets.geo)      if (!geo.includes(g)) return false;
        for (const t of facets.type)     if (i.Type !== t) return false;
        for (const st of facets.status)  if (i.InvStatus !== st) return false;
        return true;
    };
    // Only equity-taking types belong in the Investors tab.
    // Grants + Accelerators live in the Grants tab.
    // Competitions + Awards live in the Competitions tab.
    const INVESTOR_TYPES = new Set(['VC', 'Angel', 'PE/Buyout', 'Impact', 'Investor']);
    const investorsOnly = (data || []).filter(i => INVESTOR_TYPES.has(i.Type));
    const filteredInvestors = investorsOnly.filter(i => passesSearch(i) && passesSegment(i) && passesFacets(i));

    const sortedInvestors = [...filteredInvestors].sort((a, b) => {
        if (sortBy === 'fit') return (FIT_RANK[a.Fit || ''] - FIT_RANK[b.Fit || '']) || ((Number(b.AUMRaw) || 0) - (Number(a.AUMRaw) || 0));
        if (sortBy === 'aum') return (Number(b.AUMRaw) || 0) - (Number(a.AUMRaw) || 0);
        if (sortBy === 'name') return (a.Name || '').localeCompare(b.Name || '');
        if (sortBy === 'recent') return (Number(b.RecentYear) || 0) - (Number(a.RecentYear) || 0);
        return 0;
    });

    // Segment counts (for sidebar badges)
    const segCounts = {
        all: investorsOnly.length,
        target: investorsOnly.filter(i => passesSegment(i, 'target')).length,
        mena: investorsOnly.filter(i => passesSegment(i, 'mena')).length,
        warm: investorsOnly.filter(i => passesSegment(i, 'warm')).length,
    };

    // Fit summary counts (shown next to header)
    const fitCounts = {
        fit: sortedInvestors.filter(i => i.Fit === 'fit').length,
        partial: sortedInvestors.filter(i => i.Fit === 'partial').length,
        skip: sortedInvestors.filter(i => i.Fit === 'skip').length,
    };

    const requestSort = () => {}; // legacy no-op kept for compat with any lingering call site

    const handleExport = async () => {
        const exportData = sortedInvestors.map(inv => ({
            'Name': inv.Name,
            'Family': inv.Family || '',
            'Type': inv.Type,
            'HQ': inv.HQ || '',
            'Website': inv.Website || '',
            'AUM': inv.AUM || '',
            'Stages': toArr(inv.Stages).join(', '),
            'Verticals': toArr(inv.Verticals).join(', '),
            'Geography': toArr(inv.Geo).join(', '),
            'Cheque range': inv.CheckRange || '',
            'Status': inv.InvStatus || inv.Status,
            'Fit': inv.Fit || '',
            'Owner': inv.RelOwner || '',
            'Next action': inv.RelNext || '',
        }));
        await exportToExcel(exportData, 'Investor_Pipeline');
    };

    // ── Import flow (PitchBook investor Excel) ───────────────────────────
    // 1. User picks a .xlsx → read as base64
    // 2. POST to /api/investors-import with dryRun=true → preview
    // 3. Show preview modal → user confirms
    // 4. POST again with dryRun=false → saves to DB (create or merge)
    // 5. Refresh the list
    const [importPreview, setImportPreview] = useState(null); // { fileName, base64, extracted, existingMatch, counts }
    const [importSaving, setImportSaving] = useState(false);

    const handleImport = async (e) => {
        const file = e.target.files[0];
        if (!file) return;
        e.target.value = ''; // reset input so same file can be re-picked
        try {
            setGlobalLoading(true);
            const buf = await file.arrayBuffer();
            // Convert ArrayBuffer -> base64 (chunked to avoid stack overflow on large files)
            const bytes = new Uint8Array(buf);
            let bin = '';
            const CHUNK = 0x8000;
            for (let i = 0; i < bytes.length; i += CHUNK) {
                bin += String.fromCharCode.apply(null, bytes.subarray(i, i + CHUNK));
            }
            const base64 = btoa(bin);

            const API_BASE = process.env.REACT_APP_API_URL || '/api';
            const r = await fetch(`${API_BASE}/investors-import`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ fileName: file.name, fileBase64: base64, dryRun: true }),
            });
            if (!r.ok) {
                const err = await r.json().catch(() => ({ error: `HTTP ${r.status}` }));
                showToast('Import failed: ' + (err.error || err.message || 'unknown'), 'error');
                return;
            }
            const preview = await r.json();
            setImportPreview({ fileName: file.name, base64, ...preview });
        } catch (err) {
            showToast('Import failed: ' + err.message, 'error');
        } finally {
            setGlobalLoading(false);
        }
    };

    const commitImport = async (mode /* 'create' | 'merge' */) => {
        if (!importPreview) return;
        setImportSaving(true);
        try {
            const API_BASE = process.env.REACT_APP_API_URL || '/api';
            const r = await fetch(`${API_BASE}/investors-import`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    fileName: importPreview.fileName,
                    fileBase64: importPreview.base64,
                    dryRun: false,
                    mergeIntoId: mode === 'merge' && importPreview.existingMatch ? importPreview.existingMatch.Id : null,
                }),
            });
            if (!r.ok) {
                const err = await r.json().catch(() => ({ error: `HTTP ${r.status}` }));
                showToast('Save failed: ' + (err.error || err.message || 'unknown'), 'error');
                return;
            }
            const body = await r.json();
            showToast(`${body.action === 'created' ? 'Created' : 'Updated'}: ${body.investor.Name}`);
            setImportPreview(null);
            // Reload the investors data so the list refreshes
            await api.getAllData(true);
            await onDataChange();
        } catch (err) {
            showToast('Save failed: ' + err.message, 'error');
        } finally {
            setImportSaving(false);
        }
    };

    const totalAum = investorsOnly.reduce((s, i) => s + (Number(i.AUMRaw) || 0), 0);
    const totalFunding = investorsOnly.reduce((s, i) => s + Number(i.Amount || 0), 0);

    // ── Styles kept inline to match the mockup's neutral, dense look ──
    // Font stack — Inter is already loaded app-wide; Space Grotesk for headings
    // and IBM Plex Mono for numbers give the pipeline table its distinctive
    // "analyst tool" feel.
    const INTER = "'Inter', system-ui, sans-serif";
    const HEADING = "'Space Grotesk', 'Inter', system-ui, sans-serif";
    const MONO = "'IBM Plex Mono', ui-monospace, SFMono-Regular, monospace";

    const railBtn = (active) => ({
        display: 'flex', alignItems: 'center', gap: 8, padding: '8px 10px',
        borderRadius: 8, border: 'none', background: active ? '#EEF1FE' : 'transparent',
        color: active ? '#1B31B8' : '#3A4656', fontSize: 12.5, width: '100%',
        textAlign: 'left', cursor: 'pointer', fontWeight: active ? 600 : 500,
        fontFamily: INTER, transition: 'background 0.15s',
    });
    const chipBtn = (on) => ({
        padding: '5px 10px', border: `1px solid ${on ? '#0E1622' : '#E4E8EE'}`,
        borderRadius: 999, background: on ? '#0E1622' : '#fff',
        color: on ? '#fff' : '#3A4656', fontSize: 11.5, cursor: 'pointer',
        fontFamily: INTER, fontWeight: on ? 600 : 500, letterSpacing: '0.01em',
        transition: 'all 0.15s',
    });
    const tag = (t) => (
        <span key={t} style={{
            fontSize: 10.5, padding: '3px 8px', borderRadius: 999,
            background: '#EDF0F4', color: '#3A4656', marginRight: 4,
            fontFamily: INTER, fontWeight: 500, letterSpacing: '0.01em',
            display: 'inline-block', marginBottom: 2,
        }}>{t}</span>
    );
    const fitPill = (fit) => {
        const f = fit || '';
        if (!FIT_LABEL[f]) return <span style={{ color: '#9AA4B2', fontSize: 11.5 }}>—</span>;
        return (
            <span style={{
                display: 'inline-flex', alignItems: 'center', gap: 6,
                padding: '4px 11px', borderRadius: 999, fontSize: 11.5, fontWeight: 700,
                background: FIT_BG[f], color: FIT_COLOR[f], fontFamily: INTER,
                letterSpacing: '0.02em', textTransform: 'uppercase',
            }}>
                <span style={{ width: 6, height: 6, borderRadius: 999, background: FIT_COLOR[f] }} />
                {FIT_LABEL[f]}
            </span>
        );
    };

    // Small header dot for status pill — reuses the mockup's pattern
    const statusChip = (s, cold) => {
        if (!s) return <span style={{ color: '#9AA4B2', fontSize: 11.5 }}>—</span>;
        return (
            <span style={{
                display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 11.5, color: '#3A4656',
                fontFamily: INTER, fontWeight: 500,
            }}>
                <span style={{
                    width: 6, height: 6, borderRadius: 999,
                    background: cold ? '#9AA4B2' : '#12996B',
                    boxShadow: cold ? 'none' : '0 0 0 3px #E7F5EF',
                }} />
                {s}
            </span>
        );
    };

    // When viewingId is set, show the full detail view (replaces list).
    // currentInv is the row the user clicked into.
    const currentInv = viewingId ? investorsOnly.find(i => i.Id === viewingId) : null;
    // Prev / next within the currently-sorted view
    const stepInv = (dir) => {
        if (!currentInv) return;
        const idx = sortedInvestors.findIndex(i => i.Id === currentInv.Id);
        if (idx < 0) return;
        const nextIdx = (idx + dir + sortedInvestors.length) % sortedInvestors.length;
        setViewingId(sortedInvestors[nextIdx].Id);
    };

    return (
        <div style={{ fontFamily: INTER, color: '#0E1622' }}>
            {/* ═══ LIST VIEW ═══ (hidden when a row is being viewed) */}
            {!currentInv && <>
            {/* ── Header: Investor pipeline title + toolbar ── */}
            <div style={{
                display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between',
                gap: 16, marginBottom: 20, flexWrap: 'wrap',
                paddingBottom: 18, borderBottom: '1px solid #E4E8EE',
            }}>
                <div>
                    <h1 style={{
                        fontSize: 24, margin: 0, fontWeight: 600,
                        letterSpacing: '-0.02em', color: '#0E1622',
                        fontFamily: HEADING,
                    }}>Investors</h1>
                    <div style={{ color: '#6B7686', fontSize: 13, marginTop: 5, fontFamily: INTER }}>
                        <b style={{ color: '#0E1622', fontWeight: 600 }}>{sortedInvestors.length}</b> of {investorsOnly.length} firms ·{' '}
                        <span style={{ color: '#12996B', fontWeight: 600 }}>{fitCounts.fit} fit</span> ·{' '}
                        <span style={{ color: '#B7791A', fontWeight: 600 }}>{fitCounts.partial} partial</span> ·{' '}
                        <span style={{ color: '#CE4238', fontWeight: 600 }}>{fitCounts.skip} skip</span>
                    </div>
                </div>
                <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
                    <select value={sortBy} onChange={e => setSortBy(e.target.value)}
                        style={{
                            padding: '8px 30px 8px 12px', border: '1px solid #E4E8EE', borderRadius: 8,
                            background: '#fff', fontSize: 13, color: '#3A4656', fontFamily: INTER,
                            cursor: 'pointer', fontWeight: 500,
                            appearance: 'none',
                            backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' fill='none' stroke='%239AA4B2' stroke-width='1.6'%3E%3Cpath d='M3 5l3 3 3-3'/%3E%3C/svg%3E")`,
                            backgroundRepeat: 'no-repeat',
                            backgroundPosition: 'right 10px center',
                        }}>
                        <option value="fit">Sort: Best fit</option>
                        <option value="aum">Sort: AUM</option>
                        <option value="recent">Sort: Recent activity</option>
                        <option value="name">Sort: Name</option>
                    </select>
                    <button onClick={handleExport} style={{
                        padding: '8px 14px', border: '1px solid #E4E8EE', borderRadius: 8,
                        background: '#fff', color: '#3A4656', fontSize: 13, fontWeight: 500,
                        cursor: 'pointer', fontFamily: INTER,
                    }}>Export</button>
                    <button onClick={() => fileInputRef.current.click()} style={{
                        padding: '8px 14px', border: '1px solid #E4E8EE', borderRadius: 8,
                        background: '#fff', color: '#3A4656', fontSize: 13, fontWeight: 500,
                        cursor: 'pointer', fontFamily: INTER,
                    }}>Import</button>
                    <input ref={fileInputRef} type="file" accept=".xlsx,.xls" style={{ display: 'none' }} onChange={handleImport} />
                    <button onClick={() => { setEditingInvestor(null); setShowModal(true); }} style={{
                        padding: '8px 16px', border: 'none', borderRadius: 8,
                        background: '#2743E0', color: '#fff', fontSize: 13, fontWeight: 600,
                        cursor: 'pointer', fontFamily: INTER,
                        boxShadow: '0 1px 2px rgba(39,67,224,.15)',
                    }}>+ Add investor</button>
                    {deleteMode ? (
                        <button onClick={() => { setDeleteMode(false); clearSelection(); }} style={{
                            padding: '8px 14px', border: 'none', borderRadius: 8,
                            background: '#64748b', color: '#fff', fontSize: 13, fontWeight: 500,
                            cursor: 'pointer', fontFamily: INTER,
                        }}>✕ Cancel</button>
                    ) : (
                        <button onClick={() => setDeleteMode(true)} title="Select rows to delete" style={{
                            padding: '8px 12px', border: '1px solid #F0CFCB', borderRadius: 8,
                            background: '#FBECEA', color: '#CE4238', fontSize: 13, fontWeight: 500,
                            cursor: 'pointer', fontFamily: INTER,
                            display: 'inline-flex', alignItems: 'center', gap: 6,
                        }}>
                            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="3 6 5 6 21 6"/><path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6"/></svg>
                            Delete
                        </button>
                    )}
                </div>
            </div>

            {/* ── Two-column layout: left rail (segments + facets) + main table ── */}
            <div style={{ display: 'grid', gridTemplateColumns: '246px 1fr', gap: 20, alignItems: 'start' }}>
                {/* Rail */}
                <aside style={{
                    background: '#FFFFFF', border: '1px solid #E4E8EE',
                    borderRadius: 12, padding: '20px 18px',
                    boxShadow: '0 1px 2px rgba(16,26,40,.04)',
                    position: 'sticky', top: 20,
                }}>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 6 }}>
                        <h2 style={{
                            fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.09em',
                            color: '#6B7686', fontWeight: 700, margin: 0, fontFamily: INTER,
                        }}>Segments</h2>
                        <button onClick={resetAll} style={{
                            fontSize: 11.5, color: '#1B31B8', background: 'none', border: 'none',
                            cursor: 'pointer', padding: 2, fontWeight: 500, fontFamily: INTER,
                        }}>Reset</button>
                    </div>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 2, margin: '10px 0 22px' }}>
                        {[
                            { k: 'all', l: 'All investors', n: segCounts.all },
                            { k: 'target', l: 'Seed · Fintech · SEA–India–MENA', n: segCounts.target },
                            { k: 'mena', l: 'MENA priority', n: segCounts.mena },
                            { k: 'warm', l: 'Warm intro available', n: segCounts.warm },
                        ].map(s => (
                            <button key={s.k} onClick={() => setSegment(s.k)} style={railBtn(segment === s.k)}
                                onMouseEnter={e => { if (segment !== s.k) e.currentTarget.style.background = '#FAFBFD'; }}
                                onMouseLeave={e => { if (segment !== s.k) e.currentTarget.style.background = 'transparent'; }}>
                                <span style={{ flex: 1, lineHeight: 1.4 }}>{s.l}</span>
                                <span style={{
                                    fontSize: 11, color: segment === s.k ? '#1B31B8' : '#9AA4B2',
                                    fontFamily: MONO, fontVariantNumeric: 'tabular-nums',
                                }}>{s.n}</span>
                            </button>
                        ))}
                    </div>
                    {[
                        { g: 'stage',    lbl: 'Stage',     opts: STAGE_OPTIONS },
                        { g: 'vertical', lbl: 'Vertical',  opts: VERTICAL_OPTIONS },
                        { g: 'geo',      lbl: 'Geography', opts: GEO_OPTIONS },
                        { g: 'type',     lbl: 'Type',      opts: TYPE_OPTIONS },
                        { g: 'status',   lbl: 'Status',    opts: STATUS_OPTIONS },
                    ].map(({ g, lbl, opts }) => (
                        <div key={g} style={{ marginBottom: 18 }}>
                            <span style={{
                                fontSize: 10.5, textTransform: 'uppercase', letterSpacing: '0.09em',
                                color: '#9AA4B2', fontWeight: 700, marginBottom: 8, display: 'block',
                                fontFamily: INTER,
                            }}>{lbl}</span>
                            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 5 }}>
                                {opts.map(o => (
                                    <button key={o} onClick={() => toggleFacet(g, o)} style={chipBtn(facets[g].has(o))}>{o}</button>
                                ))}
                            </div>
                        </div>
                    ))}
                </aside>

                {/* Main table */}
                <div style={{
                    background: '#FFFFFF', border: '1px solid #E4E8EE',
                    borderRadius: 12, overflow: 'hidden',
                    boxShadow: '0 1px 2px rgba(16,26,40,.04), 0 8px 24px rgba(16,26,40,.05)',
                }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13.5, fontFamily: INTER }}>
                        <thead>
                            <tr>
                                {deleteMode && <th style={{ width: 32, padding: '13px 14px', borderBottom: '1px solid #E4E8EE', background: '#FAFBFD' }}>
                                    <input type="checkbox" checked={sortedInvestors.length > 0 && sortedInvestors.every(i => selectedIds.has(i.Id))}
                                        onChange={() => selectAllVisible(sortedInvestors.map(i => i.Id))}
                                        style={{ width: 15, height: 15, cursor: 'pointer', accentColor: '#2743E0' }} />
                                </th>}
                                {['Investor', 'Type', 'HQ', 'Stages', 'Verticals', 'Geo', 'Status', 'AUM', 'Fit'].map(h => (
                                    <th key={h} style={{
                                        textAlign: 'left', fontSize: 10.5, textTransform: 'uppercase',
                                        letterSpacing: '0.07em', color: '#9AA4B2', fontWeight: 700,
                                        padding: '13px 14px', borderBottom: '1px solid #E4E8EE',
                                        background: '#FAFBFD', whiteSpace: 'nowrap', fontFamily: INTER,
                                    }}>{h}</th>
                                ))}
                            </tr>
                        </thead>
                        <tbody>
                            {sortedInvestors.length === 0 && (
                                <tr>
                                    <td colSpan={deleteMode ? 10 : 9} style={{ padding: '80px 20px', textAlign: 'center', color: '#6B7686' }}>
                                        <div style={{ fontSize: 42, marginBottom: 12, opacity: 0.4 }}>🔍</div>
                                        <h3 style={{ fontSize: 16, marginBottom: 8, color: '#0E1622', fontFamily: HEADING, fontWeight: 600 }}>
                                            No investors match these filters
                                        </h3>
                                        <p style={{ margin: 0, fontSize: 13, color: '#6B7686' }}>
                                            Loosen a filter, or click <strong style={{ color: '#1B31B8' }}>Reset</strong> in the sidebar to see the full list.
                                        </p>
                                    </td>
                                </tr>
                            )}
                            {sortedInvestors.map((inv, idx) => {
                                const stages = toArr(inv.Stages);
                                const verts = toArr(inv.Verticals);
                                const geos = toArr(inv.Geo);
                                const showTags = (arr, max = 2) => (
                                    <>
                                        {arr.slice(0, max).map(tag)}
                                        {arr.length > max ? tag(`+${arr.length - max}`) : null}
                                    </>
                                );
                                const isSelected = selectedIds.has(inv.Id);
                                const cellBase = {
                                    padding: '14px 14px', borderBottom: idx === sortedInvestors.length - 1 ? 'none' : '1px solid #EDF0F4',
                                    verticalAlign: 'middle',
                                };
                                return (
                                    <tr key={inv.Id}
                                        onClick={(e) => { if (e.target.type !== 'checkbox') setViewingId(inv.Id); }}
                                        style={{ cursor: 'pointer', background: isSelected ? '#EEF1FE' : undefined, transition: 'background .12s' }}
                                        onMouseEnter={e => { if (!isSelected) e.currentTarget.style.background = '#FAFBFD'; }}
                                        onMouseLeave={e => { if (!isSelected) e.currentTarget.style.background = 'transparent'; }}
                                    >
                                        {deleteMode && (
                                            <td style={cellBase}>
                                                <input type="checkbox" checked={isSelected}
                                                    onClick={e => e.stopPropagation()}
                                                    onChange={() => toggleSelect(inv.Id)}
                                                    style={{ width: 15, height: 15, cursor: 'pointer', accentColor: '#2743E0' }} />
                                            </td>
                                        )}
                                        <td style={cellBase}>
                                            <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                                                <span style={{
                                                    fontWeight: 600, fontSize: 14, color: '#0E1622',
                                                    fontFamily: HEADING, letterSpacing: '-0.005em',
                                                }}>{inv.Name}</span>
                                                {inv.Family ? <span style={{ fontSize: 11.5, color: '#6B7686', fontFamily: INTER }}>{inv.Family}</span> : null}
                                            </div>
                                        </td>
                                        <td style={{ ...cellBase, color: '#3A4656', fontSize: 12.5 }}>{inv.Type || '—'}</td>
                                        <td style={cellBase}>
                                            <div style={{ color: '#3A4656', fontSize: 12.5 }}>
                                                {inv.HQ || <span style={{ color: '#9AA4B2' }}>—</span>}
                                            </div>
                                            {inv.HQ2 && <div style={{ fontSize: 11, color: '#9AA4B2', marginTop: 2 }}>+ {inv.HQ2}</div>}
                                        </td>
                                        <td style={cellBase}>{stages.length ? showTags(stages) : <span style={{ color: '#9AA4B2', fontSize: 12 }}>—</span>}</td>
                                        <td style={cellBase}>{verts.length ? showTags(verts) : <span style={{ color: '#9AA4B2', fontSize: 12 }}>—</span>}</td>
                                        <td style={cellBase}>{geos.length ? showTags(geos) : <span style={{ color: '#9AA4B2', fontSize: 12 }}>—</span>}</td>
                                        <td style={cellBase}>{statusChip(inv.InvStatus, inv.StatusCold)}</td>
                                        <td style={{ ...cellBase, fontFamily: MONO, fontVariantNumeric: 'tabular-nums', color: '#0E1622', fontSize: 12.5, fontWeight: 500 }}>
                                            {inv.AUM || <span style={{ color: '#9AA4B2', fontWeight: 400 }}>—</span>}
                                        </td>
                                        <td style={cellBase}>{fitPill(inv.Fit)}</td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
            </div>

            </>}

            {/* ═══ DETAIL VIEW ═══ (shown when a row is clicked) */}
            {currentInv && (() => {
                const inv = currentInv;
                const stages = toArr(inv.Stages);
                const verts = toArr(inv.Verticals);
                const geos = toArr(inv.Geo);
                const split = toArr(inv.Split); // [[category, count], ...]
                const dilInvestments = toArr(inv.DilInvestments);
                const dilTeam = toArr(inv.DilTeam);
                const dilExits = toArr(inv.DilExits);
                const maxSplit = split.length ? Math.max(...split.map(s => Number(s[1]) || 0)) || 1 : 1;
                const fitVerdictColor = FIT_COLOR[inv.Fit] || '#6B7686';
                const fitBg = FIT_BG[inv.Fit] || '#F4F6F9';
                const fitBorder = { fit: '#BFE6D5', partial: '#EDDCBB', skip: '#F0CFCB' }[inv.Fit] || '#E4E8EE';

                const initials = (name) => (name || '').split(/\s+/).map(w => w[0]).filter(Boolean).slice(0, 2).join('').toUpperCase();
                const paceFlag = inv.Inv12m <= 1
                    ? <span style={{ fontSize: 10, fontWeight: 600, padding: '1px 6px', borderRadius: 999, background: '#FBF2E1', color: '#B7791A' }}>cooling</span>
                    : inv.Inv12m >= 9
                        ? <span style={{ fontSize: 10, fontWeight: 600, padding: '1px 6px', borderRadius: 999, background: '#E7F5EF', color: '#12996B' }}>active</span>
                        : null;

                // Card wrappers, kept small so the JSX below stays readable
                const cardHead = (title, hint) => (
                    <div style={{ display: 'flex', alignItems: 'center', gap: 9, padding: '14px 20px', borderBottom: '1px solid #EDF0F4' }}>
                        <h3 style={{ fontSize: 13.5, margin: 0, fontFamily: HEADING, fontWeight: 600, letterSpacing: '-0.005em' }}>{title}</h3>
                        {hint && <span style={{ marginLeft: 'auto', fontSize: 11.5, color: '#9AA4B2' }}>{hint}</span>}
                    </div>
                );
                const cardWrap = (children) => (
                    <div style={{ background: '#FFFFFF', border: '1px solid #E4E8EE', borderRadius: 12, boxShadow: '0 1px 2px rgba(16,26,40,.04), 0 8px 24px rgba(16,26,40,.05)', marginBottom: 18, overflow: 'hidden' }}>
                        {children}
                    </div>
                );
                const stat = (k, v, sub) => (
                    <div style={{ background: '#FFFFFF', padding: '15px 18px' }}>
                        <div style={{ fontSize: 10.5, textTransform: 'uppercase', letterSpacing: '0.06em', color: '#9AA4B2', fontWeight: 600, marginBottom: 5 }}>{k}</div>
                        <div style={{ fontFamily: HEADING, fontSize: 18, fontWeight: 600, letterSpacing: '-0.02em', color: '#0E1622' }}>
                            {v || <span style={{ color: '#9AA4B2' }}>—</span>}
                        </div>
                        {sub && <div style={{ fontSize: 11, color: '#6B7686', marginTop: 3, display: 'flex', alignItems: 'center', gap: 5, flexWrap: 'wrap' }}>{sub}</div>}
                    </div>
                );

                return (
                    <div>
                        {/* Breadcrumb + prev/next */}
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12.5, color: '#6B7686', marginBottom: 16 }}>
                            <button onClick={() => setViewingId(null)}
                                style={{ background: 'none', border: 'none', color: '#6B7686', display: 'inline-flex', alignItems: 'center', gap: 6, padding: 0, fontSize: 12.5, cursor: 'pointer', fontFamily: INTER }}>
                                <svg width="13" height="13" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.7"><path d="M10 3L5 8l5 5"/></svg>
                                Investors
                            </button>
                            <span style={{ color: '#9AA4B2' }}>/</span>
                            <span style={{ color: '#0E1622', fontWeight: 500 }}>{inv.Name}</span>
                            <span style={{ marginLeft: 'auto', display: 'flex', gap: 6 }}>
                                <button onClick={() => stepInv(-1)} title="Previous"
                                    style={{ width: 28, height: 28, borderRadius: 7, border: '1px solid #E4E8EE', background: '#fff', display: 'grid', placeItems: 'center', color: '#6B7686', cursor: 'pointer' }}>
                                    <svg width="12" height="12" viewBox="0 0 12 12" fill="none" stroke="currentColor" strokeWidth="1.7"><path d="M7.5 2l-4 4 4 4"/></svg>
                                </button>
                                <button onClick={() => stepInv(1)} title="Next"
                                    style={{ width: 28, height: 28, borderRadius: 7, border: '1px solid #E4E8EE', background: '#fff', display: 'grid', placeItems: 'center', color: '#6B7686', cursor: 'pointer' }}>
                                    <svg width="12" height="12" viewBox="0 0 12 12" fill="none" stroke="currentColor" strokeWidth="1.7"><path d="M4.5 2l4 4-4 4"/></svg>
                                </button>
                            </span>
                        </div>

                        {/* Hero card */}
                        <div style={{ position: 'relative', background: '#FFFFFF', border: '1px solid #E4E8EE', borderRadius: 12, padding: '26px 28px', marginBottom: 18, overflow: 'hidden', boxShadow: '0 1px 2px rgba(16,26,40,.04), 0 8px 24px rgba(16,26,40,.06)' }}>
                            <div style={{ display: 'flex', gap: 26, alignItems: 'flex-start', flexWrap: 'wrap' }}>
                                <div style={{ flex: 1, minWidth: 0 }}>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 9, fontSize: 11, color: '#6B7686' }}>
                                        {inv.Type && <span style={{ padding: '2px 9px', borderRadius: 999, background: '#EEF1FE', color: '#1B31B8', fontWeight: 600, fontSize: 10.5 }}>{inv.Type}</span>}
                                        {inv.SourceFileName && <span style={{ padding: '2px 9px', borderRadius: 999, border: '1px solid #E4E8EE', color: '#6B7686', fontSize: 10.5 }}>Imported profile</span>}
                                    </div>
                                    <h1 style={{ fontSize: 30, letterSpacing: '-0.03em', lineHeight: 1.15, margin: 0, fontFamily: HEADING, fontWeight: 600 }}>{inv.Name}</h1>
                                    {inv.Family && <div style={{ color: '#6B7686', fontSize: 14, marginTop: 2 }}>{inv.Family}</div>}
                                    {inv.Description && <p style={{ fontSize: 14, color: '#3A4656', lineHeight: 1.6, marginTop: 14, maxWidth: '62ch' }}>{inv.Description}</p>}
                                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px 20px', fontSize: 12.5, color: '#3A4656', marginTop: 16 }}>
                                        {inv.HQ && <span>📍 {inv.HQ}{inv.HQ2 ? ` · ${inv.HQ2}` : ''}</span>}
                                        {inv.Website && <span>🌐 <a href={`https://${inv.Website}`} target="_blank" rel="noreferrer" style={{ color: '#1B31B8' }}>{inv.Website}</a></span>}
                                        {inv.Founded && <span>📅 Founded {inv.Founded}</span>}
                                        {inv.Employees != null && <span>👥 {inv.Employees} staff</span>}
                                        {inv.InvStatus && statusChip(inv.InvStatus, inv.StatusCold)}
                                    </div>
                                    <div style={{ display: 'flex', gap: 8, marginTop: 20, flexWrap: 'wrap' }}>
                                        <button onClick={() => { setEditingInvestor(inv); setShowModal(true); }} style={{ padding: '9px 18px', border: 'none', borderRadius: 8, background: '#2743E0', color: '#fff', fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>Edit</button>
                                        {inv.SourceFileName && (
                                            <a href={`${process.env.REACT_APP_API_URL || '/api'}/investors-download?id=${inv.Id}`}
                                                style={{ padding: '9px 16px', border: '1px solid #E4E8EE', borderRadius: 8, background: '#fff', color: '#3A4656', fontSize: 13, fontWeight: 500, cursor: 'pointer', textDecoration: 'none', display: 'inline-flex', alignItems: 'center', gap: 6 }}
                                                title={`Download the original file (${inv.SourceFileName})`}>
                                                ⬇ Download source
                                            </a>
                                        )}
                                    </div>
                                </div>
                                {/* Verdict card */}
                                <div style={{ width: 280, flex: 'none', borderRadius: 11, padding: '16px 17px', border: `1px solid ${fitBorder}`, background: fitBg }}>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: 9, marginBottom: 9 }}>
                                        <span style={{ width: 12, height: 12, borderRadius: 999, background: fitVerdictColor }} />
                                        <span style={{ fontFamily: HEADING, fontWeight: 700, fontSize: 15, letterSpacing: '-0.01em', color: fitVerdictColor }}>
                                            {FIT_LABEL[inv.Fit] || 'No verdict'}
                                        </span>
                                        <span style={{ marginLeft: 'auto', fontSize: 10, textTransform: 'uppercase', letterSpacing: '0.07em', color: '#6B7686', fontWeight: 600 }}>
                                            {FIT_SUB[inv.Fit] || 'Not assessed'}
                                        </span>
                                    </div>
                                    <div style={{ fontSize: 12.5, color: '#3A4656', lineHeight: 1.55 }}>
                                        {inv.FitReason || <span style={{ color: '#9AA4B2', fontStyle: 'italic' }}>No reasoning added yet. Click Edit to record why this investor is a fit / partial / skip.</span>}
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Main grid: content + side sidebar */}
                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 320px', gap: 18, alignItems: 'start' }}>
                            <div>
                                {/* Fit & capacity card */}
                                {cardWrap(<>
                                    {cardHead('Fit & capacity', 'Can they write our cheque, now?')}
                                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 1, background: '#EDF0F4' }}>
                                        {stat('Stage focus', stages.length ? stages.join(', ') : null, null)}
                                        {stat('Cheque range', inv.CheckRange, inv.MedianDeal ? <>Median <span style={{ fontFamily: MONO }}>{inv.MedianDeal}</span></> : null)}
                                        {stat('Geography', geos.length ? geos.join(', ') : null, null)}
                                        {stat('AUM', <span style={{ fontFamily: MONO }}>{inv.AUM}</span>, inv.Founded ? `Founded ${inv.Founded}` : null)}
                                        {stat('Dry powder', inv.DryPowder ? <span style={{ fontFamily: MONO }}>{inv.DryPowder}</span> : null, inv.DryPowder ? 'Available to deploy' : <span style={{ fontSize: 10, padding: '1px 6px', borderRadius: 999, background: '#FBF2E1', color: '#B7791A', fontWeight: 600 }}>not disclosed</span>)}
                                        {stat('Deployment pace', <span style={{ fontFamily: MONO }}>{inv.Inv12m != null ? inv.Inv12m : '—'}{inv.Inv12m != null && <span style={{ fontSize: 12, color: '#6B7686', fontFamily: INTER }}> / 12M</span>}</span>, paceFlag)}
                                        {stat('Total invested', <span style={{ fontFamily: MONO }}>{inv.TotalInv != null ? inv.TotalInv : '—'}</span>, 'lifetime deals')}
                                        {stat('Active portfolio', <span style={{ fontFamily: MONO }}>{inv.ActivePortfolio != null ? inv.ActivePortfolio : '—'}</span>, 'companies')}
                                        {stat('Exits', <span style={{ fontFamily: MONO }}>{inv.ExitCount != null ? inv.ExitCount : '—'}</span>, 'realised')}
                                    </div>
                                </>)}

                                {/* Evidence card */}
                                {cardWrap(<>
                                    {cardHead('Evidence', 'Do they actually back companies like us?')}
                                    <div style={{ padding: '18px 20px' }}>
                                        {split.length > 0 && <>
                                            <div style={{ fontSize: 10.5, textTransform: 'uppercase', letterSpacing: '0.06em', color: '#9AA4B2', fontWeight: 600, marginBottom: 11 }}>Where the money goes</div>
                                            <div style={{ display: 'flex', flexDirection: 'column', gap: 9, marginBottom: 22 }}>
                                                {split.slice(0, 6).map(([label, count], i) => (
                                                    <div key={i} style={{ display: 'grid', gridTemplateColumns: '180px 1fr 40px', alignItems: 'center', gap: 12, fontSize: 12.5 }}>
                                                        <span style={{ color: '#3A4656' }}>{label}</span>
                                                        <div style={{ height: 8, borderRadius: 999, background: '#EDF0F4', overflow: 'hidden' }}>
                                                            <div style={{ height: '100%', background: 'linear-gradient(90deg, #2743E0, #0FA6BC)', width: `${Math.round((Number(count) || 0) / maxSplit * 100)}%`, borderRadius: 999 }} />
                                                        </div>
                                                        <span style={{ textAlign: 'right', color: '#6B7686', fontFamily: MONO, fontSize: 11.5 }}>{count}</span>
                                                    </div>
                                                ))}
                                            </div>
                                        </>}
                                        <div style={{ fontSize: 10.5, textTransform: 'uppercase', letterSpacing: '0.06em', color: '#9AA4B2', fontWeight: 600, marginBottom: 11 }}>Representative deals</div>
                                        {dilInvestments.length === 0
                                            ? <div style={{ fontSize: 12, color: '#9AA4B2', padding: '8px 2px' }}>No investment history on record.</div>
                                            : dilInvestments.slice(0, 5).map((d, i) => (
                                                <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '11px 0', borderBottom: i === Math.min(4, dilInvestments.length - 1) ? 'none' : '1px solid #EDF0F4' }}>
                                                    <span style={{ fontWeight: 600, fontFamily: HEADING, fontSize: 13.5, minWidth: 140 }}>{d.company || '—'}</span>
                                                    <span style={{ fontSize: 12, color: '#6B7686', flex: 1 }}>
                                                        {d.type || d.stage || '—'}{d.date ? ` · ${new Date(d.date).getFullYear()}` : ''}
                                                    </span>
                                                    <span style={{ fontFamily: MONO, fontSize: 12.5, color: '#0E1622' }}>{d.amount || '—'}</span>
                                                </div>
                                            ))}
                                    </div>
                                </>)}

                                {/* People card */}
                                {cardWrap(<>
                                    {cardHead('People', 'Who to write to')}
                                    <div style={{ padding: '6px 20px' }}>
                                        {inv.ContactName ? (
                                            <div style={{ display: 'flex', alignItems: 'center', gap: 13, padding: '12px 0', borderBottom: (inv.PartnerName && inv.PartnerName !== inv.ContactName) ? '1px solid #EDF0F4' : 'none' }}>
                                                <div style={{ width: 40, height: 40, borderRadius: 999, background: '#EEF1FE', color: '#1B31B8', display: 'grid', placeItems: 'center', fontWeight: 600, fontSize: 14, fontFamily: HEADING, flex: 'none' }}>{initials(inv.ContactName)}</div>
                                                <div style={{ flex: 1, minWidth: 0 }}>
                                                    <div style={{ fontWeight: 600, fontSize: 13.5 }}>{inv.ContactName}</div>
                                                    <div style={{ fontSize: 12, color: '#6B7686' }}>
                                                        {inv.ContactTitle || 'Primary contact'}
                                                        {inv.ContactEmail && <> · <a href={`mailto:${inv.ContactEmail}`} style={{ color: '#1B31B8' }}>{inv.ContactEmail}</a></>}
                                                    </div>
                                                </div>
                                                {(!inv.PartnerName || inv.PartnerName === inv.ContactName) && <span style={{ fontSize: 10.5, padding: '3px 9px', borderRadius: 999, background: '#0FA6BC', color: '#fff', fontWeight: 600 }}>Target partner</span>}
                                            </div>
                                        ) : <div style={{ padding: '12px 0', fontSize: 12, color: '#9AA4B2' }}>No primary contact captured.</div>}
                                        {inv.PartnerName && inv.PartnerName !== inv.ContactName && (
                                            <div style={{ display: 'flex', alignItems: 'center', gap: 13, padding: '12px 0' }}>
                                                <div style={{ width: 40, height: 40, borderRadius: 999, background: '#EEF1FE', color: '#1B31B8', display: 'grid', placeItems: 'center', fontWeight: 600, fontSize: 14, fontFamily: HEADING, flex: 'none' }}>{initials(inv.PartnerName)}</div>
                                                <div style={{ flex: 1, minWidth: 0 }}>
                                                    <div style={{ fontWeight: 600, fontSize: 13.5 }}>{inv.PartnerName}</div>
                                                    <div style={{ fontSize: 12, color: '#6B7686' }}>{inv.PartnerTitle || 'Suggested partner'}</div>
                                                </div>
                                                <span style={{ fontSize: 10.5, padding: '3px 9px', borderRadius: 999, background: '#0FA6BC', color: '#fff', fontWeight: 600 }}>Target partner</span>
                                            </div>
                                        )}
                                    </div>
                                </>)}

                                {/* Diligence — collapsible sections for imported PitchBook sub-tables */}
                                {(() => {
                                    const dilBoard = toArr(inv.DilBoard);
                                    const dilFunds = toArr(inv.DilFunds);
                                    const dilNews = toArr(inv.DilNews);
                                    const totalDil = dilInvestments.length + dilExits.length + dilBoard.length + dilTeam.length + dilFunds.length + dilNews.length;
                                    if (totalDil === 0) {
                                        return cardWrap(<>
                                            {cardHead('Diligence', 'Pull when engaged')}
                                            <div style={{ padding: '20px', color: '#9AA4B2', fontSize: 13, textAlign: 'center' }}>
                                                No diligence data attached. Import a PitchBook Excel to populate this section.
                                            </div>
                                        </>);
                                    }
                                    return cardWrap(<>
                                        {cardHead('Diligence', 'Pull when engaged')}
                                        <DiligenceAccordions
                                            dilInvestments={dilInvestments}
                                            dilExits={dilExits}
                                            dilBoard={dilBoard}
                                            dilTeam={dilTeam}
                                            dilFunds={dilFunds}
                                            dilNews={dilNews}
                                        />
                                    </>);
                                })()}

                                <div style={{ fontSize: 11, color: '#9AA4B2', display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', padding: '2px 2px 40px' }}>
                                    {inv.SourceFileName && <><span>Source: {inv.SourceFileName}</span><span style={{ width: 4, height: 4, borderRadius: 999, background: '#9AA4B2' }} /></>}
                                    <span>ID {inv.Id}</span>
                                    {inv.UpdatedAt && <><span style={{ width: 4, height: 4, borderRadius: 999, background: '#9AA4B2' }} /><span>Last updated {new Date(inv.UpdatedAt).toLocaleDateString()}</span></>}
                                </div>
                            </div>

                            {/* Right sidebar — Relationship tracker + Flags */}
                            <div style={{ position: 'sticky', top: 20 }}>
                                {(() => {
                                    const REL_STEPS_UI = ['Not engaged', 'Contacted', 'Intro call', 'Deep dive', 'Term sheet', 'Committed'];
                                    const relStage = inv.RelStage != null ? Number(inv.RelStage) : 0;
                                    const relFlags = toArr(inv.Flags);
                                    const flagStyle = (level) => {
                                        const map = {
                                            ok:   { bg: '#E7F5EF', color: '#12996B', border: '#BFE6D5', icon: '✓' },
                                            warn: { bg: '#FBF2E1', color: '#B7791A', border: '#EDDCBB', icon: '!' },
                                            bad:  { bg: '#FBECEA', color: '#CE4238', border: '#F0CFCB', icon: '✗' },
                                        };
                                        return map[level] || map.warn;
                                    };
                                    const relItem = (k, v) => (
                                        <div style={{ display: 'flex', gap: 12, padding: '9px 0', borderBottom: '1px solid #EDF0F4', fontSize: 12.5 }}>
                                            <span style={{ color: '#9AA4B2', width: 104, flex: 'none' }}>{k}</span>
                                            <span style={{ color: '#0E1622', flex: 1, wordBreak: 'break-word' }}>{v || <span style={{ color: '#9AA4B2' }}>—</span>}</span>
                                        </div>
                                    );
                                    return <>
                                        {cardWrap(<>
                                            {cardHead('Relationship')}
                                            <div style={{ padding: '16px 18px' }}>
                                                {/* Stepper */}
                                                <div style={{ display: 'flex', border: '1px solid #E4E8EE', borderRadius: 7, overflow: 'hidden', marginBottom: 14 }}>
                                                    {REL_STEPS_UI.map((s, i) => (
                                                        <div key={i} style={{
                                                            flex: 1, padding: '6px 2px', textAlign: 'center', fontSize: 9,
                                                            background: i === relStage ? '#2743E0' : (i < relStage ? '#EEF1FE' : '#fff'),
                                                            color: i === relStage ? '#fff' : (i < relStage ? '#1B31B8' : '#9AA4B2'),
                                                            borderLeft: i > 0 ? '1px solid #E4E8EE' : 'none',
                                                            fontWeight: i === relStage ? 700 : 500, lineHeight: 1.2,
                                                        }}>{s}</div>
                                                    ))}
                                                </div>

                                                {/* Next action card */}
                                                {inv.RelNext ? (
                                                    <div style={{ background: '#EEF1FE', border: '1px solid #CDD6FB', borderRadius: 10, padding: 12, display: 'flex', gap: 11, marginBottom: 14 }}>
                                                        <div style={{ width: 28, height: 28, borderRadius: 7, background: '#2743E0', color: '#fff', display: 'grid', placeItems: 'center', flex: 'none' }}>
                                                            <svg width="14" height="14" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.6"><path d="M3 8h10M9 4l4 4-4 4"/></svg>
                                                        </div>
                                                        <div style={{ minWidth: 0 }}>
                                                            <div style={{ fontSize: 10, textTransform: 'uppercase', letterSpacing: '0.06em', color: '#1B31B8', fontWeight: 700 }}>Next action</div>
                                                            <div style={{ fontSize: 12.5, fontWeight: 500, color: '#0E1622', lineHeight: 1.4, marginTop: 2 }}>{inv.RelNext}</div>
                                                            {inv.RelDue && <div style={{ fontFamily: MONO, fontSize: 11, color: '#1B31B8', marginTop: 4 }}>Due {new Date(inv.RelDue).toLocaleDateString()}</div>}
                                                        </div>
                                                    </div>
                                                ) : (
                                                    <div style={{ padding: '12px', borderRadius: 10, border: '1px dashed #E4E8EE', color: '#9AA4B2', fontSize: 12, textAlign: 'center', marginBottom: 14 }}>
                                                        No next action set. Click Edit to add one.
                                                    </div>
                                                )}

                                                {/* Rel details */}
                                                <div>
                                                    {relItem('Owner', inv.RelOwner)}
                                                    {relItem('Intro path', inv.RelIntro)}
                                                    {relItem('Source', inv.RelSource)}
                                                    {relItem('Materials sent', inv.RelMaterials)}
                                                    {relItem('Data room', inv.RelDataRoom)}
                                                    {relItem('Value angle', inv.RelHook)}
                                                    <div style={{ display: 'flex', gap: 12, padding: '9px 0', fontSize: 12.5 }}>
                                                        <span style={{ color: '#9AA4B2', width: 104, flex: 'none' }}>Last touch</span>
                                                        <span style={{ color: '#0E1622', flex: 1, wordBreak: 'break-word' }}>{inv.RelLast || <span style={{ color: '#9AA4B2' }}>—</span>}</span>
                                                    </div>
                                                </div>
                                            </div>
                                        </>)}

                                        {cardWrap(<>
                                            {cardHead('Flags')}
                                            <div style={{ padding: '16px 18px' }}>
                                                {relFlags.length === 0 ? (
                                                    <div style={{ padding: '10px', borderRadius: 8, border: '1px dashed #E4E8EE', color: '#9AA4B2', fontSize: 12, textAlign: 'center' }}>
                                                        No flags added. Click Edit → Verdict & judgement to add ok/warn/bad flags.
                                                    </div>
                                                ) : (
                                                    <div style={{ display: 'flex', flexDirection: 'column', gap: 7 }}>
                                                        {relFlags.map((f, i) => {
                                                            const [level, msg] = Array.isArray(f) ? f : ['warn', String(f)];
                                                            const st = flagStyle(level);
                                                            return (
                                                                <div key={i} style={{
                                                                    display: 'flex', alignItems: 'center', gap: 8,
                                                                    padding: '8px 11px', borderRadius: 8,
                                                                    fontSize: 12, fontWeight: 500, lineHeight: 1.35,
                                                                    background: st.bg, color: st.color, border: `1px solid ${st.border}`,
                                                                }}>
                                                                    <span style={{
                                                                        width: 16, height: 16, borderRadius: 999, background: st.color,
                                                                        color: '#fff', display: 'grid', placeItems: 'center',
                                                                        fontSize: 10, fontWeight: 700, flex: 'none',
                                                                    }}>{st.icon}</span>
                                                                    {msg}
                                                                </div>
                                                            );
                                                        })}
                                                    </div>
                                                )}
                                            </div>
                                        </>)}
                                    </>;
                                })()}
                            </div>
                        </div>
                    </div>
                );
            })()}

            {/* Excel-import preview modal */}
            {importPreview && (
                <div onClick={() => !importSaving && setImportPreview(null)}
                    style={{ position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 9999, padding: 20 }}>
                    <div onClick={e => e.stopPropagation()}
                        style={{ background: '#fff', borderRadius: 14, maxWidth: 720, width: '100%', maxHeight: '90vh', overflow: 'auto', padding: 0, boxShadow: '0 20px 60px rgba(15,23,42,0.35)' }}>
                        {/* Modal header */}
                        <div style={{ padding: '20px 24px', borderBottom: '1px solid #E4E8EE', background: '#FAFBFD', borderRadius: '14px 14px 0 0' }}>
                            <div style={{ fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.08em', color: '#6B7686', fontWeight: 700 }}>Preview import</div>
                            <div style={{ fontSize: 18, fontWeight: 600, fontFamily: HEADING, letterSpacing: '-0.01em', marginTop: 2 }}>
                                {importPreview.extracted.name || '(no name)'}
                            </div>
                            <div style={{ fontSize: 11.5, color: '#9AA4B2', fontFamily: MONO, marginTop: 2 }}>
                                Source: {importPreview.fileName}
                            </div>
                        </div>

                        {/* Extracted fields */}
                        <div style={{ padding: '18px 24px' }}>
                            {importPreview.existingMatch && (
                                <div style={{ padding: '10px 12px', borderRadius: 8, background: '#FBF2E1', border: '1px solid #EDDCBB', color: '#B7791A', fontSize: 12.5, marginBottom: 14 }}>
                                    ⚠ An investor named <strong>{importPreview.existingMatch.Name}</strong> already exists (Id #{importPreview.existingMatch.Id}).
                                    Choose <strong>Merge into existing</strong> below to update it, or <strong>Create new</strong> to add a duplicate.
                                </div>
                            )}
                            <div style={{ fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.08em', color: '#9AA4B2', fontWeight: 700, marginBottom: 8 }}>Factual fields</div>
                            <div style={{ display: 'grid', gridTemplateColumns: '1fr 2fr', gap: '6px 14px', fontSize: 12.5, marginBottom: 16 }}>
                                {[
                                    ['Type', importPreview.extracted.type],
                                    ['Family', importPreview.extracted.family],
                                    ['HQ', importPreview.extracted.hq + (importPreview.extracted.hq2 ? ` (+ ${importPreview.extracted.hq2})` : '')],
                                    ['Website', importPreview.extracted.website],
                                    ['Founded', importPreview.extracted.founded],
                                    ['Employees', importPreview.extracted.employees],
                                    ['AUM', importPreview.extracted.aum],
                                    ['Status', importPreview.extracted.invStatus],
                                    ['Total investments', importPreview.extracted.totalInv],
                                    ['Active portfolio', importPreview.extracted.activePortfolio],
                                    ['Exits', importPreview.extracted.exitCount],
                                    ['Last 12M deployment', importPreview.extracted.inv12m],
                                    ['Median deal', importPreview.extracted.medianDeal],
                                    ['Median valuation', importPreview.extracted.medianVal],
                                    ['Primary contact', importPreview.extracted.contactName + (importPreview.extracted.contactEmail ? ` — ${importPreview.extracted.contactEmail}` : '')],
                                    ['Suggested partner', importPreview.extracted.partnerName + (importPreview.extracted.partnerTitle ? ` — ${importPreview.extracted.partnerTitle}` : '')],
                                ].map(([k, v]) => (
                                    <React.Fragment key={k}>
                                        <div style={{ color: '#6B7686' }}>{k}</div>
                                        <div style={{ color: '#0E1622', fontWeight: v ? 500 : 400 }}>{v != null && v !== '' && v !== 'null' && v !== 'undefined' ? v : <span style={{ color: '#9AA4B2' }}>—</span>}</div>
                                    </React.Fragment>
                                ))}
                            </div>

                            {importPreview.extracted.description && (
                                <>
                                    <div style={{ fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.08em', color: '#9AA4B2', fontWeight: 700, marginBottom: 6 }}>Description</div>
                                    <div style={{ fontSize: 12.5, lineHeight: 1.55, color: '#3A4656', marginBottom: 16, padding: '10px 12px', background: '#FAFBFD', border: '1px solid #EDF0F4', borderRadius: 8 }}>
                                        {importPreview.extracted.description}
                                    </div>
                                </>
                            )}

                            <div style={{ fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.08em', color: '#9AA4B2', fontWeight: 700, marginBottom: 8 }}>Diligence data attached</div>
                            <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginBottom: 16 }}>
                                {[
                                    ['Investments', importPreview.counts.investments],
                                    ['Exits', importPreview.counts.exits],
                                    ['Team', importPreview.counts.team],
                                    ['Board seats', importPreview.counts.board],
                                    ['Funds', importPreview.counts.funds],
                                    ['News items', importPreview.counts.news],
                                ].map(([lbl, n]) => (
                                    <div key={lbl} style={{ padding: '8px 12px', background: '#EEF1FE', color: '#1B31B8', borderRadius: 8, fontSize: 12, fontWeight: 500, minWidth: 90 }}>
                                        <div style={{ fontSize: 10.5, textTransform: 'uppercase', letterSpacing: '0.07em', opacity: 0.75 }}>{lbl}</div>
                                        <div style={{ fontFamily: MONO, fontSize: 17, fontWeight: 600, marginTop: 2 }}>{n}</div>
                                    </div>
                                ))}
                            </div>

                            <div style={{ padding: '10px 12px', borderRadius: 8, background: '#F4F6F9', color: '#6B7686', fontSize: 11.5, lineHeight: 1.5 }}>
                                <strong style={{ color: '#0E1622' }}>Analytical fields</strong> — Fit / Threat / Strengths / Weaknesses / Relationship stage / Next action — are <strong>NOT</strong> auto-imported. Fill those in manually via the edit view (coming in the next commit). The raw Excel is stored so it stays downloadable via Know more.
                            </div>
                        </div>

                        {/* Modal footer */}
                        <div style={{ padding: '14px 24px', borderTop: '1px solid #E4E8EE', display: 'flex', justifyContent: 'space-between', alignItems: 'center', background: '#FAFBFD', borderRadius: '0 0 14px 14px' }}>
                            <button onClick={() => setImportPreview(null)} disabled={importSaving}
                                style={{ padding: '9px 16px', border: '1px solid #E4E8EE', borderRadius: 8, background: '#fff', color: '#3A4656', fontSize: 13, fontWeight: 500, cursor: importSaving ? 'default' : 'pointer', opacity: importSaving ? 0.5 : 1 }}>
                                Cancel
                            </button>
                            <div style={{ display: 'flex', gap: 8 }}>
                                {importPreview.existingMatch && (
                                    <button onClick={() => commitImport('merge')} disabled={importSaving}
                                        style={{ padding: '9px 16px', border: '1px solid #E4E8EE', borderRadius: 8, background: '#fff', color: '#3A4656', fontSize: 13, fontWeight: 500, cursor: 'pointer' }}>
                                        {importSaving ? 'Saving…' : 'Merge into existing'}
                                    </button>
                                )}
                                <button onClick={() => commitImport('create')} disabled={importSaving}
                                    style={{ padding: '9px 18px', border: 'none', borderRadius: 8, background: '#2743E0', color: '#fff', fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>
                                    {importSaving ? 'Saving…' : (importPreview.existingMatch ? 'Create new anyway' : 'Import investor')}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {selectedIds.size > 0 && (
                <div style={{ position: 'fixed', bottom: 20, left: '50%', transform: 'translateX(-50%)', background: '#1e293b', color: '#ffffff', padding: '12px 20px', borderRadius: 10, boxShadow: '0 10px 30px rgba(0,0,0,0.25)', display: 'flex', alignItems: 'center', gap: 16, zIndex: 1000 }}>
                    <span style={{ fontSize: 14, fontWeight: 600 }}>{selectedIds.size} selected</span>
                    <button onClick={bulkDelete} style={{ background: '#dc2626', color: '#ffffff', border: 'none', padding: '8px 16px', borderRadius: 6, fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>Delete</button>
                    <button onClick={() => { clearSelection(); setDeleteMode(false); }} style={{ background: 'transparent', color: '#cbd5e1', border: 'none', cursor: 'pointer', fontSize: 13 }}>Cancel</button>
                </div>
            )}

            {showModal && (
                <InvestorModal
                    investor={editingInvestor}
                    onClose={() => { setShowModal(false); setEditingInvestor(null); }}
                    onSave={async (investorData) => {
                        try {
                            setSaving(true);
                            let saved;
                            if (editingInvestor) {
                                saved = await api.updateInvestor(editingInvestor.Id, investorData);
                            } else {
                                // Duplicate check: same Name + Type
                                const newKey = `${(investorData.name || '').trim().toLowerCase()}|${(investorData.type || '').trim().toLowerCase()}`;
                                const exists = (data || []).some(i => `${(i.Name || '').trim().toLowerCase()}|${(i.Type || '').trim().toLowerCase()}` === newKey);
                                if (exists) {
                                    showToast(`A ${investorData.type || 'record'} with this name already exists. Please edit the existing one.`, 'error');
                                    setSaving(false);
                                    return;
                                }
                                saved = await api.createInvestor(investorData);
                            }
                            if (onLocalUpsert && saved && saved.Id) onLocalUpsert(saved);
                            setShowModal(false);
                            setEditingInvestor(null);
                        } catch (error) {
                            await api.getAllData(true);
                            await onDataChange();
                            alert('Failed to save: ' + error.message);
                        } finally {
                            setSaving(false);
                        }
                    }}
                    saving={saving}
                />
            )}
        </div>
    );
}

// Investor Modal Component - WITH COMPETITION

// Investor Modal Component - WITH COMPETITION
// ─────────────────────────────────────────────────────────────────────────────
// InvestorModal — Rich, sectioned edit form.
// Sections (top to bottom):
//   1. Verdict & judgement — Fit / FitReason / Flags (the highest-value edit)
//   2. Relationship        — RelStage stepper + RelOwner / RelIntro / RelSource /
//                            RelMaterials / RelDataRoom / RelHook / RelNext / RelDue / RelLast
//   3. Firm details        — Name / Family / Type / HQ / HQ2 / Website / Description /
//                            AUM / Founded / Employees / InvStatus / StatusCold /
//                            DryPowder / CheckRange / Stages / Verticals / Geo
//   4. People              — ContactName / ContactTitle / ContactEmail /
//                            PartnerName / PartnerTitle
// Auto-imported numeric stats (TotalInv, MedianDeal, etc.) are read-only —
// re-import the PitchBook Excel to refresh those.
// ─────────────────────────────────────────────────────────────────────────────
function InvestorModal({ investor, onClose, onSave, saving }) {
    const INTER = "'Inter', system-ui, sans-serif";
    const HEADING = "'Space Grotesk', 'Inter', system-ui, sans-serif";

    const REL_STEPS = ['Not engaged', 'Contacted', 'Intro call', 'Deep dive', 'Term sheet', 'Committed'];
    const STAGE_OPTS = ['Pre-seed', 'Seed', 'Series A', 'Series B', 'Series C', 'Later'];
    const VERTICAL_OPTS = ['Fintech', 'RegTech', 'Deep-tech', 'Enterprise AI', 'ESG', 'Insurance', 'Payments'];
    const GEO_OPTS = ['SEA', 'India', 'MENA', 'Europe', 'US', 'China', 'Japan', 'Africa'];
    const TYPE_OPTS = ['VC', 'Angel', 'Accelerator', 'PE/Buyout', 'Impact', 'Investor'];

    const toArr = (v) => {
        if (Array.isArray(v)) return v;
        if (typeof v === 'string' && v.trim()) {
            try { const p = JSON.parse(v); return Array.isArray(p) ? p : []; } catch { return []; }
        }
        return [];
    };
    const toDateStr = (d) => d ? new Date(d).toISOString().split('T')[0] : '';

    const initial = investor ? {
        // Legacy
        name: investor.Name || '',
        type: investor.Type || 'VC',
        amount: investor.Amount || 0,
        stage: investor.Stage || '',
        status: investor.Status || '',
        contact: investor.Contact || '',
        lastContact: toDateStr(investor.LastContact),
        notes: investor.Notes || '',
        deadline: toDateStr(investor.Deadline),
        // Verdict
        fit: investor.Fit || '',
        fitReason: investor.FitReason || '',
        flags: toArr(investor.Flags),
        // Relationship
        relStage: investor.RelStage != null ? Number(investor.RelStage) : 0,
        relOwner: investor.RelOwner || '',
        relIntro: investor.RelIntro || '',
        relSource: investor.RelSource || '',
        relMaterials: investor.RelMaterials || '',
        relDataRoom: investor.RelDataRoom || '',
        relHook: investor.RelHook || '',
        relNext: investor.RelNext || '',
        relDue: toDateStr(investor.RelDue),
        relLast: investor.RelLast || '',
        // Firm details
        family: investor.Family || '',
        hq: investor.HQ || '',
        hq2: investor.HQ2 || '',
        website: investor.Website || '',
        description: investor.Description || '',
        aum: investor.AUM || '',
        aumRaw: investor.AUMRaw != null ? Number(investor.AUMRaw) : '',
        founded: investor.Founded != null ? Number(investor.Founded) : '',
        employees: investor.Employees != null ? Number(investor.Employees) : '',
        invStatus: investor.InvStatus || '',
        statusCold: !!investor.StatusCold,
        dryPowder: investor.DryPowder || '',
        checkRange: investor.CheckRange || '',
        stages: toArr(investor.Stages),
        verticals: toArr(investor.Verticals),
        geo: toArr(investor.Geo),
        // People
        contactName: investor.ContactName || '',
        contactTitle: investor.ContactTitle || '',
        contactEmail: investor.ContactEmail || '',
        partnerName: investor.PartnerName || '',
        partnerTitle: investor.PartnerTitle || '',
    } : {
        name: '', type: 'VC', amount: 0, stage: '', status: '', contact: '', lastContact: new Date().toISOString().split('T')[0], notes: '', deadline: '',
        fit: '', fitReason: '', flags: [],
        relStage: 0, relOwner: '', relIntro: '', relSource: '', relMaterials: '', relDataRoom: '', relHook: '', relNext: '', relDue: '', relLast: '',
        family: '', hq: '', hq2: '', website: '', description: '', aum: '', aumRaw: '', founded: '', employees: '', invStatus: '', statusCold: false, dryPowder: '', checkRange: '',
        stages: [], verticals: [], geo: [],
        contactName: '', contactTitle: '', contactEmail: '', partnerName: '', partnerTitle: '',
    };

    const [form, setForm] = useState(initial);
    // Section open/collapse state — Verdict + Relationship open by default (most-edited)
    const [openSection, setOpenSection] = useState({ verdict: true, rel: true, firm: false, people: false });
    // Temporary text for the "+ Add custom" inputs at the end of each chip group
    const [customInput, setCustomInput] = useState({ stages: '', verticals: '', geo: '' });
    const addCustom = (fieldKey, inputKey) => {
        const raw = (customInput[inputKey] || '').trim();
        if (!raw) return;
        // Case-insensitive dedupe against existing selections
        if (form[fieldKey].some(v => v.toLowerCase() === raw.toLowerCase())) {
            setCustomInput(c => ({ ...c, [inputKey]: '' }));
            return;
        }
        update(fieldKey, [...form[fieldKey], raw]);
        setCustomInput(c => ({ ...c, [inputKey]: '' }));
    };

    const update = (k, v) => setForm(f => ({ ...f, [k]: v }));
    const toggleInArray = (key, value) => {
        const arr = form[key];
        const has = arr.includes(value);
        update(key, has ? arr.filter(x => x !== value) : [...arr, value]);
    };
    const addFlag = () => update('flags', [...form.flags, ['warn', '']]);
    const setFlagLevel = (i, lvl) => update('flags', form.flags.map((f, idx) => idx === i ? [lvl, f[1]] : f));
    const setFlagText = (i, txt) => update('flags', form.flags.map((f, idx) => idx === i ? [f[0], txt] : f));
    const removeFlag = (i) => update('flags', form.flags.filter((_, idx) => idx !== i));

    const submit = (e) => {
        e.preventDefault();
        onSave({
            // Legacy — preserved so backwards-compat CRUD paths still work
            name: form.name,
            type: form.type,
            amount: parseFloat(form.amount) || 0,
            stage: form.stage || null,
            status: form.status || null,
            contact: form.contact || null,
            lastContact: form.lastContact || null,
            notes: form.notes || null,
            deadline: form.deadline || null,
            // Verdict
            fit: form.fit || null,
            fitReason: form.fitReason || null,
            flags: form.flags.filter(f => f && f[1]),  // drop empty
            // Relationship
            relStage: form.relStage,
            relOwner: form.relOwner || null,
            relIntro: form.relIntro || null,
            relSource: form.relSource || null,
            relMaterials: form.relMaterials || null,
            relDataRoom: form.relDataRoom || null,
            relHook: form.relHook || null,
            relNext: form.relNext || null,
            relDue: form.relDue || null,
            relLast: form.relLast || null,
            // Firm details
            family: form.family || null,
            hq: form.hq || null,
            hq2: form.hq2 || null,
            website: form.website || null,
            description: form.description || null,
            aum: form.aum || null,
            aumRaw: form.aumRaw !== '' && form.aumRaw != null ? parseFloat(form.aumRaw) : null,
            founded: form.founded !== '' && form.founded != null ? parseInt(form.founded) : null,
            employees: form.employees !== '' && form.employees != null ? parseInt(form.employees) : null,
            invStatus: form.invStatus || null,
            statusCold: !!form.statusCold,
            dryPowder: form.dryPowder || null,
            checkRange: form.checkRange || null,
            stages: form.stages,
            verticals: form.verticals,
            geo: form.geo,
            // People
            contactName: form.contactName || null,
            contactTitle: form.contactTitle || null,
            contactEmail: form.contactEmail || null,
            partnerName: form.partnerName || null,
            partnerTitle: form.partnerTitle || null,
        });
    };

    // ── Shared inline styles ─────────────────────────────────────────────
    const inp = { width: '100%', padding: '8px 11px', border: '1px solid #E4E8EE', borderRadius: 7, fontSize: 13, fontFamily: INTER, background: '#fff', color: '#0E1622' };
    const lbl = { fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.07em', color: '#6B7686', fontWeight: 600, marginBottom: 5, display: 'block' };
    const field = (label, node) => (
        <div style={{ marginBottom: 12 }}>
            <label style={lbl}>{label}</label>
            {node}
        </div>
    );
    const fieldRow = (children) => (
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 0 }}>{children}</div>
    );
    const chipBtn = (on) => ({
        padding: '5px 10px', border: `1px solid ${on ? '#0E1622' : '#E4E8EE'}`,
        borderRadius: 999, background: on ? '#0E1622' : '#fff',
        color: on ? '#fff' : '#3A4656', fontSize: 11.5, cursor: 'pointer',
        fontFamily: INTER, fontWeight: on ? 600 : 500,
    });
    const sectionHead = (key, title, subtitle) => {
        const isOpen = openSection[key];
        return (
            <button type="button" onClick={() => setOpenSection(s => ({ ...s, [key]: !s[key] }))}
                style={{
                    width: '100%', display: 'flex', alignItems: 'center', gap: 10,
                    padding: '14px 18px', background: '#FAFBFD', border: 'none',
                    borderBottom: isOpen ? '1px solid #E4E8EE' : 'none',
                    cursor: 'pointer', textAlign: 'left', fontFamily: INTER,
                }}>
                <svg width="10" height="10" viewBox="0 0 10 10" fill="none" stroke="#6B7686" strokeWidth="1.8"
                    style={{ transform: isOpen ? 'rotate(90deg)' : 'none', transition: 'transform .15s' }}>
                    <path d="M3 1.5l3.5 3.5-3.5 3.5" />
                </svg>
                <div style={{ flex: 1 }}>
                    <div style={{ fontSize: 13.5, fontWeight: 600, fontFamily: HEADING, color: '#0E1622', letterSpacing: '-0.005em' }}>{title}</div>
                    {subtitle && <div style={{ fontSize: 11, color: '#9AA4B2', marginTop: 1 }}>{subtitle}</div>}
                </div>
            </button>
        );
    };

    // Fit color helper
    const FIT_COLOR = { fit: '#12996B', partial: '#B7791A', skip: '#CE4238' };
    const FIT_BG    = { fit: '#E7F5EF', partial: '#FBF2E1', skip: '#FBECEA' };

    return (
        <div onClick={onClose}
            style={{ position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 9999, padding: 20 }}>
            <div onClick={e => e.stopPropagation()}
                style={{ background: '#fff', borderRadius: 14, width: '100%', maxWidth: 780, maxHeight: '92vh', display: 'flex', flexDirection: 'column', boxShadow: '0 20px 60px rgba(15,23,42,0.35)', fontFamily: INTER }}>
                {/* Header */}
                <div style={{ padding: '18px 22px', borderBottom: '1px solid #E4E8EE', background: '#FAFBFD', borderRadius: '14px 14px 0 0' }}>
                    <div style={{ fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.08em', color: '#6B7686', fontWeight: 700 }}>
                        {investor ? 'Edit investor' : 'Add investor'}
                    </div>
                    <div style={{ fontSize: 18, fontWeight: 600, fontFamily: HEADING, letterSpacing: '-0.01em', marginTop: 2 }}>
                        {form.name || <span style={{ color: '#9AA4B2' }}>(unnamed)</span>}
                    </div>
                </div>

                <form onSubmit={submit} style={{ flex: 1, overflowY: 'auto' }}>
                    {/* SECTION 1: Verdict */}
                    <div style={{ borderBottom: '1px solid #EDF0F4' }}>
                        {sectionHead('verdict', 'Verdict & judgement', 'The most important thing to fill in')}
                        {openSection.verdict && (
                            <div style={{ padding: '16px 20px' }}>
                                {field('Fit', (
                                    <div style={{ display: 'flex', gap: 8 }}>
                                        {[
                                            { k: '', l: 'Not set', c: '#9AA4B2', bg: '#F4F6F9' },
                                            { k: 'fit', l: 'Fit', c: FIT_COLOR.fit, bg: FIT_BG.fit },
                                            { k: 'partial', l: 'Partial', c: FIT_COLOR.partial, bg: FIT_BG.partial },
                                            { k: 'skip', l: 'Skip', c: FIT_COLOR.skip, bg: FIT_BG.skip },
                                        ].map(o => {
                                            const active = form.fit === o.k;
                                            return (
                                                <button key={o.k} type="button" onClick={() => update('fit', o.k)}
                                                    style={{
                                                        padding: '7px 14px', border: `1.5px solid ${active ? o.c : '#E4E8EE'}`,
                                                        borderRadius: 8, background: active ? o.bg : '#fff',
                                                        color: active ? o.c : '#3A4656', fontSize: 13, fontWeight: active ? 700 : 500,
                                                        cursor: 'pointer',
                                                    }}>{o.l}</button>
                                            );
                                        })}
                                    </div>
                                ))}
                                {field('Fit reason (why fit / partial / skip)', (
                                    <textarea value={form.fitReason} onChange={e => update('fitReason', e.target.value)}
                                        placeholder="e.g. Bullseye on stage/vertical/geo — actively deploying at 11 deals / 12 months. Warm intro available via Simon See."
                                        rows={3} style={{ ...inp, resize: 'vertical' }} />
                                ))}
                                {field('Flags', (
                                    <div>
                                        {form.flags.map((f, i) => {
                                            const [lvl, msg] = f;
                                            const bgMap = { ok: '#E7F5EF', warn: '#FBF2E1', bad: '#FBECEA' };
                                            const fgMap = { ok: '#12996B', warn: '#B7791A', bad: '#CE4238' };
                                            return (
                                                <div key={i} style={{ display: 'flex', gap: 6, marginBottom: 6, alignItems: 'center' }}>
                                                    <select value={lvl} onChange={e => setFlagLevel(i, e.target.value)}
                                                        style={{ ...inp, width: 90, background: bgMap[lvl], color: fgMap[lvl], fontWeight: 600 }}>
                                                        <option value="ok">✓ Good</option>
                                                        <option value="warn">! Warn</option>
                                                        <option value="bad">✗ Bad</option>
                                                    </select>
                                                    <input type="text" value={msg} onChange={e => setFlagText(i, e.target.value)}
                                                        placeholder="e.g. Cooling — 1 deal in last 12M"
                                                        style={{ ...inp, flex: 1 }} />
                                                    <button type="button" onClick={() => removeFlag(i)}
                                                        style={{ padding: '6px 10px', border: '1px solid #F0CFCB', borderRadius: 7, background: '#FBECEA', color: '#CE4238', cursor: 'pointer' }}>×</button>
                                                </div>
                                            );
                                        })}
                                        <button type="button" onClick={addFlag}
                                            style={{ padding: '6px 12px', border: '1px dashed #CBD5E1', borderRadius: 7, background: '#fff', color: '#6B7686', fontSize: 12, cursor: 'pointer', marginTop: 4 }}>
                                            + Add flag
                                        </button>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>

                    {/* SECTION 2: Relationship */}
                    <div style={{ borderBottom: '1px solid #EDF0F4' }}>
                        {sectionHead('rel', 'Relationship tracking', 'Where you are in the pipeline')}
                        {openSection.rel && (
                            <div style={{ padding: '16px 20px' }}>
                                {field('Stage', (
                                    <div style={{ display: 'flex', border: '1px solid #E4E8EE', borderRadius: 7, overflow: 'hidden' }}>
                                        {REL_STEPS.map((s, i) => (
                                            <button key={i} type="button" onClick={() => update('relStage', i)}
                                                style={{
                                                    flex: 1, padding: '8px 4px', textAlign: 'center', fontSize: 10,
                                                    background: i === form.relStage ? '#2743E0' : (i < form.relStage ? '#EEF1FE' : '#fff'),
                                                    color: i === form.relStage ? '#fff' : (i < form.relStage ? '#1B31B8' : '#9AA4B2'),
                                                    border: 'none', borderLeft: i > 0 ? '1px solid #E4E8EE' : 'none',
                                                    fontWeight: i === form.relStage ? 700 : 500, cursor: 'pointer', fontFamily: INTER,
                                                }}>{s}</button>
                                        ))}
                                    </div>
                                ))}
                                {fieldRow(<>
                                    {field('Owner (your side)', <input type="text" value={form.relOwner} onChange={e => update('relOwner', e.target.value)} placeholder="e.g. Ajay" style={inp} />)}
                                    {field('Intro path', <input type="text" value={form.relIntro} onChange={e => update('relIntro', e.target.value)} placeholder="Cold / Warm — via Simon See" style={inp} />)}
                                </>)}
                                {fieldRow(<>
                                    {field('Source', <input type="text" value={form.relSource} onChange={e => update('relSource', e.target.value)} placeholder="e.g. LEAP East, RAISE Summit" style={inp} />)}
                                    {field('Value angle', <input type="text" value={form.relHook} onChange={e => update('relHook', e.target.value)} placeholder="What you're leading with — e.g. Quantexa pedigree, Nationwide reference" style={inp} />)}
                                </>)}
                                {fieldRow(<>
                                    {field('Materials sent', <input type="text" value={form.relMaterials} onChange={e => update('relMaterials', e.target.value)} placeholder="e.g. SLINGSHOT deck v3" style={inp} />)}
                                    {field('Data room access', <input type="text" value={form.relDataRoom} onChange={e => update('relDataRoom', e.target.value)} placeholder="No / Pending / Yes (secure doc folder shared?)" style={inp} />)}
                                </>)}
                                {field('Next action', <input type="text" value={form.relNext} onChange={e => update('relNext', e.target.value)} placeholder="e.g. Send data-room link + Graph Transformer brief" style={inp} />)}
                                {fieldRow(<>
                                    {field('Due', <input type="date" value={form.relDue} onChange={e => update('relDue', e.target.value)} style={inp} />)}
                                    {field('Last touch', <input type="text" value={form.relLast} onChange={e => update('relLast', e.target.value)} placeholder="e.g. Intro call 11 Jul — asked for CJK proof" style={inp} />)}
                                </>)}
                            </div>
                        )}
                    </div>

                    {/* SECTION 3: Firm details */}
                    <div style={{ borderBottom: '1px solid #EDF0F4' }}>
                        {sectionHead('firm', 'Firm details', 'Usually auto-imported — override here if wrong')}
                        {openSection.firm && (
                            <div style={{ padding: '16px 20px' }}>
                                {fieldRow(<>
                                    {field('Name *', <input type="text" required value={form.name} onChange={e => update('name', e.target.value)} style={inp} />)}
                                    {field('Family (parent firm)', <input type="text" value={form.family} onChange={e => update('family', e.target.value)} placeholder="e.g. FortRoss" style={inp} />)}
                                </>)}
                                {fieldRow(<>
                                    {field('Type', (
                                        <select value={form.type} onChange={e => update('type', e.target.value)} style={inp}>
                                            {TYPE_OPTS.map(t => <option key={t}>{t}</option>)}
                                        </select>
                                    ))}
                                    {field('Website', <input type="text" value={form.website} onChange={e => update('website', e.target.value)} placeholder="firm.vc" style={inp} />)}
                                </>)}
                                {fieldRow(<>
                                    {field('HQ (primary office)', <input type="text" value={form.hq} onChange={e => update('hq', e.target.value)} placeholder="Woodside, CA" style={inp} />)}
                                    {field('Secondary office', <input type="text" value={form.hq2} onChange={e => update('hq2', e.target.value)} placeholder="Dubai" style={inp} />)}
                                </>)}
                                {field('Description', <textarea value={form.description} onChange={e => update('description', e.target.value)} rows={3} style={{ ...inp, resize: 'vertical' }} />)}
                                {fieldRow(<>
                                    {field('Founded (year)', <input type="number" value={form.founded} onChange={e => update('founded', e.target.value)} placeholder="2015" style={inp} />)}
                                    {field('Employees', <input type="number" value={form.employees} onChange={e => update('employees', e.target.value)} placeholder="10" style={inp} />)}
                                </>)}
                                {fieldRow(<>
                                    {field('Status', (
                                        <select value={form.invStatus} onChange={e => update('invStatus', e.target.value)} style={inp}>
                                            <option value="">(not set)</option>
                                            <option>Actively seeking</option>
                                            <option>Selective</option>
                                            <option>Paused</option>
                                        </select>
                                    ))}
                                    {field('Status cold?', (
                                        <label style={{ display: 'flex', alignItems: 'center', gap: 8, height: 34, cursor: 'pointer', fontSize: 13, color: '#3A4656' }}>
                                            <input type="checkbox" checked={form.statusCold} onChange={e => update('statusCold', e.target.checked)}
                                                style={{ width: 15, height: 15, accentColor: '#2743E0' }} />
                                            Show status as cold (grey dot)
                                        </label>
                                    ))}
                                </>)}
                                {fieldRow(<>
                                    {field('AUM (display)', <input type="text" value={form.aum} onChange={e => update('aum', e.target.value)} placeholder="$450M USD" style={inp} />)}
                                    {field('AUM (numeric — for sort)', <input type="number" value={form.aumRaw} onChange={e => update('aumRaw', e.target.value)} placeholder="450000000" style={inp} />)}
                                </>)}
                                {fieldRow(<>
                                    {field('Dry powder', <input type="text" value={form.dryPowder} onChange={e => update('dryPowder', e.target.value)} placeholder="~$120M" style={inp} />)}
                                    {field('Cheque range', <input type="text" value={form.checkRange} onChange={e => update('checkRange', e.target.value)} placeholder="$1M–$5M" style={inp} />)}
                                </>)}
                                {/* Chip picker with "+ Add custom" input at the end.
                                    Combines preset options with any custom values the user has already
                                    added (deduped case-insensitively). Enter or click + to add a new one. */}
                                {(() => {
                                    const renderChipGroup = (label, fieldKey, inputKey, presetOpts, placeholder) => {
                                        const preset = new Set(presetOpts.map(o => o.toLowerCase()));
                                        const customValues = form[fieldKey].filter(v => !preset.has((v || '').toLowerCase()));
                                        const allChips = [...presetOpts, ...customValues];
                                        return field(label, (
                                            <div>
                                                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 5 }}>
                                                    {allChips.map(s => (
                                                        <button key={s} type="button" onClick={() => toggleInArray(fieldKey, s)} style={chipBtn(form[fieldKey].includes(s))}>{s}</button>
                                                    ))}
                                                </div>
                                                <div style={{ display: 'flex', gap: 6, marginTop: 8 }}>
                                                    <input type="text" value={customInput[inputKey]}
                                                        onChange={e => setCustomInput(c => ({ ...c, [inputKey]: e.target.value }))}
                                                        onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); addCustom(fieldKey, inputKey); } }}
                                                        placeholder={placeholder}
                                                        style={{ ...inp, flex: 1, fontSize: 12, padding: '6px 10px' }} />
                                                    <button type="button" onClick={() => addCustom(fieldKey, inputKey)}
                                                        style={{ padding: '6px 12px', border: '1px dashed #CBD5E1', borderRadius: 7, background: '#fff', color: '#6B7686', fontSize: 12, cursor: 'pointer', whiteSpace: 'nowrap' }}>
                                                        + Add
                                                    </button>
                                                </div>
                                            </div>
                                        ));
                                    };
                                    return <>
                                        {renderChipGroup('Stages they invest in', 'stages', 'stages', STAGE_OPTS, 'Add stage — e.g. Growth, Bridge')}
                                        {renderChipGroup('Verticals', 'verticals', 'verticals', VERTICAL_OPTS, 'Add vertical — e.g. Cybersecurity, AgriTech, Healthtech')}
                                        {renderChipGroup('Geography focus', 'geo', 'geo', GEO_OPTS, 'Add region — e.g. Latam, Nordic, Singapore')}
                                    </>;
                                })()}
                            </div>
                        )}
                    </div>

                    {/* SECTION 4: People */}
                    <div>
                        {sectionHead('people', 'People', 'Primary contact + target partner')}
                        {openSection.people && (
                            <div style={{ padding: '16px 20px' }}>
                                <div style={{ fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.07em', color: '#9AA4B2', fontWeight: 700, marginBottom: 8 }}>Primary contact</div>
                                {fieldRow(<>
                                    {field('Name', <input type="text" value={form.contactName} onChange={e => update('contactName', e.target.value)} style={inp} />)}
                                    {field('Title', <input type="text" value={form.contactTitle} onChange={e => update('contactTitle', e.target.value)} style={inp} />)}
                                </>)}
                                {field('Email', <input type="email" value={form.contactEmail} onChange={e => update('contactEmail', e.target.value)} style={inp} />)}
                                <div style={{ fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.07em', color: '#9AA4B2', fontWeight: 700, marginBottom: 8, marginTop: 16 }}>Target partner (who to write to)</div>
                                {fieldRow(<>
                                    {field('Name', <input type="text" value={form.partnerName} onChange={e => update('partnerName', e.target.value)} style={inp} />)}
                                    {field('Title', <input type="text" value={form.partnerTitle} onChange={e => update('partnerTitle', e.target.value)} style={inp} />)}
                                </>)}
                            </div>
                        )}
                    </div>
                </form>

                {/* Footer */}
                <div style={{ padding: '14px 22px', borderTop: '1px solid #E4E8EE', display: 'flex', justifyContent: 'flex-end', gap: 8, background: '#FAFBFD', borderRadius: '0 0 14px 14px' }}>
                    <button type="button" onClick={onClose} disabled={saving}
                        style={{ padding: '9px 16px', border: '1px solid #E4E8EE', borderRadius: 8, background: '#fff', color: '#3A4656', fontSize: 13, fontWeight: 500, cursor: saving ? 'default' : 'pointer', opacity: saving ? 0.5 : 1 }}>
                        Cancel
                    </button>
                    <button type="submit" onClick={submit} disabled={saving}
                        style={{ padding: '9px 20px', border: 'none', borderRadius: 8, background: '#2743E0', color: '#fff', fontSize: 13, fontWeight: 600, cursor: saving ? 'default' : 'pointer', opacity: saving ? 0.7 : 1 }}>
                        {saving ? 'Saving…' : (investor ? 'Save changes' : 'Add investor')}
                    </button>
                </div>
            </div>
        </div>
    );
}

// ─────────────────────────────────────────────────────────────────────────────
// OpportunitiesTab — warm editorial layout for non-equity funding.
// Cream palette (#FAF6EC), Fraunces headings, JetBrains Mono for numbers,
// underlined tabs (Grants / Accelerators / Competitions / Awards),
// summary strip (Active · Value · Won · Next deadline), sortable table
// with expandable rows. Expanded row shows Overview + Details + Next action
// + a **Submitted materials** section where you can attach pitch decks, links, etc.
// ─────────────────────────────────────────────────────────────────────────────
function OpportunitiesTab({ data, searchTerm, onDataChange, onLocalRemove, onLocalUpsert, showToast, setGlobalLoading }) {
    // Editorial palette (matches the warm cream mockup)
    const PAL = {
        bg:      '#FAF6EC',
        surface: '#FFFCF4',
        ink:     '#2A2016',
        muted:   '#6B5A47',
        rule:    '#E5DDCB',
        forest:  '#2D5F4A',
        terra:   '#B8412C',
        amber:   '#A9741A',
        wheat:   '#F1EAD6',
    };
    const SERIF = "'Fraunces', 'Georgia', serif";
    const SANS  = "'Inter', system-ui, sans-serif";
    const MONO  = "'JetBrains Mono', 'IBM Plex Mono', ui-monospace, monospace";

    const [subTab, setSubTab] = useState('grants');
    const [showModal, setShowModal] = useState(false);
    const [editing, setEditing] = useState(null);
    const [saving, setSaving] = useState(false);
    const [statusFilter, setStatusFilter] = useState('all');
    const [expandedId, setExpandedId] = useState(null);
    const [sortKey, setSortKey] = useState('deadline');
    const [sortDir, setSortDir] = useState('asc'); // 'asc' | 'desc'
    const [addingMaterialFor, setAddingMaterialFor] = useState(null);
    const [newMat, setNewMat] = useState({ name: '', url: '', kind: 'Pitch deck', notes: '' });

    const SUBS = [
        { key: 'grants',       label: 'Grants',        type: 'Grant',       accent: PAL.forest, tag: 'Non-dilutive · Foundations & agencies' },
        { key: 'accelerators', label: 'Accelerators',  type: 'Accelerator', accent: PAL.amber,  tag: 'Structured programs · Mentorship & demo days' },
        { key: 'competitions', label: 'Competitions',  type: 'Competition', accent: PAL.terra,  tag: 'Prize-based · Entries & finalists' },
        { key: 'awards',       label: 'Awards',        type: 'Award',       accent: PAL.forest, tag: 'Recognitions your team has received' },
    ];
    SUBS.forEach(s => { s.count = (data || []).filter(r => r.Type === s.type).length; });
    const active = SUBS.find(s => s.key === subTab) || SUBS[0];

    // Stat strip totals across ALL rows of this type (not affected by filters)
    const allOfType = (data || []).filter(r => r.Type === active.type);
    const isWon = (s) => ['Won', 'Approved', 'Awarded', 'Winner'].includes(s);
    const isActive = (s) => ['In Progress', 'Submitted', 'Application', 'Review', 'Finalist', 'Applied', 'Shortlisted'].includes(s);

    // Rows for the currently-active sub-tab, plus search + status filter
    const filtered = allOfType
        .filter(r => statusFilter === 'all' || r.Status === statusFilter)
        .filter(r => {
            if (!searchTerm) return true;
            const q = searchTerm.toLowerCase();
            return (r.Name || '').toLowerCase().includes(q)
                || (r.Contact || '').toLowerCase().includes(q)
                || (r.Owner || '').toLowerCase().includes(q)
                || (r.Region || '').toLowerCase().includes(q);
        });

    // Sort
    const sortVal = (r, k) => {
        switch (k) {
            case 'name':     return (r.Name || '').toLowerCase();
            case 'org':      return (r.Contact || '').toLowerCase();
            case 'value':    return Number(r.Amount) || 0;
            case 'status':   return (r.Status || '').toLowerCase();
            case 'deadline': return r.Deadline ? new Date(r.Deadline).getTime() : Infinity;
            case 'owner':    return (r.Owner || '').toLowerCase();
            case 'region':   return (r.Region || '').toLowerCase();
            default: return 0;
        }
    };
    const rows = [...filtered].sort((a, b) => {
        const av = sortVal(a, sortKey);
        const bv = sortVal(b, sortKey);
        if (av < bv) return sortDir === 'asc' ? -1 : 1;
        if (av > bv) return sortDir === 'asc' ? 1 : -1;
        return 0;
    });
    const toggleSort = (k) => {
        if (sortKey === k) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
        else { setSortKey(k); setSortDir('asc'); }
    };

    const stats = {
        total: allOfType.length,
        active: allOfType.filter(r => isActive(r.Status || r.Stage)).length,
        won: allOfType.filter(r => isWon(r.Status || r.Stage)).length,
        moneyIn: allOfType.filter(r => isWon(r.Status || r.Stage))
            .reduce((s, r) => s + (Number(r.Amount) || 0), 0),
        nextDeadline: allOfType
            .filter(r => r.Deadline && new Date(r.Deadline) >= new Date(new Date().toDateString()))
            .sort((a, b) => new Date(a.Deadline) - new Date(b.Deadline))[0],
    };

    const fmtMoney = (n) => {
        if (!n) return '—';
        if (n >= 1e6) return '$' + (n / 1e6).toFixed(1).replace(/\.0$/, '') + 'M';
        if (n >= 1e3) return '$' + (n / 1e3).toFixed(0) + 'K';
        return '$' + Math.round(n).toLocaleString();
    };
    const fmtValue = (r) => r.ValueDisplay || (r.Amount ? fmtMoney(r.Amount) : '—');
    const fmtDate  = (d) => d ? new Date(d).toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' }) : '—';
    const fmtDateShort = (d) => d ? new Date(d).toLocaleDateString(undefined, { day: 'numeric', month: 'short' }) : '—';

    // Deadline chip: color by urgency
    const deadlineChip = (dueRaw) => {
        if (!dueRaw) return <span style={{ color: PAL.muted, fontSize: 12, fontFamily: MONO }}>—</span>;
        const due = new Date(dueRaw); due.setHours(0, 0, 0, 0);
        const today = new Date(); today.setHours(0, 0, 0, 0);
        const days = Math.round((due - today) / 86400000);
        let fg, label;
        if (days < 0)      { fg = PAL.terra;  label = `${Math.abs(days)}d overdue`; }
        else if (days === 0) { fg = PAL.terra;  label = 'Due today'; }
        else if (days <= 14) { fg = PAL.terra;  label = `${days}d left`; }
        else if (days <= 60) { fg = PAL.amber;  label = `${days}d left`; }
        else                 { fg = PAL.muted; label = fmtDateShort(dueRaw); }
        return <span style={{ color: fg, fontSize: 12, fontFamily: MONO, fontWeight: 500 }}>{label}</span>;
    };

    // Status colours in the editorial palette
    const statusStyle = (s) => {
        const won   = { bg: '#E8F0EB', fg: PAL.forest };
        const warn  = { bg: '#F6E9D9', fg: PAL.amber };
        const bad   = { bg: '#F5DDD6', fg: PAL.terra };
        const neu   = { bg: PAL.wheat, fg: PAL.muted };
        const active = { bg: '#EFE8D4', fg: PAL.ink };
        const map = {
            'In Progress': active, 'Submitted': active, 'Application': active, 'Applied': active,
            'Review': warn, 'Finalist': warn, 'Shortlisted': warn, 'Nominated': warn,
            'Approved': won, 'Awarded': won, 'Won': won, 'Winner': won,
            'Rejected': bad, 'Lost': bad,
        };
        return map[s] || neu;
    };

    const uniqueStatuses = Array.from(new Set(allOfType.map(r => r.Status).filter(Boolean)));

    // Materials helpers
    const materialsOf = (r) => Array.isArray(r.SubmittedMaterials) ? r.SubmittedMaterials
        : (typeof r.SubmittedMaterials === 'string' && r.SubmittedMaterials
            ? (() => { try { return JSON.parse(r.SubmittedMaterials); } catch { return []; } })()
            : []);
    const saveMaterials = async (r, next) => {
        const payload = {
            name: r.Name, type: r.Type,
            amount: Number(r.Amount) || 0,
            stage: r.Stage || null, status: r.Status || null,
            contact: r.Contact || null,
            lastContact: r.LastContact || null,
            deadline: r.Deadline || null, notes: r.Notes || null,
            owner: r.Owner || null, region: r.Region || null,
            valueDisplay: r.ValueDisplay || null,
            nextAction: r.NextAction || null,
            submittedMaterials: next,
        };
        try {
            const saved = await api.updateInvestor(r.Id, payload);
            if (onLocalUpsert && saved && saved.Id) onLocalUpsert(saved);
            showToast('Saved');
        } catch (e) {
            showToast('Save failed: ' + e.message, 'error');
        }
    };
    const addMaterial = async (r) => {
        if (!newMat.name && !newMat.url) return;
        const list = materialsOf(r);
        const item = { ...newMat, addedAt: new Date().toISOString() };
        await saveMaterials(r, [...list, item]);
        setNewMat({ name: '', url: '', kind: 'Pitch deck', notes: '' });
        setAddingMaterialFor(null);
    };
    const removeMaterial = async (r, idx) => {
        const list = materialsOf(r);
        await saveMaterials(r, list.filter((_, i) => i !== idx));
    };

    const th = (label, key, align) => {
        const isCur = sortKey === key;
        return (
            <th onClick={() => toggleSort(key)}
                style={{
                    padding: '10px 14px', textAlign: align || 'left', cursor: 'pointer',
                    fontSize: 10.5, textTransform: 'uppercase', letterSpacing: '0.09em',
                    fontWeight: 600, color: isCur ? PAL.ink : PAL.muted, fontFamily: SANS,
                    borderBottom: `1px solid ${PAL.rule}`, background: 'transparent',
                    userSelect: 'none', whiteSpace: 'nowrap',
                }}>
                {label}
                <span style={{ marginLeft: 6, opacity: isCur ? 1 : 0.35 }}>
                    {isCur ? (sortDir === 'asc' ? '↑' : '↓') : '↕'}
                </span>
            </th>
        );
    };

    return (
        <div style={{ fontFamily: SANS, color: PAL.ink }}>
            {/* Eyebrow */}
            <div style={{ fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.22em', color: PAL.muted, fontWeight: 500, marginBottom: 8 }}>
                Opportunities <span style={{ color: PAL.rule, margin: '0 6px' }}>·</span> <span style={{ color: active.accent }}>{active.label}</span>
            </div>

            {/* Editorial headline */}
            <h1 style={{
                margin: '0 0 6px', fontFamily: SERIF, fontWeight: 400,
                fontSize: 'clamp(38px, 5vw, 56px)', letterSpacing: '-0.02em',
                lineHeight: 1.05, color: PAL.ink,
            }}>
                <span style={{ fontStyle: 'italic' }}>{stats.total}</span>{' '}
                {stats.total === 1 ? active.label.replace(/s$/, '').toLowerCase() : active.label.toLowerCase()}
                <span style={{ color: PAL.muted }}> — </span>
                <span style={{ fontStyle: 'italic', color: PAL.muted, fontSize: '0.75em' }}>tracked & alive.</span>
            </h1>
            <p style={{ margin: '4px 0 26px', maxWidth: 640, color: PAL.muted, fontSize: 14.5, lineHeight: 1.55 }}>
                {active.tag}. Everything you've applied to, the decisions still open, and the materials you sent to each.
            </p>

            {/* Underlined tabs */}
            <div style={{ display: 'flex', gap: 26, borderBottom: `1px solid ${PAL.rule}`, marginBottom: 26 }}>
                {SUBS.map(s => {
                    const on = subTab === s.key;
                    return (
                        <button key={s.key}
                            onClick={() => { setSubTab(s.key); setStatusFilter('all'); setExpandedId(null); }}
                            style={{
                                background: 'transparent', border: 'none', cursor: 'pointer',
                                padding: '12px 0 14px', margin: 0,
                                fontFamily: SANS, fontSize: 13.5, fontWeight: on ? 600 : 500,
                                color: on ? PAL.ink : PAL.muted,
                                borderBottom: on ? `2px solid ${s.accent}` : '2px solid transparent',
                                marginBottom: -1, letterSpacing: '-0.005em',
                            }}>
                            {s.label}
                            <span style={{
                                marginLeft: 7, fontFamily: MONO, fontSize: 11,
                                color: on ? s.accent : PAL.muted, fontWeight: 500,
                            }}>{s.count}</span>
                        </button>
                    );
                })}
            </div>

            {/* Summary strip */}
            <div style={{
                display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 0,
                background: PAL.surface, border: `1px solid ${PAL.rule}`, borderRadius: 4,
                marginBottom: 24,
            }}>
                {[
                    { l: 'Active',         v: stats.active,                                  hint: 'in flight' },
                    { l: 'Total value',    v: fmtMoney(stats.moneyIn || allOfType.reduce((s,r)=>s+(Number(r.Amount)||0),0)), hint: stats.won ? `${fmtMoney(stats.moneyIn)} won` : 'across pipeline', mono: true },
                    { l: 'Won',            v: stats.won,                                     hint: stats.total ? Math.round(stats.won/stats.total*100)+'% hit rate' : '—' },
                    { l: 'Next deadline',  v: stats.nextDeadline ? fmtDateShort(stats.nextDeadline.Deadline) : '—', hint: stats.nextDeadline ? stats.nextDeadline.Name : 'nothing scheduled', mono: true },
                ].map((s, i) => (
                    <div key={s.l} style={{
                        padding: '18px 22px',
                        borderLeft: i === 0 ? 'none' : `1px solid ${PAL.rule}`,
                    }}>
                        <div style={{ fontSize: 10.5, textTransform: 'uppercase', letterSpacing: '0.14em', color: PAL.muted, fontWeight: 500, marginBottom: 6 }}>{s.l}</div>
                        <div style={{
                            fontSize: 26, fontFamily: s.mono ? MONO : SERIF,
                            fontWeight: s.mono ? 500 : 400,
                            letterSpacing: '-0.01em', color: PAL.ink, lineHeight: 1,
                        }}>{s.v}</div>
                        <div style={{ marginTop: 6, fontSize: 11.5, color: PAL.muted, fontStyle: 'italic' }}>{s.hint}</div>
                    </div>
                ))}
            </div>

            {/* Toolbar */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 14, flexWrap: 'wrap' }}>
                {uniqueStatuses.length > 0 && (
                    <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                        <button onClick={() => setStatusFilter('all')}
                            style={{
                                padding: '5px 12px', borderRadius: 999,
                                border: `1px solid ${statusFilter === 'all' ? PAL.ink : PAL.rule}`,
                                background: statusFilter === 'all' ? PAL.ink : 'transparent',
                                color: statusFilter === 'all' ? PAL.surface : PAL.muted,
                                fontSize: 12, fontWeight: 500, cursor: 'pointer', fontFamily: SANS,
                            }}>All</button>
                        {uniqueStatuses.map(s => {
                            const st = statusStyle(s);
                            const on = statusFilter === s;
                            return (
                                <button key={s} onClick={() => setStatusFilter(s)}
                                    style={{
                                        padding: '5px 12px', borderRadius: 999,
                                        border: `1px solid ${on ? st.fg : PAL.rule}`,
                                        background: on ? st.bg : 'transparent',
                                        color: on ? st.fg : PAL.muted,
                                        fontSize: 12, fontWeight: 500, cursor: 'pointer', fontFamily: SANS,
                                    }}>{s}</button>
                            );
                        })}
                    </div>
                )}
                <div style={{ flex: 1 }} />
                <button onClick={() => { setEditing(null); setShowModal(true); }}
                    style={{
                        padding: '9px 18px', border: 'none', borderRadius: 4,
                        background: active.accent, color: PAL.surface,
                        fontSize: 13, fontWeight: 500, cursor: 'pointer',
                        fontFamily: SANS, letterSpacing: '-0.005em',
                    }}>
                    + Add {active.label.replace(/s$/, '').toLowerCase()}
                </button>
            </div>

            {/* Table */}
            {rows.length === 0 ? (
                <div style={{
                    background: PAL.surface, border: `1px dashed ${PAL.rule}`, borderRadius: 4,
                    padding: '54px 24px', textAlign: 'center',
                }}>
                    <div style={{ fontSize: 18, fontFamily: SERIF, fontStyle: 'italic', color: PAL.ink, marginBottom: 6 }}>
                        Nothing here yet.
                    </div>
                    <p style={{ margin: '0 0 16px', fontSize: 13, color: PAL.muted }}>
                        Add the first {active.label.replace(/s$/, '').toLowerCase()} you're working on.
                    </p>
                    <button onClick={() => { setEditing(null); setShowModal(true); }}
                        style={{
                            padding: '9px 20px', border: 'none', borderRadius: 4,
                            background: active.accent, color: PAL.surface,
                            fontSize: 13, fontWeight: 500, cursor: 'pointer', fontFamily: SANS,
                        }}>
                        + Add {active.label.replace(/s$/, '').toLowerCase()}
                    </button>
                </div>
            ) : (
                <div style={{ background: PAL.surface, border: `1px solid ${PAL.rule}`, borderRadius: 4, overflow: 'hidden' }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse', fontFamily: SANS }}>
                        <thead>
                            <tr>
                                <th style={{ width: 32, borderBottom: `1px solid ${PAL.rule}` }} />
                                {th('Name', 'name')}
                                {th('Organization', 'org')}
                                {th('Value', 'value', 'right')}
                                {th('Status', 'status')}
                                {th('Deadline', 'deadline')}
                                {th('Owner', 'owner')}
                                {th('Region', 'region')}
                                <th style={{ width: 40, borderBottom: `1px solid ${PAL.rule}` }} />
                            </tr>
                        </thead>
                        <tbody>
                            {rows.map((r, ridx) => {
                                const st = statusStyle(r.Status);
                                const expanded = expandedId === r.Id;
                                const mats = materialsOf(r);
                                return (
                                    <React.Fragment key={r.Id}>
                                        <tr onClick={() => setExpandedId(expanded ? null : r.Id)}
                                            style={{
                                                cursor: 'pointer',
                                                background: expanded ? PAL.wheat : 'transparent',
                                                transition: 'background .12s',
                                            }}
                                            onMouseEnter={e => { if (!expanded) e.currentTarget.style.background = '#FBF5E4'; }}
                                            onMouseLeave={e => { if (!expanded) e.currentTarget.style.background = 'transparent'; }}
                                        >
                                            <td style={{ padding: '14px 0 14px 14px', borderBottom: expanded ? 'none' : `1px solid ${PAL.rule}`, color: PAL.muted, textAlign: 'center' }}>
                                                <span style={{
                                                    display: 'inline-block', transform: expanded ? 'rotate(90deg)' : 'none',
                                                    transition: 'transform .15s', fontSize: 11,
                                                }}>▸</span>
                                            </td>
                                            <td style={{ padding: '14px', borderBottom: expanded ? 'none' : `1px solid ${PAL.rule}` }}>
                                                <div style={{ fontFamily: SERIF, fontSize: 15.5, fontWeight: 500, color: PAL.ink, letterSpacing: '-0.005em', lineHeight: 1.25 }}>{r.Name}</div>
                                                {mats.length > 0 && (
                                                    <div style={{ marginTop: 3, fontSize: 11, color: PAL.muted, fontStyle: 'italic' }}>
                                                        {mats.length} submitted material{mats.length === 1 ? '' : 's'}
                                                    </div>
                                                )}
                                            </td>
                                            <td style={{ padding: '14px', borderBottom: expanded ? 'none' : `1px solid ${PAL.rule}`, fontSize: 13, color: PAL.ink }}>
                                                {r.Contact || <span style={{ color: PAL.muted }}>—</span>}
                                            </td>
                                            <td style={{ padding: '14px', borderBottom: expanded ? 'none' : `1px solid ${PAL.rule}`, textAlign: 'right', fontFamily: MONO, fontSize: 13, color: PAL.ink }}>
                                                {fmtValue(r)}
                                            </td>
                                            <td style={{ padding: '14px', borderBottom: expanded ? 'none' : `1px solid ${PAL.rule}` }}>
                                                {r.Status ? (
                                                    <span style={{
                                                        padding: '3px 10px', borderRadius: 999,
                                                        background: st.bg, color: st.fg,
                                                        fontSize: 11.5, fontWeight: 500, fontFamily: SANS,
                                                        display: 'inline-block',
                                                    }}>{r.Status}</span>
                                                ) : <span style={{ color: PAL.muted, fontSize: 12 }}>—</span>}
                                            </td>
                                            <td style={{ padding: '14px', borderBottom: expanded ? 'none' : `1px solid ${PAL.rule}` }}>
                                                {deadlineChip(r.Deadline)}
                                            </td>
                                            <td style={{ padding: '14px', borderBottom: expanded ? 'none' : `1px solid ${PAL.rule}`, fontSize: 13, color: PAL.ink }}>
                                                {r.Owner || <span style={{ color: PAL.muted }}>—</span>}
                                            </td>
                                            <td style={{ padding: '14px', borderBottom: expanded ? 'none' : `1px solid ${PAL.rule}`, fontSize: 13, color: PAL.muted }}>
                                                {r.Region || '—'}
                                            </td>
                                            <td style={{ padding: '14px', borderBottom: expanded ? 'none' : `1px solid ${PAL.rule}` }} />
                                        </tr>

                                        {expanded && (
                                            <tr>
                                                <td colSpan={9} style={{
                                                    padding: '4px 22px 24px', background: PAL.wheat,
                                                    borderBottom: `1px solid ${PAL.rule}`,
                                                }}>
                                                    <div style={{
                                                        display: 'grid', gridTemplateColumns: '1.35fr 1fr', gap: 28,
                                                        paddingTop: 4,
                                                    }}>
                                                        {/* Left: Overview + Next action + Materials */}
                                                        <div>
                                                            <div style={{ fontSize: 10.5, textTransform: 'uppercase', letterSpacing: '0.14em', color: PAL.muted, fontWeight: 500, marginBottom: 8 }}>Overview</div>
                                                            <div style={{ fontFamily: SERIF, fontSize: 15, fontStyle: r.Notes ? 'normal' : 'italic', color: r.Notes ? PAL.ink : PAL.muted, lineHeight: 1.55, marginBottom: 20 }}>
                                                                {r.Notes || 'No notes yet. Click Edit to add context on this opportunity.'}
                                                            </div>

                                                            {r.NextAction && (
                                                                <>
                                                                    <div style={{ fontSize: 10.5, textTransform: 'uppercase', letterSpacing: '0.14em', color: PAL.muted, fontWeight: 500, marginBottom: 8 }}>Next action</div>
                                                                    <div style={{
                                                                        padding: '10px 14px', background: PAL.surface,
                                                                        border: `1px solid ${PAL.rule}`, borderLeft: `3px solid ${active.accent}`,
                                                                        fontSize: 13.5, color: PAL.ink, marginBottom: 20, borderRadius: 2,
                                                                    }}>{r.NextAction}</div>
                                                                </>
                                                            )}

                                                            {/* Submitted materials */}
                                                            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
                                                                <div style={{ fontSize: 10.5, textTransform: 'uppercase', letterSpacing: '0.14em', color: PAL.muted, fontWeight: 500 }}>
                                                                    Submitted materials <span style={{ color: PAL.rule, margin: '0 4px' }}>·</span>
                                                                    <span style={{ fontFamily: MONO, color: PAL.ink }}>{mats.length}</span>
                                                                </div>
                                                                {addingMaterialFor !== r.Id && (
                                                                    <button onClick={() => { setAddingMaterialFor(r.Id); setNewMat({ name: '', url: '', kind: 'Pitch deck', notes: '' }); }}
                                                                        style={{
                                                                            padding: '4px 10px', border: `1px solid ${PAL.rule}`, borderRadius: 4,
                                                                            background: PAL.surface, color: PAL.ink, cursor: 'pointer',
                                                                            fontSize: 11.5, fontWeight: 500, fontFamily: SANS,
                                                                        }}>+ Add material</button>
                                                                )}
                                                            </div>

                                                            {mats.length === 0 && addingMaterialFor !== r.Id && (
                                                                <div style={{ fontSize: 13, color: PAL.muted, fontStyle: 'italic', padding: '10px 0 4px' }}>
                                                                    Nothing submitted yet. Attach pitch decks, applications, or links you sent.
                                                                </div>
                                                            )}

                                                            {mats.map((m, i) => (
                                                                <div key={i} style={{
                                                                    display: 'flex', alignItems: 'center', gap: 12,
                                                                    padding: '10px 12px', background: PAL.surface,
                                                                    border: `1px solid ${PAL.rule}`, borderRadius: 3, marginBottom: 6,
                                                                }}>
                                                                    <div style={{
                                                                        width: 30, height: 30, borderRadius: 3,
                                                                        background: PAL.wheat, color: active.accent,
                                                                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                                                                        fontSize: 13, fontWeight: 600, fontFamily: SERIF, flexShrink: 0,
                                                                    }}>{(m.kind || 'F')[0]}</div>
                                                                    <div style={{ flex: 1, minWidth: 0 }}>
                                                                        <div style={{ fontSize: 13.5, color: PAL.ink, fontWeight: 500, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                                                            {m.url
                                                                                ? <a href={m.url} target="_blank" rel="noreferrer" style={{ color: PAL.ink, textDecoration: 'none', borderBottom: `1px solid ${PAL.rule}` }}>{m.name || m.url}</a>
                                                                                : (m.name || 'Untitled')}
                                                                        </div>
                                                                        <div style={{ fontSize: 11, color: PAL.muted, marginTop: 2 }}>
                                                                            {m.kind || 'File'}
                                                                            {m.addedAt && <> <span style={{ color: PAL.rule, margin: '0 4px' }}>·</span> {fmtDateShort(m.addedAt)}</>}
                                                                            {m.notes && <> <span style={{ color: PAL.rule, margin: '0 4px' }}>·</span> <span style={{ fontStyle: 'italic' }}>{m.notes}</span></>}
                                                                        </div>
                                                                    </div>
                                                                    <button onClick={() => removeMaterial(r, i)}
                                                                        style={{
                                                                            background: 'transparent', border: 'none',
                                                                            color: PAL.muted, cursor: 'pointer', fontSize: 15, padding: 4,
                                                                        }} title="Remove">×</button>
                                                                </div>
                                                            ))}

                                                            {addingMaterialFor === r.Id && (
                                                                <div style={{
                                                                    padding: '12px', background: PAL.surface,
                                                                    border: `1px solid ${PAL.rule}`, borderRadius: 3,
                                                                    marginTop: mats.length ? 4 : 0,
                                                                }}>
                                                                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, marginBottom: 8 }}>
                                                                        <input type="text" placeholder="Name (e.g. Series A Deck v3)"
                                                                            value={newMat.name} onChange={e => setNewMat({ ...newMat, name: e.target.value })}
                                                                            style={{ padding: '7px 10px', border: `1px solid ${PAL.rule}`, borderRadius: 3, fontSize: 12.5, fontFamily: SANS, background: PAL.bg, color: PAL.ink }} />
                                                                        <select value={newMat.kind} onChange={e => setNewMat({ ...newMat, kind: e.target.value })}
                                                                            style={{ padding: '7px 10px', border: `1px solid ${PAL.rule}`, borderRadius: 3, fontSize: 12.5, fontFamily: SANS, background: PAL.bg, color: PAL.ink }}>
                                                                            <option>Pitch deck</option>
                                                                            <option>Application</option>
                                                                            <option>Business plan</option>
                                                                            <option>Financials</option>
                                                                            <option>Video</option>
                                                                            <option>Letter of support</option>
                                                                            <option>Other</option>
                                                                        </select>
                                                                    </div>
                                                                    <input type="url" placeholder="Link (Drive / Docs / URL)"
                                                                        value={newMat.url} onChange={e => setNewMat({ ...newMat, url: e.target.value })}
                                                                        style={{ width: '100%', padding: '7px 10px', border: `1px solid ${PAL.rule}`, borderRadius: 3, fontSize: 12.5, fontFamily: SANS, background: PAL.bg, color: PAL.ink, marginBottom: 8, boxSizing: 'border-box' }} />
                                                                    <input type="text" placeholder="Notes (optional)"
                                                                        value={newMat.notes} onChange={e => setNewMat({ ...newMat, notes: e.target.value })}
                                                                        style={{ width: '100%', padding: '7px 10px', border: `1px solid ${PAL.rule}`, borderRadius: 3, fontSize: 12.5, fontFamily: SANS, background: PAL.bg, color: PAL.ink, marginBottom: 10, boxSizing: 'border-box' }} />
                                                                    <div style={{ display: 'flex', gap: 6, justifyContent: 'flex-end' }}>
                                                                        <button onClick={() => setAddingMaterialFor(null)}
                                                                            style={{ padding: '6px 12px', border: `1px solid ${PAL.rule}`, borderRadius: 3, background: 'transparent', color: PAL.muted, fontSize: 12, cursor: 'pointer', fontFamily: SANS }}>Cancel</button>
                                                                        <button onClick={() => addMaterial(r)}
                                                                            style={{ padding: '6px 14px', border: 'none', borderRadius: 3, background: active.accent, color: PAL.surface, fontSize: 12, fontWeight: 500, cursor: 'pointer', fontFamily: SANS }}>Save material</button>
                                                                    </div>
                                                                </div>
                                                            )}
                                                        </div>

                                                        {/* Right: Details KV + actions */}
                                                        <div>
                                                            <div style={{ fontSize: 10.5, textTransform: 'uppercase', letterSpacing: '0.14em', color: PAL.muted, fontWeight: 500, marginBottom: 8 }}>Details</div>
                                                            <table style={{ width: '100%', fontSize: 13, fontFamily: SANS, borderCollapse: 'collapse' }}>
                                                                <tbody>
                                                                    {[
                                                                        ['Organization', r.Contact],
                                                                        ['Stage', r.Stage],
                                                                        ['Value', fmtValue(r)],
                                                                        ['Deadline', fmtDate(r.Deadline)],
                                                                        ['Last contact', fmtDate(r.LastContact)],
                                                                        ['Region', r.Region],
                                                                        ['Owner', r.Owner],
                                                                    ].map(([k, v]) => (
                                                                        <tr key={k}>
                                                                            <td style={{ padding: '7px 0', color: PAL.muted, width: '38%', verticalAlign: 'top', borderBottom: `1px dashed ${PAL.rule}` }}>{k}</td>
                                                                            <td style={{ padding: '7px 0', color: PAL.ink, fontFamily: ['Value','Deadline','Last contact'].includes(k) ? MONO : SANS, borderBottom: `1px dashed ${PAL.rule}` }}>
                                                                                {v || <span style={{ color: PAL.muted }}>—</span>}
                                                                            </td>
                                                                        </tr>
                                                                    ))}
                                                                </tbody>
                                                            </table>

                                                            <div style={{ display: 'flex', gap: 8, marginTop: 18 }}>
                                                                <button onClick={() => { setEditing(r); setShowModal(true); }}
                                                                    style={{
                                                                        flex: 1, padding: '8px 12px', border: `1px solid ${PAL.ink}`,
                                                                        borderRadius: 3, background: PAL.ink, color: PAL.surface,
                                                                        fontSize: 12.5, fontWeight: 500, cursor: 'pointer', fontFamily: SANS,
                                                                    }}>Edit</button>
                                                                <button onClick={async () => {
                                                                    if (!window.confirm(`Delete "${r.Name}"?`)) return;
                                                                    if (onLocalRemove) onLocalRemove([r.Id]);
                                                                    setExpandedId(null);
                                                                    showToast('Deleted');
                                                                    try { await api.deleteInvestor(r.Id); }
                                                                    catch (e) { showToast('Delete failed: ' + e.message, 'error'); await api.getAllData(true); await onDataChange(); }
                                                                }}
                                                                    style={{
                                                                        padding: '8px 14px', border: `1px solid ${PAL.terra}`,
                                                                        borderRadius: 3, background: 'transparent', color: PAL.terra,
                                                                        fontSize: 12.5, fontWeight: 500, cursor: 'pointer', fontFamily: SANS,
                                                                    }}>Delete</button>
                                                            </div>
                                                        </div>
                                                    </div>
                                                </td>
                                            </tr>
                                        )}
                                    </React.Fragment>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
            )}

            {/* Modal (uses extended FundingModal) */}
            {showModal && (
                <FundingModal
                    entry={editing}
                    types={[active.type]}
                    onClose={() => { setShowModal(false); setEditing(null); }}
                    onSave={async (payload) => {
                        try {
                            setSaving(true);
                            let saved;
                            if (editing) saved = await api.updateInvestor(editing.Id, payload);
                            else saved = await api.createInvestor(payload);
                            if (onLocalUpsert && saved && saved.Id) onLocalUpsert(saved);
                            setShowModal(false); setEditing(null);
                            showToast(editing ? 'Updated' : 'Added');
                        } catch (e) {
                            showToast('Failed: ' + e.message, 'error');
                            await api.getAllData(true); await onDataChange();
                        } finally { setSaving(false); }
                    }}
                    saving={saving}
                />
            )}
        </div>
    );
}

// ─────────────────────────────────────────────────────────────────────────────
// FundingListView — simple list for non-equity funding (Grants, Accelerators,
// Competitions, Awards). Reuses the Investors table since these entities share
// a schema (Name, Amount, Stage, Status, Contact, LastContact, Notes, Deadline).
// Filters by the `types` prop; add/edit uses a slim modal.
// ─────────────────────────────────────────────────────────────────────────────
function FundingListView({ data, types, title, searchTerm, onDataChange, onLocalRemove, onLocalUpsert, showToast, setGlobalLoading }) {
    const INTER = "'Inter', system-ui, sans-serif";
    const HEADING = "'Space Grotesk', 'Inter', system-ui, sans-serif";
    const MONO = "'IBM Plex Mono', ui-monospace, monospace";
    const typeSet = new Set(types);
    const [showModal, setShowModal] = useState(false);
    const [editing, setEditing] = useState(null);
    const [saving, setSaving] = useState(false);
    const [typeFilter, setTypeFilter] = useState('all'); // 'all' | one of types

    const rows = (data || [])
        .filter(r => typeSet.has(r.Type))
        .filter(r => typeFilter === 'all' || r.Type === typeFilter)
        .filter(r => !searchTerm || (r.Name || '').toLowerCase().includes(searchTerm.toLowerCase()));

    const counts = types.reduce((acc, t) => {
        acc[t] = (data || []).filter(r => r.Type === t).length;
        return acc;
    }, {});

    const fmtDate = (d) => d ? new Date(d).toLocaleDateString() : '—';
    const fmtMoney = (n) => n ? '$' + Number(n).toLocaleString() : '—';

    const statusColor = (s) => {
        const map = {
            'In Progress':  { bg: '#EEF1FE', fg: '#1B31B8' },
            'Submitted':    { bg: '#DBEAFE', fg: '#1E40AF' },
            'Approved':     { bg: '#D1FAE5', fg: '#065F46' },
            'Won':          { bg: '#D1FAE5', fg: '#065F46' },
            'Finalist':     { bg: '#FEF3C7', fg: '#92400E' },
            'Rejected':     { bg: '#FEE2E2', fg: '#991B1B' },
            'Lost':         { bg: '#FEE2E2', fg: '#991B1B' },
        };
        return map[s] || { bg: '#F1F5F9', fg: '#475569' };
    };

    return (
        <div style={{ fontFamily: INTER, color: '#0E1622' }}>
            {/* Header */}
            <div style={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between', gap: 16, marginBottom: 20, flexWrap: 'wrap', paddingBottom: 18, borderBottom: '1px solid #E4E8EE' }}>
                <div>
                    <h1 style={{ fontSize: 24, margin: 0, fontWeight: 600, letterSpacing: '-0.02em', fontFamily: HEADING }}>{title}</h1>
                    <div style={{ color: '#6B7686', fontSize: 13, marginTop: 5 }}>
                        <b style={{ color: '#0E1622' }}>{rows.length}</b> of {(data || []).filter(r => typeSet.has(r.Type)).length} entries
                        {' '}·{' '}
                        {types.map((t, i) => (
                            <span key={t}>
                                {i > 0 && ' · '}<span style={{ color: '#3A4656', fontWeight: 500 }}>{counts[t] || 0} {t.toLowerCase()}{(counts[t] || 0) !== 1 ? 's' : ''}</span>
                            </span>
                        ))}
                    </div>
                </div>
                <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
                    <select value={typeFilter} onChange={e => setTypeFilter(e.target.value)}
                        style={{ padding: '8px 30px 8px 12px', border: '1px solid #E4E8EE', borderRadius: 8, background: '#fff', fontSize: 13, color: '#3A4656', cursor: 'pointer', fontWeight: 500, appearance: 'none',
                            backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' fill='none' stroke='%239AA4B2' stroke-width='1.6'%3E%3Cpath d='M3 5l3 3 3-3'/%3E%3C/svg%3E")`,
                            backgroundRepeat: 'no-repeat', backgroundPosition: 'right 10px center' }}>
                        <option value="all">All types</option>
                        {types.map(t => <option key={t} value={t}>{t}</option>)}
                    </select>
                    <button onClick={() => { setEditing(null); setShowModal(true); }}
                        style={{ padding: '8px 16px', border: 'none', borderRadius: 8, background: '#2743E0', color: '#fff', fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>
                        + Add {types[0]}
                    </button>
                </div>
            </div>

            {/* Table */}
            <div style={{ background: '#FFFFFF', border: '1px solid #E4E8EE', borderRadius: 12, overflow: 'hidden', boxShadow: '0 1px 2px rgba(16,26,40,.04), 0 8px 24px rgba(16,26,40,.05)' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13.5 }}>
                    <thead>
                        <tr>
                            {['Name', 'Type', 'Amount', 'Stage', 'Status', 'Contact', 'Last Contact', 'Deadline', ''].map(h => (
                                <th key={h} style={{ textAlign: 'left', fontSize: 10.5, textTransform: 'uppercase', letterSpacing: '0.07em', color: '#9AA4B2', fontWeight: 700, padding: '13px 14px', borderBottom: '1px solid #E4E8EE', background: '#FAFBFD', whiteSpace: 'nowrap' }}>{h}</th>
                            ))}
                        </tr>
                    </thead>
                    <tbody>
                        {rows.length === 0 && (
                            <tr>
                                <td colSpan={9} style={{ padding: '60px 20px', textAlign: 'center', color: '#6B7686' }}>
                                    <div style={{ fontSize: 36, marginBottom: 10, opacity: 0.4 }}>{types.includes('Grant') ? '🎯' : '🏆'}</div>
                                    <h3 style={{ fontSize: 15, marginBottom: 6, color: '#0E1622', fontFamily: HEADING }}>No entries yet</h3>
                                    <p style={{ margin: 0, fontSize: 13 }}>Click <strong style={{ color: '#1B31B8' }}>+ Add {types[0]}</strong> to add your first one.</p>
                                </td>
                            </tr>
                        )}
                        {rows.map((r, idx) => {
                            const st = statusColor(r.Status);
                            const cellBase = { padding: '14px 14px', borderBottom: idx === rows.length - 1 ? 'none' : '1px solid #EDF0F4', verticalAlign: 'middle' };
                            return (
                                <tr key={r.Id}>
                                    <td style={cellBase}>
                                        <strong style={{ fontSize: 14, color: '#0E1622', fontFamily: HEADING, fontWeight: 600 }}>{r.Name}</strong>
                                    </td>
                                    <td style={{ ...cellBase, color: '#3A4656', fontSize: 12.5 }}>{r.Type}</td>
                                    <td style={{ ...cellBase, fontFamily: MONO, fontSize: 12.5, color: '#0E1622' }}>{fmtMoney(r.Amount)}</td>
                                    <td style={{ ...cellBase, color: '#3A4656', fontSize: 12.5 }}>{r.Stage || '—'}</td>
                                    <td style={cellBase}>
                                        <span style={{ display: 'inline-block', padding: '3px 10px', borderRadius: 999, background: st.bg, color: st.fg, fontSize: 11.5, fontWeight: 600 }}>{r.Status || '—'}</span>
                                    </td>
                                    <td style={{ ...cellBase, color: '#3A4656', fontSize: 12.5 }}>{r.Contact || '—'}</td>
                                    <td style={{ ...cellBase, color: '#6B7686', fontSize: 12 }}>{fmtDate(r.LastContact)}</td>
                                    <td style={{ ...cellBase, color: '#6B7686', fontSize: 12 }}>{fmtDate(r.Deadline)}</td>
                                    <td style={cellBase}>
                                        <div style={{ display: 'flex', gap: 4 }}>
                                            <button onClick={() => { setEditing(r); setShowModal(true); }} title="Edit"
                                                style={{ background: 'none', border: 'none', color: '#2563eb', cursor: 'pointer', padding: 4 }}>
                                                <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/></svg>
                                            </button>
                                            <button onClick={async () => {
                                                if (!window.confirm(`Delete "${r.Name}"?`)) return;
                                                if (onLocalRemove) onLocalRemove([r.Id]);
                                                showToast('Deleted');
                                                try { await api.deleteInvestor(r.Id); }
                                                catch (e) { showToast('Delete failed: ' + e.message, 'error'); await api.getAllData(true); await onDataChange(); }
                                            }} title="Delete"
                                                style={{ background: 'none', border: 'none', color: '#dc2626', cursor: 'pointer', padding: 4 }}>
                                                <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="3 6 5 6 21 6"/><path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6"/><path d="M10 11v6M14 11v6"/></svg>
                                            </button>
                                        </div>
                                    </td>
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
            </div>

            {showModal && (
                <FundingModal
                    entry={editing}
                    types={types}
                    onClose={() => { setShowModal(false); setEditing(null); }}
                    onSave={async (payload) => {
                        try {
                            setSaving(true);
                            let saved;
                            if (editing) saved = await api.updateInvestor(editing.Id, payload);
                            else saved = await api.createInvestor(payload);
                            if (onLocalUpsert && saved && saved.Id) onLocalUpsert(saved);
                            setShowModal(false); setEditing(null);
                            showToast(editing ? 'Updated' : 'Added');
                        } catch (e) {
                            showToast('Failed: ' + e.message, 'error');
                            await api.getAllData(true); await onDataChange();
                        } finally { setSaving(false); }
                    }}
                    saving={saving}
                />
            )}
        </div>
    );
}

// Slim modal for Grants/Accelerators/Competitions/Awards.
function FundingModal({ entry, types, onClose, onSave, saving }) {
    const INTER = "'Inter', system-ui, sans-serif";
    const HEADING = "'Space Grotesk', 'Inter', system-ui, sans-serif";
    const toDate = (d) => d ? new Date(d).toISOString().split('T')[0] : '';

    const [form, setForm] = useState(entry ? {
        name: entry.Name || '', type: entry.Type || types[0], amount: entry.Amount || 0,
        stage: entry.Stage || '', status: entry.Status || 'In Progress',
        contact: entry.Contact || '', lastContact: toDate(entry.LastContact),
        deadline: toDate(entry.Deadline), notes: entry.Notes || '',
        owner: entry.Owner || '', region: entry.Region || '',
        valueDisplay: entry.ValueDisplay || '', nextAction: entry.NextAction || '',
    } : {
        name: '', type: types[0], amount: 0, stage: '', status: 'In Progress',
        contact: '', lastContact: new Date().toISOString().split('T')[0], deadline: '', notes: '',
        owner: '', region: '', valueDisplay: '', nextAction: '',
    });

    const upd = (k, v) => setForm(f => ({ ...f, [k]: v }));
    const inp = { width: '100%', padding: '8px 11px', border: '1px solid #E4E8EE', borderRadius: 7, fontSize: 13, fontFamily: INTER, background: '#fff', color: '#0E1622' };
    const lbl = { fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.07em', color: '#6B7686', fontWeight: 600, marginBottom: 5, display: 'block' };
    const field = (label, node) => <div style={{ marginBottom: 12 }}><label style={lbl}>{label}</label>{node}</div>;
    const row = (a, b) => <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>{a}{b}</div>;

    const isCompetition = types.includes('Competition');
    const stageOpts = isCompetition
        ? ['Application', 'Review', 'Finalist', 'Winner']
        : ['Application', 'Review', 'Approved', 'Rejected', 'Awarded'];

    return (
        <div onClick={onClose}
            style={{ position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 9999, padding: 20 }}>
            <div onClick={e => e.stopPropagation()}
                style={{ background: '#fff', borderRadius: 14, width: '100%', maxWidth: 600, maxHeight: '92vh', display: 'flex', flexDirection: 'column', boxShadow: '0 20px 60px rgba(15,23,42,0.35)', fontFamily: INTER }}>
                <div style={{ padding: '18px 22px', borderBottom: '1px solid #E4E8EE', background: '#FAFBFD', borderRadius: '14px 14px 0 0' }}>
                    <div style={{ fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.08em', color: '#6B7686', fontWeight: 700 }}>
                        {entry ? 'Edit' : 'Add'} {types.join(' / ')}
                    </div>
                    <div style={{ fontSize: 18, fontWeight: 600, fontFamily: HEADING, letterSpacing: '-0.01em', marginTop: 2 }}>
                        {form.name || <span style={{ color: '#9AA4B2' }}>(unnamed)</span>}
                    </div>
                </div>
                <form onSubmit={(e) => {
                    e.preventDefault();
                    onSave({
                        name: form.name, type: form.type, amount: parseFloat(form.amount) || 0,
                        stage: form.stage || null, status: form.status || null,
                        contact: form.contact || null, lastContact: form.lastContact || null,
                        deadline: form.deadline || null, notes: form.notes || null,
                        owner: form.owner || null, region: form.region || null,
                        valueDisplay: form.valueDisplay || null, nextAction: form.nextAction || null,
                    });
                }}
                style={{ flex: 1, overflowY: 'auto', padding: '18px 22px' }}>
                    {field('Name *', <input type="text" required value={form.name} onChange={e => upd('name', e.target.value)} style={inp} />)}
                    {row(
                        field('Type', <select value={form.type} onChange={e => upd('type', e.target.value)} style={inp}>{types.map(t => <option key={t}>{t}</option>)}</select>),
                        field('Organization', <input type="text" value={form.contact} onChange={e => upd('contact', e.target.value)} placeholder="e.g. NSF, Y Combinator" style={inp} />)
                    )}
                    {row(
                        field('Amount ($)', <input type="number" value={form.amount} onChange={e => upd('amount', e.target.value)} style={inp} />),
                        field('Value display', <input type="text" value={form.valueDisplay} onChange={e => upd('valueDisplay', e.target.value)} placeholder="e.g. $250K + mentorship" style={inp} />)
                    )}
                    {row(
                        field('Stage', <select value={form.stage} onChange={e => upd('stage', e.target.value)} style={inp}><option value="">—</option>{stageOpts.map(s => <option key={s}>{s}</option>)}</select>),
                        field('Status', <select value={form.status} onChange={e => upd('status', e.target.value)} style={inp}>
                            <option>In Progress</option>
                            <option>Submitted</option>
                            <option>Approved</option>
                            <option>Rejected</option>
                            <option>Won</option>
                            <option>Lost</option>
                            <option>Finalist</option>
                        </select>)
                    )}
                    {row(
                        field('Owner (our lead)', <input type="text" value={form.owner} onChange={e => upd('owner', e.target.value)} placeholder="Who on our team owns this" style={inp} />),
                        field('Region', <input type="text" value={form.region} onChange={e => upd('region', e.target.value)} placeholder="e.g. US, EU, India" style={inp} />)
                    )}
                    {row(
                        field('Last contact / date', <input type="date" value={form.lastContact} onChange={e => upd('lastContact', e.target.value)} style={inp} />),
                        field('Deadline', <input type="date" value={form.deadline} onChange={e => upd('deadline', e.target.value)} style={inp} />)
                    )}
                    {field('Next action', <input type="text" value={form.nextAction} onChange={e => upd('nextAction', e.target.value)} placeholder="What's the next thing to do here" style={inp} />)}
                    {field('Notes', <textarea value={form.notes} onChange={e => upd('notes', e.target.value)} rows={4} style={{ ...inp, resize: 'vertical' }} placeholder="Anything to remember about this one…" />)}
                </form>
                <div style={{ padding: '14px 22px', borderTop: '1px solid #E4E8EE', display: 'flex', justifyContent: 'flex-end', gap: 8, background: '#FAFBFD', borderRadius: '0 0 14px 14px' }}>
                    <button type="button" onClick={onClose} disabled={saving}
                        style={{ padding: '9px 16px', border: '1px solid #E4E8EE', borderRadius: 8, background: '#fff', color: '#3A4656', fontSize: 13, fontWeight: 500, cursor: saving ? 'default' : 'pointer' }}>
                        Cancel
                    </button>
                    <button type="submit" onClick={(e) => { e.preventDefault(); if (!form.name) return alert('Name is required'); onSave({
                        name: form.name, type: form.type, amount: parseFloat(form.amount) || 0,
                        stage: form.stage || null, status: form.status || null,
                        contact: form.contact || null, lastContact: form.lastContact || null,
                        deadline: form.deadline || null, notes: form.notes || null,
                        owner: form.owner || null, region: form.region || null,
                        valueDisplay: form.valueDisplay || null, nextAction: form.nextAction || null,
                    }); }} disabled={saving}
                        style={{ padding: '9px 20px', border: 'none', borderRadius: 8, background: '#2743E0', color: '#fff', fontSize: 13, fontWeight: 600, cursor: saving ? 'default' : 'pointer', opacity: saving ? 0.7 : 1 }}>
                        {saving ? 'Saving…' : (entry ? 'Save' : 'Add')}
                    </button>
                </div>
            </div>
        </div>
    );
}

// ─────────────────────────────────────────────────────────────────────────────
// DiligenceAccordions — 6 collapsible sections in the investor detail view.
// Fed by the JSON arrays imported from the PitchBook Excel sub-sheets:
//   Investments · Exits · Board seats · Team · Funds · News
// All arrays are already parsed to native JS by the parseRow() call in
// /api/investors — no more JSON.parse needed here.
// ─────────────────────────────────────────────────────────────────────────────
function DiligenceAccordions({ dilInvestments, dilExits, dilBoard, dilTeam, dilFunds, dilNews }) {
    const INTER = "'Inter', system-ui, sans-serif";
    const HEADING = "'Space Grotesk', 'Inter', system-ui, sans-serif";
    const MONO = "'IBM Plex Mono', ui-monospace, monospace";

    // Investments open by default (most useful); others collapsed
    const [open, setOpen] = useState({ inv: true, exits: false, board: false, team: false, funds: false, news: false });
    const toggle = (k) => setOpen(o => ({ ...o, [k]: !o[k] }));

    const rowStyle = { padding: '8px 8px', borderBottom: '1px solid #EDF0F4', color: '#3A4656' };
    const headCellStyle = {
        textAlign: 'left', color: '#9AA4B2', fontWeight: 700, fontSize: 10,
        textTransform: 'uppercase', letterSpacing: '0.06em', padding: '7px 8px',
        borderBottom: '1px solid #EDF0F4', fontFamily: INTER,
    };

    const tbl = (head, rows, opts = {}) => {
        if (!rows || rows.length === 0) {
            return <div style={{ fontSize: 12, color: '#9AA4B2', padding: '10px 4px' }}>No records on file.</div>;
        }
        const monoCols = opts.mono || [];
        return (
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12, fontFamily: INTER }}>
                <thead>
                    <tr>{head.map(h => <th key={h} style={headCellStyle}>{h}</th>)}</tr>
                </thead>
                <tbody>
                    {rows.map((r, i) => (
                        <tr key={i}>
                            {r.map((c, j) => (
                                <td key={j} style={{
                                    ...rowStyle,
                                    fontFamily: monoCols.includes(j) ? MONO : INTER,
                                    borderBottom: i === rows.length - 1 ? 'none' : '1px solid #EDF0F4',
                                }}>
                                    {c || <span style={{ color: '#9AA4B2' }}>—</span>}
                                </td>
                            ))}
                        </tr>
                    ))}
                </tbody>
            </table>
        );
    };

    // Format helpers
    const fmtDate = (d) => d ? new Date(d).toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' }) : '';
    const fmtYear = (d) => d ? String(new Date(d).getFullYear()) : '';

    // Prep each section's rows in the shape the table expects
    const investmentRows = dilInvestments.slice(0, 100).map(r => [
        r.company, fmtDate(r.date), r.type || r.stage, r.amount, r.industry,
    ]);
    const exitRows = dilExits.slice(0, 100).map(r => [
        r.company, fmtDate(r.date), r.type, r.status, r.buyers,
    ]);
    const boardRows = dilBoard.slice(0, 100).map(r => [
        r.member, r.company, r.status || '—', fmtDate(r.startDate), fmtDate(r.endDate),
    ]);
    const teamRows = dilTeam.slice(0, 100).map(r => [
        r.name, r.title, r.office, r.email
            ? <a href={`mailto:${r.email}`} style={{ color: '#1B31B8', textDecoration: 'none' }}>{r.email}</a>
            : null,
        r.status || 'current',
    ]);
    const fundRows = dilFunds.slice(0, 100).map(r => [
        r.fund, r.vintage, r.size, r.type,
    ]);
    const newsRows = dilNews.slice(0, 30).map(r => [
        r.title
            ? (r.link
                ? <a href={r.link} target="_blank" rel="noreferrer" style={{ color: '#1B31B8', textDecoration: 'none' }}>{r.title}</a>
                : r.title)
            : null,
        r.source, fmtDate(r.date),
    ]);

    const AccItem = ({ id, title, count, children }) => {
        const isOpen = open[id];
        return (
            <div style={{ borderBottom: '1px solid #EDF0F4' }}>
                <button type="button" onClick={() => toggle(id)}
                    style={{
                        width: '100%', display: 'flex', alignItems: 'center', gap: 10,
                        padding: '13px 20px', background: isOpen ? '#FAFBFD' : '#fff',
                        border: 'none', textAlign: 'left', fontSize: 13, fontWeight: 600,
                        color: '#0E1622', fontFamily: HEADING, cursor: 'pointer',
                    }}>
                    <svg width="10" height="10" viewBox="0 0 10 10" fill="none" stroke="#9AA4B2" strokeWidth="1.6"
                        style={{ transform: isOpen ? 'rotate(90deg)' : 'none', transition: 'transform .15s' }}>
                        <path d="M3 1.5l3.5 3.5-3.5 3.5" />
                    </svg>
                    {title}
                    <span style={{ marginLeft: 'auto', fontFamily: MONO, fontSize: 11, color: '#9AA4B2', fontWeight: 400 }}>{count}</span>
                </button>
                {isOpen && <div style={{ padding: '4px 20px 16px' }}>{children}</div>}
            </div>
        );
    };

    return (
        <div>
            <AccItem id="inv" title="Investments" count={dilInvestments.length}>
                {tbl(['Company', 'Date', 'Type', 'Amount', 'Industry'], investmentRows, { mono: [3] })}
                {dilInvestments.length > 100 && <div style={{ fontSize: 11, color: '#9AA4B2', padding: '8px 0', textAlign: 'center' }}>Showing first 100. Full list in the source Excel.</div>}
            </AccItem>
            <AccItem id="exits" title="Exits" count={dilExits.length}>
                {tbl(['Company', 'Date', 'Type', 'Status', 'Buyers'], exitRows)}
            </AccItem>
            <AccItem id="board" title="Board seats" count={dilBoard.length}>
                {tbl(['Member', 'Company', 'Status', 'Start', 'End'], boardRows)}
            </AccItem>
            <AccItem id="team" title="Team" count={dilTeam.length}>
                {tbl(['Name', 'Title', 'Office', 'Email', 'Status'], teamRows)}
            </AccItem>
            <AccItem id="funds" title="Funds" count={dilFunds.length}>
                {tbl(['Fund', 'Vintage', 'Size', 'Type'], fundRows, { mono: [2] })}
            </AccItem>
            <AccItem id="news" title="News" count={dilNews.length}>
                {tbl(['Title', 'Source', 'Date'], newsRows)}
            </AccItem>
        </div>
    );
}


// Sales People View Component
// Internal View Component - COMPLETE WITH ALL FIELDS
function InternalView({ data, deals, searchTerm, onDataChange, onLocalRemove, onLocalUpsert, showToast, setGlobalLoading }) {
    const [showModal, setShowModal] = useState(false);
    const [editingPerson, setEditingPerson] = useState(null);
    const [saving, setSaving] = useState(false);
    const [selectedIds, setSelectedIds] = useState(new Set());
    const [deleteMode, setDeleteMode] = useState(false);
    const fileInputRef = useRef(null);

    const toggleSelect = (id) => setSelectedIds(prev => { const n = new Set(prev); if (n.has(id)) n.delete(id); else n.add(id); return n; });
    const selectAllVisible = (ids) => setSelectedIds(prev => { const allSel = ids.every(id => prev.has(id)); const n = new Set(prev); if (allSel) ids.forEach(id => n.delete(id)); else ids.forEach(id => n.add(id)); return n; });
    const clearSelection = () => setSelectedIds(new Set());
    const bulkDelete = async () => {
        const ids = Array.from(selectedIds);
        if (ids.length === 0) return;
        if (!window.confirm(`Delete ${ids.length} selected team member${ids.length === 1 ? '' : 's'}?`)) return;
        if (onLocalRemove) onLocalRemove(ids);
        clearSelection();
        setDeleteMode(false);
        showToast(`${ids.length} team member${ids.length === 1 ? '' : 's'} deleted!`);
        try {
            for (const id of ids) await api.deleteInternalPerson(id);
        } catch (error) {
            showToast('Delete failed: ' + error.message + ' — restoring.', 'error');
            await api.getAllData(true);
            await onDataChange();
        }
    };

    const filteredPeople = data.filter(person =>
        person.Name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        (person.Email && person.Email.toLowerCase().includes(searchTerm.toLowerCase())) ||
        (person.Territory && person.Territory.toLowerCase().includes(searchTerm.toLowerCase())) ||
        (person.DealName && person.DealName.toLowerCase().includes(searchTerm.toLowerCase()))
    );

    // ✅ ADD SORTING
    const { items: sortedPeople, requestSort, sortConfig } = useSortableData(filteredPeople);

    const handleExport = async () => {
        const exportData = filteredPeople.map(person => ({
            'Name': person.Name,
            'Email': person.Email || '',
            'Phone': person.Phone || '',
            'Territory': person.Territory,
            'Comments': person.Comments || ''
        }));
        await exportToExcel(exportData, 'Internal_Team');
    };

    const handleImport = (e) => {
        const file = e.target.files[0];
        if (!file) return;

        importFromExcel(file, async (jsonData) => {
            try {
                // dedupe by Email (most unique) or fallback to Name
                const existingKeys = new Set((Array.isArray(data) ? data : []).map(p => ((p.Email || p.Name) || '').trim().toLowerCase()));
                let imported = 0;
                let skipped = 0;
                for (const row of jsonData) {
                    const personData = {
                        name: row['Name'] || row.name,
                        email: row['Email'] || row.email || '',
                        phone: row['Phone'] || row.phone || '',
                        territory: row['Territory'] || row.territory,
                        comments: row['Comments'] || row.comments || ''
                    };
                    if (!personData.name) continue;
                    const key = ((personData.email || personData.name) || '').trim().toLowerCase();
                    if (existingKeys.has(key)) { skipped++; continue; }
                    await api.createInternalPerson(personData);
                    existingKeys.add(key);
                    imported++;
                }
                if (imported > 0 && skipped > 0) showToast(`Imported ${imported}, skipped ${skipped} duplicate${skipped === 1 ? '' : 's'}`);
                else if (imported > 0) showToast(`Imported ${imported} team member${imported === 1 ? '' : 's'}!`);
                else if (skipped > 0) showToast(`No new rows — ${skipped} already exist`, 'error');
                else showToast('No rows found in file', 'error');
                await api.getAllData(true);
                await onDataChange();
            } catch (error) {
                alert('Import failed: ' + error.message);
            }
        });
        e.target.value = '';
    };

    const totalRevenue = filteredPeople.reduce((sum, p) => sum + Number(p.TotalRevenue || 0), 0);
    const totalDeals = filteredPeople.reduce((sum, p) => sum + Number(p.DealsCount || 0), 0);
    return (
        <div>
            <div className="stats-grid">
                <div className="stat-card">
                    <h3>Total Team Members</h3>
                    <p>{filteredPeople.length}</p>
                </div>
            </div>

            <div style={{ display: 'flex', gap: '12px', marginBottom: '20px', flexWrap: 'wrap' }}>
                <button className="add-btn" onClick={() => { setEditingPerson(null); setShowModal(true); }}>+ Add Team Member</button>
                <button className="add-btn" onClick={handleExport}>📤 Export to Excel</button>
                <button className="add-btn" onClick={() => fileInputRef.current.click()}>📥 Import from Excel</button>
                <input ref={fileInputRef} type="file" accept=".xlsx,.xls" style={{ display: 'none' }} onChange={handleImport} />
                {deleteMode ? (
                    <button className="add-btn" onClick={() => { setDeleteMode(false); clearSelection(); }} style={{ background: '#64748b' }}>✕ Cancel</button>
                ) : (
                    <button className="add-btn" onClick={() => setDeleteMode(true)} style={{ background: '#dc2626', display: 'inline-flex', alignItems: 'center', gap: 6 }} title="Select rows to delete">
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="3 6 5 6 21 6"/><path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6"/><path d="M10 11v6M14 11v6"/><path d="M9 6V4a2 2 0 0 1 2-2h2a2 2 0 0 1 2 2v2"/></svg>
                        Delete
                    </button>
                )}
            </div>

            <div className="table-container">
                <table>
                    <thead>
                        <tr>
                            {deleteMode && <th style={{ width: 32 }}><input type="checkbox" checked={sortedPeople.length > 0 && sortedPeople.every(p => selectedIds.has(p.Id))} onChange={() => selectAllVisible(sortedPeople.map(p => p.Id))} style={{ width: 16, height: 16, cursor: 'pointer', accentColor: '#1e293b' }} /></th>}
                            <SortableTableHeader label="Name" sortKey="Name" currentSort={sortConfig} onSort={requestSort} />
                            <SortableTableHeader label="Email" sortKey="Email" currentSort={sortConfig} onSort={requestSort} />
                            <SortableTableHeader label="Phone" sortKey="Phone" currentSort={sortConfig} onSort={requestSort} />
                            <SortableTableHeader label="Territory" sortKey="Territory" currentSort={sortConfig} onSort={requestSort} />
                            <th>Actions</th>
                        </tr>
                    </thead>
                    <tbody>
                        {sortedPeople.map(person => (
                            <tr key={person.Id} style={{ background: selectedIds.has(person.Id) ? '#eff6ff' : undefined }}>
                                {deleteMode && <td><input type="checkbox" checked={selectedIds.has(person.Id)} onChange={() => toggleSelect(person.Id)} style={{ width: 16, height: 16, cursor: 'pointer', accentColor: '#1e293b' }} /></td>}
                                <td><strong>{person.Name}</strong></td>
                                <td>{person.Email || 'N/A'}</td>
                                <td>{person.Phone || 'N/A'}</td>
                                <td>{person.Territory}</td>
                                <td style={{ whiteSpace: 'nowrap', minWidth: 70 }}>
                                    <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                                        <button title="Edit" onClick={() => { setEditingPerson(person); setShowModal(true); }} style={{ background: 'none', border: 'none', color: '#2563eb', cursor: 'pointer', padding: '4px', display: 'inline-flex', alignItems: 'center' }}>
                                            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/></svg>
                                        </button>
                                        <button title="Delete" onClick={async () => {
                                            if (!window.confirm('Delete this team member?')) return;
                                            if (onLocalRemove) onLocalRemove([person.Id]);
                                            showToast('Team member deleted!');
                                            try { await api.deleteInternalPerson(person.Id); }
                                            catch (error) { showToast('Failed to delete: ' + error.message + ' — restoring.', 'error'); await api.getAllData(true); await onDataChange(); }
                                        }} style={{ background: 'none', border: 'none', color: '#dc2626', cursor: 'pointer', padding: '4px', display: 'inline-flex', alignItems: 'center' }}>
                                            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="3 6 5 6 21 6"/><path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6"/><path d="M10 11v6M14 11v6"/><path d="M9 6V4a2 2 0 0 1 2-2h2a2 2 0 0 1 2 2v2"/></svg>
                                        </button>
                                    </div>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>

            {selectedIds.size > 0 && (
                <div style={{ position: 'fixed', bottom: 20, left: '50%', transform: 'translateX(-50%)', background: '#1e293b', color: '#ffffff', padding: '12px 20px', borderRadius: 10, boxShadow: '0 10px 30px rgba(0,0,0,0.25)', display: 'flex', alignItems: 'center', gap: 16, zIndex: 1000 }}>
                    <span style={{ fontSize: 14, fontWeight: 600 }}>{selectedIds.size} selected</span>
                    <button onClick={bulkDelete} style={{ background: '#dc2626', color: '#ffffff', border: 'none', padding: '8px 16px', borderRadius: 6, fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>Delete</button>
                    <button onClick={() => { clearSelection(); setDeleteMode(false); }} style={{ background: 'transparent', color: '#cbd5e1', border: 'none', cursor: 'pointer', fontSize: 13 }}>Cancel</button>
                </div>
            )}

            {showModal && (
                <InternalPersonModal
                    person={editingPerson}
                    onClose={() => { setShowModal(false); setEditingPerson(null); }}
                    onSave={async (personData) => {
                        try {
                            setSaving(true);
                            let saved;
                            if (editingPerson) {
                                saved = await api.updateInternalPerson(editingPerson.Id, personData);
                            } else {
                                // Duplicate check: same Email (or Name if no email)
                                const newKey = ((personData.email || personData.name) || '').trim().toLowerCase();
                                const exists = (data || []).some(p => ((p.Email || p.Name) || '').trim().toLowerCase() === newKey);
                                if (exists) {
                                    showToast('A team member with this email/name already exists. Please edit the existing one.', 'error');
                                    setSaving(false);
                                    return;
                                }
                                saved = await api.createInternalPerson(personData);
                            }
                            if (onLocalUpsert && saved && saved.Id) onLocalUpsert(saved);
                            setShowModal(false);
                            setEditingPerson(null);
                        } catch (error) {
                            await api.getAllData(true);
                            await onDataChange();
                            alert('Failed to save: ' + error.message);
                        } finally {
                            setSaving(false);
                        }
                    }}
                    saving={saving}
                />
            )}
        </div>
    );
}

// Internal Person Modal Component
function InternalPersonModal({ person, onClose, onSave, saving }) {
    const [formData, setFormData] = useState(person ? {
        name: person.Name,
        email: person.Email || '',
        phone: person.Phone || '',
        territory: person.Territory,
        comments: person.Comments || ''
    } : {
        name: '',
        email: '',
        phone: '',
        territory: '',
        comments: ''
    });

    const countries = [
        'United States', 'Canada', 'United Kingdom', 'Germany', 'France', 'Spain', 'Italy',
        'Netherlands', 'Belgium', 'Switzerland', 'Austria', 'Sweden', 'Norway', 'Denmark',
        'Finland', 'Poland', 'Czech Republic', 'Portugal', 'Ireland', 'Greece',
        'Australia', 'New Zealand', 'Japan', 'South Korea', 'Singapore', 'Hong Kong',
        'China', 'India', 'UAE', 'Saudi Arabia', 'Israel', 'South Africa', 'Brazil',
        'Mexico', 'Argentina', 'Chile', 'Colombia'
    ];

    const handleSubmit = (e) => {
        e.preventDefault();
        onSave({
            name: formData.name,
            email: formData.email,
            phone: formData.phone,
            territory: formData.territory,
            comments: formData.comments
        });
    };

    return (
        <div className="modal-overlay" onClick={onClose}>
            <div className="modal" onClick={(e) => e.stopPropagation()} style={{ maxHeight: '90vh', overflowY: 'auto' }}>
                <h2>{person ? 'Edit Team Member' : 'Add Team Member'}</h2>
                <form onSubmit={handleSubmit}>
                    <div className="form-group">
                        <label>Name *</label>
                        <input
                            type="text"
                            required
                            value={formData.name}
                            onChange={(e) => setFormData({...formData, name: e.target.value})}
                        />
                    </div>
                    
                    <div className="form-group">
                        <label>Email</label>
                        <input
                            type="email"
                            value={formData.email}
                            onChange={(e) => setFormData({...formData, email: e.target.value})}
                        />
                    </div>
                    
                    <div className="form-group">
                        <label>Phone</label>
                        <input
                            type="tel"
                            value={formData.phone}
                            onChange={(e) => setFormData({...formData, phone: e.target.value})}
                        />
                    </div>

                    <div className="form-group">
                        <label>Territory (Countries) *</label>
                        <input
                            type="text"
                            required
                            value={formData.territory}
                            onChange={(e) => setFormData({...formData, territory: e.target.value})}
                            placeholder="e.g., United States, Canada, United Kingdom"
                            list="countries-list"
                        />
                        <datalist id="countries-list">
                            {countries.map(country => (
                                <option key={country} value={country} />
                            ))}
                        </datalist>
                        <small style={{ color: '#6b7280', fontSize: '12px' }}>Enter multiple countries separated by commas</small>
                    </div>

                    <div className="form-group">
                        <label>Comments</label>
                        <textarea
                            value={formData.comments}
                            onChange={(e) => setFormData({...formData, comments: e.target.value})}
                            placeholder="Additional notes..."
                            rows="3"
                        />
                    </div>

                    <div style={{ padding: '12px', background: '#f0f9ff', borderRadius: '8px', marginBottom: '16px' }}>
                        <strong style={{ fontSize: '14px' }}>Note:</strong>
                        <p style={{ fontSize: '12px', marginTop: '4px', color: '#6b7280' }}>
                            Deals Count and Total Revenue are automatically calculated from the Deals tab based on this person's name.
                        </p>
                    </div>

                    <div className="modal-actions">
                        <button type="submit" className="btn btn-primary" disabled={saving}>
                            {saving ? 'Saving...' : 'Save'}
                        </button>
                        <button type="button" className="btn btn-secondary" onClick={onClose} disabled={saving}>
                            Cancel
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
}




// Contacts View Component
// External Contacts View Component - WITH ALL NEW FIELDS
function ExternalContactsView({ internal, searchTerm, showToast, setGlobalLoading }) {
    // ─────────────────────────────────────────────────────────────────────────
    // Server-side pagination via /api/externalcontacts?limit=&offset=&...
    // Rows are NOT shipped in bulkData anymore. This component owns its own
    // fetch lifecycle via TanStack Query. Every filter change updates the
    // `params` object → becomes the query key → auto-refetches page 1.
    // ─────────────────────────────────────────────────────────────────────────
    const queryClient = useQueryClient();
    const API_BASE = process.env.REACT_APP_API_URL || '/api';

    const [showModal, setShowModal] = useState(false);
    const [editingContact, setEditingContact] = useState(null);
    const [selectedContact, setSelectedContact] = useState(null);
    const [saving, setSaving] = useState(false);
    const [selectedIds, setSelectedIds] = useState(new Set());
    const [deleteMode, setDeleteMode] = useState(false);
    const fileInputRef = useRef(null);

    // Pagination + filter state
    const [page, setPage] = useState(0);      // zero-indexed
    const [pageSize, setPageSize] = useState(50);
    const [contactTypeFilter, setContactTypeFilter] = useState('all');
    const [regionFilter, setRegionFilter] = useState('all');
    const [sortField, setSortField] = useState('name');
    const [sortDir, setSortDir] = useState('asc');

    // Debounce the shared search term so typing doesn't fire a request per key.
    const [debouncedSearch, setDebouncedSearch] = useState(searchTerm || '');
    useEffect(() => {
        const t = setTimeout(() => setDebouncedSearch(searchTerm || ''), 300);
        return () => clearTimeout(t);
    }, [searchTerm]);

    // Reset to page 0 whenever a filter changes (so we don't ask for e.g. page 5
    // of a search that only has 2 pages).
    useEffect(() => { setPage(0); }, [debouncedSearch, contactTypeFilter, regionFilter, pageSize, sortField, sortDir]);

    const params = React.useMemo(() => ({
        limit: pageSize,
        offset: page * pageSize,
        search: debouncedSearch || undefined,
        contactType: contactTypeFilter !== 'all' ? contactTypeFilter : undefined,
        region: regionFilter !== 'all' ? regionFilter : undefined,
        sort: sortField,
        dir: sortDir,
    }), [pageSize, page, debouncedSearch, contactTypeFilter, regionFilter, sortField, sortDir]);

    const queryKey = ['externalContacts', params];

    const { data: pageData, isLoading, isFetching, refetch } = useQuery({
        queryKey,
        queryFn: async () => {
            const qs = new URLSearchParams();
            Object.entries(params).forEach(([k, v]) => {
                if (v !== undefined && v !== null && v !== '') qs.set(k, String(v));
            });
            const r = await fetch(`${API_BASE}/externalcontacts?${qs.toString()}`);
            if (!r.ok) throw new Error(`externalcontacts ${r.status}`);
            return r.json();
        },
        keepPreviousData: true,
        refetchOnWindowFocus: false,
    });

    // Rows for the current page (already filtered + sorted by server)
    const sortedContacts = pageData?.rows || [];
    const totalRows = pageData?.total ?? 0;
    const totalPages = Math.max(1, Math.ceil(totalRows / pageSize));

    // Stat cards come from server-side counts on the first-page response.
    // Once populated they persist across pagination clicks (keepPreviousData).
    const serverCounts = pageData?.counts || null;

    // Compat with the existing sortable-header UI (SortableTableHeader expects
    // a { key, direction } config + a requestSort callback).
    const sortConfig = React.useMemo(() => ({
        key: { name: 'Name', company: 'Company', lastContact: 'LastContact', createdAt: 'CreatedAt' }[sortField] || 'Name',
        direction: sortDir === 'desc' ? 'descending' : 'ascending',
    }), [sortField, sortDir]);
    const requestSort = React.useCallback((columnLabel) => {
        // SortableTableHeader passes the display key (e.g. 'Name') — reverse-map
        const rev = { Name: 'name', Company: 'company', LastContact: 'lastContact', CreatedAt: 'createdAt' };
        const nextField = rev[columnLabel] || sortField;
        if (nextField === sortField) {
            setSortDir(d => d === 'asc' ? 'desc' : 'asc');
        } else {
            setSortField(nextField);
            setSortDir('asc');
        }
    }, [sortField]);

    // Optimistic cache mutations — replace the old prop-based onLocalRemove /
    // onLocalUpsert. Keeps the UI instant, then the network reconciles.
    const cacheRemoveIds = React.useCallback((ids) => {
        const set = new Set(ids);
        queryClient.setQueriesData({ queryKey: ['externalContacts'] }, (old) => {
            if (!old?.rows) return old;
            return { ...old, rows: old.rows.filter(r => !set.has(r.Id)) };
        });
    }, [queryClient]);
    const cacheUpsertRow = React.useCallback((row) => {
        if (!row?.Id) return;
        queryClient.setQueriesData({ queryKey: ['externalContacts'] }, (old) => {
            if (!old?.rows) return old;
            let replaced = false;
            const rows = old.rows.map(r => { if (r.Id === row.Id) { replaced = true; return row; } return r; });
            if (!replaced) rows.unshift(row);
            return { ...old, rows };
        });
    }, [queryClient]);
    const invalidateAll = React.useCallback(() => {
        queryClient.invalidateQueries({ queryKey: ['externalContacts'] });
    }, [queryClient]);

    const toggleSelect = (id) => setSelectedIds(prev => { const n = new Set(prev); if (n.has(id)) n.delete(id); else n.add(id); return n; });
    const selectAllVisible = (ids) => setSelectedIds(prev => { const allSel = ids.every(id => prev.has(id)); const n = new Set(prev); if (allSel) ids.forEach(id => n.delete(id)); else ids.forEach(id => n.add(id)); return n; });
    const clearSelection = () => setSelectedIds(new Set());
    const bulkDelete = async () => {
        const ids = Array.from(selectedIds);
        if (ids.length === 0) return;
        if (!window.confirm(`Delete ${ids.length} selected contact${ids.length === 1 ? '' : 's'}?`)) return;
        cacheRemoveIds(ids);
        clearSelection();
        setDeleteMode(false);
        showToast(`${ids.length} contact${ids.length === 1 ? '' : 's'} deleted!`);
        try {
            for (const id of ids) await api.deleteExternalContact(id);
        } catch (error) {
            showToast('Delete failed: ' + error.message + ' — restoring.', 'error');
            invalidateAll();
        }
    };


    const handleExport = async () => {
        // We only have the current page in memory. Loop the server to fetch
        // every matching row across pages (bounded by totalRows so we can't
        // infinite-loop on a broken hasMore).
        try {
            setGlobalLoading(true);
            const all = [];
            const batchSize = 200;
            for (let off = 0; off < totalRows; off += batchSize) {
                const qs = new URLSearchParams();
                Object.entries({ ...params, limit: batchSize, offset: off }).forEach(([k, v]) => {
                    if (v !== undefined && v !== null && v !== '') qs.set(k, String(v));
                });
                const r = await fetch(`${API_BASE}/externalcontacts?${qs.toString()}`);
                if (!r.ok) throw new Error(`export ${r.status}`);
                const body = await r.json();
                all.push(...(body.rows || []));
            }
            const exportData = all.map(contact => ({
                'Name': contact.Name,
                'Company': contact.Company,
                'Email': contact.Email,
                'Phone': contact.Phone,
                'Position': contact.Position,
                'Contact Type': contact.ContactType || '',
                'Region': contact.Region || '',
                'Relationship Owner': contact.RelationshipOwner || '',
                'Last Contact': contact.LastContact ? new Date(contact.LastContact).toLocaleDateString() : ''
            }));
            await exportToExcel(exportData, 'External_Contacts');
            showToast(`Exported ${exportData.length} contact${exportData.length === 1 ? '' : 's'}`);
        } catch (e) {
            showToast('Export failed: ' + e.message, 'error');
        } finally {
            setGlobalLoading(false);
        }
    };



    const handleImport = (e) => {
        const file = e.target.files[0];
        if (!file) return;

        importFromExcel(file, async (jsonData) => {
            try {
                setGlobalLoading(true);
                let successCount = 0;
                let skipped = 0;
                let errorCount = 0;
                const errors = [];

                // Helper function to convert Excel date serial numbers
                const excelDateToJSDate = (serial) => {
                    if (typeof serial === 'number') {
                        const utc_days = Math.floor(serial - 25569);
                        const date_info = new Date(utc_days * 86400 * 1000);
                        return date_info.toISOString().split('T')[0];
                    }
                    if (serial instanceof Date) return serial.toISOString().split('T')[0];
                    if (typeof serial === 'string') {
                        const d = new Date(serial);
                        return isNaN(d) ? new Date().toISOString().split('T')[0] : d.toISOString().split('T')[0];
                    }
                    return new Date().toISOString().split('T')[0];
                };

                // Dedup within this import batch only (cross-batch dedup was
                // client-side against `data` which we no longer have — the server
                // handles cross-batch dupes via its UNIQUE constraint / conflict path).
                const existingKeys = new Set();

                for (let i = 0; i < jsonData.length; i++) {
                    const row = jsonData[i];
                    try {
                        const contactData = {
                            name: row['Name'] || row.name || '',
                            company: row['Company'] || row.company || '',
                            email: row['Email'] || row.email || '',
                            phone: String(row['Phone'] || row.phone || ''),
                            position: row['Position'] || row.position || '',
                            contactType: row['ContactType'] || row['Contact Type'] || row.contactType || '',
                            region: row['Region'] || row.region || '',
                            relationshipOwner: row['RelationshipOwner'] || row['Relationship Owner'] || row.relationshipOwner || '',
                            comments: row['Comments'] || row.comments || '',
                            lastContact: excelDateToJSDate(row['LastContact'] || row['Last Contact'] || row.lastContact || new Date())
                        };

                        if (!contactData.name || !contactData.company || !contactData.email ||
                            !contactData.phone || !contactData.position) {
                            errors.push(`Row ${i + 2}: Missing required fields`);
                            errorCount++;
                            continue;
                        }

                        const key = contactData.email.trim().toLowerCase();
                        if (existingKeys.has(key)) { skipped++; continue; }

                        await api.createExternalContact(contactData);
                        existingKeys.add(key);
                        successCount++;
                    } catch (error) {
                        errors.push(`Row ${i + 2}: ${error.message}`);
                        errorCount++;
                    }
                }

                setGlobalLoading(false);

                const parts = [];
                if (successCount > 0) parts.push(`Imported ${successCount}`);
                if (skipped > 0) parts.push(`skipped ${skipped} duplicate${skipped === 1 ? '' : 's'}`);
                if (errorCount > 0) parts.push(`${errorCount} failed`);
                if (parts.length > 0) showToast(parts.join(', '), errorCount > 0 ? 'warning' : 'success');
                else showToast('No rows imported', 'error');
                if (errorCount > 0) console.error('Import errors:', errors);

                invalidateAll();
            } catch (error) {
                setGlobalLoading(false);
                showToast('Import failed: ' + error.message, 'error');
            }
        });
        e.target.value = '';
    };

    const generateAISummary = (contact) => {
        const summaries = [
            `${contact.Name} is a key ${contact.ContactType || 'contact'} at ${contact.Company}. Strong relationship with high engagement. Last contacted on ${new Date(contact.LastContact).toLocaleDateString()}.`,
            `Strategic ${contact.ContactType || 'contact'} at ${contact.Company}. ${contact.Position} with decision-making authority. ${contact.RelationshipOwner ? `Managed by ${contact.RelationshipOwner}.` : 'Responsive and interested in collaboration.'}`,
            `Important ${contact.ContactType || 'contact'} for ${contact.Company} initiatives in ${contact.Region || 'the region'}. ${contact.Comments || 'Building strong relationship.'} Follow-up recommended to maintain relationship momentum.`,
            `${contact.Name} has been instrumental in discussions with ${contact.Company}. Regular communication maintained through ${contact.RelationshipOwner || 'the team'}. Strong potential for future opportunities.`
        ];
        return summaries[contact.Id % summaries.length];
    };

    // Stat cards from server counts (across all matching rows, not just this page)
    const customerCount = serverCounts?.customers ?? 0;
    const partnerCount = serverCounts?.partners ?? 0;
    const vendorCount = serverCounts?.vendors ?? 0;
    const totalCount = serverCounts?.all ?? totalRows;

    return (
        <div>
            <div className="stats-grid">
                <div className="stat-card">
                    <h3>Total Contacts</h3>
                    <p>{totalCount}</p>
                </div>
                <div className="stat-card">
                    <h3>Customers</h3>
                    <p>{customerCount}</p>
                </div>
                <div className="stat-card">
                    <h3>Partners</h3>
                    <p>{partnerCount}</p>
                </div>
                <div className="stat-card">
                    <h3>Vendors</h3>
                    <p>{vendorCount}</p>
                </div>
            </div>

            <div style={{ display: 'flex', gap: '12px', marginBottom: '20px', flexWrap: 'wrap' }}>
                <button className="add-btn" onClick={() => { setEditingContact(null); setShowModal(true); }}>+ Add External Contact</button>
                <button className="add-btn" onClick={handleExport}>📤 Export to Excel</button>
                <button className="add-btn" onClick={() => fileInputRef.current.click()}>📥 Import from Excel</button>
                <input ref={fileInputRef} type="file" accept=".xlsx,.xls" style={{ display: 'none' }} onChange={handleImport} />
                {deleteMode ? (
                    <button className="add-btn" onClick={() => { setDeleteMode(false); clearSelection(); }} style={{ background: '#64748b' }}>✕ Cancel</button>
                ) : (
                    <button className="add-btn" onClick={() => setDeleteMode(true)} style={{ background: '#dc2626', display: 'inline-flex', alignItems: 'center', gap: 6 }} title="Select rows to delete">
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="3 6 5 6 21 6"/><path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6"/><path d="M10 11v6M14 11v6"/><path d="M9 6V4a2 2 0 0 1 2-2h2a2 2 0 0 1 2 2v2"/></svg>
                        Delete
                    </button>
                )}
            </div>

            {/* Filter bar */}
            <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'center', marginBottom: 12, fontSize: 13 }}>
                <select value={contactTypeFilter} onChange={e => setContactTypeFilter(e.target.value)} style={{ padding: '7px 10px', border: '1px solid #cbd5e1', borderRadius: 6, background: '#fff' }}>
                    <option value="all">All types</option>
                    <option value="Customer">Customer</option>
                    <option value="Partner">Partner</option>
                    <option value="Vendor">Vendor</option>
                    <option value="Prospect">Prospect</option>
                </select>
                <select value={regionFilter} onChange={e => setRegionFilter(e.target.value)} style={{ padding: '7px 10px', border: '1px solid #cbd5e1', borderRadius: 6, background: '#fff' }}>
                    <option value="all">All regions</option>
                    <option value="Americas">Americas</option>
                    <option value="Asia Pacific">Asia Pacific</option>
                    <option value="Europe">Europe</option>
                    <option value="Middle East">Middle East</option>
                    <option value="Africa">Africa</option>
                    <option value="India">India</option>
                </select>
                <select value={pageSize} onChange={e => setPageSize(Number(e.target.value))} style={{ padding: '7px 10px', border: '1px solid #cbd5e1', borderRadius: 6, background: '#fff' }}>
                    <option value={25}>25 per page</option>
                    <option value={50}>50 per page</option>
                    <option value={100}>100 per page</option>
                </select>
                {(contactTypeFilter !== 'all' || regionFilter !== 'all') && (
                    <button onClick={() => { setContactTypeFilter('all'); setRegionFilter('all'); }}
                            style={{ background: 'transparent', border: 'none', color: '#2563eb', cursor: 'pointer', fontWeight: 500 }}>
                        Clear filters
                    </button>
                )}
                <div style={{ flex: 1 }} />
                {isFetching && !isLoading && <span style={{ color: '#64748b', fontSize: 12 }}>Loading…</span>}
                <span style={{ color: '#64748b', fontSize: 12 }}>
                    Showing {sortedContacts.length ? page * pageSize + 1 : 0}–{page * pageSize + sortedContacts.length} of {totalRows}
                </span>
            </div>

            <div className="table-container">
                <table>
                    <thead>
                        <tr>
                            {deleteMode && <th style={{ width: 32 }}><input type="checkbox" checked={sortedContacts.length > 0 && sortedContacts.every(c => selectedIds.has(c.Id))} onChange={() => selectAllVisible(sortedContacts.map(c => c.Id))} style={{ width: 16, height: 16, cursor: 'pointer', accentColor: '#1e293b' }} /></th>}
                            <SortableTableHeader label="Name" sortKey="Name" currentSort={sortConfig} onSort={requestSort} />
                            <SortableTableHeader label="Company" sortKey="Company" currentSort={sortConfig} onSort={requestSort} />
                            <SortableTableHeader label="Email" sortKey="Email" currentSort={sortConfig} onSort={requestSort} />
                            <SortableTableHeader label="Phone" sortKey="Phone" currentSort={sortConfig} onSort={requestSort} />
                            <SortableTableHeader label="Position" sortKey="Position" currentSort={sortConfig} onSort={requestSort} />
                            <SortableTableHeader label="Contact Type" sortKey="ContactType" currentSort={sortConfig} onSort={requestSort} />
                            <SortableTableHeader label="Region" sortKey="Region" currentSort={sortConfig} onSort={requestSort} />
                            <SortableTableHeader label="Relationship Owner" sortKey="RelationshipOwner" currentSort={sortConfig} onSort={requestSort} />
                            <SortableTableHeader label="Last Contact" sortKey="LastContact" currentSort={sortConfig} onSort={requestSort} />
                            <th>Actions</th>
                        </tr>
                    </thead>
                    <tbody>
                        {sortedContacts.map(contact => (
                            <React.Fragment key={contact.Id}>
                                <tr style={{ background: selectedIds.has(contact.Id) ? '#eff6ff' : undefined }}>
                                    {deleteMode && <td><input type="checkbox" checked={selectedIds.has(contact.Id)} onChange={() => toggleSelect(contact.Id)} style={{ width: 16, height: 16, cursor: 'pointer', accentColor: '#1e293b' }} /></td>}
                                    <td><strong>{contact.Name}</strong></td>
                                    <td>{contact.Company}</td>
                                    <td>{contact.Email}</td>
                                    <td>{contact.Phone}</td>
                                    <td>{contact.Position}</td>
                                    <td>
                                        {contact.ContactType ? (
                                            <span className={`status-badge ${
                                                contact.ContactType === 'Customer' ? 'status-closed-won' :
                                                contact.ContactType === 'Partner' ? 'status-proposal' :
                                                contact.ContactType === 'Vendor' ? 'status-negotiation' :
                                                'status-new'
                                            }`}>
                                                {contact.ContactType}
                                            </span>
                                        ) : 'N/A'}
                                    </td>
                                    <td>{contact.Region || 'N/A'}</td>
                                    <td>{contact.RelationshipOwner || 'N/A'}</td>
                                    <td>{new Date(contact.LastContact).toLocaleDateString()}</td>
                                    <td style={{ whiteSpace: 'nowrap', minWidth: 110 }}>
                                        <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                                            <button title="AI Summary" onClick={() => setSelectedContact(selectedContact === contact.Id ? null : contact.Id)} style={{ background: 'none', border: 'none', color: '#7c3aed', cursor: 'pointer', padding: '4px', display: 'inline-flex', alignItems: 'center', fontSize: 12, fontWeight: 600 }}>AI</button>
                                            <button title="Edit" onClick={() => { setEditingContact(contact); setShowModal(true); }} style={{ background: 'none', border: 'none', color: '#2563eb', cursor: 'pointer', padding: '4px', display: 'inline-flex', alignItems: 'center' }}>
                                                <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/></svg>
                                            </button>
                                            <button title="Delete" onClick={async () => {
                                                if (!window.confirm('Delete this contact?')) return;
                                                cacheRemoveIds([contact.Id]);
                                                showToast('Contact deleted!');
                                                try { await api.deleteExternalContact(contact.Id); }
                                                catch (error) { showToast('Failed to delete: ' + error.message + ' — restoring.', 'error'); invalidateAll(); }
                                            }} style={{ background: 'none', border: 'none', color: '#dc2626', cursor: 'pointer', padding: '4px', display: 'inline-flex', alignItems: 'center' }}>
                                                <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="3 6 5 6 21 6"/><path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6"/><path d="M10 11v6M14 11v6"/><path d="M9 6V4a2 2 0 0 1 2-2h2a2 2 0 0 1 2 2v2"/></svg>
                                            </button>
                                        </div>
                                    </td>
                                </tr>
                                {selectedContact === contact.Id && (
                                    <tr>
                                        <td colSpan="10" style={{background: '#f0f9ff', padding: '16px'}}>
                                            <strong>🤖 AI Summary:</strong>
                                            <p style={{marginTop: '8px', lineHeight: '1.6'}}>{generateAISummary(contact)}</p>
                                            <p style={{marginTop: '8px'}}><strong>Comments:</strong> {contact.Comments || 'No comments available.'}</p>
                                        </td>
                                    </tr>
                                )}
                            </React.Fragment>
                        ))}
                    </tbody>
                </table>
                {isLoading && (
                    <div style={{ padding: 40, textAlign: 'center', color: '#64748b', fontSize: 14 }}>Loading contacts…</div>
                )}
                {!isLoading && sortedContacts.length === 0 && (
                    <div style={{ padding: 40, textAlign: 'center', color: '#9ca3af', fontSize: 14 }}>No contacts match your filters.</div>
                )}
            </div>

            {/* Pagination footer */}
            {totalPages > 1 && (
                <div style={{ display: 'flex', gap: 6, alignItems: 'center', justifyContent: 'center', marginTop: 16, flexWrap: 'wrap', fontSize: 13 }}>
                    <button onClick={() => setPage(0)} disabled={page === 0}
                            style={{ padding: '6px 10px', border: '1px solid #cbd5e1', borderRadius: 6, background: '#fff', cursor: page === 0 ? 'default' : 'pointer', opacity: page === 0 ? 0.4 : 1 }}>« First</button>
                    <button onClick={() => setPage(p => Math.max(0, p - 1))} disabled={page === 0}
                            style={{ padding: '6px 10px', border: '1px solid #cbd5e1', borderRadius: 6, background: '#fff', cursor: page === 0 ? 'default' : 'pointer', opacity: page === 0 ? 0.4 : 1 }}>‹ Prev</button>
                    {(() => {
                        // Compact page buttons: current ±3, plus first/last with … ellipses
                        const shown = new Set([0, totalPages - 1]);
                        for (let i = Math.max(0, page - 3); i <= Math.min(totalPages - 1, page + 3); i++) shown.add(i);
                        const arr = Array.from(shown).sort((a, b) => a - b);
                        const out = [];
                        let prev = -1;
                        for (const i of arr) {
                            if (i > prev + 1) out.push(<span key={`gap-${i}`} style={{ color: '#94a3b8', padding: '0 4px' }}>…</span>);
                            out.push(
                                <button key={i} onClick={() => setPage(i)}
                                        style={{ padding: '6px 10px', border: '1px solid ' + (i === page ? '#2563eb' : '#cbd5e1'), borderRadius: 6, background: i === page ? '#2563eb' : '#fff', color: i === page ? '#fff' : '#0c2340', fontWeight: i === page ? 700 : 500, cursor: 'pointer', minWidth: 34 }}>
                                    {i + 1}
                                </button>
                            );
                            prev = i;
                        }
                        return out;
                    })()}
                    <button onClick={() => setPage(p => Math.min(totalPages - 1, p + 1))} disabled={page >= totalPages - 1}
                            style={{ padding: '6px 10px', border: '1px solid #cbd5e1', borderRadius: 6, background: '#fff', cursor: page >= totalPages - 1 ? 'default' : 'pointer', opacity: page >= totalPages - 1 ? 0.4 : 1 }}>Next ›</button>
                    <button onClick={() => setPage(totalPages - 1)} disabled={page >= totalPages - 1}
                            style={{ padding: '6px 10px', border: '1px solid #cbd5e1', borderRadius: 6, background: '#fff', cursor: page >= totalPages - 1 ? 'default' : 'pointer', opacity: page >= totalPages - 1 ? 0.4 : 1 }}>Last »</button>
                </div>
            )}

            {selectedIds.size > 0 && (
                <div style={{ position: 'fixed', bottom: 20, left: '50%', transform: 'translateX(-50%)', background: '#1e293b', color: '#ffffff', padding: '12px 20px', borderRadius: 10, boxShadow: '0 10px 30px rgba(0,0,0,0.25)', display: 'flex', alignItems: 'center', gap: 16, zIndex: 1000 }}>
                    <span style={{ fontSize: 14, fontWeight: 600 }}>{selectedIds.size} selected</span>
                    <button onClick={bulkDelete} style={{ background: '#dc2626', color: '#ffffff', border: 'none', padding: '8px 16px', borderRadius: 6, fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>Delete</button>
                    <button onClick={() => { clearSelection(); setDeleteMode(false); }} style={{ background: 'transparent', color: '#cbd5e1', border: 'none', cursor: 'pointer', fontSize: 13 }}>Cancel</button>
                </div>
            )}

            {showModal && (
                <ExternalContactModal
                    contact={editingContact}
                    internal={internal}
                    onClose={() => { setShowModal(false); setEditingContact(null); }}
                    onSave={async (contactData) => {
                        try {
                            setSaving(true);
                            let saved;
                            if (editingContact) {
                                saved = await api.updateExternalContact(editingContact.Id, contactData);
                            } else {
                                // Cross-table dedup is no longer client-side (we don't
                                // have the full list). Server should enforce unique
                                // email; if it doesn't, that's future work.
                                saved = await api.createExternalContact(contactData);
                            }
                            if (saved && saved.Id) cacheUpsertRow(saved);
                            setShowModal(false);
                            setEditingContact(null);
                        } catch (error) {
                            invalidateAll();
                            alert('Failed to save: ' + error.message);
                        } finally {
                            setSaving(false);
                        }
                    }}
                    saving={saving}
                />
            )}
        </div>
    );
}

// External Contact Modal Component
function ExternalContactModal({ contact, internal, onClose, onSave, saving }) {
    const [formData, setFormData] = useState(contact ? {
        name: contact.Name,
        company: contact.Company,
        email: contact.Email,
        phone: contact.Phone,
        position: contact.Position,
        contactType: contact.ContactType || '',
        region: contact.Region || '',
        relationshipOwner: contact.RelationshipOwner || '',
        comments: contact.Comments || '',
        lastContact: new Date(contact.LastContact).toISOString().split('T')[0]
    } : {
        name: '',
        company: '',
        email: '',
        phone: '',
        position: '',
        contactType: '',
        region: '',
        relationshipOwner: '',
        comments: '',
        lastContact: new Date().toISOString().split('T')[0]
    });

    const regions = [
        'North America',
        'South America',
        'Europe',
        'Middle East',
        'Africa',
        'Asia Pacific',
        'Australia/Oceania'
    ];

    const contactTypes = [
        'Customer',
        'Partner',
        'Vendor',
        'Prospect',
        'Consultant',
        'Investor',
        'Other'
    ];

    const handleSubmit = (e) => {
        e.preventDefault();
        onSave(formData);
    };

    return (
        <div className="modal-overlay" onClick={onClose}>
            <div className="modal" onClick={(e) => e.stopPropagation()} style={{ maxHeight: '90vh', overflowY: 'auto' }}>
                <h2>{contact ? 'Edit External Contact' : 'Add External Contact'}</h2>
                <form onSubmit={handleSubmit}>
                    <div className="form-group">
                        <label>Name *</label>
                        <input
                            type="text"
                            required
                            value={formData.name}
                            onChange={(e) => setFormData({...formData, name: e.target.value})}
                        />
                    </div>
                    
                    <div className="form-group">
                        <label>Company *</label>
                        <input
                            type="text"
                            required
                            value={formData.company}
                            onChange={(e) => setFormData({...formData, company: e.target.value})}
                        />
                    </div>
                    
                    <div className="form-group">
                        <label>Email *</label>
                        <input
                            type="email"
                            required
                            value={formData.email}
                            onChange={(e) => setFormData({...formData, email: e.target.value})}
                        />
                    </div>
                    
                    <div className="form-group">
                        <label>Phone *</label>
                        <input
                            type="tel"
                            required
                            value={formData.phone}
                            onChange={(e) => setFormData({...formData, phone: e.target.value})}
                        />
                    </div>
                    
                    <div className="form-group">
                        <label>Position *</label>
                        <input
                            type="text"
                            required
                            value={formData.position}
                            onChange={(e) => setFormData({...formData, position: e.target.value})}
                            placeholder="e.g., VP of Sales, CTO, Director"
                        />
                    </div>

                    <div className="form-group">
                        <label>Contact Type</label>
                        <select
                            value={formData.contactType}
                            onChange={(e) => setFormData({...formData, contactType: e.target.value})}
                        >
                            <option value="">Select Type</option>
                            {contactTypes.map(type => (
                                <option key={type} value={type}>{type}</option>
                            ))}
                        </select>
                    </div>

                    <div className="form-group">
                        <label>Region</label>
                        <select
                            value={formData.region}
                            onChange={(e) => setFormData({...formData, region: e.target.value})}
                        >
                            <option value="">Select Region</option>
                            {regions.map(region => (
                                <option key={region} value={region}>{region}</option>
                            ))}
                        </select>
                    </div>

                    <div className="form-group">
                        <label>Relationship Owner</label>
                        <select
                            value={formData.relationshipOwner}
                            onChange={(e) => setFormData({...formData, relationshipOwner: e.target.value})}
                        >
                            <option value="">Select Owner</option>
                            {internal && internal.map(person => (
                                <option key={person.Id} value={person.Name}>{person.Name}</option>
                            ))}
                        </select>
                        <small style={{ color: '#6b7280', fontSize: '12px' }}>Select from Internal team</small>
                    </div>

                    <div className="form-group">
                        <label>Last Contact Date *</label>
                        <input
                            type="date"
                            required
                            value={formData.lastContact}
                            onChange={(e) => setFormData({...formData, lastContact: e.target.value})}
                        />
                    </div>

                    <div className="form-group">
                        <label>Comments</label>
                        <textarea
                            value={formData.comments}
                            onChange={(e) => setFormData({...formData, comments: e.target.value})}
                            placeholder="Additional notes about this contact..."
                            rows="4"
                        />
                    </div>

                    <div className="modal-actions">
                        <button type="submit" className="btn btn-primary" disabled={saving}>
                            {saving ? 'Saving...' : 'Save'}
                        </button>
                        <button type="button" className="btn btn-secondary" onClick={onClose} disabled={saving}>
                            Cancel
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
}

// Network Graph Component - FIXED: No Overlap, Hover Info, Edge Labels
// Network Graph Component - FIXED: Dragging, Persistence, Mouse Alignment
// Network Graph Component - COMPLETE & COMPREHENSIVE
function NetworkGraph({ data }) {
    const canvasRef = useRef(null);
    const [zoom, setZoom] = useState(1);
    const [pan, setPan] = useState({ x: 0, y: 0 });
    const [isDragging, setIsDragging] = useState(false);
    const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
    const [selectedNode, setSelectedNode] = useState(null);
    const [hoveredNode, setHoveredNode] = useState(null);
    const [nodes, setNodes] = useState([]);
    const [connections, setConnections] = useState([]);
    const [isDraggingNode, setIsDraggingNode] = useState(false);
    const [draggedNode, setDraggedNode] = useState(null);
    const animationFrameId = useRef(null);

    useEffect(() => {
        initializeNodes();
    }, [data]);

    useEffect(() => {
        drawGraph();
    }, [nodes, zoom, pan, selectedNode, hoveredNode, connections]);

    useEffect(() => {
        // Start physics simulation
        const animate = () => {
            if (!isDraggingNode) {
                simulatePhysics();
            }
            animationFrameId.current = requestAnimationFrame(animate);
        };
        animate();

        return () => {
            if (animationFrameId.current) {
                cancelAnimationFrame(animationFrameId.current);
            }
        };
    }, [isDraggingNode]);

    // ✅ SAVE/LOAD POSITIONS
    const savePositions = (updatedNodes) => {
        try {
            const positions = {};
            updatedNodes.forEach(node => {
                if (node.id !== 'company' && node.userMoved) {
                    positions[node.id] = { x: node.x, y: node.y };
                }
            });
            localStorage.setItem('networkGraphPositions', JSON.stringify(positions));
        } catch (error) {
            console.error('Save error:', error);
        }
    };

    const loadPositions = () => {
        try {
            const saved = localStorage.getItem('networkGraphPositions');
            return saved ? JSON.parse(saved) : {};
        } catch (error) {
            return {};
        }
    };

    const initializeNodes = () => {
        const canvas = canvasRef.current;
        if (!canvas) return;

        const width = canvas.width;
        const height = canvas.height;
        const centerX = width / 2;
        const centerY = height / 2;

        const newNodes = [];
        const newConnections = [];
        const savedPositions = loadPositions();

        // Center node
        newNodes.push({
            id: 'company',
            label: 'ContexQ',
            x: centerX,
            y: centerY,
            vx: 0,
            vy: 0,
            type: 'company',
            radius: 35,
            color: '#667eea',
            fixed: true,
            userMoved: false,
            data: { name: 'ContexQ', description: 'Graph Intelligence CRM Platform', type: 'Company' }
        });

        // ✅ CLUSTER ANGLES - INCLUDING ACTIVITIES
        const clusterAngles = {
            deal: 0,
            internal: Math.PI / 4,
            external: Math.PI / 2,
            activity: (3 * Math.PI) / 4,  // NEW
            investor: Math.PI,
            partnership: (5 * Math.PI) / 4,
        };

        const clusterRadius = 380; // Increased for more space

        const addClusteredNodes = (items, type, color, clusterKey, radiusSize = 18) => {
            if (!items || items.length === 0) return;

            const angle = clusterAngles[clusterKey];
            const clusterX = centerX + Math.cos(angle) * clusterRadius;
            const clusterY = centerY + Math.sin(angle) * clusterRadius;

            items.forEach((item, i) => {
                const nodeId = `${type}-${item.Id}`;
                const savedPos = savedPositions[nodeId];
                
                // Calculate spread position
                const itemsCount = items.length;
                const spreadAngle = angle + (i - itemsCount / 2) * (0.6 / Math.max(itemsCount, 5));
                const spreadRadius = clusterRadius + (Math.random() - 0.5) * 120;

                const node = {
                    id: nodeId,
                    label: item.Title || item.Task || item.Company || item.Name || item.Partner || 'Unknown',
                    x: savedPos ? savedPos.x : centerX + Math.cos(spreadAngle) * spreadRadius + (Math.random() - 0.5) * 60,
                    y: savedPos ? savedPos.y : centerY + Math.sin(spreadAngle) * spreadRadius + (Math.random() - 0.5) * 60,
                    vx: 0,
                    vy: 0,
                    targetX: clusterX,
                    targetY: clusterY,
                    type: type,
                    radius: radiusSize,
                    color: color,
                    data: item,
                    userMoved: !!savedPos,
                    fixed: false
                };
                newNodes.push(node);

                // Primary connection
                newConnections.push({
                    from: 'company',
                    to: node.id,
                    type: 'primary',
                    color: '#e5e7eb',
                    label: ''
                });
            });
        };

        // ✅ ADD ALL NODES INCLUDING ACTIVITIES
        addClusteredNodes(data.deals || [], 'deal', '#10b981', 'deal', 22);
        addClusteredNodes(data.internal || [], 'internal', '#ec4899', 'internal', 20);
        addClusteredNodes(data.externalContacts || [], 'external', '#06b6d4', 'external', 18);
        
        // ✅ COMBINE ACTIVITIES
        const allActivities = (Array.isArray(data) ? data : []).map(activity => {
            const assignedDate = activity.AssignedDate || activity.Date || activity.DueDate;
            const daysSinceAssigned = assignedDate 
                ? Math.floor((new Date() - new Date(assignedDate)) / (1000 * 60 * 60 * 24))
                : 0;
            
            return {
                ...activity,
                DaysSinceAssigned: daysSinceAssigned
            };
        });
        // 🔍 ADD THIS DEBUG LINE
        console.log('🔍 Activities data sample:', {
            count: allActivities.length,
            sample: allActivities[0],
            categories: [...new Set(allActivities.map(a => a.Category || a.Type))]
        });

        addClusteredNodes(allActivities, 'activity', '#f97316', 'activity', 19);
        
        addClusteredNodes(data.investors || [], 'investor', '#f59e0b', 'investor', 20);
        addClusteredNodes(data.partnerships || [], 'partnership', '#8b5cf6', 'partnership', 20);

        // ✅ FIND ALL 2ND-TIER CONNECTIONS
        const parseOwners = (field) => {
            if (!field) return [];
            try {
                const parsed = typeof field === 'string' ? JSON.parse(field) : field;
                return Array.isArray(parsed) ? parsed : [parsed];
            } catch {
                return typeof field === 'string' ? field.split(',').map(s => s.trim()).filter(Boolean) : [];
            }
        };

        // Internal → Deals
        (data.deals || []).forEach(deal => {
            const owners = parseOwners(deal.CustomerContacts);
            owners.forEach(ownerName => {
                const person = (data.internal || []).find(p => p.Name === ownerName);
                if (person) {
                    newConnections.push({
                        from: `internal-${person.Id}`,
                        to: `deal-${deal.Id}`,
                        type: 'secondary',
                        color: '#ec489955',
                        label: 'Owns'
                    });
                }
            });
        });

        // External → Deals
        (data.externalContacts || []).forEach(contact => {
            const relatedDeals = (data.deals || []).filter(d => 
                d.Company === contact.Company || d.Contact === contact.Name
            );
            relatedDeals.forEach(deal => {
                newConnections.push({
                    from: `external-${contact.Id}`,
                    to: `deal-${deal.Id}`,
                    type: 'secondary',
                    color: '#06b6d455',
                    label: 'Works at'
                });
            });
        });

        // Internal → Partnerships
        (data.partnerships || []).forEach(partnership => {
            const owners = parseOwners(partnership.RelationshipOwner);
            owners.forEach(ownerName => {
                const person = (data.internal || []).find(p => p.Name === ownerName);
                if (person) {
                    newConnections.push({
                        from: `internal-${person.Id}`,
                        to: `partnership-${partnership.Id}`,
                        type: 'secondary',
                        color: '#8b5cf655',
                        label: 'Manages'
                    });
                }
            });
        });

        // External → Partnerships
        (data.partnerships || []).forEach(partnership => {
            const relatedContacts = (data.externalContacts || []).filter(c => 
                c.Company === partnership.Partner
            );
            relatedContacts.forEach(contact => {
                newConnections.push({
                    from: `external-${contact.Id}`,
                    to: `partnership-${partnership.Id}`,
                    type: 'secondary',
                    color: '#06b6d444',
                    label: 'Contact'
                });
            });
        });

        // ✅ ACTIVITIES CONNECTIONS
        allActivities.forEach(activity => {
            const activityId = `activity-${activity.Id}`;
            
            // Activity → Internal (Task Owners)
            const taskOwners = parseOwners(activity.TaskOwner);
            taskOwners.forEach(ownerName => {
                const person = (data.internal || []).find(p => p.Name === ownerName);
                if (person) {
                    newConnections.push({
                        from: activityId,
                        to: `internal-${person.Id}`,
                        type: 'secondary',
                        color: '#f9731655',
                        label: 'Assigned to'
                    });
                }
            });

            // Activity → Deal (Related To)
            if (activity.RelatedTo) {
                const relatedDeal = (data.deals || []).find(d => 
                    d.Title === activity.RelatedTo || d.Company === activity.RelatedTo
                );
                if (relatedDeal) {
                    newConnections.push({
                        from: activityId,
                        to: `deal-${relatedDeal.Id}`,
                        type: 'secondary',
                        color: '#f9731655',
                        label: 'Related to'
                    });
                }
            }
        });

        setNodes(newNodes);
        setConnections(newConnections);
    };

    const simulatePhysics = () => {
        setNodes(prevNodes => {
            return prevNodes.map(node => {
                // ✅ SKIP PHYSICS FOR USER-MOVED NODES
                if (node.fixed || node.userMoved || node.id === draggedNode) {
                    return node;
                }

                let fx = 0;
                let fy = 0;

                // Weak attraction to cluster
                if (node.targetX && node.targetY) {
                    const dx = node.targetX - node.x;
                    const dy = node.targetY - node.y;
                    fx += dx * 0.005; // Very weak
                    fy += dy * 0.005;
                }

                // Strong repulsion (anti-overlap)
                prevNodes.forEach(other => {
                    if (other.id === node.id) return;
                    const dx = node.x - other.x;
                    const dy = node.y - other.y;
                    const dist = Math.sqrt(dx * dx + dy * dy);
                    const minDist = node.radius + other.radius + 50; // INCREASED

                    if (dist < minDist && dist > 0) {
                        const force = ((minDist - dist) / dist) * 8; // STRONG FORCE
                        fx += (dx / dist) * force;
                        fy += (dy / dist) * force;
                    }
                });

                // Weak same-type attraction
                const siblings = prevNodes.filter(n => n.type === node.type && n.id !== node.id);
                siblings.forEach(sibling => {
                    const dx = sibling.x - node.x;
                    const dy = sibling.y - node.y;
                    const dist = Math.sqrt(dx * dx + dy * dy);
                    if (dist > 60 && dist < 180) {
                        fx += (dx / dist) * 0.003;
                        fy += (dy / dist) * 0.003;
                    }
                });

                const damping = 0.88;
                const newVx = (node.vx + fx) * damping;
                const newVy = (node.vy + fy) * damping;

                return {
                    ...node,
                    vx: newVx,
                    vy: newVy,
                    x: node.x + newVx,
                    y: node.y + newVy
                };
            });
        });
    };

    const drawGraph = () => {
        const canvas = canvasRef.current;
        if (!canvas) return;

        const ctx = canvas.getContext('2d');
        ctx.clearRect(0, 0, canvas.width, canvas.height);
        ctx.save();
        ctx.translate(pan.x, pan.y);
        ctx.scale(zoom, zoom);

        // Draw connections
        connections.forEach(conn => {
            const fromNode = nodes.find(n => n.id === conn.from);
            const toNode = nodes.find(n => n.id === conn.to);
            if (!fromNode || !toNode) return;

            ctx.beginPath();
            ctx.moveTo(fromNode.x, fromNode.y);
            ctx.lineTo(toNode.x, toNode.y);
            ctx.strokeStyle = conn.color;
            ctx.lineWidth = conn.type === 'primary' ? 2 : 1.5;
            ctx.setLineDash(conn.type === 'secondary' ? [5, 5] : []);
            ctx.stroke();

            // Edge labels
            if (conn.type === 'secondary' && conn.label && zoom > 0.6) {
                ctx.setLineDash([]);
                const midX = (fromNode.x + toNode.x) / 2;
                const midY = (fromNode.y + toNode.y) / 2;
                
                ctx.fillStyle = 'rgba(255, 255, 255, 0.95)';
                ctx.font = '10px Inter';
                const metrics = ctx.measureText(conn.label);
                ctx.fillRect(midX - metrics.width / 2 - 3, midY - 7, metrics.width + 6, 14);
                
                ctx.fillStyle = '#374151';
                ctx.textAlign = 'center';
                ctx.textBaseline = 'middle';
                ctx.fillText(conn.label, midX, midY);
            }
        });

        ctx.setLineDash([]);

        // Draw nodes
        nodes.forEach(node => {
            const isHighlighted = hoveredNode === node.id || selectedNode === node.id;
            
            if (isHighlighted) {
                ctx.shadowColor = node.color;
                ctx.shadowBlur = 20;
            }

            ctx.beginPath();
            ctx.arc(node.x, node.y, node.radius, 0, 2 * Math.PI);
            ctx.fillStyle = node.color;
            ctx.fill();
            ctx.strokeStyle = isHighlighted ? '#000' : '#fff';
            ctx.lineWidth = isHighlighted ? 4 : 2;
            ctx.stroke();

            ctx.shadowBlur = 0;

            // Label
            ctx.fillStyle = '#1f2937';
            ctx.font = node.type === 'company' ? 'bold 16px Inter' : '12px Inter';
            ctx.textAlign = 'center';
            ctx.textBaseline = 'top';
            
            // Truncate long labels
            let displayLabel = node.label;
            if (displayLabel.length > 15) {
                displayLabel = displayLabel.substring(0, 15) + '...';
            }
            
            ctx.fillText(displayLabel, node.x, node.y + node.radius + 6);
        });

        ctx.restore();
    };

    // ✅ IMPROVED HIT DETECTION - LARGER DETECTION AREA
    const getNodeAtPosition = (canvasX, canvasY) => {
        const graphX = (canvasX - pan.x) / zoom;
        const graphY = (canvasY - pan.y) / zoom;

        // Find the closest node within detection range
        let closestNode = null;
        let closestDist = Infinity;

        nodes.forEach(node => {
            const dx = graphX - node.x;
            const dy = graphY - node.y;
            const dist = Math.sqrt(dx * dx + dy * dy);
            
            // ✅ LARGER DETECTION RADIUS (1.5x the node size)
            const detectionRadius = node.radius * 1.5;
            
            if (dist <= detectionRadius && dist < closestDist) {
                closestNode = node;
                closestDist = dist;
            }
        });

        return closestNode;
    };

    const handleMouseDown = (e) => {
        const rect = canvasRef.current.getBoundingClientRect();
        const canvasX = e.clientX - rect.left;
        const canvasY = e.clientY - rect.top;

        const node = getNodeAtPosition(canvasX, canvasY);

        if (node) {
            setSelectedNode(node.id);
            setIsDraggingNode(true);
            setDraggedNode(node.id);
        } else {
            setIsDragging(true);
            setDragStart({ x: e.clientX - pan.x, y: e.clientY - pan.y });
        }
    };

    const handleMouseMove = (e) => {
        const rect = canvasRef.current.getBoundingClientRect();
        const canvasX = e.clientX - rect.left;
        const canvasY = e.clientY - rect.top;

        const node = getNodeAtPosition(canvasX, canvasY);
        setHoveredNode(node ? node.id : null);

        if (isDraggingNode && draggedNode) {
            const graphX = (canvasX - pan.x) / zoom;
            const graphY = (canvasY - pan.y) / zoom;

            setNodes(prevNodes =>
                prevNodes.map(n =>
                    n.id === draggedNode 
                        ? { ...n, x: graphX, y: graphY, vx: 0, vy: 0, userMoved: true }
                        : n
                )
            );
        } else if (isDragging) {
            setPan({
                x: e.clientX - dragStart.x,
                y: e.clientY - dragStart.y
            });
        }
    };

    const handleMouseUp = () => {
        if (isDraggingNode && draggedNode) {
            setNodes(prevNodes => {
                const updated = prevNodes.map(n =>
                    n.id === draggedNode ? { ...n, userMoved: true } : n
                );
                savePositions(updated); // ✅ SAVE ON RELEASE
                return updated;
            });
        }

        setIsDragging(false);
        setIsDraggingNode(false);
        setDraggedNode(null);
    };

    const handleWheel = (e) => {
        e.preventDefault();
        const delta = e.deltaY > 0 ? 0.9 : 1.1;
        setZoom(prev => Math.min(Math.max(prev * delta, 0.3), 3));
    };

    const handleReset = () => {
        localStorage.removeItem('networkGraphPositions');
        setZoom(1);
        setPan({ x: 0, y: 0 });
        setSelectedNode(null);
        setHoveredNode(null);
        initializeNodes();
    };

    const selectedNodeData = selectedNode ? nodes.find(n => n.id === selectedNode) : null;
    const hoveredNodeData = hoveredNode ? nodes.find(n => n.id === hoveredNode) : null;

    // ✅ COMPREHENSIVE NODE INFO RENDERER
    const renderNodeInfo = (nodeData) => {
        if (!nodeData || !nodeData.data) return null;

        const { data: info, type } = nodeData;

        switch(type) {
            case 'company':
                return (
                    <>
                        <p><strong>Name:</strong> {info.name}</p>
                        <p><strong>Type:</strong> {info.type}</p>
                        <p>{info.description}</p>
                    </>
                );

            case 'deal':
                return (
                    <>
                        <p><strong>Company:</strong> {info.Company}</p>
                        <p><strong>Value:</strong> ${Number(info.Value || 0).toLocaleString()}</p>
                        <p><strong>Stage:</strong> <span className={`status-badge status-${info.Stage?.toLowerCase().replace(' ', '-')}`}>{info.Stage}</span></p>
                        <p><strong>Probability:</strong> {info.Probability}%</p>
                        <p><strong>Close Date:</strong> {new Date(info.CloseDate).toLocaleDateString()}</p>
                    </>
                );

            case 'internal':
                return (
                    <>
                        <p><strong>Email:</strong> {info.Email || 'N/A'}</p>
                        <p><strong>Phone:</strong> {info.Phone || 'N/A'}</p>
                        <p><strong>Territory:</strong> {info.Territory}</p>
                        <p><strong>Deals:</strong> {info.DealsCount || 0}</p>
                        <p><strong>Revenue:</strong> ${Number(info.TotalRevenue || 0).toLocaleString()}</p>
                    </>
                );

            case 'external':
                return (
                    <>
                        <p><strong>Company:</strong> {info.Company}</p>
                        <p><strong>Position:</strong> {info.Position}</p>
                        <p><strong>Email:</strong> {info.Email}</p>
                        <p><strong>Type:</strong> {info.ContactType || 'N/A'}</p>
                        <p><strong>Region:</strong> {info.Region || 'N/A'}</p>
                    </>
                );

            case 'activity':
                return (
                    <>
                        <p><strong>Type:</strong> {info.Type || info.Category || 'Task'}</p>
                        <p><strong>Date:</strong> {new Date(info.Date || info.DueDate).toLocaleDateString()}</p>
                        <p><strong>Status:</strong> <span className={`status-badge status-${info.Status?.toLowerCase().replace(' ', '-')}`}>{info.Status}</span></p>
                        {info.Priority && <p><strong>Priority:</strong> <span className={`priority-badge priority-${info.Priority?.toLowerCase()}`}>{info.Priority}</span></p>}
                        <p><strong>Task Owner:</strong> {info.TaskOwner || 'N/A'}</p>
                        {info.RelatedTo && <p><strong>Related To:</strong> {info.RelatedTo}</p>}
                    </>
                );

            case 'investor':
                return (
                    <>
                        <p><strong>Type:</strong> <span className={`status-badge status-${info.Type?.toLowerCase()}`}>{info.Type}</span></p>
                        <p><strong>Amount:</strong> ${Number(info.Amount || 0).toLocaleString()}</p>
                        <p><strong>Stage:</strong> {info.Stage}</p>
                        <p><strong>Status:</strong> <span className={`status-badge status-${info.Status?.toLowerCase().replace(' ', '-')}`}>{info.Status}</span></p>
                        <p><strong>Contact:</strong> {info.Contact}</p>
                    </>
                );

            case 'partnership':
                return (
                    <>
                        <p><strong>Type:</strong> {info.PartnerType}</p>
                        <p><strong>Status:</strong> <span className={`status-badge status-${info.Status?.toLowerCase()}`}>{info.Status}</span></p>
                        <p><strong>Region:</strong> {info.Region || 'N/A'}</p>
                        <p><strong>Value:</strong> ${Number(info.Value || 0).toLocaleString()}</p>
                        <p><strong>Contact:</strong> {info.Contact}</p>
                    </>
                );

            default:
                return <p>No details available</p>;
        }
    };

    return (
        <div>
            <h2 style={{ marginBottom: '16px' }}>🌐 Network Relationship Graph</h2>
            <p style={{ marginBottom: '16px', color: '#6b7280' }}>
                Comprehensive visualization of all CRM relationships. 
                <strong> Hover nodes for quick info, click for details, drag to reposition.</strong> 
                Your custom layout is saved automatically.
            </p>

            <div className="graph-container" style={{ position: 'relative' }}>
                <div className="graph-controls">
                    <button className="graph-btn" onClick={() => setZoom(z => Math.min(z * 1.2, 3))}>🔍+ In</button>
                    <button className="graph-btn" onClick={() => setZoom(z => Math.max(z * 0.8, 0.3))}>🔍- Out</button>
                    <button className="graph-btn" onClick={handleReset}>↻ Reset</button>
                </div>

                <canvas
                    ref={canvasRef}
                    width={1200}
                    height={600}
                    onMouseDown={handleMouseDown}
                    onMouseMove={handleMouseMove}
                    onMouseUp={handleMouseUp}
                    onMouseLeave={() => { handleMouseUp(); setHoveredNode(null); }}
                    onWheel={handleWheel}
                    style={{ 
                        cursor: isDragging ? 'grabbing' : 
                               isDraggingNode ? 'move' : 
                               hoveredNode ? 'pointer' : 'grab',
                        borderRadius: '12px'
                    }}
                />

                {/* Hover Tooltip */}
                {hoveredNodeData && !selectedNode && (
                    <div style={{
                        position: 'absolute',
                        top: '10px',
                        left: '10px',
                        background: 'rgba(0, 0, 0, 0.92)',
                        color: 'white',
                        padding: '14px 18px',
                        borderRadius: '10px',
                        fontSize: '13px',
                        maxWidth: '260px',
                        pointerEvents: 'none',
                        zIndex: 50,
                        boxShadow: '0 8px 24px rgba(0,0,0,0.3)'
                    }}>
                        <div style={{ fontWeight: '700', marginBottom: '6px', color: hoveredNodeData.color, fontSize: '15px' }}>
                            {hoveredNodeData.label}
                        </div>
                        <div style={{ fontSize: '11px', textTransform: 'uppercase', opacity: 0.8, marginBottom: '8px', letterSpacing: '0.5px' }}>
                            {hoveredNodeData.type}
                        </div>
                        <div style={{ fontSize: '12px', opacity: 0.95 }}>
                            Click for full details • Drag to move
                        </div>
                    </div>
                )}

                {/* Full Info Panel */}
                {selectedNodeData && (
                    <div style={{
                        position: 'absolute',
                        top: '20px',
                        right: '20px',
                        background: 'white',
                        padding: '26px',
                        borderRadius: '18px',
                        boxShadow: '0 12px 48px rgba(0,0,0,0.25)',
                        maxWidth: '340px',
                        zIndex: 100,
                        border: `3px solid ${selectedNodeData.color}`
                    }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', marginBottom: '14px' }}>
                            <h3 style={{ margin: 0, color: selectedNodeData.color, fontSize: '21px', fontWeight: '700' }}>
                                {selectedNodeData.label}
                            </h3>
                            <button 
                                onClick={() => setSelectedNode(null)}
                                style={{
                                    background: 'none',
                                    border: 'none',
                                    fontSize: '30px',
                                    cursor: 'pointer',
                                    padding: '0',
                                    color: '#9ca3af',
                                    lineHeight: '0.8'
                                }}
                            >
                                ×
                            </button>
                        </div>

                        <div style={{ 
                            fontSize: '11px', 
                            fontWeight: '700', 
                            textTransform: 'uppercase', 
                            color: '#6b7280',
                            letterSpacing: '1px',
                            marginBottom: '18px'
                        }}>
                            {selectedNodeData.type}
                        </div>

                        <div style={{ fontSize: '14px', lineHeight: '1.9' }}>
                            {renderNodeInfo(selectedNodeData)}
                        </div>

                        <div style={{ marginTop: '22px', paddingTop: '18px', borderTop: '2px solid #e5e7eb' }}>
                            <div style={{ fontSize: '12px', color: '#6b7280', fontWeight: '700', marginBottom: '8px' }}>
                                CONNECTIONS
                            </div>
                            <div style={{ fontSize: '28px', fontWeight: '800', color: selectedNodeData.color }}>
                                {connections.filter(c => c.from === selectedNodeData.id || c.to === selectedNodeData.id).length}
                            </div>
                        </div>
                    </div>
                )}
            </div>

            {/* Legend */}
            <div style={{ marginTop: '22px', display: 'flex', gap: '22px', flexWrap: 'wrap', alignItems: 'center' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <div style={{ width: '22px', height: '22px', borderRadius: '50%', background: '#667eea' }}></div>
                    <span style={{ fontSize: '14px', fontWeight: '600' }}>Company</span>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <div style={{ width: '22px', height: '22px', borderRadius: '50%', background: '#ec4899' }}></div>
                    <span style={{ fontSize: '14px', fontWeight: '600' }}>Internal Team</span>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <div style={{ width: '22px', height: '22px', borderRadius: '50%', background: '#10b981' }}></div>
                    <span style={{ fontSize: '14px', fontWeight: '600' }}>Deals</span>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <div style={{ width: '22px', height: '22px', borderRadius: '50%', background: '#f97316' }}></div>
                    <span style={{ fontSize: '14px', fontWeight: '600' }}>Activities & Tasks</span>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <div style={{ width: '22px', height: '22px', borderRadius: '50%', background: '#f59e0b' }}></div>
                    <span style={{ fontSize: '14px', fontWeight: '600' }}>Investors/Grants</span>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <div style={{ width: '22px', height: '22px', borderRadius: '50%', background: '#8b5cf6' }}></div>
                    <span style={{ fontSize: '14px', fontWeight: '600' }}>Partnerships</span>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <div style={{ width: '22px', height: '22px', borderRadius: '50%', background: '#06b6d4' }}></div>
                    <span style={{ fontSize: '14px', fontWeight: '600' }}>External Contacts</span>
                </div>
            </div>

            <div style={{ marginTop: '14px', fontSize: '13px', color: '#6b7280' }}>
                <strong>—</strong> Primary | <strong>- - -</strong> 2nd-tier | 
                <strong> Positions persist across sessions</strong>
            </div>
        </div>
    );
}
// OLD AI Assistant Component - REPLACED BY imported AIAssistant from ./AIAssistant.js
function AIAssistant_OLD({ data }) {
    const [messages, setMessages] = useState([
        {
            role: 'assistant',
            content: 'Hello! I\'m your ContexQ CRM AI Assistant. I can help you analyze deals, track activities, and provide insights about your team, contacts, and opportunities. What would you like to know?'
        }
    ]);
    const [input, setInput] = useState('');
    const [isTyping, setIsTyping] = useState(false);
    const messagesEndRef = useRef(null);

    const scrollToBottom = () => {
        messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    };

    useEffect(() => {
        scrollToBottom();
    }, [messages]);

    const generateAIResponse = (userMessage) => {
        const lowerMessage = userMessage.toLowerCase();

        if (lowerMessage.includes('deal') || lowerMessage.includes('pipeline')) {
            const totalValue = data.deals.reduce((sum, d) => sum + Number(d.Value), 0);
            const avgProbability = data.deals.length > 0 
                ? data.deals.reduce((sum, d) => sum + Number(d.Probability), 0) / data.deals.length 
                : 0;
            const topDeal = data.deals.length > 0 
                ? data.deals.reduce((max, d) => Number(d.Probability) > Number(max.Probability) ? d : max)
                : null;
            return `You have ${data.deals.length} active deals with a total value of $${totalValue.toLocaleString()}. The average probability across all deals is ${avgProbability.toFixed(1)}%. ${topDeal ? `Your strongest deal is "${topDeal.Title}" with ${topDeal.Probability}% probability.` : ''}`;
        }

        if (lowerMessage.includes('activity') || lowerMessage.includes('activities') || lowerMessage.includes('task')) {
            const pending = data.activities.filter(a => a.Status === 'Pending').length;
            const overdue = data.activities.filter(a => {
                const dueDate = new Date(a.Date || a.DueDate);
                return dueDate < new Date() && a.Status !== 'Completed';
            }).length;
            return `You have ${data.activities.length} total activities. ${pending} are pending and ${overdue} are overdue. Stay on top of your tasks to maintain momentum!`;
        }

        if (lowerMessage.includes('investor') || lowerMessage.includes('funding') || lowerMessage.includes('grant') || lowerMessage.includes('competition')) {
            const totalFunding = data.investors.reduce((sum, i) => sum + Number(i.Amount), 0);
            const investorCount = data.investors.filter(i => i.Type === 'Investor').length;
            const grantCount = data.investors.filter(i => i.Type === 'Grant').length;
            const competitionCount = data.investors.filter(i => i.Type === 'Competition').length;
            const inProgress = data.investors.filter(i => i.Status === 'In Progress').length;
            return `You're pursuing $${totalFunding.toLocaleString()} in funding across ${data.investors.length} sources: ${investorCount} investors, ${grantCount} grants, and ${competitionCount} competitions. ${inProgress} are currently in progress.`;
        }

        if (lowerMessage.includes('partner') || lowerMessage.includes('partnership')) {
            const active = data.partnerships.filter(p => p.Status === 'Active').length;
            const totalValue = data.partnerships.reduce((sum, p) => sum + Number(p.Value), 0);
            return `You have ${data.partnerships.length} partnerships with a combined value of $${totalValue.toLocaleString()}. ${active} partnerships are currently active.`;
        }

        if (lowerMessage.includes('team') || lowerMessage.includes('internal')) {
            const totalARR = data.internal.reduce((sum, p) => sum + Number(p.ARR || 0), 0);
            const totalRevenue = data.internal.reduce((sum, p) => sum + Number(p.TotalRevenue || 0), 0);
            const territories = [...new Set(data.internal.map(p => p.Territory))].length;
            return `Your internal team has ${data.internal.length} members covering ${territories} territories. Total ARR: $${totalARR.toLocaleString()}. Total revenue from deals: $${totalRevenue.toLocaleString()}.`;
        }

        if (lowerMessage.includes('contact') || lowerMessage.includes('external')) {
            const customerCount = data.externalContacts.filter(c => c.ContactType === 'Customer').length;
            const partnerCount = data.externalContacts.filter(c => c.ContactType === 'Partner').length;
            return `You have ${data.externalContacts.length} external contacts: ${customerCount} customers, ${partnerCount} partners, and more. Regular engagement with key contacts is essential for relationship growth.`;
        }

        if (lowerMessage.includes('summary') || lowerMessage.includes('overview') || lowerMessage.includes('status')) {
            const dealsValue = data.deals.reduce((s, d) => s + Number(d.Value), 0);
            const fundingValue = data.investors.reduce((s, i) => s + Number(i.Amount), 0);
            return `Here's your CRM overview: ${data.deals.length} active deals worth $${dealsValue.toLocaleString()}, ${data.activities.length} activities, ${data.investors.length} funding opportunities worth $${fundingValue.toLocaleString()}, ${data.partnerships.length} partnerships, ${data.internal.length} internal team members, and ${data.externalContacts.length} external contacts. Your pipeline looks strong!`;
        }

        if (lowerMessage.includes('recommend') || lowerMessage.includes('suggest') || lowerMessage.includes('advice')) {
            return `Based on your data, I recommend: 1) Follow up on high-probability deals (>70%) to close them faster. 2) Review overdue activities and reassign if needed. 3) Engage with external contacts who haven't been contacted recently. 4) Focus on active partnerships and nurture investor relationships. 5) Ensure your internal team is aligned on territory coverage.`;
        }

        return `I can help you with information about deals, activities, investors/grants/competitions, partnerships, your internal team, and external contacts. Try asking me about your pipeline status, funding opportunities, or team performance!`;
    };

    const handleSend = () => {
        if (!input.trim()) return;

        const userMessage = {
            role: 'user',
            content: input
        };

        setMessages(prev => [...prev, userMessage]);
        setInput('');
        setIsTyping(true);

        setTimeout(() => {
            const aiResponse = {
                role: 'assistant',
                content: generateAIResponse(input)
            };
            setMessages(prev => [...prev, aiResponse]);
            setIsTyping(false);
        }, 1000);
    };

    const handleKeyPress = (e) => {
        if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            handleSend();
        }
    };

    return (
        <div className="ai-assistant">
            <h2 style={{ marginBottom: '16px' }}>🤖 AI Assistant</h2>
            <p style={{ marginBottom: '16px', color: '#6b7280' }}>
                Ask me anything about your CRM data, deals, activities, team, or get recommendations.
            </p>

            <div className="chat-messages">
                {messages.map((message, index) => (
                    <div
                        key={index}
                        className={`message ${message.role}`}
                    >
                        {message.content}
                    </div>
                ))}
                {isTyping && (
                    <div className="message assistant">
                        <em>AI is typing...</em>
                    </div>
                )}
                <div ref={messagesEndRef} />
            </div>

            <div className="chat-input-container">
                <input
                    type="text"
                    className="chat-input"
                    placeholder="Ask me anything about your CRM..."
                    value={input}
                    onChange={(e) => setInput(e.target.value)}
                    onKeyPress={handleKeyPress}
                />
                <button className="send-btn" onClick={handleSend}>
                    Send
                </button>
            </div>

            <div style={{ marginTop: '16px', padding: '12px', background: '#f0f9ff', borderRadius: '8px' }}>
                <strong>💡 Try asking:</strong>
                <ul style={{ marginTop: '8px', marginLeft: '20px', color: '#6b7280' }}>
                    <li>"What's my pipeline status?"</li>
                    <li>"Show me my funding opportunities"</li>
                    <li>"How is my internal team performing?"</li>
                    <li>"Tell me about my external contacts"</li>
                    <li>"Give me a complete summary"</li>
                    <li>"What are my recommendations?"</li>
                </ul>
            </div>
        </div>
    );
}

// ===== Admin Users Panel =====
const TAB_CATALOG = [
    { id: 'dashboard', label: 'Dashboard' },
    { id: 'deals', label: 'Deals' },
    { id: 'activities', label: 'Activities & Tasks' },
    { id: 'investors', label: 'Investors/Grants/Competition' },
    { id: 'partnerships', label: 'Partnerships' },
    { id: 'internal', label: 'Internal' },
    { id: 'externalContacts', label: 'External Contacts' },
    { id: 'network', label: 'Network Graph' },
    { id: 'ai', label: 'SalesActivity' },
    { id: 'competitors', label: 'Competitor' },
    { id: 'usecases', label: 'Use-cases' },
    { id: 'gtm', label: 'GTMStrategy' },
    { id: 'roi', label: 'ROI Calculator' },
    { id: 'discovery', label: 'Discovery Q&A' },
];

function AdminUsersPanel({ callerEmail }) {
    const [users, setUsers] = React.useState([]);
    const [internalTeam, setInternalTeam] = React.useState([]);
    const [loading, setLoading] = React.useState(true);
    const [newEmail, setNewEmail] = React.useState('');
    const [newTabs, setNewTabs] = React.useState([]);
    const [saving, setSaving] = React.useState(false);
    const API_BASE = process.env.REACT_APP_API_URL || '/api';

    const fetchUsers = async () => {
        setLoading(true);
        try {
            const r = await fetch(`${API_BASE}/team-access?callerEmail=${encodeURIComponent(callerEmail)}`, {
                headers: { 'x-user-email': callerEmail }
            });
            const data = await r.json();
            setUsers(Array.isArray(data) ? data : []);
        } catch (e) {
            console.error('Fetch users failed:', e);
        } finally { setLoading(false); }
    };

    const fetchInternalTeam = async () => {
        try {
            const r = await fetch(`${API_BASE}/internal`);
            const data = await r.json();
            setInternalTeam(Array.isArray(data) ? data.filter(p => p.Email) : []);
        } catch (e) {
            console.error('Fetch internal team failed:', e);
        }
    };

    React.useEffect(() => {
        fetchUsers();
        fetchInternalTeam();
        /* eslint-disable-next-line */
    }, []);

    const addUser = async () => {
        if (!newEmail.trim()) { alert('Enter an email'); return; }
        setSaving(true);
        try {
            await fetch(`${API_BASE}/team-access`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', 'x-user-email': callerEmail },
                body: JSON.stringify({ callerEmail, email: newEmail.trim(), allowedTabs: newTabs }),
            });
            setNewEmail(''); setNewTabs([]);
            await fetchUsers();
        } catch (e) { alert('Failed: ' + e.message); }
        finally { setSaving(false); }
    };

    const updateUser = async (user, allowedTabs) => {
        try {
            await fetch(`${API_BASE}/team-access/${user.Id}`, {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json', 'x-user-email': callerEmail },
                body: JSON.stringify({ callerEmail, allowedTabs, isActive: true }),
            });
            await fetchUsers();
        } catch (e) { alert('Update failed: ' + e.message); }
    };

    const deleteUser = async (user) => {
        if (!window.confirm(`Remove ${user.Email}'s access?`)) return;
        try {
            await fetch(`${API_BASE}/team-access/${user.Id}?callerEmail=${encodeURIComponent(callerEmail)}`, {
                method: 'DELETE',
                headers: { 'x-user-email': callerEmail },
            });
            await fetchUsers();
        } catch (e) { alert('Delete failed: ' + e.message); }
    };

    const toggleNewTab = (id) =>
        setNewTabs(newTabs.includes(id) ? newTabs.filter(t => t !== id) : [...newTabs, id]);

    return (
        <div style={{ padding: 20 }}>
            <h2 style={{ marginBottom: 8 }}>⚙ User Access Management</h2>
            <p style={{ color: '#6b7280', marginBottom: 24, fontSize: 14 }}>
                Add users by email and check which tabs they can access. Removing a user revokes all access.
            </p>

            {/* Add new user card */}
            <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 12, padding: 20, marginBottom: 24, boxShadow: '0 1px 2px rgba(0,0,0,0.04)' }}>
                <div style={{ fontWeight: 700, marginBottom: 12 }}>Add New User</div>
                <div style={{ display: 'flex', gap: 8, marginBottom: 12 }}>
                    <input
                        value={newEmail}
                        onChange={e => setNewEmail(e.target.value)}
                        placeholder="Type a name or email — suggestions from Internal team"
                        type="email"
                        list="internal-team-emails"
                        style={{ flex: 1, padding: '8px 12px', border: '1px solid #e5e7eb', borderRadius: 8, fontSize: 13 }}
                    />
                    <datalist id="internal-team-emails">
                        {internalTeam.map(p => (
                            <option key={p.Id} value={p.Email}>{p.Name}</option>
                        ))}
                    </datalist>
                    <button onClick={addUser} disabled={saving} style={{ padding: '8px 18px', background: '#2563eb', color: '#fff', border: 'none', borderRadius: 8, fontWeight: 600, cursor: 'pointer', fontSize: 13 }}>
                        {saving ? 'Adding…' : '+ Add'}
                    </button>
                </div>
                {/* Clone-from dropdown */}
                {users.length > 0 && (
                    <div style={{ marginBottom: 12, display: 'flex', alignItems: 'center', gap: 8 }}>
                        <label style={{ fontSize: 12, color: '#6b7280', fontWeight: 600 }}>Clone permissions from:</label>
                        <select onChange={e => {
                            if (!e.target.value) { setNewTabs([]); return; }
                            const src = users.find(u => String(u.Id) === e.target.value);
                            if (src) setNewTabs(src.AllowedTabs || []);
                        }} style={{ padding: '6px 10px', border: '1px solid #e5e7eb', borderRadius: 6, fontSize: 12, background: '#fff' }}>
                            <option value="">— select user —</option>
                            {users.map(u => <option key={u.Id} value={u.Id}>{u.Email}</option>)}
                        </select>
                        {newTabs.length > 0 && (
                            <button onClick={() => setNewTabs([])} style={{ padding: '4px 10px', background: '#f3f4f6', border: '1px solid #e5e7eb', borderRadius: 6, fontSize: 11, cursor: 'pointer', color: '#6b7280' }}>Clear</button>
                        )}
                    </div>
                )}
                <div style={{ fontSize: 11, color: '#6b7280', textTransform: 'uppercase', letterSpacing: 0.8, marginBottom: 8 }}>Tabs they can access</div>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                    {TAB_CATALOG.map(t => (
                        <label key={t.id} style={{ display: 'flex', alignItems: 'center', gap: 4, padding: '4px 10px', border: `1px solid ${newTabs.includes(t.id) ? '#2563eb' : '#e5e7eb'}`, borderRadius: 6, fontSize: 12, cursor: 'pointer', background: newTabs.includes(t.id) ? 'rgba(37,99,235,0.08)' : '#fff' }}>
                            <input type="checkbox" checked={newTabs.includes(t.id)} onChange={() => toggleNewTab(t.id)} style={{ margin: 0 }} />
                            {t.label}
                        </label>
                    ))}
                </div>
            </div>

            {/* User list */}
            <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 12, boxShadow: '0 1px 2px rgba(0,0,0,0.04)' }}>
                {loading && <div style={{ padding: 20, color: '#6b7280' }}>Loading…</div>}
                {!loading && users.length === 0 && <div style={{ padding: 20, color: '#9ca3af', fontSize: 13 }}>No users added yet.</div>}
                {!loading && users.map(u => (
                    <UserRow key={u.Id} user={u} onSave={(tabs) => updateUser(u, tabs)} onDelete={() => deleteUser(u)} />
                ))}
            </div>
        </div>
    );
}

function UserRow({ user, onSave, onDelete }) {
    const [tabs, setTabs] = React.useState(user.AllowedTabs || []);
    const [dirty, setDirty] = React.useState(false);

    const toggle = (id) => {
        const next = tabs.includes(id) ? tabs.filter(t => t !== id) : [...tabs, id];
        setTabs(next);
        setDirty(true);
    };

    const save = async () => {
        await onSave(tabs);
        setDirty(false);
    };

    return (
        <div style={{ padding: 16, borderBottom: '1px solid #f3f4f6' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
                <div style={{ fontWeight: 600 }}>{user.Email}</div>
                <div style={{ display: 'flex', gap: 8 }}>
                    {dirty && <button onClick={save} style={{ padding: '5px 12px', background: '#10b981', color: '#fff', border: 'none', borderRadius: 6, fontSize: 12, cursor: 'pointer', fontWeight: 600 }}>Save</button>}
                    <button onClick={onDelete} style={{ padding: '5px 12px', background: '#fff', border: '1px solid #fecaca', color: '#dc2626', borderRadius: 6, fontSize: 12, cursor: 'pointer' }}>🗑 Remove</button>
                </div>
            </div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                {TAB_CATALOG.map(t => (
                    <label key={t.id} style={{ display: 'flex', alignItems: 'center', gap: 4, padding: '3px 8px', border: `1px solid ${tabs.includes(t.id) ? '#2563eb' : '#e5e7eb'}`, borderRadius: 5, fontSize: 11, cursor: 'pointer', background: tabs.includes(t.id) ? 'rgba(37,99,235,0.08)' : '#fff' }}>
                        <input type="checkbox" checked={tabs.includes(t.id)} onChange={() => toggle(t.id)} style={{ margin: 0 }} />
                        {t.label}
                    </label>
                ))}
            </div>
        </div>
    );
}

// ─────────────────────────────────────────────────────────────────────────────
// Activities tab wrapper — adds a Tasks | Events sub-toggle on top of the
// existing ActivitiesView. Events live under Activities because they're the
// same conceptual bucket (things the team does day-to-day).
// ─────────────────────────────────────────────────────────────────────────────
function ActivitiesTab({ activitiesProps, showToast, internalTeam }) {
    const [subTab, setSubTab] = React.useState('tasks');

    const tabStyle = (active) => ({
        padding: '10px 18px', fontSize: 13, fontWeight: 600,
        color: active ? '#0c2340' : '#64748b',
        cursor: 'pointer', borderBottom: `2px solid ${active ? '#2563eb' : 'transparent'}`,
        marginBottom: -1, background: 'transparent', border: 'none',
        borderBottomWidth: 2, borderBottomStyle: 'solid',
    });

    return (
        <div>
            <div style={{ display: 'flex', gap: 6, borderBottom: '1px solid #e5e7eb', padding: '0 20px', marginBottom: 16, marginTop: 0 }}>
                <button onClick={() => setSubTab('tasks')} style={tabStyle(subTab === 'tasks')}>Tasks</button>
                <button onClick={() => setSubTab('events')} style={tabStyle(subTab === 'events')}>Events</button>
            </div>
            {subTab === 'tasks'
                ? <ActivitiesView {...activitiesProps} />
                : <EventsView showToast={showToast} internalTeam={internalTeam} />}
        </div>
    );
}

// ─────────────────────────────────────────────────────────────────────────────
// EventsView — events grid (tiles) + drill-into-event detail view.
// Holds its own state for list vs detail and the meetings of the active event.
// ─────────────────────────────────────────────────────────────────────────────
function EventsView({ showToast, internalTeam }) {
    const API_BASE = process.env.REACT_APP_API_URL || '/api';
    const [events, setEvents] = React.useState([]);
    const [loading, setLoading] = React.useState(true);
    const [selectedEvent, setSelectedEvent] = React.useState(null); // null = list view
    const [meetings, setMeetings] = React.useState([]);
    const [meetingsLoading, setMeetingsLoading] = React.useState(false);
    const [showEventModal, setShowEventModal] = React.useState(false);
    const [editingEvent, setEditingEvent] = React.useState(null);
    const [showMeetingModal, setShowMeetingModal] = React.useState(false);
    const [editingMeeting, setEditingMeeting] = React.useState(null);
    const [statusFilter, setStatusFilter] = React.useState('all');
    const [search, setSearch] = React.useState('');

    const fetchEvents = React.useCallback(async () => {
        setLoading(true);
        try {
            const r = await fetch(`${API_BASE}/events`);
            const data = await r.json();
            setEvents(Array.isArray(data) ? data : []);
        } catch (e) { console.error('Fetch events failed:', e); }
        finally { setLoading(false); }
    }, [API_BASE]);

    const fetchMeetings = React.useCallback(async (eventId) => {
        setMeetingsLoading(true);
        try {
            const r = await fetch(`${API_BASE}/meetings?eventId=${eventId}`);
            const data = await r.json();
            setMeetings(Array.isArray(data) ? data : []);
        } catch (e) { console.error('Fetch meetings failed:', e); }
        finally { setMeetingsLoading(false); }
    }, [API_BASE]);

    React.useEffect(() => { fetchEvents(); }, [fetchEvents]);
    React.useEffect(() => {
        if (selectedEvent) fetchMeetings(selectedEvent.Id);
    }, [selectedEvent, fetchMeetings]);

    const saveEvent = async (payload) => {
        const method = editingEvent ? 'PUT' : 'POST';
        const url = editingEvent ? `${API_BASE}/events/${editingEvent.Id}` : `${API_BASE}/events`;
        try {
            const r = await fetch(url, {
                method,
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(payload),
            });
            if (!r.ok) throw new Error('Save failed');
            await fetchEvents();
            // If editing the currently open event, refresh its header
            if (editingEvent && selectedEvent && selectedEvent.Id === editingEvent.Id) {
                const updated = await r.json();
                setSelectedEvent(updated);
            }
            setShowEventModal(false);
            setEditingEvent(null);
            showToast && showToast(editingEvent ? 'Event updated' : 'Event created');
        } catch (e) { alert('Failed to save event: ' + e.message); }
    };

    const deleteEvent = async (event) => {
        if (!window.confirm(`Delete "${event.Name}"? Its meetings will be kept (unlinked).`)) return;
        try {
            const r = await fetch(`${API_BASE}/events/${event.Id}`, { method: 'DELETE' });
            if (!r.ok && r.status !== 204) throw new Error('Delete failed');
            await fetchEvents();
            if (selectedEvent && selectedEvent.Id === event.Id) setSelectedEvent(null);
            showToast && showToast('Event deleted');
        } catch (e) { alert('Failed to delete event: ' + e.message); }
    };

    const saveMeeting = async (payload) => {
        const method = editingMeeting ? 'PUT' : 'POST';
        const url = editingMeeting ? `${API_BASE}/meetings/${editingMeeting.Id}` : `${API_BASE}/meetings`;
        try {
            const r = await fetch(url, {
                method,
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(payload),
            });
            if (!r.ok) throw new Error('Save failed');
            if (selectedEvent) await fetchMeetings(selectedEvent.Id);
            await fetchEvents(); // refresh meeting counts on tiles
            setShowMeetingModal(false);
            setEditingMeeting(null);
            showToast && showToast(editingMeeting ? 'Meeting updated' : 'Meeting added');
        } catch (e) { alert('Failed to save meeting: ' + e.message); }
    };

    const deleteMeeting = async (m) => {
        if (!window.confirm(`Delete the meeting "${m.Title}"?`)) return;
        try {
            const r = await fetch(`${API_BASE}/meetings/${m.Id}`, { method: 'DELETE' });
            if (!r.ok && r.status !== 204) throw new Error('Delete failed');
            if (selectedEvent) await fetchMeetings(selectedEvent.Id);
            await fetchEvents();
            showToast && showToast('Meeting deleted');
        } catch (e) { alert('Failed to delete meeting: ' + e.message); }
    };

    // ── Detail view ──
    if (selectedEvent) {
        const e = selectedEvent;
        const fmt = (d) => d ? new Date(d).toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' }) : '';
        const dateRange = e.StartDate && e.EndDate
            ? `${fmt(e.StartDate)} → ${fmt(e.EndDate)}`
            : (fmt(e.StartDate) || fmt(e.EndDate) || '—');

        return (
            <div style={{ padding: '0 20px 20px' }}>
                <div style={{ fontSize: 13, color: '#64748b', marginBottom: 12 }}>
                    <span onClick={() => setSelectedEvent(null)} style={{ color: '#2563eb', cursor: 'pointer' }}>← All events</span>
                    <span style={{ margin: '0 8px', color: '#cbd5e1' }}>/</span>
                    <span>{e.Name}</span>
                </div>

                <div style={{ background: 'linear-gradient(135deg,#0c2340 0%,#1e40af 100%)', color: '#fff', padding: '22px 24px', borderRadius: 12, marginBottom: 18 }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 16 }}>
                        <div>
                            <h1 style={{ margin: 0, fontSize: 22 }}>{e.Name}</h1>
                            <div style={{ marginTop: 4, fontSize: 13, opacity: 0.85 }}>{e.Location || '—'} · {dateRange}</div>
                            {e.AttendedBy && <div style={{ marginTop: 6, fontSize: 12, opacity: 0.85 }}>Attended by: {e.AttendedBy}</div>}
                        </div>
                        <div style={{ display: 'flex', gap: 8 }}>
                            <button onClick={() => { setEditingEvent(e); setShowEventModal(true); }} style={{ background: 'rgba(255,255,255,0.15)', color: '#fff', border: '1px solid rgba(255,255,255,0.3)', padding: '7px 14px', borderRadius: 7, fontSize: 12, fontWeight: 600, cursor: 'pointer' }}>Edit</button>
                            <button onClick={() => deleteEvent(e)} style={{ background: 'transparent', color: '#fecaca', border: '1px solid rgba(254,202,202,0.4)', padding: '7px 14px', borderRadius: 7, fontSize: 12, fontWeight: 600, cursor: 'pointer' }}>Delete</button>
                        </div>
                    </div>
                </div>

                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
                    <div style={{ fontSize: 13, fontWeight: 600, color: '#475569' }}>
                        {meetings.length} meeting{meetings.length === 1 ? '' : 's'} at this event
                    </div>
                    <button onClick={() => { setEditingMeeting(null); setShowMeetingModal(true); }} style={{ background: '#0c2340', backgroundImage: 'linear-gradient(135deg,#0c2340 0%,#1e40af 60%,#2563eb 100%)', color: '#fff', border: 'none', padding: '9px 16px', borderRadius: 7, fontSize: 13, fontWeight: 600, cursor: 'pointer', boxShadow: '0 2px 6px rgba(12,35,64,0.15)' }}>+ Add meeting</button>
                </div>

                {meetingsLoading ? (
                    <div style={{ padding: 30, textAlign: 'center', color: '#64748b' }}>Loading meetings…</div>
                ) : meetings.length === 0 ? (
                    <div style={{ padding: 40, textAlign: 'center', color: '#9ca3af', background: '#fff', borderRadius: 12, border: '1px dashed #e5e7eb' }}>
                        No meetings logged yet. Click <strong>+ Add meeting</strong> to log the first one.
                    </div>
                ) : (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                        {meetings.map(m => (
                            <MeetingCard
                                key={m.Id}
                                meeting={m}
                                onEdit={() => { setEditingMeeting(m); setShowMeetingModal(true); }}
                                onDelete={() => deleteMeeting(m)}
                            />
                        ))}
                    </div>
                )}

                {showEventModal && (
                    <EventModal
                        event={editingEvent}
                        onClose={() => { setShowEventModal(false); setEditingEvent(null); }}
                        onSave={saveEvent}
                    />
                )}
                {showMeetingModal && (
                    <MeetingModal
                        meeting={editingMeeting}
                        events={events}
                        defaultEventId={selectedEvent.Id}
                        internalTeam={internalTeam}
                        onClose={() => { setShowMeetingModal(false); setEditingMeeting(null); }}
                        onSave={saveMeeting}
                    />
                )}
            </div>
        );
    }

    // ── List view (events grid) ──
    const today = new Date(); today.setHours(0, 0, 0, 0);
    const statusOf = (ev) => {
        const start = ev.StartDate ? new Date(ev.StartDate) : null;
        const end = ev.EndDate ? new Date(ev.EndDate) : start;
        if (!start) return 'past';
        if (start > today) return 'upcoming';
        if (end >= today) return 'live';
        return 'past';
    };

    const filtered = events.filter(ev => {
        if (statusFilter !== 'all' && statusOf(ev) !== statusFilter) return false;
        if (search && !ev.Name.toLowerCase().includes(search.toLowerCase())) return false;
        return true;
    });

    const fmt = (d) => d ? new Date(d).toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' }) : '';

    return (
        <div style={{ padding: '0 20px 20px' }}>
            <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginBottom: 18, alignItems: 'center' }}>
                <input
                    type="text"
                    value={search}
                    onChange={e => setSearch(e.target.value)}
                    placeholder="Search events…"
                    style={{ padding: '8px 12px', border: '1px solid #cbd5e1', borderRadius: 6, fontSize: 13, minWidth: 240, background: '#fff' }}
                />
                <select value={statusFilter} onChange={e => setStatusFilter(e.target.value)} style={{ padding: '8px 12px', border: '1px solid #cbd5e1', borderRadius: 6, fontSize: 13, background: '#fff' }}>
                    <option value="all">All events</option>
                    <option value="upcoming">Upcoming</option>
                    <option value="live">Live now</option>
                    <option value="past">Past</option>
                </select>
                <button
                    onClick={() => { setEditingEvent(null); setShowEventModal(true); }}
                    style={{ marginLeft: 'auto', background: '#0c2340', backgroundImage: 'linear-gradient(135deg,#0c2340 0%,#1e40af 60%,#2563eb 100%)', color: '#fff', border: 'none', padding: '9px 16px', borderRadius: 7, fontSize: 13, fontWeight: 600, cursor: 'pointer', boxShadow: '0 2px 6px rgba(12,35,64,0.15)' }}
                >+ New event</button>
            </div>

            {loading ? (
                <div style={{ padding: 40, textAlign: 'center', color: '#64748b' }}>Loading events…</div>
            ) : filtered.length === 0 ? (
                <div style={{ padding: 40, textAlign: 'center', color: '#9ca3af', background: '#fff', borderRadius: 12, border: '1px dashed #e5e7eb' }}>
                    {events.length === 0
                        ? <>No events yet. Click <strong>+ New event</strong> to add a conference, summit, or expo.</>
                        : 'No events match your filters.'}
                </div>
            ) : (
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: 14 }}>
                    {filtered.map(ev => {
                        const s = statusOf(ev);
                        const pill = s === 'upcoming'
                            ? { bg: '#dbeafe', fg: '#1e40af', label: 'Upcoming' }
                            : s === 'live'
                                ? { bg: '#fef3c7', fg: '#92400e', label: 'Live now' }
                                : { bg: '#e2e8f0', fg: '#475569', label: 'Past' };
                        const dateRange = ev.StartDate && ev.EndDate && ev.StartDate !== ev.EndDate
                            ? `${fmt(ev.StartDate)} → ${fmt(ev.EndDate)}`
                            : (fmt(ev.StartDate) || fmt(ev.EndDate) || '—');
                        return (
                            <div
                                key={ev.Id}
                                onClick={() => setSelectedEvent(ev)}
                                style={{ border: '1px solid #e5e7eb', borderRadius: 12, padding: 18, background: '#fff', cursor: 'pointer', transition: 'border-color .15s, box-shadow .15s' }}
                                onMouseEnter={e => { e.currentTarget.style.borderColor = '#93c5fd'; e.currentTarget.style.boxShadow = '0 4px 12px rgba(37,99,235,0.08)'; }}
                                onMouseLeave={e => { e.currentTarget.style.borderColor = '#e5e7eb'; e.currentTarget.style.boxShadow = 'none'; }}
                            >
                                <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 8 }}>
                                    <div style={{ fontSize: 15, fontWeight: 700, color: '#0c2340', lineHeight: 1.3 }}>{ev.Name}</div>
                                    <span style={{ background: pill.bg, color: pill.fg, fontSize: 10, fontWeight: 600, padding: '2px 8px', borderRadius: 999, textTransform: 'uppercase', letterSpacing: 0.4, whiteSpace: 'nowrap' }}>{pill.label}</span>
                                </div>
                                <div style={{ marginTop: 8, fontSize: 12, color: '#64748b', lineHeight: 1.6 }}>
                                    {ev.Location || '—'}<br />
                                    {dateRange}
                                </div>
                                <div style={{ display: 'flex', gap: 12, marginTop: 12, fontSize: 12, color: '#475569' }}>
                                    <span><span style={{ fontWeight: 700, color: '#0c2340' }}>{ev.MeetingsCount || 0}</span> meetings</span>
                                    <span><span style={{ fontWeight: 700, color: '#0c2340' }}>{ev.FollowupsCount || 0}</span> follow-ups</span>
                                    <span><span style={{ fontWeight: 700, color: '#0c2340' }}>{ev.ClosedCount || 0}</span> closed</span>
                                </div>
                            </div>
                        );
                    })}
                </div>
            )}

            {showEventModal && (
                <EventModal
                    event={editingEvent}
                    onClose={() => { setShowEventModal(false); setEditingEvent(null); }}
                    onSave={saveEvent}
                />
            )}
        </div>
    );
}

// ─────────────────────────────────────────────────────────────────────────────
// MeetingCard — a single meeting row inside the event detail view.
// ─────────────────────────────────────────────────────────────────────────────
function MeetingCard({ meeting, onEdit, onDelete }) {
    const [expanded, setExpanded] = React.useState(false);
    const m = meeting;
    const fmt = (d) => d ? new Date(d).toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' }) : '';
    const statusPill = (s) => {
        const map = {
            'New':          { bg: '#dbeafe', fg: '#1e40af' },
            'In Follow-up': { bg: '#fef3c7', fg: '#92400e' },
            'Closed':       { bg: '#d1fae5', fg: '#166534' },
        };
        const p = map[s] || { bg: '#e2e8f0', fg: '#475569' };
        return <span style={{ background: p.bg, color: p.fg, fontSize: 10, fontWeight: 600, padding: '2px 8px', borderRadius: 999, textTransform: 'uppercase', letterSpacing: 0.4 }}>{s || 'New'}</span>;
    };

    return (
        <div style={{ border: '1px solid #e5e7eb', borderRadius: 10, padding: '14px 16px', background: '#fff' }}>
            <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: 12 }}>
                <div style={{ fontSize: 14, fontWeight: 700, color: '#0c2340' }}>{m.Title}</div>
                <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
                    {statusPill(m.Status)}
                    <span style={{ fontSize: 12, color: '#64748b', whiteSpace: 'nowrap' }}>{fmt(m.Date)}</span>
                </div>
            </div>
            <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginTop: 6, fontSize: 12, color: '#475569' }}>
                {m.Owner && <><span>Owner: <strong style={{ color: '#0c2340' }}>{m.Owner}</strong></span><span style={{ color: '#cbd5e1' }}>·</span></>}
                {m.Attendees && <><span>{m.Attendees}</span><span style={{ color: '#cbd5e1' }}>·</span></>}
                {m.Sector && <span>{m.Sector}</span>}
            </div>
            {m.Summary && (
                <div style={{ marginTop: 8, fontSize: 12, color: '#475569', lineHeight: 1.5 }}>{m.Summary}</div>
            )}
            {expanded && (
                <div style={{ marginTop: 10, padding: '10px 12px', background: '#f8fafc', borderRadius: 6, fontSize: 12, color: '#0f172a', whiteSpace: 'pre-wrap', lineHeight: 1.5 }}>
                    {m.FullNotes && <><strong>Notes:</strong><br />{m.FullNotes}<br /><br /></>}
                    {m.ActionItems && <><strong>Action items:</strong><br />{m.ActionItems}<br /><br /></>}
                    {m.Tags && <><strong>Tags:</strong> {m.Tags}</>}
                </div>
            )}
            <div style={{ marginTop: 8, display: 'flex', gap: 8 }}>
                {(m.FullNotes || m.ActionItems || m.Tags) && (
                    <button onClick={() => setExpanded(!expanded)} style={{ background: 'transparent', color: '#2563eb', border: 'none', cursor: 'pointer', fontSize: 12, fontWeight: 600, padding: 0 }}>
                        {expanded ? '− Less' : '+ More'}
                    </button>
                )}
                <button onClick={onEdit} style={{ marginLeft: 'auto', background: '#fff', color: '#0c2340', border: '1px solid #cbd5e1', padding: '5px 12px', borderRadius: 6, fontSize: 12, fontWeight: 600, cursor: 'pointer' }}>Edit</button>
                <button onClick={onDelete} style={{ background: '#fff', color: '#b91c1c', border: '1px solid #fecaca', padding: '5px 12px', borderRadius: 6, fontSize: 12, fontWeight: 600, cursor: 'pointer' }}>Delete</button>
            </div>
        </div>
    );
}

// ─────────────────────────────────────────────────────────────────────────────
// EventModal — add/edit an event.
// ─────────────────────────────────────────────────────────────────────────────
function EventModal({ event, onClose, onSave }) {
    const [name, setName] = React.useState(event?.Name || '');
    const [location, setLocation] = React.useState(event?.Location || '');
    const [startDate, setStartDate] = React.useState(event?.StartDate ? event.StartDate.slice(0, 10) : '');
    const [endDate, setEndDate] = React.useState(event?.EndDate ? event.EndDate.slice(0, 10) : '');
    const [attendedBy, setAttendedBy] = React.useState(event?.AttendedBy || '');
    const [notes, setNotes] = React.useState(event?.Notes || '');
    const [saving, setSaving] = React.useState(false);

    const submit = async (e) => {
        e.preventDefault();
        if (!name.trim()) { alert('Event name is required'); return; }
        setSaving(true);
        try {
            await onSave({
                name: name.trim(),
                location: location.trim() || null,
                startDate: startDate || null,
                endDate: endDate || null,
                attendedBy: attendedBy.trim() || null,
                notes: notes.trim() || null,
            });
        } finally { setSaving(false); }
    };

    return (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.45)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 9999, padding: 20 }} onClick={onClose}>
            <form onSubmit={submit} onClick={e => e.stopPropagation()} style={{ background: '#fff', borderRadius: 12, maxWidth: 720, width: '100%', boxShadow: '0 20px 50px rgba(12,35,64,0.18)', overflow: 'hidden', maxHeight: '90vh', display: 'flex', flexDirection: 'column' }}>
                <div style={{ padding: '16px 22px', borderBottom: '1px solid #e5e7eb', display: 'flex', justifyContent: 'space-between', alignItems: 'center', background: '#f8fafc' }}>
                    <div style={{ fontSize: 16, fontWeight: 700, color: '#0c2340' }}>{event ? 'Edit event' : 'New event'}</div>
                    <button type="button" onClick={onClose} style={{ background: 'transparent', border: 'none', fontSize: 20, color: '#64748b', cursor: 'pointer' }}>×</button>
                </div>
                <div style={{ padding: 22, display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14, overflow: 'auto' }}>
                    <div style={{ gridColumn: '1 / span 2', display: 'flex', flexDirection: 'column', gap: 4 }}>
                        <label style={{ fontSize: 11, fontWeight: 600, color: '#64748b', textTransform: 'uppercase', letterSpacing: 0.4 }}>Event name *</label>
                        <input value={name} onChange={e => setName(e.target.value)} placeholder="e.g. Finovate Fall 2026" style={{ padding: '8px 10px', border: '1px solid #cbd5e1', borderRadius: 6, fontSize: 13 }} autoFocus />
                    </div>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                        <label style={{ fontSize: 11, fontWeight: 600, color: '#64748b', textTransform: 'uppercase', letterSpacing: 0.4 }}>Location</label>
                        <input value={location} onChange={e => setLocation(e.target.value)} placeholder="City, Country" style={{ padding: '8px 10px', border: '1px solid #cbd5e1', borderRadius: 6, fontSize: 13 }} />
                    </div>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                        <label style={{ fontSize: 11, fontWeight: 600, color: '#64748b', textTransform: 'uppercase', letterSpacing: 0.4 }}>Attended by</label>
                        <input value={attendedBy} onChange={e => setAttendedBy(e.target.value)} placeholder="Names, comma-separated" style={{ padding: '8px 10px', border: '1px solid #cbd5e1', borderRadius: 6, fontSize: 13 }} />
                    </div>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                        <label style={{ fontSize: 11, fontWeight: 600, color: '#64748b', textTransform: 'uppercase', letterSpacing: 0.4 }}>Start date</label>
                        <input type="date" value={startDate} onChange={e => setStartDate(e.target.value)} style={{ padding: '8px 10px', border: '1px solid #cbd5e1', borderRadius: 6, fontSize: 13 }} />
                    </div>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                        <label style={{ fontSize: 11, fontWeight: 600, color: '#64748b', textTransform: 'uppercase', letterSpacing: 0.4 }}>End date</label>
                        <input type="date" value={endDate} onChange={e => setEndDate(e.target.value)} style={{ padding: '8px 10px', border: '1px solid #cbd5e1', borderRadius: 6, fontSize: 13 }} />
                    </div>
                    <div style={{ gridColumn: '1 / span 2', display: 'flex', flexDirection: 'column', gap: 4 }}>
                        <label style={{ fontSize: 11, fontWeight: 600, color: '#64748b', textTransform: 'uppercase', letterSpacing: 0.4 }}>Notes</label>
                        <textarea value={notes} onChange={e => setNotes(e.target.value)} placeholder="Anything relevant about this event…" style={{ padding: '8px 10px', border: '1px solid #cbd5e1', borderRadius: 6, fontSize: 13, minHeight: 80, fontFamily: 'inherit', resize: 'vertical' }} />
                    </div>
                </div>
                <div style={{ padding: '14px 22px', borderTop: '1px solid #e5e7eb', display: 'flex', justifyContent: 'flex-end', gap: 8, background: '#f8fafc' }}>
                    <button type="button" onClick={onClose} style={{ background: '#fff', color: '#0c2340', border: '1px solid #cbd5e1', padding: '9px 16px', borderRadius: 7, fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>Cancel</button>
                    <button type="submit" disabled={saving} style={{ background: '#0c2340', backgroundImage: 'linear-gradient(135deg,#0c2340 0%,#1e40af 60%,#2563eb 100%)', color: '#fff', border: 'none', padding: '9px 18px', borderRadius: 7, fontSize: 13, fontWeight: 600, cursor: 'pointer', boxShadow: '0 2px 6px rgba(12,35,64,0.15)' }}>
                        {saving ? 'Saving…' : (event ? 'Save changes' : 'Create event')}
                    </button>
                </div>
            </form>
        </div>
    );
}

// ─────────────────────────────────────────────────────────────────────────────
// MeetingModal — add/edit a meeting. defaultEventId pre-selects the event when
// opened from inside an event's detail view.
// ─────────────────────────────────────────────────────────────────────────────
function MeetingModal({ meeting, events, defaultEventId, internalTeam, onClose, onSave }) {
    const [eventId, setEventId]         = React.useState(meeting?.EventId ?? defaultEventId ?? '');
    const [title, setTitle]             = React.useState(meeting?.Title || '');
    const [date, setDate]               = React.useState(meeting?.Date ? meeting.Date.slice(0, 10) : '');
    const [owner, setOwner]             = React.useState(meeting?.Owner || '');
    const [attendees, setAttendees]     = React.useState(meeting?.Attendees || '');
    const [sector, setSector]           = React.useState(meeting?.Sector || '');
    const [summary, setSummary]         = React.useState(meeting?.Summary || '');
    const [fullNotes, setFullNotes]     = React.useState(meeting?.FullNotes || '');
    const [actionItems, setActionItems] = React.useState(meeting?.ActionItems || '');
    const [status, setStatus]           = React.useState(meeting?.Status || 'New');
    const [tags, setTags]               = React.useState(meeting?.Tags || '');
    const [saving, setSaving] = React.useState(false);

    const submit = async (e) => {
        e.preventDefault();
        if (!title.trim()) { alert('Title is required'); return; }
        setSaving(true);
        try {
            await onSave({
                eventId: eventId ? Number(eventId) : null,
                title: title.trim(),
                date: date || null,
                owner: owner.trim() || null,
                attendees: attendees.trim() || null,
                sector: sector.trim() || null,
                summary: summary.trim() || null,
                fullNotes: fullNotes.trim() || null,
                actionItems: actionItems.trim() || null,
                status,
                tags: tags.trim() || null,
            });
        } finally { setSaving(false); }
    };

    return (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.45)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 9999, padding: 20 }} onClick={onClose}>
            <form onSubmit={submit} onClick={e => e.stopPropagation()} style={{ background: '#fff', borderRadius: 12, maxWidth: 760, width: '100%', boxShadow: '0 20px 50px rgba(12,35,64,0.18)', overflow: 'hidden', maxHeight: '92vh', display: 'flex', flexDirection: 'column' }}>
                <div style={{ padding: '16px 22px', borderBottom: '1px solid #e5e7eb', display: 'flex', justifyContent: 'space-between', alignItems: 'center', background: '#f8fafc' }}>
                    <div style={{ fontSize: 16, fontWeight: 700, color: '#0c2340' }}>{meeting ? 'Edit meeting' : 'New meeting'}</div>
                    <button type="button" onClick={onClose} style={{ background: 'transparent', border: 'none', fontSize: 20, color: '#64748b', cursor: 'pointer' }}>×</button>
                </div>
                <div style={{ padding: 22, display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14, overflow: 'auto' }}>
                    <div style={{ gridColumn: '1 / span 2', display: 'flex', flexDirection: 'column', gap: 4 }}>
                        <label style={{ fontSize: 11, fontWeight: 600, color: '#64748b', textTransform: 'uppercase', letterSpacing: 0.4 }}>Title *</label>
                        <input value={title} onChange={e => setTitle(e.target.value)} placeholder="e.g. HSBC — risk team chat on KYC pilot" style={{ padding: '8px 10px', border: '1px solid #cbd5e1', borderRadius: 6, fontSize: 13 }} autoFocus />
                    </div>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                        <label style={{ fontSize: 11, fontWeight: 600, color: '#64748b', textTransform: 'uppercase', letterSpacing: 0.4 }}>Event</label>
                        <select value={eventId || ''} onChange={e => setEventId(e.target.value)} style={{ padding: '8px 10px', border: '1px solid #cbd5e1', borderRadius: 6, fontSize: 13, background: '#fff' }}>
                            <option value="">— No event —</option>
                            {(events || []).map(ev => <option key={ev.Id} value={ev.Id}>{ev.Name}</option>)}
                        </select>
                    </div>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                        <label style={{ fontSize: 11, fontWeight: 600, color: '#64748b', textTransform: 'uppercase', letterSpacing: 0.4 }}>Date</label>
                        <input type="date" value={date} onChange={e => setDate(e.target.value)} style={{ padding: '8px 10px', border: '1px solid #cbd5e1', borderRadius: 6, fontSize: 13 }} />
                    </div>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                        <label style={{ fontSize: 11, fontWeight: 600, color: '#64748b', textTransform: 'uppercase', letterSpacing: 0.4 }}>Our team (Owner)</label>
                        <input value={owner} onChange={e => setOwner(e.target.value)} list="meeting-owner-list" placeholder="Who from our side" style={{ padding: '8px 10px', border: '1px solid #cbd5e1', borderRadius: 6, fontSize: 13 }} />
                        <datalist id="meeting-owner-list">
                            {(internalTeam || []).map(p => <option key={p.Id} value={p.Name}>{p.Email}</option>)}
                        </datalist>
                    </div>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                        <label style={{ fontSize: 11, fontWeight: 600, color: '#64748b', textTransform: 'uppercase', letterSpacing: 0.4 }}>Status</label>
                        <select value={status} onChange={e => setStatus(e.target.value)} style={{ padding: '8px 10px', border: '1px solid #cbd5e1', borderRadius: 6, fontSize: 13, background: '#fff' }}>
                            <option>New</option>
                            <option>In Follow-up</option>
                            <option>Closed</option>
                        </select>
                    </div>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                        <label style={{ fontSize: 11, fontWeight: 600, color: '#64748b', textTransform: 'uppercase', letterSpacing: 0.4 }}>Sector</label>
                        <input value={sector} onChange={e => setSector(e.target.value)} placeholder="e.g. Banking, FinCrime" style={{ padding: '8px 10px', border: '1px solid #cbd5e1', borderRadius: 6, fontSize: 13 }} />
                    </div>
                    <div style={{ gridColumn: '1 / span 2', display: 'flex', flexDirection: 'column', gap: 4 }}>
                        <label style={{ fontSize: 11, fontWeight: 600, color: '#64748b', textTransform: 'uppercase', letterSpacing: 0.4 }}>Attendees (their side)</label>
                        <input value={attendees} onChange={e => setAttendees(e.target.value)} placeholder="Names, roles, comma-separated" style={{ padding: '8px 10px', border: '1px solid #cbd5e1', borderRadius: 6, fontSize: 13 }} />
                    </div>
                    <div style={{ gridColumn: '1 / span 2', display: 'flex', flexDirection: 'column', gap: 4 }}>
                        <label style={{ fontSize: 11, fontWeight: 600, color: '#64748b', textTransform: 'uppercase', letterSpacing: 0.4 }}>Summary (one-liner)</label>
                        <input value={summary} onChange={e => setSummary(e.target.value)} placeholder="What was the meeting really about" style={{ padding: '8px 10px', border: '1px solid #cbd5e1', borderRadius: 6, fontSize: 13 }} />
                    </div>
                    <div style={{ gridColumn: '1 / span 2', display: 'flex', flexDirection: 'column', gap: 4 }}>
                        <label style={{ fontSize: 11, fontWeight: 600, color: '#64748b', textTransform: 'uppercase', letterSpacing: 0.4 }}>Full notes</label>
                        <textarea value={fullNotes} onChange={e => setFullNotes(e.target.value)} placeholder="Detailed notes from the meeting…" style={{ padding: '8px 10px', border: '1px solid #cbd5e1', borderRadius: 6, fontSize: 13, minHeight: 100, fontFamily: 'inherit', resize: 'vertical' }} />
                    </div>
                    <div style={{ gridColumn: '1 / span 2', display: 'flex', flexDirection: 'column', gap: 4 }}>
                        <label style={{ fontSize: 11, fontWeight: 600, color: '#64748b', textTransform: 'uppercase', letterSpacing: 0.4 }}>Action items</label>
                        <textarea value={actionItems} onChange={e => setActionItems(e.target.value)} placeholder="What needs to happen next…" style={{ padding: '8px 10px', border: '1px solid #cbd5e1', borderRadius: 6, fontSize: 13, minHeight: 70, fontFamily: 'inherit', resize: 'vertical' }} />
                    </div>
                    <div style={{ gridColumn: '1 / span 2', display: 'flex', flexDirection: 'column', gap: 4 }}>
                        <label style={{ fontSize: 11, fontWeight: 600, color: '#64748b', textTransform: 'uppercase', letterSpacing: 0.4 }}>Tags</label>
                        <input value={tags} onChange={e => setTags(e.target.value)} placeholder="comma-separated" style={{ padding: '8px 10px', border: '1px solid #cbd5e1', borderRadius: 6, fontSize: 13 }} />
                    </div>
                </div>
                <div style={{ padding: '14px 22px', borderTop: '1px solid #e5e7eb', display: 'flex', justifyContent: 'flex-end', gap: 8, background: '#f8fafc' }}>
                    <button type="button" onClick={onClose} style={{ background: '#fff', color: '#0c2340', border: '1px solid #cbd5e1', padding: '9px 16px', borderRadius: 7, fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>Cancel</button>
                    <button type="submit" disabled={saving} style={{ background: '#0c2340', backgroundImage: 'linear-gradient(135deg,#0c2340 0%,#1e40af 60%,#2563eb 100%)', color: '#fff', border: 'none', padding: '9px 18px', borderRadius: 7, fontSize: 13, fontWeight: 600, cursor: 'pointer', boxShadow: '0 2px 6px rgba(12,35,64,0.15)' }}>
                        {saving ? 'Saving…' : (meeting ? 'Save changes' : 'Add meeting')}
                    </button>
                </div>
            </form>
        </div>
    );
}

export default CRMApp;